
      import { PROTOCOL_VERSION } from "./shared/protocol.ts";
      import { createQuestionMediaFinder } from "./tv/questionMedia.ts";
      import { cupcakeStackLayout } from "./tv/cupcakeLayout.ts";
      import {
        FixedRateSnapshotRenderer,
        interpolateSnapshotPosition,
      } from "./tv/fixedRateSnapshotRenderer.ts";
      import {
        ClientPerformanceSampler,
        exceedsTvPresentationBudget,
      } from "./shared/clientPerformance.ts";
      import { gameCatalog, gameDefinition } from "./shared/gameCatalog.ts";
      import { fuseBeatStage, fuseBeatWindowMs } from "./shared/fuseRunners.ts";
      import {
        recordRunwayRendererDiagnostic,
        shouldUseRunwayWebGl2,
      } from "./tv/runwayRendererPolicy.ts";
      import { runwayDepthCue, runwayPerspectiveCue } from "./tv/runwayVisualDepth.ts";
      import { joinQrDataUrl } from "./shared/joinQr.ts";

      window.onerror = function (message, source, lineno, colno, error) {
        var msgStr = String(message || "");
        var srcStr = String(source || "");
        if (msgStr.indexOf("Script error") !== -1 || !source) {
          return false;
        }
        if (
          srcStr.indexOf("cloudflareinsights.com") !== -1 ||
          srcStr.indexOf("beacon.min.js") !== -1
        ) {
          return false;
        }
        if (
          srcStr.indexOf("http") === 0 &&
          srcStr.indexOf("pretheory.com") === -1 &&
          srcStr.indexOf("localhost") === -1
        ) {
          return false;
        }

        var errDiv = document.getElementById("tv-fatal-error");
        if (!errDiv) {
          errDiv = document.createElement("div");
          errDiv.id = "tv-fatal-error";
          errDiv.setAttribute("data-testid", "tv-fatal-error");
          errDiv.setAttribute("role", "alert");
          document.body.appendChild(errDiv);
        }
        errDiv.style.position = "fixed";
        errDiv.style.bottom = "0";
        errDiv.style.left = "0";
        errDiv.style.right = "0";
        errDiv.style.background = "rgba(220, 38, 38, 0.95)";
        errDiv.style.color = "#ffffff";
        errDiv.style.padding = "16px";
        errDiv.style.paddingRight = "64px";
        errDiv.style.fontFamily = "monospace";
        errDiv.style.fontSize = "16px";
        errDiv.style.zIndex = "999999";
        errDiv.style.maxHeight = "300px";
        errDiv.style.overflowY = "auto";
        errDiv.style.wordBreak = "break-all";

        var escMsg = String(message || "")
          .split("&")
          .join("&amp;")
          .split("<")
          .join("&lt;")
          .split(">")
          .join("&gt;");

        var escSrc = String(source || "")
          .split("&")
          .join("&amp;")
          .split("<")
          .join("&lt;")
          .split(">")
          .join("&gt;");

        var escStack =
          error && error.stack
            ? String(error.stack)
                .split("&")
                .join("&amp;")
                .split("<")
                .join("&lt;")
                .split(">")
                .join("&gt;")
            : "";

        errDiv.innerHTML =
          "<strong>JS Error:</strong> " +
          escMsg +
          "<br/>Source: " +
          escSrc +
          ":" +
          lineno +
          ":" +
          colno +
          (escStack ? "<br/>Stack: " + escStack : "");
        var dismissButton = document.createElement("button");
        dismissButton.type = "button";
        dismissButton.textContent = "Close";
        dismissButton.setAttribute("aria-label", "Dismiss TV error");
        dismissButton.style.position = "absolute";
        dismissButton.style.top = "8px";
        dismissButton.style.right = "8px";
        dismissButton.style.padding = "8px 12px";
        dismissButton.style.border = "2px solid #ffffff";
        dismissButton.style.borderRadius = "6px";
        dismissButton.style.background = "#7f1d1d";
        dismissButton.style.color = "#ffffff";
        dismissButton.style.fontWeight = "700";
        dismissButton.addEventListener("click", function () {
          if (errDiv.parentNode) errDiv.parentNode.removeChild(errDiv);
        });
        errDiv.appendChild(dismissButton);
        return false;
      };

      (function () {
        var SoundFx = function () {
          this.ctx = null;
          this.racingEngine = null;
          this.racingEngineGain = null;
          this.runwayEngine = null;
          this.runwayEngineGain = null;
          this.fuseMusicNextAt = 0;
          try {
            this.muted = localStorage.getItem("3dtanks.tv.muted") === "true";
            this.fuseSfxVolume = Number(localStorage.getItem("3dtanks.fuse.sfxVolume") || 0.8);
            this.fuseMusicVolume = Number(localStorage.getItem("3dtanks.fuse.musicVolume") || 0.45);
          } catch (_e) {
            this.muted = false;
            this.fuseSfxVolume = 0.8;
            this.fuseMusicVolume = 0.45;
          }
        };

        SoundFx.prototype.initCtx = function () {
          if (this.muted) return null;
          if (!this.ctx) {
            var AudioContextClass = window.AudioContext || window.webkitAudioContext;
            this.ctx = new AudioContextClass();
          }
          if (this.ctx.state === "suspended") {
            this.ctx.resume();
          }
          return this.ctx;
        };

        SoundFx.prototype.setMuted = function (muted) {
          this.muted = Boolean(muted);
          if (this.muted) {
            this.stopRacingEngine();
            this.stopRunwayEngine();
          }
          try {
            localStorage.setItem("3dtanks.tv.muted", this.muted ? "true" : "false");
          } catch (_e) {}
          if (!this.muted) {
            try {
              this.initCtx();
            } catch (_e) {}
          }
        };

        SoundFx.prototype.setFuseVolume = function (kind, value) {
          var clamped = Math.max(0, Math.min(1, Number(value) || 0));
          if (kind === "music") this.fuseMusicVolume = clamped;
          else this.fuseSfxVolume = clamped;
          try {
            localStorage.setItem(
              kind === "music" ? "3dtanks.fuse.musicVolume" : "3dtanks.fuse.sfxVolume",
              String(clamped),
            );
          } catch (_e) {}
          if (clamped > 0) {
            try {
              this.initCtx();
            } catch (_e) {}
          }
        };

        SoundFx.prototype.fuseOutput = function (ctx, volume, pan) {
          var gain = ctx.createGain();
          gain.gain.value = volume * this.fuseSfxVolume;
          if (ctx.createStereoPanner) {
            var panner = ctx.createStereoPanner();
            panner.pan.value = Math.max(-0.85, Math.min(0.85, pan || 0));
            gain.connect(panner);
            panner.connect(ctx.destination);
          } else {
            gain.connect(ctx.destination);
          }
          return gain;
        };

        SoundFx.prototype.playFuseTone = function (
          frequency,
          endFrequency,
          duration,
          volume,
          pan,
          wave,
        ) {
          try {
            if (this.fuseSfxVolume <= 0) return;
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            var osc = ctx.createOscillator();
            var envelope = ctx.createGain();
            var output = this.fuseOutput(ctx, volume, pan);
            osc.type = wave || "triangle";
            osc.frequency.setValueAtTime(frequency, now);
            osc.frequency.exponentialRampToValueAtTime(Math.max(10, endFrequency), now + duration);
            envelope.gain.setValueAtTime(0.001, now);
            envelope.gain.linearRampToValueAtTime(1, now + Math.min(0.018, duration * 0.2));
            envelope.gain.exponentialRampToValueAtTime(0.001, now + duration);
            osc.connect(envelope);
            envelope.connect(output);
            osc.start(now);
            osc.stop(now + duration + 0.01);
          } catch (_e) {}
        };

        SoundFx.prototype.playFuseNoise = function (duration, volume, pan, filterFrequency) {
          try {
            if (this.fuseSfxVolume <= 0) return;
            var ctx = this.initCtx();
            var length = Math.max(1, Math.floor(ctx.sampleRate * duration));
            var buffer = ctx.createBuffer(1, length, ctx.sampleRate);
            var data = buffer.getChannelData(0);
            for (var index = 0; index < length; index += 1) {
              data[index] = (Math.random() * 2 - 1) * Math.pow(1 - index / length, 1.8);
            }
            var source = ctx.createBufferSource();
            var filter = ctx.createBiquadFilter();
            var envelope = ctx.createGain();
            var output = this.fuseOutput(ctx, volume, pan);
            filter.type = "lowpass";
            filter.frequency.value = filterFrequency;
            envelope.gain.setValueAtTime(1, ctx.currentTime);
            envelope.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
            source.buffer = buffer;
            source.connect(filter);
            filter.connect(envelope);
            envelope.connect(output);
            source.start();
          } catch (_e) {}
        };

        SoundFx.prototype.playFuseBombPlaced = function (pan) {
          this.playFuseTone(118, 72, 0.11, 0.15, pan, "square");
          this.playFuseTone(620, 390, 0.07, 0.045, pan, "triangle");
        };

        SoundFx.prototype.playFuseExplosion = function (pan) {
          this.playFuseNoise(0.42, 0.22, pan, 980);
          this.playFuseTone(92, 24, 0.38, 0.24, pan, "sawtooth");
          this.playFuseTone(460, 85, 0.16, 0.06, pan, "square");
        };

        SoundFx.prototype.playFusePowerUp = function (pan) {
          var sound = this;
          this.playFuseTone(440, 660, 0.11, 0.055, pan, "sine");
          window.setTimeout(function () {
            sound.playFuseTone(660, 990, 0.16, 0.06, pan, "sine");
          }, 70);
        };

        SoundFx.prototype.playFuseBeat = function (beat, pan) {
          var frequencies = [520, 690, 920];
          var volumes = [0.045, 0.058, 0.075];
          var index = Math.max(0, Math.min(2, beat - 1));
          this.playFuseTone(
            frequencies[index],
            frequencies[index] * 0.78,
            0.075 + index * 0.018,
            volumes[index],
            pan,
            beat === 3 ? "square" : "triangle",
          );
        };

        SoundFx.prototype.playFuseBreakable = function (pan) {
          this.playFuseNoise(0.16, 0.07, pan, 1900);
          this.playFuseTone(210, 95, 0.12, 0.04, pan, "square");
        };

        SoundFx.prototype.playFuseChainReaction = function (pan) {
          this.playFuseTone(760, 1240, 0.1, 0.055, pan, "sawtooth");
        };

        SoundFx.prototype.playFuseKickImpact = function (pan) {
          this.playFuseTone(145, 82, 0.13, 0.085, pan, "square");
          this.playFuseTone(680, 980, 0.18, 0.05, pan, "triangle");
        };

        SoundFx.prototype.playFuseHazardWarning = function (arenaId) {
          var frequency = arenaId === "cinder-crown" ? 180 : arenaId === "tidal-engine" ? 260 : 340;
          this.playFuseTone(frequency, frequency * 1.7, 0.24, 0.065, 0, "square");
          this.playFuseTone(frequency * 1.5, frequency, 0.3, 0.035, 0, "triangle");
        };

        SoundFx.prototype.playFuseHandoff = function (pan) {
          this.playFuseTone(360, 720, 0.16, 0.05, pan, "triangle");
          this.playFuseTone(720, 1040, 0.2, 0.045, pan, "sine");
        };

        SoundFx.prototype.playFuseShield = function (pan) {
          this.playFuseTone(980, 320, 0.22, 0.07, pan, "sine");
          this.playFuseTone(510, 760, 0.18, 0.045, pan, "triangle");
        };

        SoundFx.prototype.playFuseDefeat = function (pan) {
          this.playFuseTone(330, 92, 0.48, 0.09, pan, "sawtooth");
          this.playFuseNoise(0.2, 0.065, pan, 1500);
        };

        SoundFx.prototype.playFuseRoundCue = function (go) {
          this.playFuseTone(go ? 440 : 260, go ? 880 : 220, go ? 0.28 : 0.11, 0.07, 0, "square");
        };

        SoundFx.prototype.playFuseRoundWin = function () {
          this.playFuseTone(520, 780, 0.26, 0.06, 0, "triangle");
        };

        SoundFx.prototype.playFuseVictory = function () {
          var sound = this;
          var notes = [392, 523.25, 659.25, 783.99];
          for (var index = 0; index < notes.length; index += 1) {
            (function (note, delay) {
              window.setTimeout(function () {
                sound.playFuseTone(note, note * 1.04, 0.24, 0.065, 0, "triangle");
              }, delay);
            })(notes[index], index * 90);
          }
        };

        SoundFx.prototype.updateFuseMusic = function (snapshot) {
          if (
            this.muted ||
            this.fuseMusicVolume <= 0 ||
            snapshot.phase !== "battle" ||
            Date.now() < this.fuseMusicNextAt
          ) {
            return;
          }
          try {
            var ctx = this.initCtx();
            var urgent = snapshot.timeRemainingMs <= 20000;
            var interval = urgent ? 240 : snapshot.timeRemainingMs <= 45000 ? 360 : 480;
            this.fuseMusicNextAt = Date.now() + interval;
            var now = ctx.currentTime;
            var beat = Math.floor(Date.now() / interval);
            var notes = [110, 146.83, 164.81, 146.83];
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = beat % 4 === 0 ? "square" : "triangle";
            osc.frequency.setValueAtTime(notes[beat % notes.length], now);
            gain.gain.setValueAtTime((beat % 4 === 0 ? 0.026 : 0.015) * this.fuseMusicVolume, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now);
            osc.stop(now + 0.11);
            if (urgent && beat % 2 === 1) {
              var tick = ctx.createOscillator();
              var tickGain = ctx.createGain();
              tick.type = "square";
              tick.frequency.value = 1240;
              tickGain.gain.setValueAtTime(0.012 * this.fuseMusicVolume, now);
              tickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.035);
              tick.connect(tickGain);
              tickGain.connect(ctx.destination);
              tick.start(now);
              tick.stop(now + 0.04);
            }
          } catch (_e) {}
        };

        SoundFx.prototype.playShoot = function () {
          try {
            var ctx = this.initCtx();
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();

            osc.type = "sawtooth";
            osc.frequency.setValueAtTime(600, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.12);

            gain.gain.setValueAtTime(0.04, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start();
            osc.stop(ctx.currentTime + 0.12);
          } catch (_e) {}
        };

        SoundFx.prototype.playKill = function () {
          try {
            var ctx = this.initCtx();
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();

            osc.type = "triangle";
            osc.frequency.setValueAtTime(160, ctx.currentTime);
            osc.frequency.linearRampToValueAtTime(10, ctx.currentTime + 0.22);

            gain.gain.setValueAtTime(0.18, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start();
            osc.stop(ctx.currentTime + 0.22);
          } catch (_e) {}
        };

        SoundFx.prototype.playPlayerDowned = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;

            var playNote = function (freq, start, duration) {
              var osc = ctx.createOscillator();
              var gain = ctx.createGain();
              osc.type = "sawtooth";
              osc.frequency.setValueAtTime(freq, start);
              gain.gain.setValueAtTime(0.08, start);
              gain.gain.exponentialRampToValueAtTime(0.001, start + duration - 0.01);
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(start);
              osc.stop(start + duration);
            };

            playNote(300, now, 0.15);
            playNote(220, now + 0.15, 0.15);
            playNote(180, now + 0.3, 0.3);
          } catch (_e) {}
        };

        SoundFx.prototype.playPlayerRevived = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;

            var playNote = function (freq, start, duration) {
              var osc = ctx.createOscillator();
              var gain = ctx.createGain();
              osc.type = "sine";
              osc.frequency.setValueAtTime(freq, start);
              gain.gain.setValueAtTime(0.06, start);
              gain.gain.exponentialRampToValueAtTime(0.001, start + duration - 0.01);
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(start);
              osc.stop(start + duration);
            };

            playNote(220, now, 0.1);
            playNote(330, now + 0.1, 0.1);
            playNote(440, now + 0.2, 0.2);
          } catch (_e) {}
        };

        SoundFx.prototype.playPowerUp = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;

            var playNote = function (freq, start, duration) {
              var osc = ctx.createOscillator();
              var gain = ctx.createGain();
              osc.type = "sine";
              osc.frequency.setValueAtTime(freq, start);
              gain.gain.setValueAtTime(0.05, start);
              gain.gain.exponentialRampToValueAtTime(0.001, start + duration - 0.01);
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(start);
              osc.stop(start + duration);
            };

            playNote(261.63, now, 0.06); // C4
            playNote(329.63, now + 0.06, 0.06); // E4
            playNote(392.0, now + 0.12, 0.06); // G4
            playNote(523.25, now + 0.18, 0.18); // C5
          } catch (_e) {}
        };

        SoundFx.prototype.playDungeonAttack = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = "square";
            osc.frequency.setValueAtTime(145, now);
            osc.frequency.exponentialRampToValueAtTime(65, now + 0.075);
            gain.gain.setValueAtTime(0.025, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.075);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now);
            osc.stop(now + 0.08);
          } catch (_e) {}
        };

        SoundFx.prototype.playDungeonMagic = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(210, now);
            osc.frequency.exponentialRampToValueAtTime(720, now + 0.22);
            gain.gain.setValueAtTime(0.055, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now);
            osc.stop(now + 0.31);
          } catch (_e) {}
        };

        SoundFx.prototype.playDungeonRift = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            for (var tone = 0; tone < 2; tone += 1) {
              var osc = ctx.createOscillator();
              var gain = ctx.createGain();
              osc.type = tone === 0 ? "sawtooth" : "triangle";
              osc.frequency.setValueAtTime(tone === 0 ? 120 : 75, now);
              osc.frequency.exponentialRampToValueAtTime(24, now + 0.42);
              gain.gain.setValueAtTime(tone === 0 ? 0.045 : 0.065, now);
              gain.gain.exponentialRampToValueAtTime(0.001, now + 0.44);
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(now);
              osc.stop(now + 0.45);
            }
          } catch (_e) {}
        };

        SoundFx.prototype.playDungeonCoin = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            for (var note = 0; note < 2; note += 1) {
              var osc = ctx.createOscillator();
              var gain = ctx.createGain();
              osc.type = "sine";
              osc.frequency.setValueAtTime(note === 0 ? 880 : 1320, now + note * 0.055);
              gain.gain.setValueAtTime(0.035, now + note * 0.055);
              gain.gain.exponentialRampToValueAtTime(0.001, now + note * 0.055 + 0.09);
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(now + note * 0.055);
              osc.stop(now + note * 0.055 + 0.1);
            }
          } catch (_e) {}
        };

        SoundFx.prototype.playChime = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;

            var playNote = function (freq, start, duration) {
              var osc = ctx.createOscillator();
              var gain = ctx.createGain();
              osc.type = "sine";
              osc.frequency.setValueAtTime(freq, start);
              gain.gain.setValueAtTime(0.05, start);
              gain.gain.exponentialRampToValueAtTime(0.001, start + duration - 0.01);
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(start);
              osc.stop(start + duration);
            };

            playNote(880, now, 0.08);
            playNote(1320, now + 0.08, 0.15);
          } catch (_e) {}
        };

        SoundFx.prototype.playRacingCountdown = function (go) {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = "square";
            osc.frequency.setValueAtTime(go ? 880 : 440, now);
            if (go) osc.frequency.exponentialRampToValueAtTime(1320, now + 0.16);
            gain.gain.setValueAtTime(go ? 0.055 : 0.035, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + (go ? 0.22 : 0.1));
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now);
            osc.stop(now + (go ? 0.23 : 0.11));
          } catch (_e) {}
        };

        SoundFx.prototype.playRacingNotes = function (notes, spacing, volume) {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            for (var index = 0; index < notes.length; index += 1) {
              var start = now + index * spacing;
              var osc = ctx.createOscillator();
              var gain = ctx.createGain();
              osc.type = "triangle";
              osc.frequency.setValueAtTime(notes[index], start);
              gain.gain.setValueAtTime(volume, start);
              gain.gain.exponentialRampToValueAtTime(0.001, start + spacing + 0.06);
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(start);
              osc.stop(start + spacing + 0.07);
            }
          } catch (_e) {}
        };

        SoundFx.prototype.playRacingPickup = function () {
          this.playRacingNotes([660, 990], 0.07, 0.04);
        };

        SoundFx.prototype.playRacingHit = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = "sawtooth";
            osc.frequency.setValueAtTime(150, now);
            osc.frequency.exponentialRampToValueAtTime(45, now + 0.18);
            gain.gain.setValueAtTime(0.07, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now);
            osc.stop(now + 0.21);
          } catch (_e) {}
        };

        SoundFx.prototype.playRacingFinish = function () {
          this.playRacingNotes([523.25, 659.25, 783.99, 1046.5], 0.1, 0.05);
        };

        SoundFx.prototype.playRacingUpgrade = function () {
          this.playRacingNotes([330, 440, 660], 0.08, 0.04);
        };

        SoundFx.prototype.updateRacingEngine = function (speedRatio, active) {
          if (!active) {
            this.stopRacingEngine();
            return;
          }
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            if (!this.racingEngine || !this.racingEngineGain) {
              this.racingEngine = ctx.createOscillator();
              this.racingEngineGain = ctx.createGain();
              var filter = ctx.createBiquadFilter();
              this.racingEngine.type = "sawtooth";
              filter.type = "lowpass";
              filter.frequency.value = 520;
              this.racingEngineGain.gain.value = 0.001;
              this.racingEngine.connect(filter);
              filter.connect(this.racingEngineGain);
              this.racingEngineGain.connect(ctx.destination);
              this.racingEngine.start();
            }
            var speed = Math.max(0, Math.min(1, speedRatio));
            this.racingEngine.frequency.setTargetAtTime(72 + speed * 150, now, 0.04);
            this.racingEngineGain.gain.setTargetAtTime(0.012 + speed * 0.025, now, 0.05);
          } catch (_e) {}
        };

        SoundFx.prototype.stopRacingEngine = function () {
          if (this.racingEngine) {
            try {
              this.racingEngine.stop();
            } catch (_e) {}
          }
          this.racingEngine = null;
          this.racingEngineGain = null;
        };

        SoundFx.prototype.updateRunwayEngine = function (speedRatio, active) {
          if (this.muted || !active) {
            this.stopRunwayEngine();
            return;
          }
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            if (!this.runwayEngine || !this.runwayEngineGain) {
              this.runwayEngine = ctx.createOscillator();
              this.runwayEngineGain = ctx.createGain();
              var filter = ctx.createBiquadFilter();
              this.runwayEngine.type = "triangle";
              filter.type = "lowpass";
              filter.frequency.value = 360;
              this.runwayEngineGain.gain.value = 0.001;
              this.runwayEngine.connect(filter);
              filter.connect(this.runwayEngineGain);
              this.runwayEngineGain.connect(ctx.destination);
              this.runwayEngine.start();
            }
            var speed = Math.max(0, Math.min(1.35, speedRatio));
            this.runwayEngine.frequency.setTargetAtTime(82 + speed * 76, now, 0.12);
            this.runwayEngineGain.gain.setTargetAtTime(0.008 + speed * 0.009, now, 0.16);
          } catch (_e) {}
        };

        SoundFx.prototype.stopRunwayEngine = function () {
          if (this.runwayEngine) {
            try {
              this.runwayEngine.stop();
            } catch (_e) {}
          }
          this.runwayEngine = null;
          this.runwayEngineGain = null;
        };

        SoundFx.prototype.playRunwayReady = function () {
          this.playRacingNotes([392, 523.25], 0.09, 0.022);
        };

        SoundFx.prototype.playRunwayTouchdown = function (softness) {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = "triangle";
            osc.frequency.setValueAtTime(softness === "hard" ? 105 : 82, now);
            osc.frequency.exponentialRampToValueAtTime(48, now + 0.18);
            gain.gain.setValueAtTime(softness === "hard" ? 0.045 : 0.026, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now);
            osc.stop(now + 0.21);
          } catch (_e) {}
        };

        SoundFx.prototype.playRunwayBounce = function () {
          this.playRacingNotes([180, 145], 0.08, 0.028);
        };

        SoundFx.prototype.playRunwayResult = function (finale) {
          this.playRacingNotes(
            finale ? [523.25, 659.25, 783.99] : [440, 554.37],
            finale ? 0.1 : 0.09,
            finale ? 0.032 : 0.025,
          );
        };

        SoundFx.prototype.playMysteryClock = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            var notes = [659.25, 523.25, 783.99];
            for (var index = 0; index < notes.length; index += 1) {
              var osc = ctx.createOscillator();
              var gain = ctx.createGain();
              osc.type = "sine";
              osc.frequency.setValueAtTime(notes[index], now + index * 0.17);
              gain.gain.setValueAtTime(0.025, now + index * 0.17);
              gain.gain.exponentialRampToValueAtTime(0.001, now + index * 0.17 + 0.42);
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(now + index * 0.17);
              osc.stop(now + index * 0.17 + 0.44);
            }
          } catch (_e) {}
        };

        SoundFx.prototype.playMysteryPaper = function () {
          try {
            var ctx = this.initCtx();
            var length = Math.floor(ctx.sampleRate * 0.18);
            var buffer = ctx.createBuffer(1, length, ctx.sampleRate);
            var data = buffer.getChannelData(0);
            for (var index = 0; index < length; index += 1) {
              data[index] = (Math.random() * 2 - 1) * (1 - index / length);
            }
            var source = ctx.createBufferSource();
            var filter = ctx.createBiquadFilter();
            var gain = ctx.createGain();
            filter.type = "bandpass";
            filter.frequency.value = 1800;
            gain.gain.value = 0.018;
            source.buffer = buffer;
            source.connect(filter);
            filter.connect(gain);
            gain.connect(ctx.destination);
            source.start();
          } catch (_e) {}
        };

        SoundFx.prototype.playMysteryGasp = function () {
          try {
            var ctx = this.initCtx();
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = "triangle";
            osc.frequency.setValueAtTime(180, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(420, ctx.currentTime + 0.25);
            gain.gain.setValueAtTime(0.001, ctx.currentTime);
            gain.gain.linearRampToValueAtTime(0.025, ctx.currentTime + 0.08);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.32);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.34);
          } catch (_e) {}
        };

        SoundFx.prototype.playMysterySting = function () {
          try {
            var ctx = this.initCtx();
            var now = ctx.currentTime;
            var notes = [261.63, 329.63, 392, 523.25];
            for (var index = 0; index < notes.length; index += 1) {
              var osc = ctx.createOscillator();
              var gain = ctx.createGain();
              osc.type = index === notes.length - 1 ? "sine" : "triangle";
              osc.frequency.setValueAtTime(notes[index], now + index * 0.11);
              gain.gain.setValueAtTime(0.03, now + index * 0.11);
              gain.gain.exponentialRampToValueAtTime(0.001, now + index * 0.11 + 0.6);
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(now + index * 0.11);
              osc.stop(now + index * 0.11 + 0.62);
            }
          } catch (_e) {}
        };

        SoundFx.prototype.scheduleColorEchoSignal = function (signal, start, durationMs) {
          try {
            var ctx = this.initCtx();
            if (!ctx) return null;
            var frequencies = { red: 261.63, yellow: 329.63, green: 392, blue: 523.25 };
            var frequency = frequencies[signal];
            if (!frequency) return null;
            var safeStart = Math.max(ctx.currentTime + 0.005, start);
            var attackEnd = safeStart + 0.01;
            var releaseEnd = Math.max(attackEnd + 0.05, safeStart + durationMs / 1000);
            var releaseStart = Math.max(attackEnd, releaseEnd - 0.05);
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(frequency, safeStart);
            gain.gain.setValueAtTime(0, safeStart);
            gain.gain.linearRampToValueAtTime(0.045, attackEnd);
            gain.gain.setValueAtTime(0.045, releaseStart);
            gain.gain.linearRampToValueAtTime(0, releaseEnd);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(safeStart);
            osc.stop(releaseEnd + 0.01);
            return { startsAt: safeStart, endsAt: releaseEnd };
          } catch (_e) {
            return null;
          }
        };

        var sfx = new SoundFx();
        var socket = null;
        var pendingSocketMessages = [];
        var performanceSampler = new ClientPerformanceSampler();
        var performanceFrameRequest = null;
        var roomCode = roomFromUrl();
        var tvId = loadTvId();
        var connectionStatus = "offline";
        var latestSnapshot = null;
        var latestTriviaSnapshot = null;
        var latestQuickDrawSnapshot = null;
        var latestColorEchoSnapshot = null;
        var latestCupcakeStackSnapshot = null;
        var latestCritterCrossingSnapshot = null;
        var latestFuseRunnersSnapshot = null;
        var colorEchoRenderKey = "";
        var latestMysterySnapshot = null;
        var latestPlatformSnapshot = null;
        var latestRoshamboSnapshot = null;
        var previousRoshamboPhase = null;
        var lastRoshamboCountdownSec = null;
        var previousRoshamboRoundId = "";
        var tvCanControlRoom = false;
        var tvLaunchMode = "watch";
        var fixedGameChoice = null;
        var previousMysteryPhase = null;
        var previousMysteryChapter = 0;
        var previousMysteryRevealSignature = "";
        var previousMysterySuspicionCount = 0;
        var triviaMediaQuestionId = null;
        var triviaMedia = null;
        var questionMediaFinder = createQuestionMediaFinder();
        var lastEventId = 0;
        var lastDungeonEventId = 0;
        var lastDungeonAttackSoundAt = 0;
        var lastRacingEventId = 0;
        var previousRacingPhase = null;
        var previousRacingCountdown = 0;
        var previousRacingRaceIndex = 0;
        var lastRacingHudAt = 0;
        var racingTrackLayer = null;
        var racingTrackLayerKey = "";
        var critterBoardLayer = null;
        var critterBoardLayerKey = "";
        var fireflyGardenLayer = null;
        var fireflyGardenLayerKey = "";
        var cupcakeBackdropLayer = null;
        var cupcakeBackdropLayerKey = "";
        var racingOffsetX = 0;
        var racingOffsetZ = 0;
        var racingScale = 1;
        var racingPerPlayerById = new Map();
        var racingSharedById = new Map();
        var lastDungeonHudAt = 0;
        var dungeonHudRenderKey = "";
        var dungeonRoomLayer = null;
        var dungeonRoomLayerKey = "";
        var dungeonRoomLayerSnapshot = null;
        var latestDungeonSnapshot = null;
        var dungeonEffects = [];
        var previousDungeonEnemies = new Map();
        var previousDungeonRifts = new Map();
        var previousDungeonHeroes = new Map();
        var previousDungeonPickups = new Map();
        var lastTankHudAt = 0;
        var tankOffsetX = 0;
        var tankOffsetY = 0;
        var tankScale = 1;
        var tankArenaWidth = 1;
        var tankPowerUpColors = {
          repair: "#22c55e",
          rapidFire: "#ffd447",
          shield: "#57e8ff",
          spreadShot: "#ff65e5",
          heavyShells: "#ff6b35",
          overdrive: "#8cff3d",
          shockwave: "#a78bfa",
          chaos: "#c084fc",
        };
        var previousProjectiles = new Map();
        var impactRings = [];
        var canvas = document.getElementById("render-canvas");
        var primaryCanvasContext = canvas.getContext("2d", { alpha: false });
        var root = document.getElementById("ui-root");
        var tvShellRail = document.getElementById("tv-shell-rail");
        var tvShellTitle = document.getElementById("tv-shell-title");
        var tvShellRoom = document.getElementById("tv-shell-room");
        var tvShellGameInfo = document.getElementById("tv-shell-game-info");
        var tvShellInvite = document.getElementById("tv-shell-invite");
        var tvShellGameType = "";
        var TV_GAME_FRAME_INTERVAL_MS = 1000 / 30;
        var tankSnapshotRenderer = new FixedRateSnapshotRenderer({
          frameIntervalMs: TV_GAME_FRAME_INTERVAL_MS,
          onRender: function (snapshot, now) {
            renderTankFrame(snapshot, now);
            if (snapshot.team.state === "gameOver" || snapshot.team.state === "victory")
              tankSnapshotRenderer.stop();
          },
          requestFrame: function (callback) {
            return window.requestAnimationFrame(callback);
          },
          cancelFrame: function (requestId) {
            window.cancelAnimationFrame(requestId);
          },
          now: function () {
            return performance.now();
          },
          onPerformanceSample: function (sample) {
            performanceSampler.observePresentation(sample);
          },
        });
        var racingSnapshotRenderer = new FixedRateSnapshotRenderer({
          frameIntervalMs: TV_GAME_FRAME_INTERVAL_MS,
          onRender: function (snapshot, now) {
            renderRacingFrame(snapshot, now);
            if (snapshot.phase !== "racing") racingSnapshotRenderer.stop();
          },
          requestFrame: function (callback) {
            return window.requestAnimationFrame(callback);
          },
          cancelFrame: function (requestId) {
            window.cancelAnimationFrame(requestId);
          },
          now: function () {
            return performance.now();
          },
          onPerformanceSample: function (sample) {
            performanceSampler.observePresentation(sample);
          },
        });
        var dungeonSnapshotRenderer = new FixedRateSnapshotRenderer({
          frameIntervalMs: TV_GAME_FRAME_INTERVAL_MS,
          onRender: function (snapshot, now) {
            renderDungeonFrame(snapshot, now);
            if (snapshot.phase === "completed") dungeonSnapshotRenderer.stop();
          },
          requestFrame: function (callback) {
            return window.requestAnimationFrame(callback);
          },
          cancelFrame: function (requestId) {
            window.cancelAnimationFrame(requestId);
          },
          now: function () {
            return performance.now();
          },
          onPerformanceSample: function (sample) {
            performanceSampler.observePresentation(sample);
          },
        });
        var tvShareJoinPanel = null;
        var tvShareJoinQr = null;
        var tvShareJoinQrUrl = "";
        var tvShareJoinInput = null;
        var tvShareJoinStatus = null;
        var tvChangeGameButton = null;
        var lastSessionsList = [];
        var attractTimer = null;
        var attractIndex = 0;
        var attractGames = [];
        var catalogGames = gameCatalog();
        for (var catalogIndex = 0; catalogIndex < catalogGames.length; catalogIndex += 1) {
          var catalogGame = catalogGames[catalogIndex];
          if (catalogGame.variants.length === 0) {
            attractGames.push({ gameType: catalogGame.id });
          } else {
            for (
              var variantIndex = 0;
              variantIndex < catalogGame.variants.length;
              variantIndex += 1
            ) {
              attractGames.push({
                gameType: catalogGame.id,
                variantId: catalogGame.variants[variantIndex].id,
              });
            }
          }
        }

        var ColorEchoPlaybackScheduler = function () {
          this.roundId = "";
          this.epochSeconds = 0;
          this.scheduled = {};
          this.cues = [];
          this.frame = null;
          this.activeSignal = "";
        };

        ColorEchoPlaybackScheduler.prototype.clockNow = function () {
          if (this.audioClock && sfx.ctx && sfx.ctx.state === "running") return sfx.ctx.currentTime;
          return window.performance.now() / 1000;
        };

        ColorEchoPlaybackScheduler.prototype.update = function (snapshot) {
          if (snapshot.roundId !== this.roundId) {
            this.reset();
            try {
              var ctx = sfx.initCtx();
              this.audioClock = Boolean(ctx && ctx.state === "running");
            } catch (_error) {
              this.audioClock = false;
            }
            var now = this.clockNow();
            this.roundId = snapshot.roundId || "";
            this.epochSeconds = now - Number(snapshot.playbackPositionMs || 0) / 1000;
          }
          var cues = snapshot.playbackCues || [];
          for (var index = 0; index < cues.length; index += 1) {
            var cue = cues[index];
            var key = String(cue.cueId);
            if (this.scheduled[key]) continue;
            var startsAt = this.epochSeconds + Number(cue.startsAtPlaybackMs || 0) / 1000;
            this.scheduled[key] = true;
            var scheduledInterval = this.audioClock
              ? sfx.scheduleColorEchoSignal(cue.signal, startsAt, Number(cue.durationMs || 0))
              : null;
            if (!scheduledInterval) {
              scheduledInterval = {
                startsAt: startsAt,
                endsAt: startsAt + Number(cue.durationMs || 0) / 1000,
              };
            }
            this.cues.push({
              signal: cue.signal,
              startsAt: scheduledInterval.startsAt,
              endsAt: scheduledInterval.endsAt,
            });
          }
          if (this.frame === null) this.frame = window.requestAnimationFrame(this.tick.bind(this));
        };

        ColorEchoPlaybackScheduler.prototype.tick = function () {
          var now = this.clockNow();
          var active = "";
          for (var index = 0; index < this.cues.length; index += 1) {
            var cue = this.cues[index];
            if (now >= cue.startsAt && now < cue.endsAt) {
              active = cue.signal;
              break;
            }
          }
          if (active !== this.activeSignal) {
            this.activeSignal = active;
            var pads = document.querySelectorAll("[data-color-echo-tv-signal]");
            for (var padIndex = 0; padIndex < pads.length; padIndex += 1) {
              var pad = pads[padIndex];
              if (pad.getAttribute("data-color-echo-tv-signal") === active)
                pad.classList.add("active");
              else pad.classList.remove("active");
            }
          }
          this.cues = this.cues.filter(function (cue) {
            return cue.endsAt >= now - 0.25;
          });
          this.frame = window.requestAnimationFrame(this.tick.bind(this));
        };

        ColorEchoPlaybackScheduler.prototype.reset = function () {
          if (this.frame !== null) window.cancelAnimationFrame(this.frame);
          this.frame = null;
          this.roundId = "";
          this.scheduled = {};
          this.cues = [];
          this.activeSignal = "";
          this.audioClock = false;
        };

        var colorEchoPlayback = new ColorEchoPlaybackScheduler();

        window.handleWatchClick = function () {
          var input = byId("room");
          var nextRoom = normalizeRoomCode(input ? input.value : "");
          joinRoom(nextRoom || "LOCALTV");
        };

        window.handleCreateClick = function (mode) {
          createRoom(mode);
        };

        window.handleJoinRowClick = function (room) {
          joinRoom(room);
        };

        window.handleRefreshClick = function () {
          listRooms();
        };

        window.handleTvSetupClick = function () {
          renderMenu();
        };

        window.handleWatchCodeClick = function () {
          var input = byId("room");
          var nextRoom = normalizeRoomCode(input ? input.value : "");
          joinRoom(nextRoom || "LOCALTV");
        };

        window.handleMysterySoundToggle = function () {
          sfx.setMuted(!sfx.muted);
          if (latestMysterySnapshot) renderMidnightAlibisSnapshot(latestMysterySnapshot);
        };

        window.handleColorEchoSoundToggle = function () {
          sfx.setMuted(!sfx.muted);
          colorEchoRenderKey = "";
          if (latestColorEchoSnapshot) renderColorEchoSnapshot(latestColorEchoSnapshot);
        };

        window.handleFuseSetting = function (setting) {
          if (setting === "motion") {
            fuseReducedMotion = !fuseReducedMotion;
            try {
              localStorage.setItem("3dtanks.fuse.reducedMotion", String(fuseReducedMotion));
            } catch (_e) {}
          } else if (setting === "flash") {
            fuseReducedFlash = !fuseReducedFlash;
            try {
              localStorage.setItem("3dtanks.fuse.reducedFlash", String(fuseReducedFlash));
            } catch (_e) {}
          }
          fuseTvRenderKey = "";
          if (latestFuseRunnersSnapshot) renderFuseRunnersSnapshot(latestFuseRunnersSnapshot);
        };

        window.handleFuseVolume = function (kind, value, labelId) {
          sfx.setFuseVolume(kind, Number(value) / 100);
          var label = byId(labelId);
          if (label) label.textContent = String(Math.round(Number(value))) + "%";
        };

        window.handleTvShareJoinLink = function () {
          var joinUrl = tvPlayerJoinUrl();
          if (navigator.share) {
            navigator
              .share({
                title: "Join room " + roomCode,
                text: "Join my game in room " + roomCode + ".",
                url: joinUrl,
              })
              .then(
                function () {
                  setTvShareJoinStatus("Invite shared.");
                },
                function (error) {
                  if (error && error.name === "AbortError") {
                    setTvShareJoinStatus("Share cancelled. The link is still ready to copy.");
                    return;
                  }
                  copyTvPlayerJoinUrl(joinUrl);
                },
              );
            return;
          }
          copyTvPlayerJoinUrl(joinUrl);
        };

        function tvPlayerJoinUrl() {
          var joinUrl = new URL("./", window.location.href);
          joinUrl.search = "";
          joinUrl.hash = "";
          joinUrl.searchParams.set("room", roomCode);
          return joinUrl.toString();
        }

        function clearTvShellGameInfo() {
          if (tvShellGameInfo) tvShellGameInfo.innerHTML = "";
        }

        function activateTvShell(gameType, presentation) {
          document.body.classList.add("tv-shell-active");
          var nextGameType = gameType || "";
          if (nextGameType) document.body.setAttribute("data-tv-game", nextGameType);
          else document.body.removeAttribute("data-tv-game");
          if (tvShellTitle) {
            tvShellTitle.textContent = nextGameType
              ? gameDefinition(nextGameType).title
              : presentation === "lobby"
                ? "Choose a Game"
                : "Multiplayer";
          }
          if (tvShellRoom) tvShellRoom.textContent = roomCode ? "ROOM " + roomCode : "";
          if (tvShellGameType !== nextGameType || presentation === "lobby") {
            clearTvShellGameInfo();
          }
          tvShellGameType = nextGameType;
          resizeCanvas();
        }

        function deactivateTvShell() {
          document.body.classList.remove("tv-shell-active");
          document.body.removeAttribute("data-tv-game");
          tvShellGameType = "";
          clearTvShellGameInfo();
          if (tvShellInvite) tvShellInvite.innerHTML = "";
          resizeCanvas();
        }

        function mountTvShellGameInfo(element) {
          if (!tvShellGameInfo || !element) return;
          tvShellGameInfo.innerHTML = "";
          tvShellGameInfo.appendChild(element);
        }

        function renderTvShareJoinPanel(presentation, activeGameType) {
          if (!roomCode) return;
          activateTvShell(activeGameType, presentation);
          if (tvShareJoinPanel && !tvShareJoinPanel.isConnected) {
            tvShareJoinPanel = null;
            tvShareJoinQr = null;
            tvShareJoinQrUrl = "";
            tvShareJoinInput = null;
            tvShareJoinStatus = null;
            tvChangeGameButton = null;
          }
          if (!tvShareJoinPanel) {
            tvShareJoinPanel = document.createElement("div");
            tvShareJoinPanel.className = "tv-share-join";
            tvShareJoinQr = document.createElement("img");
            tvShareJoinQr.className = "tv-share-join-qr";
            var label = document.createElement("label");
            label.textContent = "PLAYER INVITE LINK";
            tvShareJoinInput = document.createElement("input");
            tvShareJoinInput.type = "text";
            tvShareJoinInput.readOnly = true;
            tvShareJoinInput.setAttribute("aria-label", "Player join link");
            tvShareJoinInput.addEventListener("click", function () {
              tvShareJoinInput.select();
            });
            label.appendChild(tvShareJoinInput);
            var shareButton = document.createElement("button");
            shareButton.type = "button";
            shareButton.textContent = "SHARE JOIN LINK";
            shareButton.addEventListener("click", window.handleTvShareJoinLink);
            tvShareJoinStatus = document.createElement("span");
            tvShareJoinStatus.className = "tv-share-join-status";
            tvShareJoinStatus.setAttribute("aria-live", "polite");
            tvShareJoinPanel.appendChild(label);
            tvShareJoinPanel.appendChild(shareButton);
            tvShareJoinPanel.appendChild(tvShareJoinStatus);
          }
          if (tvCanControlRoom && !tvChangeGameButton) {
            tvChangeGameButton = document.createElement("button");
            tvChangeGameButton.type = "button";
            tvChangeGameButton.className = "tv-change-game";
            tvChangeGameButton.textContent = "CHANGE GAME";
            tvChangeGameButton.addEventListener("click", window.handleTvChooseAnotherGame);
          }
          if (tvCanControlRoom && tvChangeGameButton) {
            if (
              tvChangeGameButton.parentNode !== tvShareJoinPanel ||
              tvChangeGameButton.nextSibling !== tvShareJoinStatus
            ) {
              tvShareJoinPanel.insertBefore(tvChangeGameButton, tvShareJoinStatus);
            }
          } else if (tvChangeGameButton && tvChangeGameButton.parentNode) {
            tvChangeGameButton.parentNode.removeChild(tvChangeGameButton);
          }
          var lobbyPresentation = presentation === "lobby";
          if (activeGameType) {
            tvShareJoinPanel.setAttribute("data-game-type", activeGameType);
          } else {
            tvShareJoinPanel.removeAttribute("data-game-type");
          }
          if (tvShareJoinQr.parentNode !== tvShareJoinPanel) {
            tvShareJoinPanel.insertBefore(tvShareJoinQr, tvShareJoinPanel.firstChild);
          }
          var nextPanelClass = "tv-share-join" + (lobbyPresentation ? " lobby" : "");
          if (tvShareJoinPanel.className !== nextPanelClass) {
            tvShareJoinPanel.className = nextPanelClass;
          }
          var nextShareParent = tvShellInvite || tvShellRail || document.body;
          if (tvShareJoinPanel.parentNode !== nextShareParent) {
            nextShareParent.appendChild(tvShareJoinPanel);
          }
          var shareButton = shareButtonElement();
          if (shareButton.textContent !== "SHARE LINK") shareButton.textContent = "SHARE LINK";
          var nextJoinUrl = tvPlayerJoinUrl();
          if (tvShareJoinQrUrl !== nextJoinUrl) {
            tvShareJoinQrUrl = nextJoinUrl;
            tvShareJoinQr.src = joinQrDataUrl(nextJoinUrl);
            tvShareJoinQr.alt = "Scan to join Room " + roomCode;
          }
          if (tvShareJoinInput.value !== nextJoinUrl) tvShareJoinInput.value = nextJoinUrl;
          var nextShareStatus = "Message this link to join Room " + roomCode + ".";
          if (tvShareJoinStatus.textContent !== nextShareStatus) {
            tvShareJoinStatus.textContent = nextShareStatus;
          }
        }

        function shareButtonElement() {
          return tvShareJoinPanel.querySelector("button:not(.tv-change-game)");
        }

        function removeTvShareJoinPanel() {
          if (tvShareJoinPanel && tvShareJoinPanel.parentNode)
            tvShareJoinPanel.parentNode.removeChild(tvShareJoinPanel);
          tvShareJoinPanel = null;
          tvShareJoinQr = null;
          tvShareJoinQrUrl = "";
          tvShareJoinInput = null;
          tvShareJoinStatus = null;
          tvChangeGameButton = null;
          deactivateTvShell();
        }

        function copyTvPlayerJoinUrl(joinUrl) {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(joinUrl).then(
              function () {
                setTvShareJoinStatus("Invite link copied. Paste it into any message.");
              },
              function () {
                fallbackCopyTvPlayerJoinUrl();
              },
            );
            return;
          }
          fallbackCopyTvPlayerJoinUrl();
        }

        function fallbackCopyTvPlayerJoinUrl() {
          if (!tvShareJoinInput) return;
          tvShareJoinInput.focus();
          tvShareJoinInput.select();
          var copied = false;
          try {
            copied = document.execCommand("copy");
          } catch (_error) {}
          setTvShareJoinStatus(
            copied
              ? "Invite link copied. Paste it into any message."
              : "Copy the selected invite link, then paste it into your message.",
          );
        }

        function setTvShareJoinStatus(message) {
          if (tvShareJoinStatus) tvShareJoinStatus.textContent = message;
        }

        window.handleTvChooseAnotherGame = function () {
          if (!tvCanControlRoom) return;
          tvLaunchMode = "party";
          fixedGameChoice = null;
          saveTvLaunch(roomCode);
          sendWhenOpen({
            type: "room",
            protocolVersion: PROTOCOL_VERSION,
            payload: { type: "backToLobby" },
          });
        };

        window.handleTvPlayAgain = function () {
          if (!tvCanControlRoom) return;
          if (tvLaunchMode !== "fixed" || !fixedGameChoice) {
            window.handleTvChooseAnotherGame();
            return;
          }
          sendWhenOpen({
            type: "room",
            protocolVersion: PROTOCOL_VERSION,
            payload: { type: "backToLobby" },
          });
        };

        window.handleTvGameChoice = function (gameType, variantId) {
          if (!tvCanControlRoom) return;
          sendWhenOpen({
            type: "room",
            protocolVersion: PROTOCOL_VERSION,
            payload: {
              type: "changeGame",
              gameType: gameType,
              variantId: variantId || undefined,
            },
          });
        };

        function byId(id) {
          return document.getElementById(id);
        }

        function resizeCanvas() {
          var displayWidth = Math.max(1, canvas.clientWidth || window.innerWidth || 1280);
          var displayHeight = Math.max(1, canvas.clientHeight || window.innerHeight || 720);
          var resolutionScale = Math.min(1, 1280 / displayWidth, 720 / displayHeight);
          var w = Math.max(1, Math.round(displayWidth * resolutionScale));
          var h = Math.max(1, Math.round(displayHeight * resolutionScale));
          if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w;
            canvas.height = h;
            racingTrackLayer = null;
            racingTrackLayerKey = "";
          }
        }

        function escapeHtml(value) {
          return String(value)
            .split("&")
            .join("&amp;")
            .split("<")
            .join("&lt;")
            .split(">")
            .join("&gt;");
        }

        function escapeAttribute(value) {
          return escapeHtml(value).split('"').join("&quot;");
        }

        function normalizeRoomCode(value) {
          return String(value || "")
            .replace(/^\s+|\s+$/g, "")
            .toUpperCase()
            .replace(/[^A-Z0-9-]/g, "")
            .slice(0, 16);
        }

        function queryValue(name) {
          var query = window.location.search.replace(/^\?/, "").split("&");
          for (var i = 0; i < query.length; i += 1) {
            var pair = query[i];
            if (!pair) continue;
            var parts = pair.split("=");
            if (decodeURIComponent(parts[0] || "") === name) {
              return decodeURIComponent(parts[1] || "");
            }
          }
          return "";
        }

        function roomFromUrl() {
          return normalizeRoomCode(queryValue("watch") || queryValue("room"));
        }

        function tvReconnectKey(room) {
          return "tvRoomController:" + normalizeRoomCode(room);
        }

        function loadTvId() {
          var key = "3dtanks.tv.id";
          try {
            var saved = window.localStorage.getItem(key);
            if (saved) return saved;
            var randomPart = "";
            if (window.crypto && window.crypto.getRandomValues) {
              var values = new Uint32Array(4);
              window.crypto.getRandomValues(values);
              for (var index = 0; index < values.length; index += 1) {
                randomPart += values[index].toString(36);
              }
            } else {
              randomPart = Math.random().toString(36).slice(2) + Date.now().toString(36);
            }
            var created = "tv_" + randomPart;
            window.localStorage.setItem(key, created);
            return created;
          } catch (_error) {
            return "tv_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
          }
        }

        function tvLaunchKey(room) {
          return "tvLaunchMode:" + normalizeRoomCode(room);
        }

        function savedTvReconnect(room) {
          try {
            var parsed = JSON.parse(window.localStorage.getItem(tvReconnectKey(room)) || "null");
            if (
              parsed &&
              typeof parsed.spectatorId === "string" &&
              typeof parsed.reconnectToken === "string"
            ) {
              return parsed;
            }
          } catch (_error) {}
          return null;
        }

        function saveTvReconnect(room, spectatorId, reconnectToken) {
          try {
            window.localStorage.setItem(
              tvReconnectKey(room),
              JSON.stringify({ spectatorId: spectatorId, reconnectToken: reconnectToken }),
            );
          } catch (_error) {}
        }

        function savedTvLaunch(room) {
          try {
            var parsed = JSON.parse(window.localStorage.getItem(tvLaunchKey(room)) || "null");
            if (
              parsed &&
              (parsed.mode === "party" || parsed.mode === "fixed") &&
              (!parsed.fixedGame || typeof parsed.fixedGame.gameType === "string")
            ) {
              return parsed;
            }
          } catch (_error) {}
          return null;
        }

        function saveTvLaunch(room) {
          if (tvLaunchMode !== "party" && tvLaunchMode !== "fixed") return;
          try {
            window.localStorage.setItem(
              tvLaunchKey(room),
              JSON.stringify({ mode: tvLaunchMode, fixedGame: fixedGameChoice }),
            );
          } catch (_error) {}
        }

        function choiceForCreateMode(mode) {
          if (mode === "trivia") return { gameType: "trivia" };
          if (mode === "quickdraw") return { gameType: "quickdraw" };
          if (mode === "midnight-alibis") return { gameType: "midnight-alibis" };
          if (mode === "racing") return { gameType: "racing", variantId: "championship" };
          if (mode === "dungeon-relay") return { gameType: "dungeon-relay" };
          if (mode === "color-echo") return { gameType: "color-echo" };
          if (mode === "cupcake-stack") return { gameType: "cupcake-stack" };
          if (mode === "critter-crossing") return { gameType: "critter-crossing" };
          if (mode === "firefly-friends") return { gameType: "firefly-friends" };
          if (mode === "fuse-runners") return { gameType: "fuse-runners", variantId: "blastTour" };
          if (mode === "runway-rookies")
            return { gameType: "runway-rookies", variantId: "calm-skies" };
          return { gameType: "tanks", variantId: mode === "campaign" ? "campaign" : "survival" };
        }

        function choiceTitle(choice) {
          if (!choice) return "Game";
          var definition = gameDefinition(choice.gameType);
          if (choice.gameType === "tanks") {
            for (var index = 0; index < definition.variants.length; index += 1) {
              if (definition.variants[index].id === choice.variantId)
                return definition.title + " " + definition.variants[index].title;
            }
          }
          return definition.title;
        }

        function wsUrl() {
          var configured = "wss://tanks-ws.pretheory.com/multiplayer";
          if (configured && configured.charAt(0) !== "%") return configured;
          var protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
          return protocol + "//" + window.location.hostname + ":8787/multiplayer";
        }

        function renderMenu(status) {
          stopFuseRunnersPresentation();
          deactivateTvShell();
          var existingRoomInput = byId("room");
          if (existingRoomInput && existingRoomInput.value) {
            roomCode = normalizeRoomCode(existingRoomInput.value);
          }
          root.innerHTML =
            '<section class="panel tv-launch-panel">' +
            "<h1>3D Tanks TV</h1>" +
            '<label>Room Code <input id="room" maxlength="8" placeholder="LOCALTV" value="' +
            escapeAttribute(roomCode) +
            '" /></label>' +
            '<div class="tv-launch-primary"><button id="create-tv-party" class="tv-party-button" type="button" onclick="handleCreateClick(\'tv-party\')">Start TV Party</button><button id="watch-tv" type="button" onclick="handleWatchClick()">Watch Existing Room</button></div>' +
            '<div class="tv-launch-divider">Or host one specific game</div>' +
            '<button id="create-survival-tv" type="button" style="background:#ff5e00;color:#fff" onclick="handleCreateClick(\'survival\')">Create Survival</button>' +
            '<button id="create-campaign-tv" type="button" style="background:#38bdf8;color:#06111a" onclick="handleCreateClick(\'campaign\')">Create Campaign</button>' +
            '<button id="create-trivia-tv" type="button" style="background:#8b5cf6;color:#fff" onclick="handleCreateClick(\'trivia\')">Create TV Trivia</button>' +
            '<button id="create-quickdraw-tv" type="button" style="background:#f59e0b;color:#2b1608" onclick="handleCreateClick(\'quickdraw\')">Create Quick Draw</button>' +
            '<button id="create-midnight-tv" type="button" style="background:#d4af37;color:#120f18" onclick="handleCreateClick(\'midnight-alibis\')">Create Midnight Alibis</button>' +
            '<button id="create-racing-tv" type="button" style="background:#f97316;color:#fff" onclick="handleCreateClick(\'racing\')">Create Pocket Rally</button>' +
            '<button id="create-dungeon-tv" type="button" style="background:#7c3aed;color:#fff" onclick="handleCreateClick(\'dungeon-relay\')">Create Dungeon Relay</button>' +
            '<button id="create-color-echo-tv" type="button" style="background:#0891b2;color:#fff" onclick="handleCreateClick(\'color-echo\')">Create Color Echo</button>' +
            '<button id="create-cupcake-stack-tv" type="button" style="background:#fb7185;color:#4a1938" onclick="handleCreateClick(\'cupcake-stack\')">Create Cupcake Stack</button>' +
            '<button id="create-critter-crossing-tv" type="button" style="background:#84cc16;color:#17351f" onclick="handleCreateClick(\'critter-crossing\')">Create Critter Crossing</button>' +
            '<button id="create-firefly-friends-tv" type="button" style="background:#facc15;color:#11153b" onclick="handleCreateClick(\'firefly-friends\')">Create Firefly Friends</button>' +
            '<button id="create-fuse-runners-tv" type="button" style="background:#f97316;color:#1c0a00" onclick="handleCreateClick(\'fuse-runners\')">Create Fuse Runners</button>' +
            '<button id="create-runway-rookies-tv" type="button" style="background:#38bdf8;color:#082f49" onclick="handleCreateClick(\'runway-rookies\')">Create Runway Rookies</button>' +
            '<button id="back-to-watch-tv" type="button" onclick="handleRefreshClick()">Back to Watch Rooms</button>' +
            '<a class="benchmark-link" href="./?tv=0">Use This Computer as a Controller</a>' +
            '<a class="benchmark-link" href="./tv-benchmark.html">Run TV Graphics Benchmark</a>' +
            '<p class="status" id="status">' +
            escapeHtml(
              status ||
                "TV Party rotates game previews. Choosing one game keeps this TV and QR code dedicated to that game.",
            ) +
            "</p>" +
            "</section>";
        }

        function connect() {
          if (socket) return;
          connectionStatus = "connecting";
          socket = new WebSocket(wsUrl());
          socket.onopen = function () {
            connectionStatus = "online";
            var queuedMessages = pendingSocketMessages;
            pendingSocketMessages = [];
            for (var index = 0; index < queuedMessages.length; index += 1) {
              socket.send(queuedMessages[index]);
            }
          };
          socket.onclose = function () {
            connectionStatus = "offline";
            pendingSocketMessages = [];
            updatePerformanceSampling(false);
            socket = null;
            removeTvShareJoinPanel();
            stopAttractRotation();
            resetRacingRenderer();
            resetDungeonRenderer();
            colorEchoPlayback.reset();
            colorEchoRenderKey = "";
            canvas.style.display = "none";
            renderRoomList([], "Disconnected. Check the server and room code.");
          };
          socket.onerror = function () {
            connectionStatus = "error";
          };
          socket.onmessage = function (event) {
            handleMessage(event.data);
          };
        }

        function sendWhenOpen(message) {
          var text = JSON.stringify(message);
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(text);
            return;
          }
          pendingSocketMessages.push(text);
        }

        function updatePerformanceSampling(active, nowMs) {
          var sampleNow = typeof nowMs === "number" ? nowMs : performance.now();
          performanceSampler.setActive(active, sampleNow);
          if (!active) {
            if (performanceFrameRequest !== null) {
              window.cancelAnimationFrame(performanceFrameRequest);
              performanceFrameRequest = null;
            }
            return;
          }
          if (performanceFrameRequest === null) {
            performanceFrameRequest = window.requestAnimationFrame(samplePerformanceFrame);
          }
        }

        function samplePerformanceFrame(frameNow) {
          performanceSampler.observeFrame(frameNow);
          var report = performanceSampler.takeReport(frameNow);
          if (report) {
            maybeDowngradeFuseRunnersForPerformance(report);
            if (socket && socket.readyState === WebSocket.OPEN) {
              socket.send(
                JSON.stringify({
                  type: "room",
                  protocolVersion: PROTOCOL_VERSION,
                  payload: { type: "clientPerformance", report: report },
                }),
              );
            }
          }
          performanceFrameRequest = performanceSampler.isActive()
            ? window.requestAnimationFrame(samplePerformanceFrame)
            : null;
        }

        function startAttractRotation() {
          if (attractTimer || tvLaunchMode !== "party" || attractGames.length === 0) return;
          attractTimer = window.setInterval(function () {
            if (!socket || socket.readyState !== WebSocket.OPEN) return;
            attractIndex = (attractIndex + 1) % attractGames.length;
            var offered = attractGames[attractIndex];
            socket.send(
              JSON.stringify({
                type: "room",
                protocolVersion: PROTOCOL_VERSION,
                payload: {
                  type: "changeGame",
                  gameType: offered.gameType,
                  variantId: offered.variantId,
                },
              }),
            );
          }, 12000);
        }

        function stopAttractRotation() {
          if (!attractTimer) return;
          window.clearInterval(attractTimer);
          attractTimer = null;
        }

        function joinRoom(nextRoom) {
          removeTvShareJoinPanel();
          roomCode = normalizeRoomCode(nextRoom);
          if (!roomCode) return;
          var saved = savedTvReconnect(roomCode);
          var savedLaunch = savedTvLaunch(roomCode);
          tvLaunchMode = savedLaunch ? savedLaunch.mode : "watch";
          fixedGameChoice = savedLaunch ? savedLaunch.fixedGame : null;
          connect();
          sendWhenOpen({
            type: "room",
            protocolVersion: PROTOCOL_VERSION,
            payload: {
              type: "joinSpectator",
              roomCode: roomCode,
              displayName: "TV",
              spectatorId: saved ? saved.spectatorId : undefined,
              reconnectToken: saved ? saved.reconnectToken : undefined,
              tvId: tvId,
            },
          });
          renderMenu("Connecting to room " + roomCode + "...");
        }

        function createRoom(mode) {
          removeTvShareJoinPanel();
          var input = byId("room");
          var enteredCode = normalizeRoomCode(input ? input.value : "");
          var roomCodeToUse = enteredCode || "LOCALTV";
          var partyMode = mode === "tv-party";
          var createMode = partyMode ? "survival" : mode;
          var choice = choiceForCreateMode(createMode);
          var saved = savedTvReconnect(roomCodeToUse);
          tvLaunchMode = partyMode ? "party" : "fixed";
          fixedGameChoice = partyMode ? null : choice;
          attractIndex = Math.max(
            0,
            attractGames.findIndex(function (candidate) {
              return (
                candidate.gameType === choice.gameType && candidate.variantId === choice.variantId
              );
            }),
          );

          connect();
          sendWhenOpen({
            type: "room",
            protocolVersion: PROTOCOL_VERSION,
            payload: {
              type: "createSession",
              gameType: choice.gameType,
              displayName: "TV",
              variantId: choice.variantId,
              mode:
                createMode === "trivia" ||
                createMode === "quickdraw" ||
                createMode === "midnight-alibis" ||
                createMode === "racing" ||
                createMode === "dungeon-relay" ||
                createMode === "color-echo" ||
                createMode === "cupcake-stack" ||
                createMode === "critter-crossing" ||
                createMode === "firefly-friends" ||
                createMode === "fuse-runners" ||
                createMode === "runway-rookies"
                  ? "survival"
                  : createMode || "survival",
              roomCode: roomCodeToUse,
              isSpectator: true,
              spectatorId: saved ? saved.spectatorId : undefined,
              reconnectToken: saved ? saved.reconnectToken : undefined,
              tvId: tvId,
            },
          });
          renderMenu("Creating room " + roomCodeToUse + "...");
        }

        function listRooms() {
          connect();
          sendWhenOpen({
            type: "room",
            protocolVersion: PROTOCOL_VERSION,
            payload: { type: "listSessions" },
          });
          renderMenu("Looking for available rooms...");
        }

        function handleMessage(data) {
          var message;
          try {
            message = JSON.parse(data);
          } catch (_error) {
            return;
          }
          if (message.type !== "snapshot" || message.gameType !== "racing") {
            resetRacingRenderer();
          }
          if (message.type !== "snapshot" || message.gameType !== "tanks") {
            resetTankRenderer();
          }
          if (message.type !== "snapshot" || message.gameType !== "dungeon-relay") {
            resetDungeonRenderer();
          }
          if (message.type !== "snapshot" || message.gameType !== "color-echo") {
            colorEchoPlayback.reset();
            colorEchoRenderKey = "";
          }
          if (message.type === "spectatorJoined") {
            roomCode = message.roomCode;
            tvCanControlRoom = message.roomController === true;
            if (tvCanControlRoom) {
              saveTvReconnect(roomCode, message.spectatorId, message.reconnectToken);
              saveTvLaunch(roomCode);
              var roomUrl = new URL(window.location.href);
              roomUrl.searchParams.set("watch", roomCode);
              window.history.replaceState(null, "", roomUrl.toString());
            }
            resizeCanvas();
            canvas.style.display = "block";
            renderTvShareJoinPanel("gameplay");
            return;
          }
          if (message.type === "gameSelection") {
            updatePerformanceSampling(false);
            stopFuseRunnersPresentation();
            stopAttractRotation();
            canvas.style.display = "none";
            if (tvCanControlRoom && tvLaunchMode === "fixed" && fixedGameChoice) {
              root.innerHTML =
                '<section class="tv-lobby" data-tv-launch-mode="fixed"><div class="tv-launch-badge">FIXED GAME</div><h1>Returning to ' +
                escapeHtml(choiceTitle(fixedGameChoice)) +
                "</h1><p>This TV stays dedicated to the selected game. Room " +
                escapeHtml(roomCode) +
                " remains open.</p></section>";
              renderTvShareJoinPanel("lobby", fixedGameChoice.gameType);
              window.handleTvGameChoice(
                fixedGameChoice.gameType,
                fixedGameChoice.variantId || undefined,
              );
              return;
            }
            root.innerHTML =
              '<section class="tv-lobby" data-tv-launch-mode="' +
              escapeAttribute(tvLaunchMode) +
              '">' +
              (tvLaunchMode === "party"
                ? '<div class="tv-launch-badge">TV PARTY</div><h1>Choose the Next Party Game</h1>'
                : "<h1>Choose a Game</h1>") +
              (tvCanControlRoom
                ? '<p style="font-size:24px;color:#cbd5e1">Keep Room ' +
                  escapeHtml(roomCode) +
                  ' together and choose the next game.</p><div class="tv-game-selection-actions"><button type="button" onclick="handleTvGameChoice(\'tanks\', \'survival\')">Choose Tank Survival</button><button type="button" onclick="handleTvGameChoice(\'trivia\')">Choose TV Trivia</button><button type="button" onclick="handleTvGameChoice(\'quickdraw\')">Choose Quick Draw</button><button type="button" onclick="handleTvGameChoice(\'midnight-alibis\')">Choose Midnight Alibis</button><button type="button" onclick="handleTvGameChoice(\'racing\', \'championship\')">Choose Pocket Rally</button><button type="button" onclick="handleTvGameChoice(\'dungeon-relay\')">Choose Dungeon Relay</button><button type="button" onclick="handleTvGameChoice(\'color-echo\')">Choose Color Echo</button><button type="button" onclick="handleTvGameChoice(\'cupcake-stack\')">Choose Cupcake Stack</button><button type="button" onclick="handleTvGameChoice(\'critter-crossing\')">Choose Critter Crossing</button><button type="button" onclick="handleTvGameChoice(\'firefly-friends\')">Choose Firefly Friends</button><button type="button" onclick="handleTvGameChoice(\'fuse-runners\', \'blastTour\')">Choose Fuse Runners</button><button type="button" onclick="handleTvGameChoice(\'runway-rookies\', \'calm-skies\')">Choose Runway Rookies</button><button type="button" onclick="handleTvGameChoice(\'roshambo-showdown\')">Choose Roshambo Showdown</button></div>'
                : '<p style="font-size:24px;color:#cbd5e1">The Host is choosing on their controller.</p>') +
              "</section>";
            renderTvShareJoinPanel("lobby");
            return;
          }
          if (message.type === "lobby") {
            updatePerformanceSampling(false);
            stopFuseRunnersPresentation();
            if (message.slots.length === 0 && tvLaunchMode === "party") startAttractRotation();
            else stopAttractRotation();
            var slotsHtml = "";
            for (var s = 0; s < message.slots.length; s++) {
              var slot = message.slots[s];
              slotsHtml +=
                '<div class="lobby-slot">' +
                '<span class="swatch" style="background:' +
                escapeAttribute(slot.color) +
                '"></span>' +
                '<strong style="flex-grow:1;text-align:left">' +
                escapeHtml(slot.displayName) +
                "</strong>" +
                '<span style="font-size:14px;font-weight:bold;color:' +
                (slot.connected ? (slot.ready ? "#22c55e" : "#eab308") : "#64748b") +
                '">' +
                (slot.connected ? (slot.ready ? "READY" : "JOINED") : "EMPTY") +
                "</span>" +
                "</div>";
            }

            var offeredDefinition = gameDefinition(message.gameType);
            var offeredTitle = offeredDefinition.title;
            if (message.gameType === "tanks" && message.mode === "campaign")
              offeredTitle += " Campaign";
            else if (message.gameType === "tanks") offeredTitle += " Survival";
            var attractCopy = offeredDefinition.attract.tagline;
            root.innerHTML =
              '<section class="tv-lobby' +
              (message.gameType === "fuse-runners" ? " tv-fuse-lobby" : "") +
              '" data-tv-launch-mode="' +
              escapeAttribute(tvLaunchMode) +
              '">' +
              (tvLaunchMode === "party"
                ? '<div class="tv-launch-badge">TV PARTY · GAME ROTATION</div>'
                : tvLaunchMode === "fixed"
                  ? '<div class="tv-launch-badge">FIXED GAME · ONE QR</div>'
                  : "") +
              "<h1>" +
              escapeHtml(message.slots.length === 0 ? offeredTitle : offeredTitle + " - Lobby") +
              "</h1>" +
              (message.gameType === "fuse-runners" ? renderFuseLobbyPowerUpGuide() : "") +
              (message.slots.length === 0
                ? '<p style="font-size:26px;color:#cbd5e1;max-width:760px;margin:0 auto 24px">' +
                  escapeHtml(attractCopy) +
                  (tvLaunchMode === "party"
                    ? "<br><strong>Previews rotate until the first phone joins.</strong>"
                    : "<br><strong>Scan this QR to join this game.</strong>") +
                  "</p>"
                : "") +
              '<div class="lobby-slots">' +
              slotsHtml +
              "</div>" +
              "</section>";
            renderTvShareJoinPanel("lobby", message.gameType);
            return;
          }
          if (message.type === "sessionList") {
            lastSessionsList = message.sessions || [];
            renderRoomList(lastSessionsList);
            return;
          }
          if (message.type === "snapshot") {
            var performanceNow = performance.now();
            var performanceActive = message.platform.lifecycle === "playing";
            updatePerformanceSampling(performanceActive, performanceNow);
            if (performanceActive) performanceSampler.observeSnapshot(performanceNow);
            if (message.platform.lifecycle === "lobby") {
              stopFuseRunnersPresentation();
              return;
            }
            if (message.gameType !== "fuse-runners") stopFuseRunnersPresentation();
            if (message.gameType !== "runway-rookies") disposeRunwayTvRenderer();
            latestPlatformSnapshot = message.platform;
            renderTvShareJoinPanel("gameplay", message.gameType);
            var registeredTvRenderers = {
              tanks: renderTankSnapshot,
              trivia: renderTriviaSnapshot,
              quickdraw: renderQuickDrawSnapshot,
              "midnight-alibis": renderMidnightAlibisSnapshot,
              racing: renderRacingSnapshot,
              "dungeon-relay": renderDungeonRelaySnapshot,
              "color-echo": renderColorEchoSnapshot,
              "cupcake-stack": renderCupcakeStackSnapshot,
              "critter-crossing": renderCritterCrossingSnapshot,
              "firefly-friends": renderFireflyFriendsSnapshot,
              "fuse-runners": renderFuseRunnersSnapshot,
              "runway-rookies": renderRunwayRookiesSnapshot,
              "roshambo-showdown": renderRoshamboShowdownSnapshot,
            };
            var registeredTvRenderer = registeredTvRenderers[message.gameType];
            if (registeredTvRenderer) {
              var snapshotWorkStartedAt = performance.now();
              registeredTvRenderer(message.snapshot, message.platform);
              if (performanceActive) {
                performanceSampler.observeSnapshotWork(performance.now() - snapshotWorkStartedAt);
              }
              return;
            }
            return;
          }
          if (message.type === "error") {
            canvas.style.display = "none";
            renderMenu(message.message);
          }
        }

        var runwayTvRenderKey = "";
        var runwayTvRenderer = null;
        var runwaySoundRoundId = "";
        var runwaySoundPhase = "";
        var runwaySoundPlaneStates = {};

        function updateRunwaySounds(snapshot) {
          var planes = snapshot.planes || [];
          if (snapshot.roundId !== runwaySoundRoundId) {
            runwaySoundRoundId = snapshot.roundId;
            runwaySoundPhase = "";
            runwaySoundPlaneStates = {};
          }

          var activeSpeedTotal = 0;
          var activePlaneCount = 0;
          var touchdownSoftness = null;
          var bounced = false;
          for (var index = 0; index < planes.length; index += 1) {
            var plane = planes[index];
            var previousState = runwaySoundPlaneStates[plane.playerId];
            if (!plane.resolved && plane.flightState === "airborne") {
              activeSpeedTotal += plane.airspeedKnots;
              activePlaneCount += 1;
            }
            if (previousState === "airborne" && plane.flightState === "bouncing") bounced = true;
            if (previousState !== "rollout" && plane.flightState === "rollout") {
              touchdownSoftness = plane.landingFeedback ? plane.landingFeedback.softness : "smooth";
            }
            runwaySoundPlaneStates[plane.playerId] = plane.flightState;
          }

          sfx.updateRunwayEngine(
            activePlaneCount
              ? activeSpeedTotal / activePlaneCount / snapshot.challenge.targetSpeedKnots
              : 0,
            snapshot.phase === "approach" && activePlaneCount > 0,
          );
          if (bounced) sfx.playRunwayBounce();
          else if (touchdownSoftness) sfx.playRunwayTouchdown(touchdownSoftness);

          if (snapshot.phase !== runwaySoundPhase) {
            if (snapshot.phase === "approach") sfx.playRunwayReady();
            else if (snapshot.phase === "roundResult") sfx.playRunwayResult(false);
            else if (snapshot.phase === "completed") sfx.playRunwayResult(true);
            runwaySoundPhase = snapshot.phase;
          }
        }

        function RunwayTvRenderer(canvas) {
          this.canvas = canvas;
          this.ctx = canvas.getContext("2d", { alpha: false });
          this.enhanced = null;
          this.latestSnapshot = null;
          this.disposed = false;
          this.enhancementRequested = false;
        }

        RunwayTvRenderer.prototype.draw = function (snapshot) {
          if (this.disposed) return;
          this.latestSnapshot = snapshot;
          if (this.canvas.width !== 1280 || this.canvas.height !== 640) {
            this.canvas.width = 1280;
            this.canvas.height = 640;
          }
          var planes = snapshot.planes || [];
          var viewports = runwayViewports(planes.length, this.canvas.width, this.canvas.height);
          if (this.enhanced) {
            this.enhanced.draw(snapshot);
            return;
          }
          if (this.ctx) runwayCanvasDraw(this.ctx, snapshot, viewports);
          if (!this.enhancementRequested && shouldUseRunwayWebGl2(window.localStorage)) {
            this.enhancementRequested = true;
            requestRunwayEnhancedRenderer(this);
          }
        };

        RunwayTvRenderer.prototype.dispose = function () {
          if (this.disposed) return;
          this.disposed = true;
          if (this.enhanced) this.enhanced.dispose();
          this.enhanced = null;
          this.ctx = null;
          this.latestSnapshot = null;
        };

        function requestRunwayEnhancedRenderer(renderer) {
          import("./tv/runwayEnhancedRenderer.ts")
            .then(function (module) {
              if (renderer.disposed || runwayTvRenderer !== renderer) return;
              renderer.enhanced = module.createRunwayEnhancedRenderer(
                renderer.canvas,
                function (fallbackCanvas) {
                  if (renderer.disposed) return;
                  renderer.canvas = fallbackCanvas;
                  renderer.enhanced = null;
                  renderer.ctx = fallbackCanvas.getContext("2d", { alpha: false });
                  recordRunwayRendererDiagnostic(window.localStorage, "webgl-context-lost");
                  if (renderer.latestSnapshot) renderer.draw(renderer.latestSnapshot);
                },
              );
              renderer.canvas = renderer.enhanced.canvas;
              renderer.ctx = null;
              if (renderer.latestSnapshot) renderer.enhanced.draw(renderer.latestSnapshot);
            })
            .catch(function (error) {
              recordRunwayRendererDiagnostic(
                window.localStorage,
                "webgl-initialization-failed:" + String(error),
              );
            });
        }

        function disposeRunwayTvRenderer() {
          sfx.stopRunwayEngine();
          if (runwayTvRenderer) runwayTvRenderer.dispose();
          runwayTvRenderer = null;
          runwayTvRenderKey = "";
          if (tvShareJoinPanel) tvShareJoinPanel.style.display = "";
        }

        function runwayViewports(count, width, height) {
          if (count <= 1) return [{ x: 0, y: 0, w: width, h: height }];
          if (count === 2)
            return [
              { x: 0, y: 0, w: width / 2, h: height },
              { x: width / 2, y: 0, w: width / 2, h: height },
            ];
          return [
            { x: 0, y: height / 2, w: width / 2, h: height / 2 },
            { x: width / 2, y: height / 2, w: width / 2, h: height / 2 },
            { x: 0, y: 0, w: width / 2, h: height / 2 },
            { x: width / 2, y: 0, w: width / 2, h: height / 2 },
          ];
        }

        function runwayCanvasDraw(ctx, snapshot, viewports) {
          var planes = snapshot.planes || [];
          for (var index = 0; index < planes.length; index += 1) {
            var v = viewports[index];
            var plane = planes[index];
            ctx.save();
            ctx.beginPath();
            ctx.rect(v.x, 640 - v.y - v.h, v.w, v.h);
            ctx.clip();
            var top = 640 - v.y - v.h;
            var horizon = top + v.h * 0.46;
            var bottom = top + v.h;
            var center = v.x + v.w * 0.5;
            var depthCue = runwayDepthCue(plane.distanceToThresholdMeters, plane.altitudeMeters);
            ctx.fillStyle = snapshot.challenge.timeOfDay === "night" ? "#07152e" : "#7dd3fc";
            ctx.fillRect(v.x, top, v.w, v.h);
            ctx.fillStyle = snapshot.challenge.timeOfDay === "night" ? "#13233f" : "#86b8c8";
            ctx.beginPath();
            ctx.moveTo(v.x, horizon);
            ctx.lineTo(v.x + v.w * 0.14, horizon - v.h * 0.12);
            ctx.lineTo(v.x + v.w * 0.29, horizon - v.h * 0.025);
            ctx.lineTo(v.x + v.w * 0.43, horizon - v.h * 0.16);
            ctx.lineTo(v.x + v.w * 0.58, horizon - v.h * 0.03);
            ctx.lineTo(v.x + v.w * 0.76, horizon - v.h * 0.13);
            ctx.lineTo(v.x + v.w, horizon);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = snapshot.challenge.timeOfDay === "night" ? "#14351f" : "#65a30d";
            ctx.fillRect(v.x, horizon, v.w, bottom - horizon);
            var offset =
              (-plane.lateralMeters / Math.max(35, snapshot.challenge.runwayWidthMeters)) *
              v.w *
              0.34;
            var headingShift = (-plane.headingErrorDegrees / 40) * v.w * 0.12;
            var runwayTopX = center + headingShift;
            var perspectiveCue = runwayPerspectiveCue(plane.distanceToThresholdMeters);
            var nearVisibility = perspectiveCue.nearRunwayVisibility;
            var runwayBottomX = runwayTopX + (center + offset - runwayTopX) * nearVisibility;
            var runwayBottomY = horizon + (bottom - horizon) * nearVisibility;
            var runwayBottomHalf = 20 + (v.w * 0.43 - 20) * nearVisibility;
            ctx.fillStyle = "#454b52";
            ctx.beginPath();
            ctx.moveTo(runwayTopX - 20, horizon);
            ctx.lineTo(runwayTopX + 20, horizon);
            ctx.lineTo(runwayBottomX + runwayBottomHalf, runwayBottomY);
            ctx.lineTo(runwayBottomX - runwayBottomHalf, runwayBottomY);
            ctx.fill();

            var distanceCycle = (((plane.distanceToThresholdMeters / 90) % 1) + 1) % 1;
            var motionPhase = 1 - distanceCycle;
            for (var stripe = 0; stripe < 8; stripe += 1) {
              var t = (stripe + motionPhase) / 8;
              if (t <= 0 || t >= 1 || nearVisibility <= 0.02) continue;
              var depth = t * t * nearVisibility;
              var y = horizon + depth * (bottom - horizon);
              var stripeX = runwayTopX + (runwayBottomX - runwayTopX) * depth;
              var stripeWidth = 2 + depth * 13;
              var stripeHeight = 3 + depth * 24;
              ctx.fillStyle = "#fffdf0";
              ctx.fillRect(stripeX - stripeWidth / 2, y, stripeWidth, stripeHeight);
              var runwayHalf = 20 + (v.w * 0.43 - 20) * depth;
              ctx.fillStyle = snapshot.challenge.timeOfDay === "night" ? "#fde68a" : "#f8fafc";
              ctx.fillRect(stripeX - runwayHalf + 4, y, 3 + depth * 4, 3 + depth * 4);
              ctx.fillRect(stripeX + runwayHalf - 7 - depth * 4, y, 3 + depth * 4, 3 + depth * 4);
            }

            if (snapshot.challenge.guided && plane.flightState === "airborne") {
              ctx.strokeStyle = "rgba(167,243,208,.72)";
              ctx.lineWidth = Math.max(2, v.w / 260);
              for (var gate = 1; gate <= 3; gate += 1) {
                var gateDepth = gate * 0.2;
                var gateY = horizon + gateDepth * gateDepth * (bottom - horizon);
                var gateX = runwayTopX + (runwayBottomX - runwayTopX) * gateDepth * gateDepth;
                var gateHalf = 16 + gateDepth * v.w * 0.16;
                var gateHeight = 9 + gateDepth * v.h * 0.08;
                ctx.beginPath();
                ctx.moveTo(gateX - gateHalf, gateY + gateHeight);
                ctx.lineTo(gateX - gateHalf, gateY);
                ctx.lineTo(gateX + gateHalf, gateY);
                ctx.lineTo(gateX + gateHalf, gateY + gateHeight);
                ctx.stroke();
              }
            }

            if (plane.distanceToThresholdMeters < 420 && plane.distanceToThresholdMeters > -40) {
              var thresholdT = Math.max(0, Math.min(1, 1 - plane.distanceToThresholdMeters / 420));
              var thresholdDepth = thresholdT * thresholdT;
              var thresholdY = horizon + thresholdDepth * (bottom - horizon);
              var thresholdX = runwayTopX + (runwayBottomX - runwayTopX) * thresholdDepth;
              var thresholdHalf = 18 + (v.w * 0.4 - 18) * thresholdDepth;
              var barWidth = thresholdHalf * 0.16;
              ctx.fillStyle = "#ffffff";
              for (var bar = -2; bar <= 2; bar += 1) {
                ctx.fillRect(
                  thresholdX + bar * barWidth * 1.8 - barWidth / 2,
                  thresholdY,
                  barWidth,
                  5 + thresholdDepth * 18,
                );
              }
            }

            if (snapshot.challenge.precipitation === "rain") {
              ctx.strokeStyle = "rgba(224,242,254,.45)";
              ctx.lineWidth = 2;
              for (var rain = 0; rain < 18; rain += 1) {
                var rainX =
                  v.x + ((rain * 71 + plane.distanceToThresholdMeters * 1.7) % Math.max(1, v.w));
                var rainY =
                  top + ((rain * 43 + plane.distanceToThresholdMeters * 2.3) % Math.max(1, v.h));
                ctx.beginPath();
                ctx.moveTo(rainX, rainY);
                ctx.lineTo(rainX - 8, rainY + 18);
                ctx.stroke();
              }
            }

            var planeScale = Math.max(0.72, Math.min(1.15, v.w / 640));
            var planeY =
              top +
              v.h * 0.69 -
              depthCue.altitudeLift * v.h -
              depthCue.planeVerticalOffset * v.h +
              Math.sin(plane.distanceToThresholdMeters * 0.045) * 2 -
              plane.pitchDegrees * 1.2;
            var planeX = center + Math.max(-1, Math.min(1, plane.bankDegrees / 18)) * v.w * 0.08;
            var shadowY = planeY + depthCue.shadowSeparation * v.h;
            ctx.save();
            ctx.strokeStyle = "rgba(255,255,255,.58)";
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 6]);
            ctx.beginPath();
            ctx.moveTo(planeX, planeY + 18);
            ctx.lineTo(planeX, shadowY);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.translate(planeX, shadowY);
            ctx.scale(planeScale * depthCue.shadowScale, planeScale * depthCue.shadowScale);
            ctx.fillStyle = "rgba(2,6,23," + depthCue.shadowAlpha + ")";
            ctx.beginPath();
            ctx.ellipse(5, 0, 56, 12, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
            ctx.save();
            ctx.translate(planeX, planeY);
            ctx.rotate((plane.bankDegrees * Math.PI) / 180);
            ctx.scale(planeScale, planeScale);
            ctx.fillStyle = plane.color || "#fbbf24";
            ctx.beginPath();
            ctx.moveTo(0, -30);
            ctx.lineTo(13, 2);
            ctx.lineTo(58, 18);
            ctx.lineTo(10, 15);
            ctx.lineTo(7, 30);
            ctx.lineTo(-7, 30);
            ctx.lineTo(-10, 15);
            ctx.lineTo(-58, 18);
            ctx.lineTo(-13, 2);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = "rgba(255,255,255,.82)";
            ctx.beginPath();
            ctx.ellipse(0, -7, 7, 12, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
            ctx.restore();
          }
        }

        function renderRunwayRookiesSnapshot(snapshot, platform) {
          updateRunwaySounds(snapshot);
          if (snapshot.phase === "completed" && snapshot.result) {
            clearTvShellGameInfo();
            disposeRunwayTvRenderer();
            var rows = snapshot.result.standings
              .map(function (standing) {
                return (
                  "<li><strong>" +
                  standing.rank +
                  ". " +
                  escapeHtml(standing.displayName) +
                  "</strong><span>" +
                  standing.finalScore +
                  " pts</span></li>"
                );
              })
              .join("");
            root.innerHTML =
              '<section class="tv-runway-finale" data-testid="tv-runway-rookies"><p class="eyebrow">Three landings complete</p><h1>Flight School Results</h1><ol>' +
              rows +
              "</ol>" +
              renderTvPostGameActions(platform) +
              "</section>";
            return;
          }
          var planes = snapshot.planes || [];
          var key = snapshot.roundId + ":" + planes.length;
          if (key !== runwayTvRenderKey) {
            runwayTvRenderKey = key;
            var cards = planes
              .map(function (plane, index) {
                return (
                  '<div class="tv-runway-card" id="runway-card-' +
                  index +
                  '" style="--player-color:' +
                  escapeAttribute(plane.color) +
                  '"><span class="tv-runway-marker marker-' +
                  (plane.markerIndex % 4) +
                  '" aria-hidden="true"></span><span class="tv-runway-name">' +
                  escapeHtml(plane.displayName) +
                  '</span><span class="tv-runway-gauges" id="runway-gauge-' +
                  index +
                  '"></span><span class="tv-runway-score" id="runway-score-' +
                  index +
                  '"></span></div>'
                );
              })
              .join("");
            if (planes.length === 3) {
              var sharedStandings = snapshot.standings
                .slice(0, 3)
                .map(function (standing) {
                  return (
                    "<li><span>" +
                    standing.rank +
                    ". " +
                    escapeHtml(standing.displayName) +
                    "</span><strong>" +
                    standing.score +
                    "</strong></li>"
                  );
                })
                .join("");
              var planTitle =
                snapshot.flightPlanId === "weather-adventure"
                  ? "WEATHER ADVENTURE"
                  : snapshot.flightPlanId === "ace-approaches"
                    ? "ACE APPROACHES"
                    : "CALM SKIES";
              cards +=
                '<div class="tv-runway-shared"><span>' +
                planTitle +
                "</span><ol>" +
                sharedStandings +
                "</ol><small>Room " +
                escapeHtml(roomCode) +
                "</small></div>";
            }
            root.innerHTML =
              '<section class="tv-runway-rookies" data-testid="tv-runway-rookies"><header class="tv-runway-header"><h1>' +
              escapeHtml(snapshot.challenge.title) +
              '</h1><p id="runway-tv-phase"></p><p>Room ' +
              escapeHtml(roomCode) +
              '</p></header><div class="tv-runway-stage"><canvas id="runway-tv-canvas" class="tv-runway-canvas"></canvas><div class="tv-runway-overlay players-' +
              planes.length +
              '">' +
              cards +
              "</div></div></section>";
            runwayTvRenderer = new RunwayTvRenderer(byId("runway-tv-canvas"));
          }
          if (runwayTvRenderer) runwayTvRenderer.draw(snapshot);
          var phase = byId("runway-tv-phase");
          if (phase)
            phase.textContent =
              snapshot.phase === "countdown"
                ? "Ready " + Math.max(1, Math.ceil(snapshot.countdownMs / 1000))
                : "Landing " + snapshot.roundNumber + " of 3";
          for (var index = 0; index < planes.length; index += 1) {
            var gauge = byId("runway-gauge-" + index);
            var score = byId("runway-score-" + index);
            var card = byId("runway-card-" + index);
            if (gauge)
              gauge.textContent =
                "ALT " +
                Math.max(0, Math.round(planes[index].altitudeMeters * 3.281)) +
                " ft  SPD " +
                Math.round(planes[index].airspeedKnots) +
                " / " +
                snapshot.challenge.targetSpeedKnots +
                " kt";
            if (score) {
              if (planes[index].landingScore === null) {
                score.classList.remove("result");
                score.textContent = planes[index].assisted
                  ? "INSTRUCTOR HELP"
                  : planes[index].flightState === "rollout"
                    ? "STEADY ROLLOUT"
                    : planes[index].flightState === "bouncing"
                      ? "EASE IT DOWN"
                      : "GENTLE MOVES";
              } else {
                score.classList.add("result");
                score.innerHTML = runwayLandingFeedbackHtml(
                  planes[index],
                  snapshot.challenge.targetSpeedKnots,
                );
              }
            }
            if (card && planes[index].landingScore === 0) card.classList.add("training-reset");
          }
        }

        function runwayLandingFeedbackHtml(plane, targetSpeedKnots) {
          var feedback = plane.landingFeedback;
          if (!feedback)
            return "<strong>" + escapeHtml(plane.rating) + " - " + plane.landingScore + "</strong>";
          var timing =
            feedback.timing === "on-target"
              ? "ON TARGET TOUCHDOWN"
              : feedback.timing === "no-touchdown"
                ? "NO TOUCHDOWN"
                : feedback.timing.toUpperCase() + " TOUCHDOWN";
          var centerline =
            feedback.centerline === "centered"
              ? "CENTERED"
              : Math.abs(feedback.lateralMeters).toFixed(1) +
                " m " +
                feedback.centerline.toUpperCase() +
                " OF CENTER";
          var speed =
            feedback.speed === "on-speed"
              ? "ON SPEED"
              : Math.abs(feedback.airspeedKnots - targetSpeedKnots) +
                " kt " +
                feedback.speed.toUpperCase();
          var breakdown = feedback.scoreBreakdown;
          return (
            "<strong>" +
            escapeHtml(plane.rating) +
            " - " +
            plane.landingScore +
            " POINTS</strong>" +
            '<span class="tv-runway-result-diagnosis"><b>' +
            timing +
            "</b><b>" +
            centerline +
            "</b><b>" +
            speed +
            "</b><b>" +
            feedback.softness.toUpperCase() +
            " " +
            feedback.verticalSpeedMps.toFixed(1) +
            " m/s</b></span>" +
            '<small class="tv-runway-result-breakdown">APP ' +
            breakdown.approach +
            "/200 | ZONE " +
            breakdown.touchdownZone +
            "/200 | SOFT " +
            breakdown.softness +
            "/250 | CENTER " +
            breakdown.centerline +
            "/150 | HEAD " +
            breakdown.heading +
            "/100 | SPEED " +
            breakdown.speed +
            "/50 | ROLLOUT " +
            breakdown.rollout +
            "/50" +
            (breakdown.adjustment === 0 ? "" : " | ADJUST " + breakdown.adjustment) +
            "</small>"
          );
        }

        function renderTriviaSnapshot(snapshot) {
          latestTriviaSnapshot = snapshot;
          if (snapshot.phase === "completed") {
            renderTriviaResults(snapshot);
            return;
          }
          loadTriviaMedia(snapshot);
          canvas.style.display = "none";
          var options = snapshot.options || [];
          var optionBadgeShapes = ["shape-circle", "shape-square", "shape-diamond", "shape-hex"];
          var optionHtml = "";
          for (var index = 0; index < options.length; index++) {
            var isCorrect = snapshot.correctAnswerIndex === index;
            var badgeShape = optionBadgeShapes[index % optionBadgeShapes.length];
            var badgeLetter = String.fromCharCode(65 + index);
            optionHtml +=
              '<div class="trivia-tv-option' +
              (isCorrect ? " correct" : "") +
              '"><span class="trivia-tv-option-badge ' +
              badgeShape +
              '"><span>' +
              badgeLetter +
              "</span></span>" +
              '<span class="trivia-tv-option-text">' +
              escapeHtml(options[index]) +
              "</span>" +
              (isCorrect ? '<span class="trivia-tv-option-check">✓</span>' : "") +
              "</div>";
          }
          var standingsHtml = "";
          for (var s = 0; s < snapshot.standings.length; s++) {
            var standing = snapshot.standings[s];
            standingsHtml +=
              '<li style="display:flex;justify-content:space-between;padding:8px 0"><span>' +
              escapeHtml(standing.displayName) +
              "</span><strong>" +
              escapeHtml(String(standing.score)) +
              "</strong></li>";
          }
          var mediaHtml = "";
          if (triviaMedia) {
            mediaHtml =
              '<img id="trivia-question-media" src="' +
              escapeAttribute(triviaMedia.imageUrl) +
              '" alt="Related trivia image">' +
              '<a class="trivia-media-attribution" href="' +
              escapeAttribute(triviaMedia.sourceUrl) +
              '" target="_blank" rel="noopener noreferrer">' +
              escapeHtml(triviaMedia.attribution) +
              "</a>";
          }
          root.innerHTML =
            '<section class="tv-lobby tv-trivia-panel">' +
            '<p style="text-transform:uppercase;color:#38bdf8">TV Trivia · ' +
            escapeHtml(snapshot.phase) +
            "</p><h1>" +
            escapeHtml(
              snapshot.prompt || (snapshot.phase === "countdown" ? "Get ready!" : "Trivia"),
            ) +
            "</h1><p>Question " +
            escapeHtml(String(snapshot.questionNumber)) +
            " of " +
            escapeHtml(String(snapshot.questionCount)) +
            " · " +
            escapeHtml(String(Math.ceil(snapshot.timeRemainingMs / 1000))) +
            "s</p>" +
            '<div class="trivia-question-layout"><div id="trivia-media-stage" class="trivia-media-stage">' +
            mediaHtml +
            '</div><div class="trivia-tv-options">' +
            optionHtml +
            "</div></div><ol>" +
            standingsHtml +
            "</ol></section>";
          var mediaImage = byId("trivia-question-media");
          if (mediaImage) {
            mediaImage.onerror = function () {
              var stage = byId("trivia-media-stage");
              if (stage) stage.innerHTML = "";
            };
          }
        }

        function renderTriviaResults(snapshot) {
          canvas.style.display = "none";
          var winner = snapshot.standings[0] || null;
          var rankingHtml = renderFinalRankings(snapshot.standings, false);
          root.innerHTML =
            '<section class="tv-finale tv-trivia-results">' +
            renderFinaleConfetti() +
            '<p class="tv-finale-kicker">Final Results</p>' +
            '<div class="quickdraw-winner-icon" aria-hidden="true">🏆</div>' +
            "<h1>Trivia Champions</h1>" +
            '<p class="tv-winner-name">' +
            escapeHtml(winner ? winner.displayName : "Great game, everyone!") +
            "</p>" +
            '<ol class="tv-final-rankings">' +
            rankingHtml +
            "</ol>" +
            renderTvPostGameActions() +
            "</section>";
        }

        function renderQuickDrawSnapshot(snapshot) {
          latestQuickDrawSnapshot = snapshot;
          if (snapshot.phase === "completed") {
            renderQuickDrawResults(snapshot);
            return;
          }
          canvas.style.display = "none";
          var cue = "QUICK DRAW";
          if (snapshot.phase === "countdown") cue = "GET READY";
          else if (snapshot.phase === "armed") cue = "WAIT FOR IT…";
          else if (snapshot.phase === "draw") cue = "DRAW!";
          else if (snapshot.phase === "paused") cue = "WAITING FOR PLAYERS";
          else if (snapshot.phase === "reveal" || snapshot.phase === "intermission")
            cue = "ROUND RESULTS";
          else if (snapshot.phase === "completed") cue = "FASTEST GUN";

          var standings = "";
          for (var index = 0; index < snapshot.standings.length; index += 1) {
            var standing = snapshot.standings[index];
            standings +=
              '<li style="display:flex;justify-content:space-between;gap:24px;padding:12px 0;border-bottom:1px solid rgba(253,230,138,.24)"><strong>' +
              escapeHtml(standing.displayName) +
              "</strong><span>" +
              escapeHtml(String(standing.score)) +
              " pts · " +
              escapeHtml(String(standing.roundWins)) +
              " wins</span></li>";
          }
          var results = "";
          var roundResults = snapshot.roundResults || [];
          for (var resultIndex = 0; resultIndex < roundResults.length; resultIndex += 1) {
            var result = roundResults[resultIndex];
            var resultClass = result.falseStarted
              ? "false-start"
              : result.placement
                ? "placed"
                : "no-draw";
            var resultText = result.falseStarted
              ? "✗ FALSE START"
              : result.placement
                ? "#" + result.placement + " +" + result.points
                : "No draw";
            results +=
              '<span class="tv-quickdraw-result ' +
              resultClass +
              '">' +
              escapeHtml(result.displayName) +
              ": " +
              resultText +
              "</span>";
          }
          var badgeArmed = snapshot.phase === "armed" || snapshot.phase === "draw" ? " armed" : "";
          root.innerHTML =
            '<section class="tv-lobby tv-quickdraw-live" style="background:linear-gradient(145deg,#2b1608,#78350f);color:#fff7ed;min-height:100vh;padding:40px;box-sizing:border-box">' +
            '<div class="quickdraw-sun" aria-hidden="true"></div>' +
            '<div class="tv-quickdraw-scene">' +
            '<p style="text-transform:uppercase;color:#fde68a">Quick Draw · ' +
            escapeHtml(snapshot.phase) +
            "</p>" +
            '<div class="tv-quickdraw-badge' +
            badgeArmed +
            '"><span class="tv-quickdraw-hat" aria-hidden="true">🤠</span></div>' +
            '<h1 class="tv-quickdraw-cue-text" style="color:' +
            (snapshot.drawCueVisible ? "#fff" : "#fde68a") +
            '">' +
            escapeHtml(cue) +
            "</h1>" +
            '<p style="font-size:28px">' +
            (snapshot.phase === "paused"
              ? "Waiting for players to return or join"
              : "Round " + snapshot.roundNumber + " of " + snapshot.roundCount) +
            "</p>" +
            '<div style="display:flex;flex-wrap:wrap;justify-content:center;gap:10px;margin:20px">' +
            results +
            "</div>" +
            '<ol style="list-style:none;max-width:760px;margin:20px auto;padding:0;font-size:24px">' +
            standings +
            "</ol>" +
            "</div></section>";
        }

        function renderQuickDrawResults(snapshot) {
          canvas.style.display = "none";
          var winner = snapshot.standings[0] || null;
          var rankingHtml = renderFinalRankings(snapshot.standings, true);
          root.innerHTML =
            '<section class="tv-finale tv-quickdraw-results">' +
            '<div class="quickdraw-sun" aria-hidden="true"></div>' +
            renderFinaleConfetti() +
            '<p class="tv-finale-kicker">Eleven Rounds · One Fastest Gun</p>' +
            '<div class="quickdraw-winner-icon" aria-hidden="true">🤠</div>' +
            "<h1>Fastest Gun in the Room!</h1>" +
            '<p class="tv-winner-name">' +
            escapeHtml(winner ? winner.displayName : "Quick Draw Complete") +
            "</p>" +
            (winner
              ? '<div class="quickdraw-winner-stats"><span>' +
                escapeHtml(String(winner.score)) +
                " points</span><span>" +
                escapeHtml(String(winner.roundWins)) +
                " round wins</span><span>" +
                escapeHtml(String(winner.falseStarts)) +
                " false starts</span></div>"
              : "") +
            '<ol class="tv-final-rankings">' +
            rankingHtml +
            "</ol>" +
            renderTvPostGameActions() +
            "</section>";
        }

        function renderColorEchoSnapshot(snapshot) {
          latestColorEchoSnapshot = snapshot;
          if (snapshot.phase === "completed") {
            colorEchoPlayback.reset();
            renderColorEchoResults(snapshot);
            return;
          }
          canvas.style.display = "none";
          if (snapshot.phase === "playback") colorEchoPlayback.update(snapshot);
          else colorEchoPlayback.reset();

          var summaries = snapshot.roundSummaries || [];
          var summaryKey = "";
          for (var summaryIndex = 0; summaryIndex < summaries.length; summaryIndex += 1) {
            summaryKey +=
              summaries[summaryIndex].playerId +
              ":" +
              summaries[summaryIndex].roundDelta +
              ":" +
              summaries[summaryIndex].perfect +
              "|";
          }
          var renderKey =
            snapshot.phase +
            "|" +
            snapshot.roundId +
            "|" +
            snapshot.submittedCount +
            "|" +
            summaryKey;
          if (renderKey === colorEchoRenderKey) return;
          colorEchoRenderKey = renderKey;

          var headline = "COLOR ECHO";
          if (snapshot.phase === "countdown") headline = "LISTEN, THEN ECHO";
          else if (snapshot.phase === "roundIntro") headline = "ROUND " + snapshot.roundNumber;
          else if (snapshot.phase === "playback") headline = "WATCH · LISTEN";
          else if (snapshot.phase === "input") headline = "PLAYERS, ECHO NOW";
          else if (snapshot.phase === "paused") headline = "WAITING FOR PLAYERS";
          else if (snapshot.phase === "reveal" || snapshot.phase === "intermission")
            headline = snapshot.roundNumber > 6 ? "KEEP THE ECHO ALIVE" : "ROUND RESULTS";

          var resultHtml = "";
          for (var resultIndex = 0; resultIndex < summaries.length; resultIndex += 1) {
            var result = summaries[resultIndex];
            resultHtml +=
              '<span class="' +
              (result.perfect ? "perfect" : "") +
              '"><strong>' +
              escapeHtml(result.displayName) +
              "</strong> · " +
              (result.perfect
                ? "PERFECT · "
                : result.correct + "/" + snapshot.patternLength + " · ") +
              (result.roundDelta >= 0 ? "+" : "") +
              escapeHtml(String(result.roundDelta)) +
              "</span>";
          }

          var standingsHtml = "";
          for (
            var standingIndex = 0;
            standingIndex < snapshot.standings.length;
            standingIndex += 1
          ) {
            var standing = snapshot.standings[standingIndex];
            standingsHtml +=
              "<li><strong>" +
              escapeHtml(standing.displayName) +
              "</strong> · " +
              escapeHtml(String(standing.score)) +
              " pts · " +
              escapeHtml(String(standing.perfectReproductions)) +
              " perfect</li>";
          }

          root.innerHTML =
            '<section class="tv-color-echo" data-testid="tv-color-echo" data-color-echo-phase="' +
            escapeAttribute(snapshot.phase) +
            '">' +
            '<p class="tv-color-echo-meta">COLOR ECHO · ROUND ' +
            escapeHtml(String(snapshot.roundNumber || "—")) +
            " · " +
            escapeHtml(String(snapshot.patternLength)) +
            " SIGNALS</p>" +
            "<h1>" +
            escapeHtml(headline) +
            "</h1>" +
            '<button type="button" onclick="handleColorEchoSoundToggle()" aria-pressed="' +
            (sfx.muted ? "true" : "false") +
            '">' +
            (sfx.muted ? "🔇 Sound off" : "🔊 Sound on") +
            "</button>" +
            '<div class="tv-color-echo-pad">' +
            '<div class="tv-color-echo-signal red" data-color-echo-tv-signal="red"><span class="shape circle"></span></div>' +
            '<div class="tv-color-echo-signal yellow" data-color-echo-tv-signal="yellow"><span class="shape triangle"></span></div>' +
            '<div class="tv-color-echo-signal green" data-color-echo-tv-signal="green"><span class="shape square"></span></div>' +
            '<div class="tv-color-echo-signal blue" data-color-echo-tv-signal="blue"><span class="shape star"></span></div>' +
            "</div>" +
            '<p class="tv-color-echo-meta">' +
            (snapshot.phase === "input"
              ? snapshot.submittedCount + " of " + snapshot.eligibleCount + " submitted"
              : snapshot.phase === "paused"
                ? "The Pattern will replay when a Player returns"
                : snapshot.phase === "playback"
                  ? "Follow every light and tone"
                  : "") +
            "</p>" +
            '<div class="tv-color-echo-results-row">' +
            resultHtml +
            "</div>" +
            '<ol class="tv-color-echo-standings">' +
            standingsHtml +
            "</ol></section>";
        }

        function renderColorEchoResults(snapshot) {
          canvas.style.display = "none";
          var winner = snapshot.standings[0] || null;
          var rankings = "";
          for (var index = 0; index < snapshot.standings.length; index += 1) {
            var standing = snapshot.standings[index];
            rankings +=
              '<li><span class="tv-final-rank-number">' +
              escapeHtml(String(index + 1)) +
              "</span><strong>" +
              escapeHtml(standing.displayName) +
              '</strong><span class="tv-final-rank-detail">' +
              escapeHtml(String(standing.score)) +
              " pts · " +
              escapeHtml(String(standing.perfectReproductions)) +
              " perfect</span></li>";
          }
          root.innerHTML =
            '<section class="tv-finale tv-color-echo-results">' +
            renderFinaleConfetti() +
            '<p class="tv-finale-kicker">' +
            (snapshot.finishReason === "mastery"
              ? "64-Signal Mastery"
              : "The Echo Broke on Round " + snapshot.roundNumber) +
            "</p>" +
            '<div class="quickdraw-winner-icon" aria-hidden="true">🔊</div>' +
            "<h1>Color Echo Champions</h1>" +
            '<p class="tv-winner-name">' +
            escapeHtml(winner ? winner.displayName : "Great memory, everyone!") +
            "</p>" +
            '<p style="font-size:28px">The room reached <strong>' +
            escapeHtml(String(snapshot.patternLength)) +
            " Signals</strong></p>" +
            '<ol class="tv-final-rankings">' +
            rankings +
            "</ol>" +
            renderTvPostGameActions() +
            "</section>";
        }

        function renderCupcakeStackSnapshot(snapshot) {
          latestCupcakeStackSnapshot = snapshot;
          if (snapshot.phase === "completed") {
            renderCupcakeStackResults(snapshot);
            return;
          }
          canvas.style.display = "block";
          if (canvas.width !== 1280 || canvas.height !== 720) {
            canvas.width = 1280;
            canvas.height = 720;
          }
          drawCupcakeStack(snapshot);
          var headline =
            snapshot.phase === "tutorial"
              ? "CAKE = CATCH · JUNK = DODGE"
              : snapshot.phase === "countdown"
                ? "READY TO BAKE!"
                : snapshot.phase === "finalDrop"
                  ? "LAST CHANCE!"
                  : snapshot.phase === "measuring"
                    ? "MEASURING THE STACKS…"
                    : "STACK IT SKY-HIGH!";
          var timer =
            snapshot.phase === "playing"
              ? Math.max(
                  0,
                  Math.ceil((snapshot.roundDurationMs - snapshot.roundElapsedMs) / 1000),
                ) + "s"
              : snapshot.phase === "countdown"
                ? String(Math.max(1, Math.ceil(snapshot.timeRemainingMs / 1000)))
                : "";
          root.innerHTML =
            '<section class="tv-cupcake-hud" data-testid="tv-cupcake-stack" data-cupcake-phase="' +
            escapeAttribute(snapshot.phase) +
            '"><div class="tv-cupcake-room">CUPCAKE STACK · ' +
            escapeHtml(roomCode) +
            "</div><h1>" +
            escapeHtml(headline) +
            '</h1><div class="tv-cupcake-top-right"><div class="tv-cupcake-invite-slot"></div><div class="timer">' +
            escapeHtml(timer) +
            "</div></div></section>";
        }

        function drawCupcakeStack(snapshot) {
          var ctx = canvas.getContext("2d");
          if (!ctx) return;

          // The backdrop gradient never changes between frames — only the
          // station panels drawn on top do. Rebuilding the gradient and
          // filling the whole canvas with it on every snapshot wastes time
          // the stations/falling ingredients actually need. Render it once
          // into an offscreen canvas and blit that instead, following the
          // same pattern as critterBoardLayer/fireflyGardenLayer.
          var backdropLayerKey = canvas.width + ":" + canvas.height;
          if (!cupcakeBackdropLayer || cupcakeBackdropLayerKey !== backdropLayerKey) {
            var layer = document.createElement("canvas");
            layer.width = canvas.width;
            layer.height = canvas.height;
            var layerCtx = layer.getContext("2d");
            var gradient = layerCtx.createLinearGradient(0, 0, canvas.width, canvas.height);
            gradient.addColorStop(0, "#fff7ed");
            gradient.addColorStop(0.55, "#fce7f3");
            gradient.addColorStop(1, "#fbcfe8");
            layerCtx.fillStyle = gradient;
            layerCtx.fillRect(0, 0, canvas.width, canvas.height);
            cupcakeBackdropLayer = layer;
            cupcakeBackdropLayerKey = backdropLayerKey;
          }
          ctx.drawImage(cupcakeBackdropLayer, 0, 0);

          var count = Math.max(1, snapshot.stations.length);
          var columns = count <= 2 ? count : 2;
          var rows = count <= 2 ? 1 : 2;
          var gap = 18;
          var top = 88;
          var stationWidth = (canvas.width - gap * (columns + 1)) / columns;
          var stationHeight = (canvas.height - top - gap * (rows + 1)) / rows;
          for (var index = 0; index < snapshot.stations.length; index += 1) {
            var column = index % columns;
            var row = Math.floor(index / columns);
            drawCupcakeStation(
              ctx,
              snapshot.stations[index],
              gap + column * (stationWidth + gap),
              top + gap + row * (stationHeight + gap),
              stationWidth,
              stationHeight,
            );
          }
        }

        function drawCupcakeStation(ctx, station, x, y, width, height) {
          ctx.save();
          cupcakeRoundedRect(ctx, x, y, width, height, 22);
          ctx.fillStyle = "rgba(255,255,255,0.72)";
          ctx.fill();
          ctx.lineWidth = 4;
          ctx.strokeStyle = station.color || "#fb7185";
          ctx.stroke();

          ctx.fillStyle = "#4a1938";
          ctx.font = "bold 22px Arial";
          ctx.textAlign = "left";
          ctx.fillText(station.displayName, x + 18, y + 30);
          ctx.textAlign = "right";
          ctx.fillText(
            (station.completedCupcakes > 0 ? station.completedCupcakes + " FULL · " : "") +
              station.heightUnits +
              " HIGH · ×" +
              station.combo,
            x + width - 18,
            y + 30,
          );

          var playTop = y + 42;
          var catchY = y + height - 36;
          var laneWidth = width / 3;
          ctx.strokeStyle = "rgba(159,18,57,0.18)";
          ctx.lineWidth = 2;
          for (var laneLine = 1; laneLine < 3; laneLine += 1) {
            ctx.beginPath();
            ctx.moveTo(x + laneLine * laneWidth, playTop);
            ctx.lineTo(x + laneLine * laneWidth, y + height - 8);
            ctx.stroke();
          }

          var trayX = x + laneWidth * (station.lane + 0.5);
          ctx.fillStyle = station.color || "#fb7185";
          cupcakeRoundedRect(
            ctx,
            trayX - Math.min(54, laneWidth * 0.32),
            catchY,
            Math.min(108, laneWidth * 0.64),
            15,
            7,
          );
          ctx.fill();
          ctx.fillStyle = "#9f1239";
          ctx.beginPath();
          ctx.moveTo(trayX - 36, catchY);
          ctx.lineTo(trayX + 36, catchY);
          ctx.lineTo(trayX + 28, catchY - 28);
          ctx.lineTo(trayX - 28, catchY - 28);
          ctx.closePath();
          ctx.fill();

          var stackLayout = cupcakeStackLayout(height, catchY, station.stack.length);
          var pieceHeight = stackLayout.pieceHeight;
          var stackY = stackLayout.stackBaseY;
          for (var stackIndex = 0; stackIndex < station.stack.length; stackIndex += 1) {
            var kind = station.stack[stackIndex];
            stackY -= pieceHeight;
            drawCupcakeItem(ctx, kind, trayX, stackY, Math.min(76, laneWidth * 0.55), pieceHeight);
          }

          for (var dropIndex = 0; dropIndex < station.drops.length; dropIndex += 1) {
            var drop = station.drops[dropIndex];
            var dropX = x + laneWidth * (drop.lane + 0.5);
            var dropY = playTop + 12 + drop.progress * (stackLayout.dropTargetY - playTop - 12);
            drawCupcakeItem(ctx, drop.kind, dropX, dropY, Math.min(68, laneWidth * 0.5), 30);
          }

          if (station.brineRemainingMs > 0) {
            ctx.fillStyle = "rgba(101, 163, 13, 0.2)";
            for (var splash = 0; splash < 7; splash += 1) {
              ctx.beginPath();
              ctx.arc(
                x + 30 + ((splash * 83) % Math.max(50, width - 60)),
                y + 70 + ((splash * 47) % Math.max(60, height - 120)),
                12 + (splash % 3) * 7,
                0,
                Math.PI * 2,
              );
              ctx.fill();
            }
            ctx.fillStyle = "#3f6212";
            ctx.font = "bold 18px Arial";
            ctx.textAlign = "center";
            ctx.fillText("PICKLE SPLASH!", x + width / 2, y + 62);
          }
          if (!station.connected) {
            ctx.fillStyle = "rgba(74,25,56,0.58)";
            ctx.fillRect(x, y, width, height);
            ctx.fillStyle = "#ffffff";
            ctx.font = "bold 24px Arial";
            ctx.textAlign = "center";
            ctx.fillText("RECONNECTING…", x + width / 2, y + height / 2);
          }
          ctx.restore();
        }

        function drawCupcakeFace(ctx, centerX, centerY, unit, mood) {
          ctx.save();
          ctx.fillStyle = "#3f1330";
          ctx.strokeStyle = "#3f1330";
          if (mood === "grumpy") {
            ctx.lineWidth = Math.max(1, 1.4 * unit);
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(centerX - 6 * unit, centerY - 4 * unit);
            ctx.lineTo(centerX - 2 * unit, centerY - 1 * unit);
            ctx.moveTo(centerX - 6 * unit, centerY - 1 * unit);
            ctx.lineTo(centerX - 2 * unit, centerY - 4 * unit);
            ctx.moveTo(centerX + 2 * unit, centerY - 4 * unit);
            ctx.lineTo(centerX + 6 * unit, centerY - 1 * unit);
            ctx.moveTo(centerX + 2 * unit, centerY - 1 * unit);
            ctx.lineTo(centerX + 6 * unit, centerY - 4 * unit);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(centerX, centerY + 4 * unit, 3.6 * unit, Math.PI * 1.15, Math.PI * 1.85);
            ctx.stroke();
          } else {
            ctx.beginPath();
            ctx.arc(centerX - 4 * unit, centerY - 2 * unit, 1.3 * unit, 0, Math.PI * 2);
            ctx.arc(centerX + 4 * unit, centerY - 2 * unit, 1.3 * unit, 0, Math.PI * 2);
            ctx.fill();
            ctx.lineWidth = Math.max(1, 1.3 * unit);
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.arc(centerX, centerY + 1.5 * unit, 3.4 * unit, Math.PI * 0.12, Math.PI * 0.88);
            ctx.stroke();
            ctx.fillStyle = "#fb7185";
            ctx.globalAlpha = 0.55;
            ctx.beginPath();
            ctx.arc(centerX - 7.5 * unit, centerY + 1.5 * unit, 2 * unit, 0, Math.PI * 2);
            ctx.arc(centerX + 7.5 * unit, centerY + 1.5 * unit, 2 * unit, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          ctx.restore();
        }

        function drawCupcakeItem(ctx, kind, centerX, centerY, itemWidth, itemHeight) {
          ctx.save();
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          var unit = Math.min(itemWidth, itemHeight) / 24;
          if (kind === "cake") {
            var wrapWidth = itemWidth * 0.94;
            var wrapTop = centerY - itemHeight / 2 + itemHeight * 0.22;
            var wrapHeight = itemHeight * 0.78;
            ctx.fillStyle = "#fbbf24";
            ctx.beginPath();
            ctx.moveTo(centerX - wrapWidth / 2, wrapTop);
            var pleats = 5;
            for (var pleat = 0; pleat <= pleats; pleat += 1) {
              var pleatX = centerX - wrapWidth / 2 + (wrapWidth / pleats) * pleat;
              var pleatBottom = wrapTop + wrapHeight + (pleat % 2 === 0 ? 0 : wrapHeight * 0.14);
              ctx.lineTo(pleatX, pleat === 0 ? wrapTop : pleatBottom);
            }
            ctx.lineTo(centerX + wrapWidth / 2, wrapTop);
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = "#d97706";
            ctx.lineWidth = Math.max(1, unit * 0.7);
            ctx.beginPath();
            for (var stripe = 1; stripe < pleats; stripe += 1) {
              var stripeX = centerX - wrapWidth / 2 + (wrapWidth / pleats) * stripe;
              ctx.moveTo(stripeX, wrapTop + 2);
              ctx.lineTo(stripeX, wrapTop + wrapHeight);
            }
            ctx.stroke();
            var domeGradient = ctx.createLinearGradient(
              0,
              centerY - itemHeight / 2,
              0,
              wrapTop + 2,
            );
            domeGradient.addColorStop(0, "#fed7aa");
            domeGradient.addColorStop(1, "#f59e42");
            ctx.fillStyle = domeGradient;
            ctx.beginPath();
            ctx.ellipse(
              centerX,
              wrapTop,
              wrapWidth * 0.52,
              itemHeight * 0.26,
              0,
              Math.PI,
              Math.PI * 2,
            );
            ctx.fill();
            ctx.fillStyle = "rgba(255,255,255,0.55)";
            ctx.beginPath();
            ctx.ellipse(
              centerX - wrapWidth * 0.18,
              wrapTop - itemHeight * 0.1,
              wrapWidth * 0.12,
              itemHeight * 0.08,
              -0.4,
              0,
              Math.PI * 2,
            );
            ctx.fill();
            drawCupcakeFace(ctx, centerX, wrapTop + wrapHeight * 0.32, unit * 0.85, "happy");
          } else if (kind === "frosting") {
            var swirlGradient = ctx.createLinearGradient(
              0,
              centerY - itemHeight / 2,
              0,
              centerY + itemHeight / 2,
            );
            swirlGradient.addColorStop(0, "#f5d0fe");
            swirlGradient.addColorStop(1, "#c084fc");
            ctx.fillStyle = swirlGradient;
            var swirlTiers = 3;
            for (var tier = swirlTiers - 1; tier >= 0; tier -= 1) {
              var tierRadius = (itemWidth / 2) * (1 - tier * 0.22);
              var tierY = centerY + itemHeight / 2 - tierRadius * 1.05 - tier * itemHeight * 0.22;
              ctx.beginPath();
              ctx.ellipse(centerX, tierY, tierRadius, tierRadius * 0.82, 0, 0, Math.PI * 2);
              ctx.fill();
            }
            ctx.fillStyle = "rgba(255,255,255,0.7)";
            ctx.beginPath();
            ctx.arc(
              centerX - itemWidth * 0.14,
              centerY - itemHeight * 0.28,
              itemWidth * 0.09,
              0,
              Math.PI * 2,
            );
            ctx.fill();
            var sprinkleColors = ["#fb7185", "#38bdf8", "#facc15", "#4ade80"];
            for (var sprinkle = 0; sprinkle < 4; sprinkle += 1) {
              ctx.strokeStyle = sprinkleColors[sprinkle % sprinkleColors.length];
              ctx.lineWidth = Math.max(1.2, unit * 0.9);
              ctx.lineCap = "round";
              var sprinkleAngle = sprinkle * 1.4 + 0.4;
              var sprinkleX = centerX + Math.cos(sprinkleAngle) * itemWidth * 0.26;
              var sprinkleY =
                centerY - itemHeight * 0.05 + Math.sin(sprinkleAngle) * itemHeight * 0.22;
              ctx.beginPath();
              ctx.moveTo(sprinkleX - unit, sprinkleY - unit * 0.4);
              ctx.lineTo(sprinkleX + unit, sprinkleY + unit * 0.4);
              ctx.stroke();
            }
          } else if (kind === "cherry") {
            var cherryRadius = Math.min(itemHeight, itemWidth) * 0.36;
            ctx.strokeStyle = "#166534";
            ctx.lineWidth = Math.max(2, unit);
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(centerX, centerY - cherryRadius * 0.7);
            ctx.quadraticCurveTo(
              centerX + cherryRadius * 0.9,
              centerY - cherryRadius * 2.1,
              centerX + cherryRadius * 1.7,
              centerY - cherryRadius * 1.8,
            );
            ctx.stroke();
            ctx.fillStyle = "#4ade80";
            ctx.beginPath();
            ctx.ellipse(
              centerX + cherryRadius * 1.9,
              centerY - cherryRadius * 1.9,
              cherryRadius * 0.6,
              cherryRadius * 0.32,
              0.5,
              0,
              Math.PI * 2,
            );
            ctx.fill();
            var cherryGradient = ctx.createRadialGradient(
              centerX - cherryRadius * 0.35,
              centerY - cherryRadius * 0.35,
              cherryRadius * 0.1,
              centerX,
              centerY,
              cherryRadius,
            );
            cherryGradient.addColorStop(0, "#fca5a5");
            cherryGradient.addColorStop(0.45, "#e11d48");
            cherryGradient.addColorStop(1, "#9f1239");
            ctx.fillStyle = cherryGradient;
            ctx.beginPath();
            ctx.arc(centerX, centerY, cherryRadius, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = "rgba(255,255,255,0.75)";
            ctx.beginPath();
            ctx.arc(
              centerX - cherryRadius * 0.32,
              centerY - cherryRadius * 0.32,
              cherryRadius * 0.22,
              0,
              Math.PI * 2,
            );
            ctx.fill();
            drawCupcakeFace(ctx, centerX, centerY + cherryRadius * 0.18, unit * 0.5, "happy");
          } else if (kind === "sock") {
            var sockUnit = Math.min(itemWidth, itemHeight) / 42;
            ctx.fillStyle = "#a1a1aa";
            ctx.beginPath();
            ctx.moveTo(centerX - 13 * sockUnit, centerY - 20 * sockUnit);
            ctx.lineTo(centerX + 13 * sockUnit, centerY - 20 * sockUnit);
            ctx.lineTo(centerX + 13 * sockUnit, centerY + 2 * sockUnit);
            ctx.quadraticCurveTo(
              centerX + 13 * sockUnit,
              centerY + 20 * sockUnit,
              centerX + 26 * sockUnit,
              centerY + 20 * sockUnit,
            );
            ctx.lineTo(centerX + 26 * sockUnit, centerY + 12 * sockUnit);
            ctx.quadraticCurveTo(
              centerX + 3 * sockUnit,
              centerY + 12 * sockUnit,
              centerX - 13 * sockUnit,
              centerY + 2 * sockUnit,
            );
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = "#52525b";
            ctx.lineWidth = Math.max(1.5, 1.8 * sockUnit);
            ctx.stroke();
            ctx.fillStyle = "#f472b6";
            ctx.fillRect(
              centerX - 13 * sockUnit,
              centerY - 20 * sockUnit,
              26 * sockUnit,
              6 * sockUnit,
            );
            ctx.strokeStyle = "rgba(82,82,91,0.7)";
            ctx.lineWidth = Math.max(1, sockUnit);
            for (var wiggle = 0; wiggle < 3; wiggle += 1) {
              var wiggleY = centerY - 8 * sockUnit + wiggle * 9 * sockUnit;
              ctx.beginPath();
              ctx.moveTo(centerX - 5 * sockUnit, wiggleY);
              ctx.lineTo(centerX + 4 * sockUnit, wiggleY + 2 * sockUnit);
              ctx.stroke();
            }
            ctx.fillStyle = "rgba(75,85,99,0.25)";
            for (var stink = 0; stink < 2; stink += 1) {
              ctx.beginPath();
              ctx.arc(
                centerX + 20 * sockUnit,
                centerY - 22 * sockUnit - stink * 8 * sockUnit,
                (3 - stink) * sockUnit,
                0,
                Math.PI * 2,
              );
              ctx.fill();
            }
            drawCupcakeFace(
              ctx,
              centerX - 4 * sockUnit,
              centerY + 6 * sockUnit,
              sockUnit * 0.7,
              "grumpy",
            );
          } else {
            var pickleUnit = Math.min(itemWidth, itemHeight) / 42;
            var pickleGradient = ctx.createLinearGradient(
              centerX - 10 * pickleUnit,
              centerY - 20 * pickleUnit,
              centerX + 10 * pickleUnit,
              centerY + 20 * pickleUnit,
            );
            pickleGradient.addColorStop(0, "#84cc16");
            pickleGradient.addColorStop(1, "#4d7c0f");
            ctx.fillStyle = pickleGradient;
            ctx.beginPath();
            ctx.ellipse(centerX, centerY, 10 * pickleUnit, 20 * pickleUnit, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = "#365314";
            ctx.lineWidth = Math.max(1.5, 1.6 * pickleUnit);
            ctx.stroke();
            ctx.beginPath();
            for (var bump = -1; bump <= 1; bump += 1) {
              ctx.moveTo(centerX - 6 * pickleUnit, centerY + bump * 8 * pickleUnit);
              ctx.lineTo(centerX + 6 * pickleUnit, centerY + bump * 8 * pickleUnit);
            }
            ctx.stroke();
            ctx.fillStyle = "rgba(255,255,255,0.4)";
            ctx.beginPath();
            ctx.ellipse(
              centerX - 3 * pickleUnit,
              centerY - 10 * pickleUnit,
              2.4 * pickleUnit,
              6 * pickleUnit,
              0.2,
              0,
              Math.PI * 2,
            );
            ctx.fill();
            drawCupcakeFace(ctx, centerX, centerY + 7 * pickleUnit, pickleUnit * 0.55, "grumpy");
          }
          ctx.restore();
        }

        function renderCupcakeStackResults(snapshot) {
          canvas.style.display = "none";
          var standings = snapshot.standings || [];
          var winner = standings.length ? standings[0] : null;
          var rankings = "";
          for (var index = 0; index < standings.length; index += 1) {
            var standing = standings[index];
            rankings +=
              '<li><span class="tv-final-rank-number">#' +
              escapeHtml(String(standing.place)) +
              "</span><strong>" +
              escapeHtml(standing.displayName) +
              '</strong><span class="tv-final-rank-detail">' +
              escapeHtml(String(standing.heightUnits)) +
              " high" +
              (standing.completedCupcakes
                ? " · " + escapeHtml(String(standing.completedCupcakes)) + " full"
                : "") +
              " · " +
              escapeHtml(String(standing.stylePoints)) +
              " style" +
              (standing.awards.length
                ? ' · <span class="tv-cupcake-award">AWARD</span> ' +
                  escapeHtml(standing.awards.join(" · "))
                : "") +
              "</span></li>";
          }
          root.innerHTML =
            '<section class="tv-finale tv-cupcake-results">' +
            renderFinaleConfetti() +
            '<p class="tv-finale-kicker">THE FINAL MEASUREMENT</p>' +
            '<div class="tv-cupcake-winner-icon" aria-hidden="true"><span></span></div>' +
            "<h1>" +
            escapeHtml(winner ? winner.displayName + " Wins!" : "Beautiful Bakes!") +
            "</h1>" +
            '<p class="tv-winner-name">' +
            escapeHtml(winner ? winner.heightUnits + " HIGH" : "") +
            "</p>" +
            '<ol class="tv-final-rankings">' +
            rankings +
            "</ol>" +
            renderTvPostGameActions() +
            "</section>";
        }

        var fuseTvRenderKey = "";
        var fuseVisualEffects = [];
        var fusePreviousSeats = new Map();
        var fusePreviousBreakables = new Set();
        var fusePreviousPickups = new Map();
        var fusePreviousBombs = new Map();
        var fuseBombBeatStates = new Map();
        var fuseRunnerMotion = new Map();
        var fuseScorchMarks = [];
        var fuseEffectRoundId = -1;
        var fuseLastEffectAt = 0;
        var fuseHadBlasts = false;
        var fusePreviousPhase = "";
        var fusePreviousCountdownSecond = -1;
        var fuseRunCueUntil = 0;
        var fuseVictoryPlayedFor = -1;
        var fuseHazardSignature = "";
        var fuseArenaLayer = null;
        var fuseArenaLayerKey = "";
        var fuseStaticLayer = null;
        var fuseStaticLayerKey = "";
        var fuseStaticLayerSnapshot = null;
        var fuseVignetteLayer = null;
        var fuseBoardCanvas = null;
        var fuseBoardContext = null;
        var fuseFrameNow = 0;
        var fuseImageAssets = {};
        var FUSE_TV_FRAME_INTERVAL_MS = 1000 / 30;
        var fuseSnapshotRenderer = new FixedRateSnapshotRenderer({
          frameIntervalMs: FUSE_TV_FRAME_INTERVAL_MS,
          onRender: function (snapshot) {
            renderFuseRunnersFrame(snapshot);
            if (snapshot.phase === "completed") fuseSnapshotRenderer.stop();
          },
          requestFrame: function (callback) {
            return window.requestAnimationFrame(callback);
          },
          cancelFrame: function (requestId) {
            window.cancelAnimationFrame(requestId);
          },
          now: function () {
            return performance.now();
          },
          onPerformanceSample: function (sample) {
            performanceSampler.observePresentation(sample);
          },
        });
        // Trusts classification.recommendedFusePreset rather than re-deriving its thresholds here:
        // classifyTvSupport() in benchmarkMetrics.ts is the single source of truth for what counts
        // as fast enough, and the benchmarkVersion gate already rejects a result computed by an
        // older classifier whose thresholds (or presence of this field) might differ.
        function fuseRichBenchmarkApproved() {
          try {
            var stored = JSON.parse(
              localStorage.getItem("tvGraphicsBenchmark:lastResult") || "null",
            );
            return !!(
              stored &&
              stored.benchmarkVersion >= 2 &&
              stored.classification &&
              stored.classification.recommendedFusePreset === "canvas-rich"
            );
          } catch (_e) {
            return false;
          }
        }

        var fuseKnownTvBrowser = /Web0S|WebOS|NetCast|SmartTV|Tizen/i.test(navigator.userAgent);
        var fuseLowPowerTv = fuseKnownTvBrowser && !fuseRichBenchmarkApproved();

        // A one-time benchmark can approve rich graphics on a cold TV and still miss sustained
        // degradation (thermal throttling, background contention) over a multi-minute Blast Tour.
        // Reuses the platform's existing rolling ClientPerformanceReport (see performanceSampler
        // below) rather than sampling render time separately — its p95FrameMs/longFrameCount are
        // already computed over the trailing window on the same 40 ms frame budget classifyTvSupport
        // uses, so this only needs to react to a report, not measure anything new. Downgrade is
        // one-way: once compatibility graphics are active there is no lower tier to fall further to.
        function maybeDowngradeFuseRunnersForPerformance(report) {
          if (fuseLowPowerTv) return;
          if (!latestFuseRunnersSnapshot || latestFuseRunnersSnapshot.phase !== "battle") return;
          if (!exceedsTvPresentationBudget(report)) return;
          fuseLowPowerTv = true;
          console.warn(
            "[fuse-runners] falling back to compatibility graphics after a sustained slow window",
            report,
          );
        }
        var fuseReducedMotion = false;
        var fuseReducedFlash = false;
        try {
          fuseReducedMotion = localStorage.getItem("3dtanks.fuse.reducedMotion") === "true";
          fuseReducedFlash = localStorage.getItem("3dtanks.fuse.reducedFlash") === "true";
        } catch (_e) {}

        function fuseMarker(marker) {
          if (marker === "circle") return "●";
          if (marker === "triangle") return "▲";
          if (marker === "diamond") return "◆";
          return "■";
        }

        function fusePanForX(x, width) {
          return Math.max(-0.85, Math.min(0.85, (x / Math.max(1, width - 1)) * 1.7 - 0.85));
        }

        function fusePercentLabel(value) {
          return String(Math.round(value * 100)) + "%";
        }

        function renderFuseSettings() {
          return (
            '<div class="tv-fuse-settings" aria-label="Fuse Runners accessibility controls">' +
            '<label>SFX <span id="fuse-sfx-volume">' +
            escapeHtml(fusePercentLabel(sfx.fuseSfxVolume)) +
            '</span><input type="range" min="0" max="100" step="10" value="' +
            escapeAttribute(String(Math.round(sfx.fuseSfxVolume * 100))) +
            "\" oninput=\"handleFuseVolume('sfx', this.value, 'fuse-sfx-volume')\"></label>" +
            '<label>MUSIC <span id="fuse-music-volume">' +
            escapeHtml(fusePercentLabel(sfx.fuseMusicVolume)) +
            '</span><input type="range" min="0" max="100" step="10" value="' +
            escapeAttribute(String(Math.round(sfx.fuseMusicVolume * 100))) +
            "\" oninput=\"handleFuseVolume('music', this.value, 'fuse-music-volume')\"></label>" +
            '<button type="button" class="' +
            (fuseReducedMotion ? "active" : "") +
            '" onclick="handleFuseSetting(\'motion\')">CALM MOTION</button>' +
            '<button type="button" class="' +
            (fuseReducedFlash ? "active" : "") +
            '" onclick="handleFuseSetting(\'flash\')">LOW FLASH</button></div>'
          );
        }

        function processFuseAudio(snapshot) {
          var countdownSecond = Math.max(1, Math.ceil(snapshot.countdownMs / 1000));
          if (snapshot.phase === "countdown" && countdownSecond !== fusePreviousCountdownSecond) {
            fusePreviousCountdownSecond = countdownSecond;
            sfx.playFuseRoundCue(false);
          }
          if (snapshot.phase === "battle" && fusePreviousPhase === "countdown") {
            fuseRunCueUntil = Date.now() + 900;
            sfx.playFuseRoundCue(true);
          }
          if (snapshot.phase === "roundResult" && fusePreviousPhase === "battle") {
            sfx.playFuseRoundWin();
          }
          if (snapshot.phase === "completed" && fuseVictoryPlayedFor !== snapshot.roundId) {
            fuseVictoryPlayedFor = snapshot.roundId;
            sfx.playFuseVictory();
          }
          if (snapshot.phase !== "countdown") fusePreviousCountdownSecond = -1;
          fusePreviousPhase = snapshot.phase;
        }

        function renderFuseRunnersSnapshot(snapshot) {
          latestFuseRunnersSnapshot = snapshot;
          processFuseAudio(snapshot);
          updateFuseVisualEffects(snapshot);
          fuseSnapshotRenderer.submit(snapshot);
        }

        function renderFuseRunnersFrame(snapshot) {
          if (snapshot.phase === "completed" && snapshot.result) {
            clearTvShellGameInfo();
            var resultRows = "";
            var fuseWinnerCount = 0;
            for (
              var resultIndex = 0;
              resultIndex < snapshot.result.standings.length;
              resultIndex += 1
            ) {
              var standing = snapshot.result.standings[resultIndex];
              if (standing.place === 1) fuseWinnerCount += 1;
              resultRows +=
                "<li><strong>" +
                escapeHtml(String(standing.place) + ". " + standing.displayName) +
                "</strong><span>" +
                escapeHtml(String(standing.points)) +
                " pts · " +
                escapeHtml(String(standing.roundWins)) +
                " crowns · " +
                escapeHtml(String(standing.nearMisses)) +
                " near misses</span></li>";
            }
            var winningStanding = snapshot.result.standings.length
              ? snapshot.result.standings[0]
              : null;
            root.innerHTML =
              '<section class="tv-finale tv-fuse-finale" data-testid="tv-fuse-runners">' +
              renderFinaleConfetti() +
              '<p class="tv-finale-kicker">BLAST TOUR COMPLETE</p><div class="tv-fuse-winner-mascot" aria-hidden="true"></div><div class="quickdraw-winner-icon">👑</div><h1>' +
              escapeHtml(
                fuseWinnerCount > 1
                  ? "Shared Victory!"
                  : snapshot.result.standings.length
                    ? snapshot.result.standings[0].displayName + " Wins!"
                    : "Tour Complete",
              ) +
              '</h1><p class="tv-fuse-winner-callout">' +
              escapeHtml(
                winningStanding
                  ? winningStanding.displayName +
                      " mastered the clockwork arenas with " +
                      winningStanding.points +
                      " points!"
                  : "The Blast Tour is complete!",
              ) +
              '</p><ol class="tv-final-rankings tv-fuse-podium">' +
              resultRows +
              "</ol>" +
              renderTvPostGameActions() +
              "</section>";
            fuseTvRenderKey = "";
            return;
          }
          var seatKey = "";
          for (var keyIndex = 0; keyIndex < snapshot.seats.length; keyIndex += 1) {
            var keySeat = snapshot.seats[keyIndex];
            seatKey +=
              ":" +
              keySeat.displayName +
              ":" +
              keySeat.points +
              ":" +
              keySeat.nearMisses +
              ":" +
              keySeat.alive +
              ":" +
              keySeat.aiControlled +
              ":" +
              keySeat.placement +
              ":" +
              (keySeat.handoffRemainingMs > 0);
          }
          var phaseLabel =
            snapshot.phase === "paused"
              ? "WAITING FOR A PLAYER"
              : snapshot.phase === "roundResult"
                ? "ROUND COMPLETE"
                : snapshot.phase === "countdown"
                  ? "GET READY"
                  : Math.ceil(snapshot.timeRemainingMs / 1000) + "s";
          var countdownRenderSecond =
            snapshot.phase === "countdown"
              ? Math.ceil((snapshot.countdownMs || snapshot.timeRemainingMs) / 1000)
              : "";
          var renderKey =
            snapshot.phase +
            ":" +
            snapshot.arenaId +
            ":" +
            snapshot.roundId +
            ":" +
            countdownRenderSecond +
            ":" +
            fuseLowPowerTv +
            seatKey;
          if (renderKey !== fuseTvRenderKey) {
            fuseTvRenderKey = renderKey;
            var seatRows = "";
            var handoffSeat = null;
            for (var seatIndex = 0; seatIndex < snapshot.seats.length; seatIndex += 1) {
              var seat = snapshot.seats[seatIndex];
              if (seat.handoffRemainingMs > 0) handoffSeat = seat;
              seatRows +=
                '<div class="tv-fuse-seat ' +
                (seat.alive ? "" : "out") +
                '" style="--fuse-seat:' +
                escapeAttribute(seat.color) +
                '"><b>' +
                fuseMarker(seat.marker) +
                "</b><div><strong>" +
                escapeHtml(seat.displayName) +
                "</strong><span>" +
                (seat.aiControlled ? escapeHtml(seat.aiProfile || "Rival") : "PLAYER") +
                (seat.placement ? " · #" + escapeHtml(String(seat.placement)) : "") +
                (seat.nearMisses ? " · ⚡" + escapeHtml(String(seat.nearMisses)) : "") +
                "</span></div><strong>" +
                escapeHtml(String(seat.points)) +
                "</strong></div>";
            }
            var presentation = "";
            if (snapshot.phase === "countdown") {
              presentation =
                '<div class="tv-fuse-presentation"><small>ROUND ' +
                escapeHtml(String(snapshot.roundNumber)) +
                "</small><strong>" +
                (snapshot.countdownMs > 1000
                  ? escapeHtml(String(Math.max(1, Math.ceil(snapshot.countdownMs / 1000))))
                  : "READY") +
                "</strong><span>" +
                escapeHtml(snapshot.arenaName) +
                "</span></div>";
            } else if (Date.now() < fuseRunCueUntil) {
              presentation =
                '<div class="tv-fuse-presentation run"><small>FUSES LIT</small><strong>RUN!</strong><span>Find an escape.</span></div>';
            } else if (snapshot.phase === "roundResult") {
              var roundWinner = null;
              for (var winnerIndex = 0; winnerIndex < snapshot.seats.length; winnerIndex += 1) {
                if (snapshot.seats[winnerIndex].placement === 1) {
                  roundWinner = snapshot.seats[winnerIndex];
                  break;
                }
              }
              presentation =
                '<div class="tv-fuse-presentation result"><small>ROUND COMPLETE</small><strong>' +
                escapeHtml(roundWinner ? roundWinner.displayName : "DRAW") +
                "</strong><span>" +
                (roundWinner ? "takes the crown!" : "No runner left standing") +
                "</span></div>";
            }
            root.innerHTML =
              '<section class="tv-fuse-shell ' +
              (fuseReducedMotion ? "reduce-motion " : "") +
              (fuseReducedFlash ? "reduce-flash" : "") +
              (fuseLowPowerTv ? "low-power-tv" : "") +
              '" data-testid="tv-fuse-runners" data-fuse-phase="' +
              escapeAttribute(snapshot.phase) +
              '"><canvas id="tv-fuse-board" class="tv-fuse-board" width="780" height="660"></canvas><aside class="tv-fuse-panel"><p class="eyebrow">FUSE RUNNERS · ROUND ' +
              escapeHtml(String(snapshot.roundNumber)) +
              "/" +
              escapeHtml(String(snapshot.roundCount)) +
              "</p><h1>" +
              escapeHtml(snapshot.arenaName) +
              '</h1><h2 id="tv-fuse-phase-label">' +
              escapeHtml(phaseLabel) +
              "</h2>" +
              seatRows +
              "<p>Scan to replace a computer Rival.</p>" +
              renderFuseSettings() +
              "</aside></section>" +
              presentation +
              (handoffSeat
                ? '<div class="tv-fuse-countdown">' +
                  escapeHtml(handoffSeat.displayName + " joined!") +
                  "</div>"
                : "");
            mountTvShellGameInfo(root.querySelector(".tv-fuse-panel"));
          }
          var livePhaseLabel = byId("tv-fuse-phase-label");
          if (livePhaseLabel && livePhaseLabel.textContent !== phaseLabel) {
            livePhaseLabel.textContent = phaseLabel;
          }
          if (snapshot.phase === "battle" && Date.now() >= fuseRunCueUntil) {
            var expiredRunCue = root.querySelector(".tv-fuse-presentation.run");
            if (expiredRunCue && expiredRunCue.parentNode) {
              expiredRunCue.parentNode.removeChild(expiredRunCue);
            }
          }
          var board = byId("tv-fuse-board");
          if (!board || !board.getContext) return;
          if (board.width !== 780 || board.height !== 660) {
            board.width = 780;
            board.height = 660;
          }
          if (fuseBoardCanvas !== board) {
            fuseBoardCanvas = board;
            fuseBoardContext = board.getContext("2d", { alpha: false });
          }
          var context = fuseBoardContext;
          if (!context) return;
          var cellSize = 60;
          fuseFrameNow = Date.now();
          advanceFuseVisualEffects(fuseFrameNow);
          sfx.updateFuseMusic(snapshot);
          var hasBlasts = snapshot.blasts.length > 0;
          if (hasBlasts !== fuseHadBlasts) {
            board.className = hasBlasts ? "tv-fuse-board blasting" : "tv-fuse-board";
            fuseHadBlasts = hasBlasts;
          }
          drawFuseRunnersBoard(context, snapshot, cellSize);
        }

        function advanceFuseVisualEffects(now) {
          var elapsed = fuseLastEffectAt ? Math.min(100, Math.max(0, now - fuseLastEffectAt)) : 50;
          fuseLastEffectAt = now;
          for (var effectIndex = fuseVisualEffects.length - 1; effectIndex >= 0; effectIndex -= 1) {
            fuseVisualEffects[effectIndex].ageMs += elapsed;
            if (fuseVisualEffects[effectIndex].ageMs >= fuseVisualEffects[effectIndex].lifeMs) {
              fuseVisualEffects.splice(effectIndex, 1);
            }
          }
        }

        function updateFuseVisualEffects(snapshot) {
          var now = Date.now();
          if (fuseEffectRoundId !== snapshot.roundId) {
            fuseEffectRoundId = snapshot.roundId;
            fuseVisualEffects = [];
            fuseScorchMarks = [];
            fusePreviousSeats.clear();
            fusePreviousBreakables.clear();
            fusePreviousPickups.clear();
            fusePreviousBombs.clear();
            fuseBombBeatStates.clear();
            fuseRunnerMotion.clear();
            fuseHazardSignature = "";
          }

          var currentSeats = new Map();
          for (var seatIndex = 0; seatIndex < snapshot.seats.length; seatIndex += 1) {
            var seat = snapshot.seats[seatIndex];
            currentSeats.set(seat.seatId, {
              alive: seat.alive,
              x: seat.x,
              y: seat.y,
              color: seat.color,
              marker: seat.marker,
              shield: seat.shield,
              powerUpsCollected: seat.powerUpsCollected,
              aiControlled: seat.aiControlled,
              handoffRemainingMs: seat.handoffRemainingMs,
            });
            var previousSeat = fusePreviousSeats.get(seat.seatId);
            var moveX = previousSeat ? seat.x - previousSeat.x : 0;
            var moveY = previousSeat ? seat.y - previousSeat.y : 0;
            var previousMotion = fuseRunnerMotion.get(seat.seatId);
            if (Math.abs(moveX) + Math.abs(moveY) > 0.001) {
              fuseRunnerMotion.set(seat.seatId, {
                x: moveX,
                y: moveY,
                fromX: previousSeat.x,
                fromY: previousSeat.y,
                toX: seat.x,
                toY: seat.y,
                startedAt: now,
                endsAt: now + 75,
                movingUntil: now + 150,
              });
            } else if (!previousMotion) {
              fuseRunnerMotion.set(seat.seatId, { x: 0, y: 0, movingUntil: 0 });
            }
            if (previousSeat && previousSeat.alive && !seat.alive) {
              fuseVisualEffects.push({
                kind: "death",
                x: previousSeat.x,
                y: previousSeat.y,
                color: previousSeat.color,
                marker: previousSeat.marker,
                seatId: seat.seatId,
                ageMs: 0,
                lifeMs: 1050,
                seed: seat.seatId * 17 + snapshot.roundId * 31,
              });
              sfx.playFuseDefeat(fusePanForX(previousSeat.x, snapshot.width));
            } else if (previousSeat && previousSeat.shield && !seat.shield && seat.alive) {
              fuseVisualEffects.push({
                kind: "shield",
                x: seat.x,
                y: seat.y,
                color: seat.color,
                ageMs: 0,
                lifeMs: 520,
                seed: seat.seatId * 53,
              });
              sfx.playFuseShield(fusePanForX(seat.x, snapshot.width));
            }
            if (
              previousSeat &&
              previousSeat.aiControlled &&
              !seat.aiControlled &&
              seat.handoffRemainingMs > 0
            ) {
              sfx.playFuseHandoff(fusePanForX(seat.x, snapshot.width));
            }
          }

          var currentBreakables = new Set();
          for (
            var breakableIndex = 0;
            breakableIndex < snapshot.breakables.length;
            breakableIndex += 1
          ) {
            var breakable = snapshot.breakables[breakableIndex];
            currentBreakables.add(breakable.x + "," + breakable.y);
          }
          fusePreviousBreakables.forEach(function (key) {
            if (currentBreakables.has(key)) return;
            var parts = key.split(",");
            fuseVisualEffects.push({
              kind: "debris",
              x: Number(parts[0]),
              y: Number(parts[1]),
              ageMs: 0,
              lifeMs: 720,
              seed: Number(parts[0]) * 13 + Number(parts[1]) * 29,
            });
            sfx.playFuseBreakable(fusePanForX(Number(parts[0]), snapshot.width));
          });

          var currentPickups = new Map();
          for (var pickupIndex = 0; pickupIndex < snapshot.pickups.length; pickupIndex += 1) {
            var pickup = snapshot.pickups[pickupIndex];
            currentPickups.set(pickup.x + "," + pickup.y, pickup.type);
          }
          fusePreviousPickups.forEach(function (type, key) {
            if (currentPickups.has(key)) return;
            var parts = key.split(",");
            fuseVisualEffects.push({
              kind: "pickup",
              x: Number(parts[0]),
              y: Number(parts[1]),
              pickupType: type,
              ageMs: 0,
              lifeMs: 760,
              seed: Number(parts[0]) * 23 + Number(parts[1]) * 37,
            });
            sfx.playFusePowerUp(fusePanForX(Number(parts[0]), snapshot.width));
          });

          var currentBombs = new Map();
          for (var bombIndex = 0; bombIndex < snapshot.bombs.length; bombIndex += 1) {
            var bomb = snapshot.bombs[bombIndex];
            currentBombs.set(bomb.id, bomb);
            var previousBomb = fusePreviousBombs.get(bomb.id);
            if (!fusePreviousBombs.has(bomb.id)) {
              sfx.playFuseBombPlaced(fusePanForX(bomb.x, snapshot.width));
            }
            if (previousBomb && !previousBomb.kickArmed && bomb.kickArmed) {
              fuseVisualEffects.push({
                kind: "kick-impact",
                x: bomb.x,
                y: bomb.y,
                ageMs: 0,
                lifeMs: 760,
                seed: bomb.id * 67,
              });
              sfx.playFuseKickImpact(fusePanForX(bomb.x, snapshot.width));
            }
            var beatState = fuseBombBeatStates.get(bomb.id);
            if (!beatState) {
              beatState = {
                windowMs: fuseBeatWindowMs(bomb.remainingMs),
                stage: 0,
              };
            }
            var nextBeatStage = fuseBeatStage(bomb.remainingMs, beatState.windowMs);
            if (nextBeatStage > beatState.stage) {
              beatState.stage = nextBeatStage;
              sfx.playFuseBeat(nextBeatStage, fusePanForX(bomb.x, snapshot.width));
            }
            fuseBombBeatStates.set(bomb.id, beatState);
          }
          fusePreviousBombs.forEach(function (bomb, id) {
            if (currentBombs.has(id)) return;
            fuseBombBeatStates.delete(id);
            var detonated = false;
            for (var blastIndex = 0; blastIndex < snapshot.blasts.length; blastIndex += 1) {
              var blast = snapshot.blasts[blastIndex];
              if (blast.x === bomb.x && blast.y === bomb.y) detonated = true;
            }
            if (!detonated) return;
            if (bomb.remainingMs > 650) {
              sfx.playFuseChainReaction(fusePanForX(bomb.x, snapshot.width));
            }
            fuseVisualEffects.push({
              kind: "detonation",
              x: bomb.x,
              y: bomb.y,
              ageMs: 0,
              lifeMs: 620,
              seed: bomb.id * 41,
            });
            fuseScorchMarks.push({
              x: bomb.x,
              y: bomb.y,
              createdAt: now,
              seed: bomb.id * 41,
            });
            if (fuseScorchMarks.length > 24) fuseScorchMarks.shift();
            sfx.playFuseExplosion(fusePanForX(bomb.x, snapshot.width));
          });

          var hazardKeys = [];
          for (var hazardIndex = 0; hazardIndex < snapshot.hazardCells.length; hazardIndex += 1) {
            hazardKeys.push(
              "h" + snapshot.hazardCells[hazardIndex].x + "," + snapshot.hazardCells[hazardIndex].y,
            );
          }
          for (
            var suddenIndex = 0;
            suddenIndex < snapshot.suddenSparkCells.length;
            suddenIndex += 1
          ) {
            hazardKeys.push(
              "s" +
                snapshot.suddenSparkCells[suddenIndex].x +
                "," +
                snapshot.suddenSparkCells[suddenIndex].y,
            );
          }
          hazardKeys.sort();
          var nextHazardSignature = hazardKeys.join("|");
          if (nextHazardSignature && nextHazardSignature !== fuseHazardSignature) {
            sfx.playFuseHazardWarning(snapshot.arenaId);
          }
          fuseHazardSignature = nextHazardSignature;

          fusePreviousSeats = currentSeats;
          fusePreviousBreakables = currentBreakables;
          fusePreviousPickups = currentPickups;
          fusePreviousBombs = currentBombs;
        }

        function stopFuseRunnersPresentation() {
          if (!fuseSnapshotRenderer.isRunning() && latestFuseRunnersSnapshot === null) return;
          fuseSnapshotRenderer.stop();
          latestFuseRunnersSnapshot = null;
          fuseTvRenderKey = "";
          fuseEffectRoundId = -1;
          fuseLastEffectAt = 0;
          fuseVisualEffects = [];
          fuseScorchMarks = [];
          fusePreviousSeats.clear();
          fusePreviousBreakables.clear();
          fusePreviousPickups.clear();
          fusePreviousBombs.clear();
          fuseBombBeatStates.clear();
          fuseRunnerMotion.clear();
          fuseHazardSignature = "";
          fusePreviousPhase = "";
          fusePreviousCountdownSecond = -1;
          fuseArenaLayer = null;
          fuseArenaLayerKey = "";
          fuseStaticLayer = null;
          fuseStaticLayerKey = "";
          fuseStaticLayerSnapshot = null;
          fuseVignetteLayer = null;
          fuseBoardCanvas = null;
          fuseBoardContext = null;
        }

        function fuseArenaPalette(arenaId) {
          if (arenaId === "tidal-engine") {
            return { floor: "#173b46", tile: "#1f5060", edge: "#67e8f9", accent: "#0e7490" };
          }
          if (arenaId === "gust-gallery") {
            return { floor: "#243b53", tile: "#36536d", edge: "#bae6fd", accent: "#64748b" };
          }
          if (arenaId === "cloud-foundry") {
            return { floor: "#3b4162", tile: "#535b80", edge: "#e0e7ff", accent: "#818cf8" };
          }
          if (arenaId === "cinder-crown") {
            return { floor: "#4a1d16", tile: "#6b2d1d", edge: "#fdba74", accent: "#c2410c" };
          }
          return { floor: "#173d2a", tile: "#24593a", edge: "#bef264", accent: "#3f6212" };
        }

        function drawFuseRunnersBoard(context, snapshot, cellSize) {
          if (fuseLowPowerTv) {
            drawFuseCompatibilityBoard(context, snapshot, cellSize);
            return;
          }
          var palette = fuseArenaPalette(snapshot.arenaId);
          ensureFuseStaticLayer(snapshot, cellSize, palette);
          if (fuseStaticLayer) context.drawImage(fuseStaticLayer, 0, 0);

          drawFuseArenaAmbience(context, snapshot.arenaId);
          drawFuseHazards(context, snapshot.hazardCells, cellSize, palette.edge, false);
          drawFuseHazards(context, snapshot.suddenSparkCells, cellSize, "#fb4934", true);
          drawFuseScorchMarks(context, cellSize);

          for (var pickupIndex = 0; pickupIndex < snapshot.pickups.length; pickupIndex += 1) {
            drawFusePickup(context, snapshot.pickups[pickupIndex], cellSize);
          }
          for (var bombIndex = 0; bombIndex < snapshot.bombs.length; bombIndex += 1) {
            drawFuseBomb(context, snapshot.bombs[bombIndex], snapshot.seats, cellSize);
          }
          for (var blastIndex = 0; blastIndex < snapshot.blasts.length; blastIndex += 1) {
            drawFuseBlast(context, snapshot.blasts[blastIndex], cellSize);
          }
          for (var runnerIndex = 0; runnerIndex < snapshot.seats.length; runnerIndex += 1) {
            drawFuseRunner(context, snapshot.seats[runnerIndex], cellSize);
          }
          drawFuseVisualEffects(context, cellSize);
          if (fuseVignetteLayer) context.drawImage(fuseVignetteLayer, 0, 0);
        }

        function drawFuseCompatibilityBoard(context, snapshot, cellSize) {
          var palette = fuseArenaPalette(snapshot.arenaId);
          ensureFuseStaticLayer(snapshot, cellSize, palette);
          if (fuseStaticLayer) context.drawImage(fuseStaticLayer, 0, 0);
          drawFuseCompatibilityHazards(context, snapshot.hazardCells, cellSize, palette.edge);
          drawFuseCompatibilityHazards(context, snapshot.suddenSparkCells, cellSize, "#fb4934");
          for (var pickupIndex = 0; pickupIndex < snapshot.pickups.length; pickupIndex += 1) {
            drawFuseCompatibilityPickup(context, snapshot.pickups[pickupIndex], cellSize);
          }
          for (var bombIndex = 0; bombIndex < snapshot.bombs.length; bombIndex += 1) {
            drawFuseCompatibilityBomb(context, snapshot.bombs[bombIndex], snapshot.seats, cellSize);
          }
          for (var blastIndex = 0; blastIndex < snapshot.blasts.length; blastIndex += 1) {
            drawFuseCompatibilityBlast(context, snapshot.blasts[blastIndex], cellSize);
          }
          for (var runnerIndex = 0; runnerIndex < snapshot.seats.length; runnerIndex += 1) {
            drawFuseCompatibilityRunner(context, snapshot.seats[runnerIndex], cellSize);
          }
          drawFuseCompatibilityEffects(context, cellSize);
        }

        function drawFuseCompatibilityHazards(context, cells, cellSize, color) {
          context.fillStyle = color;
          context.globalAlpha = 0.42;
          for (var index = 0; index < cells.length; index += 1) {
            var target = cells[index];
            context.fillRect(
              target.x * cellSize + 7,
              target.y * cellSize + 7,
              cellSize - 14,
              cellSize - 14,
            );
          }
          context.globalAlpha = 1;
        }

        function drawFuseCompatibilityPickup(context, pickup, cellSize) {
          var x = pickup.x * cellSize + 30;
          var y = pickup.y * cellSize + 30;
          var style = fusePowerupStyle(pickup.type);
          var token = fuseImageAsset("fuse-powerup-sprite-asset");
          context.save();
          context.translate(x, y);
          if (token && token.complete && token.naturalWidth > 0) {
            context.drawImage(token, -23, -23, 46, 46);
          } else {
            context.fillStyle = "#172033";
            context.beginPath();
            context.arc(0, 0, 20, 0, Math.PI * 2);
            context.fill();
          }
          context.strokeStyle = style.color;
          context.lineWidth = 5;
          context.stroke();
          context.scale(0.82, 0.82);
          drawFusePowerupGlyph(context, pickup.type);
          context.restore();
        }

        function drawFuseCompatibilityBomb(context, bomb, seats, cellSize) {
          var x = bomb.x * cellSize + 30;
          var y = bomb.y * cellSize + 32;
          var ownerColor = "#f97316";
          for (var seatIndex = 0; seatIndex < seats.length; seatIndex += 1) {
            if (seats[seatIndex].seatId === bomb.ownerSeatId) ownerColor = seats[seatIndex].color;
          }
          var urgency = Math.max(0.08, Math.min(1, 1 - bomb.remainingMs / 2400));
          context.fillStyle = "#05070b";
          context.beginPath();
          context.arc(x, y, 21, 0, Math.PI * 2);
          context.fill();
          context.strokeStyle = ownerColor;
          context.lineWidth = 4;
          context.stroke();
          context.strokeStyle = "#fff7ed";
          context.lineWidth = 3;
          context.beginPath();
          context.arc(x, y, 26, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * urgency);
          context.stroke();
          context.strokeStyle = "#f59e0b";
          context.lineWidth = 4;
          context.beginPath();
          context.moveTo(x + 7, y - 19);
          context.lineTo(x + 17, y - 31);
          context.stroke();
          context.fillStyle = bomb.kickArmed ? "#ef4444" : "#fde047";
          context.fillRect(x + 14, y - 36, 8, 8);
        }

        function drawFuseCompatibilityBlast(context, blast, cellSize) {
          var x = blast.x * cellSize + 8;
          var y = blast.y * cellSize + 8;
          context.globalAlpha = blast.remainingMs <= 100 ? 0.55 : 0.9;
          context.fillStyle = "#f97316";
          context.fillRect(x, y, cellSize - 16, cellSize - 16);
          context.fillStyle = "#fde047";
          context.fillRect(x + 9, y + 9, cellSize - 34, cellSize - 34);
          context.fillStyle = "#fff7ed";
          context.fillRect(x + 16, y + 16, cellSize - 48, cellSize - 48);
          context.globalAlpha = 1;
        }

        function drawFuseCompatibilityRunner(context, runner, cellSize) {
          if (!runner.alive) return;
          var position = fuseRunnerDrawPosition(runner, cellSize);
          var x = position.x;
          var y = position.y;
          var sprite = fuseRunnerSprite(runner.seatId);
          context.fillStyle = "rgba(0,0,0,0.38)";
          context.beginPath();
          context.ellipse(x, y + 18, 21, 7, 0, 0, Math.PI * 2);
          context.fill();
          context.fillStyle = runner.color;
          context.beginPath();
          context.arc(x, y, 23, 0, Math.PI * 2);
          context.fill();
          context.fillStyle = "#fff7ed";
          context.beginPath();
          context.arc(x, y, 17, 0, Math.PI * 2);
          context.fill();
          if (sprite && sprite.complete && sprite.naturalWidth > 0) {
            context.drawImage(sprite, x - 24, y - 29, 48, 48);
          } else {
            context.fillStyle = "#172033";
            context.beginPath();
            context.arc(x - 6, y - 4, 2.5, 0, Math.PI * 2);
            context.arc(x + 6, y - 4, 2.5, 0, Math.PI * 2);
            context.fill();
            context.strokeStyle = "#172033";
            context.lineWidth = 2.5;
            context.beginPath();
            if (runner.seatId % 2 === 0) {
              context.arc(x, y + 2, 8, 0.15 * Math.PI, 0.85 * Math.PI);
            } else {
              context.moveTo(x - 7, y + 7);
              context.quadraticCurveTo(x, y + 12, x + 7, y + 7);
            }
            context.stroke();
          }
          drawFuseCompatibilityMarker(context, runner.marker, x + 19, y - 19, runner.color);
          if (runner.arrivalShieldMs > 0 || runner.shield) {
            context.strokeStyle = runner.shield ? "#67e8f9" : "#ffffff";
            context.lineWidth = 3;
            context.beginPath();
            context.arc(x, y, 28, 0, Math.PI * 2);
            context.stroke();
          }
        }

        function drawFuseCompatibilityMarker(context, marker, x, y, color) {
          context.fillStyle = color;
          context.beginPath();
          context.arc(x, y, 10, 0, Math.PI * 2);
          context.fill();
          context.fillStyle = "#07111f";
          context.beginPath();
          if (marker === "triangle") {
            context.moveTo(x, y - 5);
            context.lineTo(x + 5, y + 5);
            context.lineTo(x - 5, y + 5);
            context.closePath();
          } else if (marker === "diamond") {
            context.moveTo(x, y - 6);
            context.lineTo(x + 6, y);
            context.lineTo(x, y + 6);
            context.lineTo(x - 6, y);
            context.closePath();
          } else if (marker === "square") {
            context.rect(x - 5, y - 5, 10, 10);
          } else {
            context.arc(x, y, 5, 0, Math.PI * 2);
          }
          context.fill();
        }

        function drawFuseCompatibilityEffects(context, cellSize) {
          var firstEffect = Math.max(0, fuseVisualEffects.length - 8);
          for (
            var effectIndex = firstEffect;
            effectIndex < fuseVisualEffects.length;
            effectIndex += 1
          ) {
            var effect = fuseVisualEffects[effectIndex];
            if (effect.kind === "debris") continue;
            var progress = Math.min(1, effect.ageMs / effect.lifeMs);
            var x = effect.x * cellSize + 30;
            var y = effect.y * cellSize + 30;
            context.save();
            context.globalAlpha = Math.max(0, 1 - progress);
            if (effect.kind === "detonation") {
              var explosion = fuseImageAsset("fuse-explosion-sprite-asset");
              var explosionSize = 58 + Math.sin(progress * Math.PI) * 34;
              if (explosion && explosion.complete && explosion.naturalWidth > 0) {
                context.drawImage(
                  explosion,
                  x - explosionSize / 2,
                  y - explosionSize / 2,
                  explosionSize,
                  explosionSize,
                );
              } else {
                context.fillStyle = "#f97316";
                drawFuseSparkShape(context, x, y, explosionSize / 2, 10);
              }
            } else if (effect.kind === "death") {
              var runnerSprite = fuseRunnerSprite(effect.seatId);
              if (runnerSprite && runnerSprite.complete && runnerSprite.naturalWidth > 0) {
                context.drawImage(runnerSprite, x - 22, y - 26 - progress * 24, 44, 44);
              }
              context.strokeStyle = effect.color || "#fff7ed";
              context.lineWidth = 4;
              context.beginPath();
              context.arc(x, y, 20 + progress * 28, 0, Math.PI * 2);
              context.stroke();
            } else if (effect.kind === "pickup") {
              var token = fuseImageAsset("fuse-powerup-sprite-asset");
              if (token && token.complete && token.naturalWidth > 0) {
                context.drawImage(token, x - 19, y - 22 - progress * 18, 38, 38);
              }
            } else {
              context.strokeStyle = effect.color || "#67e8f9";
              context.lineWidth = 4;
              context.beginPath();
              context.arc(x, y, 24 + progress * 24, 0, Math.PI * 2);
              context.stroke();
            }
            context.restore();
          }
        }

        function ensureFuseArenaLayers(snapshot, cellSize, palette) {
          var hardBlockKey = "";
          for (var hardIndex = 0; hardIndex < snapshot.hardBlocks.length; hardIndex += 1) {
            var hardBlock = snapshot.hardBlocks[hardIndex];
            hardBlockKey += ":" + hardBlock.x + "," + hardBlock.y;
          }
          var nextKey =
            snapshot.arenaId + ":" + snapshot.width + "x" + snapshot.height + hardBlockKey;
          if (fuseArenaLayer && fuseArenaLayerKey === nextKey) return;

          fuseArenaLayerKey = nextKey;
          fuseArenaLayer = document.createElement("canvas");
          fuseArenaLayer.width = 780;
          fuseArenaLayer.height = 660;
          var layerContext = fuseArenaLayer.getContext("2d", { alpha: false });
          var backdrop = layerContext.createLinearGradient(0, 0, 780, 660);
          backdrop.addColorStop(0, palette.tile);
          backdrop.addColorStop(0.55, palette.floor);
          backdrop.addColorStop(1, "#081412");
          layerContext.fillStyle = backdrop;
          layerContext.fillRect(0, 0, 780, 660);

          for (var floorY = 0; floorY < snapshot.height; floorY += 1) {
            for (var floorX = 0; floorX < snapshot.width; floorX += 1) {
              var tileX = floorX * cellSize;
              var tileY = floorY * cellSize;
              layerContext.fillStyle =
                (floorX + floorY) % 2 === 0 ? "rgba(255,255,255,0.035)" : "rgba(0,0,0,0.035)";
              layerContext.fillRect(tileX + 1, tileY + 1, cellSize - 2, cellSize - 2);
              layerContext.strokeStyle = "rgba(255,255,255,0.055)";
              layerContext.lineWidth = 1;
              layerContext.strokeRect(tileX + 1.5, tileY + 1.5, cellSize - 3, cellSize - 3);
            }
          }
          for (var blockIndex = 0; blockIndex < snapshot.hardBlocks.length; blockIndex += 1) {
            drawFuseHardBlock(layerContext, snapshot.hardBlocks[blockIndex], cellSize, palette);
          }

          if (!fuseVignetteLayer) {
            fuseVignetteLayer = document.createElement("canvas");
            fuseVignetteLayer.width = 780;
            fuseVignetteLayer.height = 660;
            var vignetteContext = fuseVignetteLayer.getContext("2d");
            var vignette = vignetteContext.createRadialGradient(390, 330, 220, 390, 330, 520);
            vignette.addColorStop(0, "rgba(0,0,0,0)");
            vignette.addColorStop(1, "rgba(0,0,0,0.3)");
            vignetteContext.fillStyle = vignette;
            vignetteContext.fillRect(0, 0, 780, 660);
          }
        }

        function ensureFuseStaticLayer(snapshot, cellSize, palette) {
          if (fuseStaticLayer && fuseStaticLayerSnapshot === snapshot) return;
          fuseStaticLayerSnapshot = snapshot;
          ensureFuseArenaLayers(snapshot, cellSize, palette);
          var nextKey = "";
          for (var index = 0; index < snapshot.breakables.length; index += 1) {
            var target = snapshot.breakables[index];
            nextKey += ":" + target.x + "," + target.y;
          }
          nextKey = fuseArenaLayerKey + nextKey;
          if (fuseStaticLayer && fuseStaticLayerKey === nextKey) return;
          fuseStaticLayerKey = nextKey;
          fuseStaticLayer = document.createElement("canvas");
          fuseStaticLayer.width = 780;
          fuseStaticLayer.height = 660;
          var layerContext = fuseStaticLayer.getContext("2d", { alpha: false });
          if (fuseArenaLayer) layerContext.drawImage(fuseArenaLayer, 0, 0);
          for (
            var breakableIndex = 0;
            breakableIndex < snapshot.breakables.length;
            breakableIndex += 1
          ) {
            drawFuseBreakable(layerContext, snapshot.breakables[breakableIndex], cellSize);
          }
        }

        function fuseMotionIsReduced() {
          return (
            fuseReducedMotion ||
            (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches)
          );
        }

        function drawFuseArenaAmbience(context, arenaId) {
          if (fuseMotionIsReduced() || fuseLowPowerTv) return;
          var time = fuseFrameNow / 1000;
          context.save();
          for (var lamp = 0; lamp < 7; lamp += 1) {
            var lampX = 28 + lamp * 120;
            var lampGlow = 0.12 + Math.sin(time * 2.2 + lamp) * 0.035;
            context.fillStyle =
              arenaId === "cinder-crown"
                ? "rgba(251,146,60," + lampGlow + ")"
                : "rgba(253,224,71," + lampGlow + ")";
            context.beginPath();
            context.arc(lampX, 12, 18, 0, Math.PI * 2);
            context.fill();
          }
          for (var mote = 0; mote < 14; mote += 1) {
            var phase = time * (0.35 + (mote % 4) * 0.08) + mote * 1.73;
            var x = (mote * 89 + Math.sin(phase) * 30 + 780) % 780;
            var y = (mote * 53 - phase * 24 + 1320) % 660;
            if (arenaId === "cinder-crown") {
              context.fillStyle = mote % 2 ? "#fb923c" : "#fde047";
              context.globalAlpha = 0.28 + (mote % 3) * 0.12;
              drawFuseSparkShape(context, x, y, 2 + (mote % 2), 4);
            } else if (arenaId === "tidal-engine" || arenaId === "cloud-foundry") {
              context.strokeStyle = "rgba(224,242,254,0.2)";
              context.lineWidth = 3;
              context.beginPath();
              context.arc(x, y, 7 + (mote % 4) * 3, 0, Math.PI * 1.45);
              context.stroke();
            } else if (arenaId === "gust-gallery") {
              context.strokeStyle = "rgba(224,242,254,0.16)";
              context.lineWidth = 2;
              context.beginPath();
              context.moveTo(x - 18, y);
              context.quadraticCurveTo(x, y - 7, x + 22, y);
              context.stroke();
            } else {
              context.fillStyle = "rgba(190,242,100,0.18)";
              context.beginPath();
              context.arc(x, y, 2 + (mote % 3), 0, Math.PI * 2);
              context.fill();
            }
          }
          context.restore();
        }

        function drawFuseScorchMarks(context, cellSize) {
          var now = fuseFrameNow;
          for (var index = 0; index < fuseScorchMarks.length; index += 1) {
            var mark = fuseScorchMarks[index];
            var age = Math.max(0, now - mark.createdAt);
            var alpha = Math.max(0.08, 0.42 - age / 90000);
            var x = mark.x * cellSize + 30;
            var y = mark.y * cellSize + 30;
            context.save();
            context.translate(x, y);
            context.rotate((mark.seed % 11) * 0.17);
            context.fillStyle = "rgba(20,8,4," + alpha + ")";
            context.beginPath();
            context.ellipse(0, 3, 25, 18, 0, 0, Math.PI * 2);
            context.fill();
            context.strokeStyle = "rgba(251,146,60," + alpha * 0.45 + ")";
            context.lineWidth = 2;
            context.setLineDash([3, 6]);
            context.beginPath();
            context.arc(0, 0, 20, 0, Math.PI * 2);
            context.stroke();
            context.setLineDash([]);
            context.restore();
          }
        }

        function drawFuseHazards(context, cells, cellSize, color, urgent) {
          for (var index = 0; index < cells.length; index += 1) {
            var target = cells[index];
            var x = target.x * cellSize;
            var y = target.y * cellSize;
            context.save();
            context.globalAlpha = urgent ? 0.68 : 0.42;
            context.fillStyle = color;
            context.fillRect(x + 3, y + 3, cellSize - 6, cellSize - 6);
            context.globalAlpha = 0.9;
            context.strokeStyle = urgent ? "#fff7ed" : color;
            context.lineWidth = urgent ? 4 : 3;
            for (var stripe = -cellSize; stripe < cellSize * 2; stripe += 14) {
              context.beginPath();
              context.moveTo(x + stripe, y + cellSize);
              context.lineTo(x + stripe + cellSize, y);
              context.stroke();
            }
            if (urgent) {
              context.fillStyle = "#fff7ed";
              drawFuseSparkShape(context, x + 30, y + 30, 15, 7);
            }
            context.restore();
          }
        }

        function drawFuseHardBlock(context, target, cellSize, palette) {
          var x = target.x * cellSize;
          var y = target.y * cellSize;
          context.fillStyle = "rgba(0,0,0,0.4)";
          context.fillRect(x + 7, y + 9, 48, 47);
          var metal = context.createLinearGradient(x + 5, y + 5, x + 54, y + 54);
          metal.addColorStop(0, "#64748b");
          metal.addColorStop(0.5, "#334155");
          metal.addColorStop(1, "#172033");
          context.fillStyle = metal;
          context.fillRect(x + 5, y + 5, 50, 48);
          context.strokeStyle = palette.edge;
          context.globalAlpha = 0.32;
          context.lineWidth = 2;
          context.strokeRect(x + 8, y + 8, 44, 42);
          context.globalAlpha = 1;
          context.fillStyle = "#cbd5e1";
          for (var rivet = 0; rivet < 4; rivet += 1) {
            var rx = x + (rivet % 2 === 0 ? 12 : 48);
            var ry = y + (rivet < 2 ? 12 : 46);
            context.beginPath();
            context.arc(rx, ry, 2.3, 0, Math.PI * 2);
            context.fill();
          }
        }

        function drawFuseBreakable(context, target, cellSize) {
          var x = target.x * cellSize;
          var y = target.y * cellSize;
          context.fillStyle = "rgba(0,0,0,0.4)";
          context.fillRect(x + 9, y + 12, 45, 43);
          var wood = context.createLinearGradient(x + 7, y + 7, x + 53, y + 53);
          wood.addColorStop(0, "#f59e0b");
          wood.addColorStop(0.5, "#9a4b19");
          wood.addColorStop(1, "#572b16");
          context.fillStyle = wood;
          context.fillRect(x + 7, y + 7, 46, 46);
          context.strokeStyle = "#4a2412";
          context.lineWidth = 5;
          context.strokeRect(x + 8.5, y + 8.5, 43, 43);
          context.beginPath();
          context.moveTo(x + 12, y + 13);
          context.lineTo(x + 48, y + 48);
          context.moveTo(x + 48, y + 13);
          context.lineTo(x + 12, y + 48);
          context.stroke();
          context.strokeStyle = "rgba(255,237,180,0.55)";
          context.lineWidth = 2;
          context.strokeRect(x + 11, y + 11, 38, 38);
        }

        function drawFusePickup(context, pickup, cellSize) {
          var x = pickup.x * cellSize + 30;
          var y = pickup.y * cellSize + 30;
          var token = fuseImageAsset("fuse-powerup-sprite-asset");
          var style = fusePowerupStyle(pickup.type);
          var rotation =
            ((fuseFrameNow / 35 + pickup.x * 7 + pickup.y * 11) % 360) * (Math.PI / 180);
          context.save();
          context.shadowColor = style.color;
          context.shadowBlur = 16;
          context.translate(x, y);
          context.rotate(rotation);
          if (token && token.complete && token.naturalWidth > 0) {
            context.drawImage(token, -24, -24, 48, 48);
          } else {
            context.fillStyle = "#f59e0b";
            context.beginPath();
            context.arc(0, 0, 22, 0, Math.PI * 2);
            context.fill();
          }
          context.rotate(-rotation);
          context.shadowBlur = 0;
          context.fillStyle = style.color;
          context.beginPath();
          context.arc(0, 0, 12.5, 0, Math.PI * 2);
          context.fill();
          drawFusePowerupGlyph(context, pickup.type);
          context.restore();
        }

        var fusePowerupStyles = {
          "extra-charge": { color: "#fb923c", label: "Extra Bomb" },
          "long-spark": { color: "#facc15", label: "Long Spark" },
          "quick-soles": { color: "#34d399", label: "Quick Soles" },
          "bubble-shield": { color: "#22d3ee", label: "Shield" },
          "remote-spark": { color: "#a78bfa", label: "Remote" },
          "piercing-spark": { color: "#f472b6", label: "Piercing" },
          "kick-toe": { color: "#60a5fa", label: "Bomb Kick" },
          "hover-belt": { color: "#818cf8", label: "Hover" },
          "sticky-soles": { color: "#84cc16", label: "Sticky" },
          "hot-fuse": { color: "#ef4444", label: "Hot Fuse" },
        };

        function fusePowerupStyle(type) {
          return fusePowerupStyles[type] || { color: "#facc15", label: "Power Up" };
        }

        function drawFusePowerupGlyph(context, type) {
          context.strokeStyle = "#fff7ed";
          context.fillStyle = "#fff7ed";
          context.lineWidth = 2.5;
          context.lineCap = "round";
          context.lineJoin = "round";
          context.beginPath();
          if (type === "extra-charge") {
            context.arc(-1, 2, 6, 0, Math.PI * 2);
            context.stroke();
            context.beginPath();
            context.moveTo(3, -3);
            context.quadraticCurveTo(7, -7, 8, -10);
            context.stroke();
            context.beginPath();
            context.moveTo(8, -10);
            context.lineTo(10, -7);
            context.moveTo(8, -10);
            context.lineTo(5, -8);
          } else if (type === "long-spark") {
            context.moveTo(-10, 0);
            context.lineTo(10, 0);
            context.moveTo(0, -10);
            context.lineTo(0, 10);
            context.moveTo(-10, 0);
            context.lineTo(-6, -3);
            context.moveTo(-10, 0);
            context.lineTo(-6, 3);
            context.moveTo(10, 0);
            context.lineTo(6, -3);
            context.moveTo(10, 0);
            context.lineTo(6, 3);
          } else if (type === "quick-soles") {
            context.moveTo(-9, -7);
            context.lineTo(-2, 0);
            context.lineTo(-9, 7);
            context.moveTo(0, -7);
            context.lineTo(7, 0);
            context.lineTo(0, 7);
          } else if (type === "bubble-shield") {
            context.moveTo(0, -10);
            context.lineTo(8, -6);
            context.lineTo(6, 4);
            context.quadraticCurveTo(0, 10, -6, 4);
            context.lineTo(-8, -6);
            context.closePath();
          } else if (type === "remote-spark") {
            context.moveTo(3, -11);
            context.lineTo(-6, 1);
            context.lineTo(0, 1);
            context.lineTo(-3, 11);
            context.lineTo(8, -3);
            context.lineTo(2, -3);
            context.closePath();
          } else if (type === "piercing-spark") {
            context.moveTo(0, -11);
            context.lineTo(7, 0);
            context.lineTo(0, 11);
            context.lineTo(-7, 0);
            context.closePath();
            context.moveTo(0, -7);
            context.lineTo(0, 7);
          } else if (type === "kick-toe") {
            context.moveTo(-8, -9);
            context.lineTo(-1, -9);
            context.lineTo(1, 2);
            context.lineTo(9, 4);
            context.lineTo(8, 9);
            context.lineTo(-7, 9);
            context.closePath();
          } else if (type === "hover-belt") {
            context.arc(0, 0, 3, 0, Math.PI * 2);
            context.moveTo(3, 0);
            context.lineTo(10, -5);
            context.moveTo(-3, 0);
            context.lineTo(-10, 5);
            context.moveTo(0, 3);
            context.lineTo(5, 10);
            context.moveTo(0, -3);
            context.lineTo(-5, -10);
          } else if (type === "sticky-soles") {
            context.arc(-4, 0, 5, 0, Math.PI * 2);
            context.moveTo(4, -5);
            context.arc(4, 0, 5, -Math.PI / 2, Math.PI / 2);
            context.moveTo(-4, 5);
            context.quadraticCurveTo(0, 10, 4, 5);
          } else {
            context.moveTo(0, 10);
            context.quadraticCurveTo(-9, 4, -4, -3);
            context.quadraticCurveTo(0, -8, 1, -12);
            context.quadraticCurveTo(9, -4, 7, 4);
            context.quadraticCurveTo(5, 10, 0, 10);
          }
          if (
            type === "remote-spark" ||
            type === "bubble-shield" ||
            type === "piercing-spark" ||
            type === "kick-toe" ||
            type === "hot-fuse"
          ) {
            context.fill();
          } else {
            context.stroke();
          }
        }

        function drawFuseBomb(context, bomb, seats, cellSize) {
          var x = bomb.x * cellSize + 30;
          var y = bomb.y * cellSize + 33;
          var ownerColor = "#f97316";
          for (var seatIndex = 0; seatIndex < seats.length; seatIndex += 1) {
            if (seats[seatIndex].seatId === bomb.ownerSeatId) ownerColor = seats[seatIndex].color;
          }
          var pulse = bomb.kickArmed || bomb.remainingMs % 500 < 250 ? 1 : 0;
          var urgency = Math.max(0, Math.min(1, 1 - bomb.remainingMs / 2400));
          var wobble =
            bomb.kickArmed || bomb.remainingMs < 800
              ? Math.sin(bomb.remainingMs * 0.09) * (bomb.kickArmed ? 4 : 2.6)
              : 0;
          context.save();
          context.translate(wobble, 0);
          context.shadowColor = pulse ? "#fb923c" : "rgba(0,0,0,0.6)";
          context.shadowBlur = pulse ? 16 : 7;
          context.fillStyle = "rgba(0,0,0,0.45)";
          context.beginPath();
          context.ellipse(x + 2, y + 17, 23, 8, 0, 0, Math.PI * 2);
          context.fill();
          var shell = context.createRadialGradient(x - 7, y - 9, 3, x, y, 24);
          shell.addColorStop(0, "#64748b");
          shell.addColorStop(0.32, "#1f2937");
          shell.addColorStop(1, "#030712");
          context.fillStyle = shell;
          context.beginPath();
          context.arc(x, y, 22, 0, Math.PI * 2);
          context.fill();
          context.shadowBlur = 0;
          context.strokeStyle = ownerColor;
          context.lineWidth = 4;
          context.stroke();
          context.strokeStyle = "#fff7ed";
          context.lineWidth = 2.5;
          context.beginPath();
          context.arc(x, y, 27, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.max(0.08, urgency));
          context.stroke();
          if (bomb.kickArmed) {
            context.strokeStyle = "#ef4444";
            context.lineWidth = 4;
            context.setLineDash([5, 4]);
            context.beginPath();
            context.arc(x, y, 31, 0, Math.PI * 2);
            context.stroke();
            context.setLineDash([]);
          }
          context.strokeStyle = "#d97706";
          context.lineWidth = 5;
          context.beginPath();
          context.moveTo(x + 7, y - 20);
          context.quadraticCurveTo(x + 18, y - 31, x + 19, y - 39);
          context.stroke();
          context.fillStyle = pulse ? "#fff7ed" : "#fb923c";
          drawFuseSparkShape(context, x + 19, y - 40, pulse ? 7 : 5, 5);
          context.restore();
        }

        function drawFuseBlast(context, blast, cellSize) {
          var x = blast.x * cellSize + 30;
          var y = blast.y * cellSize + 30;
          var fading = blast.remainingMs <= 100;
          context.save();
          context.globalAlpha = (fading ? 0.55 : 0.9) * (fuseReducedFlash ? 0.55 : 1);
          context.shadowColor = "#fb923c";
          context.shadowBlur = fading ? 8 : 22;
          context.fillStyle = fading ? "#fff7ed" : "#f97316";
          drawFuseSparkShape(context, x, y, fading ? 22 : 29, 10);
          context.shadowBlur = 0;
          context.fillStyle = fading ? "#fed7aa" : "#fde047";
          drawFuseSparkShape(context, x, y, fading ? 13 : 19, 7);
          context.fillStyle = "#fff7ed";
          context.beginPath();
          context.arc(x, y, fading ? 5 : 8, 0, Math.PI * 2);
          context.fill();
          context.restore();
        }

        function drawFuseSparkShape(context, x, y, outerRadius, points) {
          context.beginPath();
          for (var point = 0; point < points * 2; point += 1) {
            var radius = point % 2 === 0 ? outerRadius : outerRadius * 0.43;
            var angle = -Math.PI / 2 + (Math.PI * point) / points;
            var px = x + Math.cos(angle) * radius;
            var py = y + Math.sin(angle) * radius;
            if (point === 0) context.moveTo(px, py);
            else context.lineTo(px, py);
          }
          context.closePath();
          context.fill();
        }

        function drawFuseRunner(context, runner, cellSize) {
          if (!runner.alive) return;
          var position = fuseRunnerDrawPosition(runner, cellSize);
          var x = position.x;
          var y = position.y;
          var sprite = fuseRunnerSprite(runner.seatId);
          var motion = fuseRunnerMotion.get(runner.seatId);
          var moving = motion && fuseFrameNow < motion.movingUntil && !fuseMotionIsReduced();
          var stride = moving ? Math.sin(fuseFrameNow / 55 + runner.seatId) : 0;
          var lean = moving ? Math.max(-0.14, Math.min(0.14, motion.x * 0.12)) : 0;
          y += moving ? Math.abs(stride) * -3 : 0;
          context.save();
          if (moving) {
            context.globalAlpha = 0.22;
            context.fillStyle = "#fed7aa";
            for (var dust = 0; dust < 3; dust += 1) {
              context.beginPath();
              context.arc(
                x - motion.x * (20 + dust * 6) + motion.y * (dust - 1) * 5,
                y + 20 - motion.y * 12,
                3 + dust,
                0,
                Math.PI * 2,
              );
              context.fill();
            }
            context.globalAlpha = 1;
          }
          context.translate(x, y);
          context.rotate(lean);
          context.translate(-x, -y);
          context.fillStyle = "rgba(0,0,0,0.42)";
          context.beginPath();
          context.ellipse(x, y + 19, 22, 8, 0, 0, Math.PI * 2);
          context.fill();
          context.shadowColor = runner.color;
          context.shadowBlur = 18;
          context.fillStyle = runner.color;
          context.beginPath();
          context.arc(x, y, 24, 0, Math.PI * 2);
          context.fill();
          context.shadowBlur = 0;
          context.fillStyle = "#fff7ed";
          context.beginPath();
          context.arc(x, y, 20, 0, Math.PI * 2);
          context.fill();
          if (sprite && sprite.complete && sprite.naturalWidth > 0) {
            context.drawImage(sprite, x - 28, y - 34, 56, 56);
          } else {
            context.fillStyle = "#172033";
            context.beginPath();
            context.arc(x, y, 17, 0, Math.PI * 2);
            context.fill();
          }
          if (runner.arrivalShieldMs > 0 || runner.shield) {
            context.strokeStyle = runner.shield ? "#67e8f9" : "#ffffff";
            context.lineWidth = 3;
            context.setLineDash([5, 4]);
            context.beginPath();
            context.arc(x, y, 29, 0, Math.PI * 2);
            context.stroke();
            context.setLineDash([]);
          }
          context.fillStyle = runner.color;
          context.beginPath();
          context.arc(x + 20, y - 20, 10, 0, Math.PI * 2);
          context.fill();
          context.strokeStyle = "#fff7ed";
          context.lineWidth = 2;
          context.stroke();
          context.fillStyle = "#07111f";
          context.font = "900 11px sans-serif";
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText(fuseMarker(runner.marker), x + 20, y - 19);
          context.restore();
        }

        function fuseRunnerDrawPosition(runner, cellSize) {
          var now = fuseFrameNow;
          var motion = fuseRunnerMotion.get(runner.seatId);
          if (!motion || typeof motion.startedAt !== "number") {
            return { x: runner.x * cellSize + 30, y: runner.y * cellSize + 30 };
          }
          return {
            x:
              interpolateSnapshotPosition(
                motion.fromX,
                motion.toX,
                motion.startedAt,
                motion.endsAt,
                now,
              ) *
                cellSize +
              30,
            y:
              interpolateSnapshotPosition(
                motion.fromY,
                motion.toY,
                motion.startedAt,
                motion.endsAt,
                now,
              ) *
                cellSize +
              30,
          };
        }

        function fuseRunnerSprite(seatId) {
          var ids = [
            "fuse-runner-sprite-asset",
            "fuse-runner-wink-asset",
            "fuse-runner-alert-asset",
            "fuse-runner-grin-asset",
          ];
          var variant = fuseImageAsset(ids[Math.max(0, Math.min(3, Number(seatId) || 0))]);
          return variant || fuseImageAsset("fuse-runner-sprite-asset");
        }

        function fuseImageAsset(id) {
          var asset = fuseImageAssets[id];
          if (!asset) {
            asset = byId(id);
            if (asset) fuseImageAssets[id] = asset;
          }
          return asset && asset.complete && asset.naturalWidth > 0 ? asset : null;
        }

        function drawFuseVisualEffects(context, cellSize) {
          var reducedMotion = fuseMotionIsReduced();
          for (var effectIndex = 0; effectIndex < fuseVisualEffects.length; effectIndex += 1) {
            var effect = fuseVisualEffects[effectIndex];
            var progress = Math.min(1, effect.ageMs / effect.lifeMs);
            var x = effect.x * cellSize + 30;
            var y = effect.y * cellSize + 30;
            if (effect.kind === "detonation") {
              drawFuseExplosionSprite(
                context,
                x,
                y,
                64 + Math.sin(progress * Math.PI) * 58,
                1 - progress,
                progress * 0.7,
              );
              drawFuseEffectSparks(context, x, y, progress, effect.seed, "#fde047", 11, 54);
            } else if (effect.kind === "death") {
              drawFuseDeathEffect(context, effect, x, y, progress, reducedMotion);
            } else if (effect.kind === "debris") {
              drawFuseDebrisEffect(context, x, y, progress, effect.seed, reducedMotion);
            } else if (effect.kind === "pickup") {
              drawFusePickupEffect(
                context,
                x,
                y,
                progress,
                effect.seed,
                reducedMotion,
                effect.pickupType,
              );
            } else if (effect.kind === "shield") {
              drawFuseShieldEffect(context, x, y, progress, effect.color);
            } else if (effect.kind === "kick-impact") {
              drawFuseKickImpactEffect(context, x, y, progress);
            }
          }
        }

        function drawFuseExplosionSprite(context, x, y, size, alpha, rotation) {
          var sprite = fuseImageAsset("fuse-explosion-sprite-asset");
          context.save();
          context.translate(x, y);
          context.rotate(rotation);
          context.globalAlpha = Math.max(0, alpha) * (fuseReducedFlash ? 0.58 : 1);
          context.shadowColor = "#fb923c";
          context.shadowBlur = 24;
          if (sprite && sprite.complete && sprite.naturalWidth > 0) {
            context.drawImage(sprite, -size / 2, -size / 2, size, size);
          } else {
            context.fillStyle = "#f97316";
            drawFuseSparkShape(context, 0, 0, size / 2, 12);
          }
          context.restore();
        }

        function drawFuseDeathEffect(context, effect, x, y, progress, reducedMotion) {
          var burstProgress = Math.min(1, progress / 0.48);
          if (progress < 0.58) {
            drawFuseExplosionSprite(
              context,
              x,
              y,
              58 + Math.sin(burstProgress * Math.PI) * 72,
              1 - burstProgress * 0.78,
              -progress * 0.8,
            );
          }
          drawFuseEffectSparks(context, x, y, progress, effect.seed, effect.color, 12, 74);

          var ghostProgress = Math.max(0, (progress - 0.18) / 0.82);
          var sprite = fuseRunnerSprite(effect.seatId);
          if (ghostProgress > 0 && sprite && sprite.complete && sprite.naturalWidth > 0) {
            context.save();
            context.globalAlpha = (1 - ghostProgress) * 0.78;
            context.translate(x, y - (reducedMotion ? 12 : ghostProgress * 62));
            context.rotate(reducedMotion ? 0 : Math.sin(ghostProgress * Math.PI * 2) * 0.12);
            context.shadowColor = effect.color;
            context.shadowBlur = 24;
            context.drawImage(sprite, -24, -30, 48, 48);
            context.restore();
          }

          context.save();
          context.globalAlpha = 1 - progress;
          context.strokeStyle = effect.color;
          context.lineWidth = 5 * (1 - progress);
          context.beginPath();
          context.arc(x, y, 18 + progress * 46, 0, Math.PI * 2);
          context.stroke();
          context.fillStyle = effect.color;
          context.beginPath();
          context.arc(x + 26, y - 25 - progress * 18, 11, 0, Math.PI * 2);
          context.fill();
          context.fillStyle = "#07111f";
          context.font = "900 12px sans-serif";
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText(fuseMarker(effect.marker), x + 26, y - 24 - progress * 18);
          context.restore();
        }

        function drawFuseEffectSparks(context, x, y, progress, seed, color, count, distance) {
          context.save();
          context.globalAlpha = Math.max(0, 1 - progress);
          context.fillStyle = color;
          for (var spark = 0; spark < count; spark += 1) {
            var angle = ((seed + spark * 47) % 360) * (Math.PI / 180);
            var travel = progress * distance * (0.72 + ((seed + spark * 13) % 30) / 100);
            var sx = x + Math.cos(angle) * travel;
            var sy = y + Math.sin(angle) * travel + progress * progress * 18;
            context.save();
            context.translate(sx, sy);
            context.rotate(angle + progress * 4);
            if (spark % 3 === 0) {
              context.fillRect(-4, -4, 8, 8);
              context.fillStyle = "#b45309";
              context.fillRect(-2, -2, 4, 4);
              context.fillStyle = color;
            } else {
              drawFuseSparkShape(context, 0, 0, 3 + (spark % 2) * 2, 4);
            }
            context.restore();
          }
          context.restore();
        }

        function drawFuseDebrisEffect(context, x, y, progress, seed, reducedMotion) {
          context.save();
          context.globalAlpha = 1 - progress;
          for (var piece = 0; piece < 9; piece += 1) {
            var angle = ((seed + piece * 41) % 360) * (Math.PI / 180);
            var travel = reducedMotion ? 16 : progress * (34 + (piece % 3) * 9);
            var px = x + Math.cos(angle) * travel;
            var py = y + Math.sin(angle) * travel + (reducedMotion ? 0 : progress * progress * 35);
            context.save();
            context.translate(px, py);
            context.rotate(angle + progress * 7);
            context.fillStyle = piece % 2 === 0 ? "#f59e0b" : "#78350f";
            context.fillRect(-5, -3, 10, 6);
            context.strokeStyle = "#451a03";
            context.lineWidth = 1.5;
            context.strokeRect(-5, -3, 10, 6);
            context.restore();
          }
          context.globalAlpha = Math.max(0, (1 - progress) * 0.28);
          context.fillStyle = "#dbeafe";
          for (var smoke = 0; smoke < 3; smoke += 1) {
            context.beginPath();
            context.arc(
              x - 10 + smoke * 10,
              y - progress * (reducedMotion ? 10 : 36) - smoke * 5,
              8 + progress * 8,
              0,
              Math.PI * 2,
            );
            context.fill();
          }
          context.restore();
        }

        function drawFusePickupEffect(context, x, y, progress, seed, reducedMotion, pickupType) {
          context.save();
          context.globalAlpha = 1 - progress;
          context.strokeStyle = "#fde047";
          context.lineWidth = 5 * (1 - progress);
          context.shadowColor = "#fde047";
          context.shadowBlur = 20;
          context.beginPath();
          context.arc(x, y, 18 + progress * 38, 0, Math.PI * 2);
          context.stroke();
          context.restore();
          drawFuseEffectSparks(
            context,
            x,
            y,
            progress,
            seed,
            "#fff7c2",
            8,
            reducedMotion ? 24 : 48,
          );
          if (pickupType) {
            var style = fusePowerupStyle(pickupType);
            context.save();
            context.globalAlpha = Math.min(1, (1 - progress) * 2.5);
            context.translate(x, y - 28 - (reducedMotion ? 8 : progress * 34));
            context.fillStyle = "rgba(7,17,31,0.9)";
            context.strokeStyle = style.color;
            context.lineWidth = 2;
            context.fillRect(-47, -17, 94, 34);
            context.strokeRect(-47, -17, 94, 34);
            context.fillStyle = "#fff7ed";
            context.font = "900 12px sans-serif";
            context.textAlign = "center";
            context.textBaseline = "middle";
            context.fillText(style.label.toUpperCase(), 8, 0);
            context.translate(-31, 0);
            context.scale(0.65, 0.65);
            drawFusePowerupGlyph(context, pickupType);
            context.restore();
          }
        }

        function drawFuseShieldEffect(context, x, y, progress, color) {
          context.save();
          context.globalAlpha = 1 - progress;
          context.strokeStyle = color || "#67e8f9";
          context.shadowColor = "#67e8f9";
          context.shadowBlur = 20;
          context.lineWidth = 5 * (1 - progress) + 1;
          for (var ring = 0; ring < 3; ring += 1) {
            context.beginPath();
            context.arc(x, y, 26 + progress * (22 + ring * 12), 0, Math.PI * 2);
            context.stroke();
          }
          context.fillStyle = "#fff7ed";
          drawFuseSparkShape(context, x, y - 32 - progress * 12, 8, 6);
          context.restore();
        }

        function drawFuseKickImpactEffect(context, x, y, progress) {
          context.save();
          context.globalAlpha = 1 - progress;
          context.strokeStyle = progress < 0.45 ? "#fff7ed" : "#fb923c";
          context.lineWidth = 4;
          context.beginPath();
          context.arc(x, y, 31 + progress * 18, 0, Math.PI * 2);
          context.stroke();
          context.fillStyle = "#fde047";
          context.font = "950 13px sans-serif";
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText("ARMED!", x, y - 42 - progress * 14);
          context.restore();
        }

        function renderFireflyFriendsSnapshot(snapshot) {
          if (snapshot.phase === "completed") {
            renderFireflyFriendsResults(snapshot);
            return;
          }
          canvas.style.display = "block";
          if (canvas.width !== 1280 || canvas.height !== 720) {
            canvas.width = 1280;
            canvas.height = 720;
          }
          drawFireflyGarden(snapshot);
          var headline =
            snapshot.phase === "countdown"
              ? "GET READY TO GLOW"
              : snapshot.phase === "paused"
                ? snapshot.countdownMs > 0
                  ? "RETURNING TO THE GARDEN"
                  : "WAITING FOR FIREFLY FRIENDS"
                : snapshot.phase === "nightEnd"
                  ? "LAST LIGHTS"
                  : snapshot.phase === "celebration"
                    ? snapshot.won
                      ? "THE GARDEN IS GLOWING!"
                      : "LOOK WHAT YOU LIT TOGETHER"
                    : "GUIDE THEM HOME";
          root.innerHTML =
            '<section class="tv-firefly-hud" data-testid="tv-firefly-friends" data-firefly-phase="' +
            escapeAttribute(snapshot.phase) +
            '"><div class="tv-firefly-room">FIREFLY FRIENDS · ' +
            escapeHtml(roomCode) +
            "<br>GLOW CHAIN ×" +
            escapeHtml(String(snapshot.glowChain)) +
            "</div><h1>" +
            escapeHtml(headline) +
            '</h1><div class="tv-firefly-stats">' +
            escapeHtml(String(Math.ceil(snapshot.timeRemainingMs / 1000))) +
            "s<br>" +
            escapeHtml(String(snapshot.blooms.length)) +
            "/6 BLOOMS · " +
            escapeHtml(String(snapshot.escapes)) +
            " ESCAPES</div></section>";
        }

        function drawFireflyGarden(snapshot) {
          var ctx = canvas.getContext("2d");
          if (!ctx) return;

          // The night sky gradient, stars, moon, and hill silhouette never
          // change between frames — only the blooms/fireflies/lanterns drawn
          // on top do. Rebuilding the gradient and redrawing 36 stars plus the
          // hill path on every snapshot wastes time the animated elements
          // actually need. Render the backdrop once into an offscreen canvas
          // and blit that instead, following the same pattern as
          // critterBoardLayer/racingTrackLayer.
          var gardenLayerKey = canvas.width + ":" + canvas.height;
          if (!fireflyGardenLayer || fireflyGardenLayerKey !== gardenLayerKey) {
            var layer = document.createElement("canvas");
            layer.width = canvas.width;
            layer.height = canvas.height;
            var layerCtx = layer.getContext("2d");
            var gradient = layerCtx.createLinearGradient(0, 0, 0, canvas.height);
            gradient.addColorStop(0, "#11153b");
            gradient.addColorStop(0.62, "#172554");
            gradient.addColorStop(1, "#14532d");
            layerCtx.fillStyle = gradient;
            layerCtx.fillRect(0, 0, canvas.width, canvas.height);

            layerCtx.fillStyle = "rgba(254,240,138,0.7)";
            for (var star = 0; star < 36; star += 1) {
              var starX = 25 + ((star * 149) % 1230);
              var starY = 80 + ((star * 83) % 330);
              layerCtx.globalAlpha = 0.25 + ((star * 7) % 60) / 100;
              layerCtx.beginPath();
              layerCtx.arc(starX, starY, 1 + (star % 3), 0, Math.PI * 2);
              layerCtx.fill();
            }
            layerCtx.globalAlpha = 1;

            layerCtx.fillStyle = "#fef3c7";
            layerCtx.beginPath();
            layerCtx.arc(1080, 128, 62, 0, Math.PI * 2);
            layerCtx.fill();
            layerCtx.fillStyle = "#11153b";
            layerCtx.beginPath();
            layerCtx.arc(1055, 108, 60, 0, Math.PI * 2);
            layerCtx.fill();

            layerCtx.fillStyle = "#166534";
            layerCtx.beginPath();
            layerCtx.moveTo(0, 565);
            for (var hill = 0; hill <= 1280; hill += 80) {
              layerCtx.quadraticCurveTo(hill + 40, 520 + ((hill / 80) % 2) * 28, hill + 80, 565);
            }
            layerCtx.lineTo(1280, 720);
            layerCtx.lineTo(0, 720);
            layerCtx.fill();

            fireflyGardenLayer = layer;
            fireflyGardenLayerKey = gardenLayerKey;
          }
          ctx.drawImage(fireflyGardenLayer, 0, 0);

          drawFireflyBlooms(ctx, snapshot.blooms || []);

          var lanterns = [
            { type: "rose", x: 640, y: 145, color: "#fb7185" },
            { type: "gold", x: 1080, y: 380, color: "#facc15" },
            { type: "mint", x: 640, y: 610, color: "#34d399" },
            { type: "blue", x: 200, y: 380, color: "#60a5fa" },
          ];
          var friends = snapshot.friends || [];
          var activeLanternTypes = {};
          for (
            var activeFriendIndex = 0;
            activeFriendIndex < friends.length;
            activeFriendIndex += 1
          ) {
            var activeFirefly = friends[activeFriendIndex].activeFirefly;
            if (activeFirefly && activeFirefly.status === "active") {
              activeLanternTypes[activeFirefly.type] = true;
            }
          }
          var hasActiveLantern = Object.keys(activeLanternTypes).length > 0;
          for (var lanternIndex = 0; lanternIndex < lanterns.length; lanternIndex += 1) {
            var lantern = lanterns[lanternIndex];
            drawFireflyLantern(
              ctx,
              lantern,
              snapshot.lanternCounts[lantern.type] || 0,
              snapshot.lanternGoal || 1,
              !hasActiveLantern || activeLanternTypes[lantern.type] === true,
            );
          }

          for (var friendIndex = 0; friendIndex < friends.length; friendIndex += 1) {
            var friend = friends[friendIndex];
            var angle = -Math.PI / 2 + (Math.PI * 2 * friendIndex) / Math.max(1, friends.length);
            var friendX = 640 + Math.cos(angle) * 300;
            var friendY = 385 + Math.sin(angle) * 165;
            drawFireflyFriend(ctx, friend, friendX, friendY);
          }
        }

        function drawFireflyBlooms(ctx, blooms) {
          if (blooms.indexOf("flowers") >= 0) {
            for (var flower = 0; flower < 28; flower += 1) {
              var x = 30 + ((flower * 113) % 1220);
              var y = 585 + ((flower * 47) % 115);
              drawFireflyFlower(
                ctx,
                x,
                y,
                16 + (flower % 3) * 4,
                flower % 2 ? "#f9a8d4" : "#fde68a",
              );
            }
          }
          if (blooms.indexOf("mushrooms") >= 0) {
            drawFireflyMushroom(ctx, 360, 650, 46);
            drawFireflyMushroom(ctx, 890, 670, 46);
          }
          if (blooms.indexOf("vines") >= 0) {
            ctx.strokeStyle = "#86efac";
            ctx.lineWidth = 6;
            ctx.setLineDash([4, 10]);
            ctx.beginPath();
            ctx.arc(640, 380, 250, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
          }
          if (blooms.indexOf("critters") >= 0) {
            drawFireflyFrog(ctx, 420, 580, 44);
            drawFireflySnail(ctx, 820, 610, 44);
          }
          if (blooms.indexOf("canopy") >= 0) {
            ctx.fillStyle = "#fef08a";
            for (var canopy = 0; canopy < 12; canopy += 1) {
              drawFireflySparkle(ctx, 240 + canopy * 72, 210 + (canopy % 2) * 22, 15);
            }
          }
          if (blooms.indexOf("moonbloom") >= 0) {
            var aurora = ctx.createLinearGradient(240, 0, 1040, 0);
            aurora.addColorStop(0, "rgba(96,165,250,0)");
            aurora.addColorStop(0.5, "rgba(52,211,153,0.25)");
            aurora.addColorStop(1, "rgba(251,113,133,0)");
            ctx.fillStyle = aurora;
            ctx.fillRect(160, 230, 960, 260);
          }
        }

        function drawFireflyFlower(ctx, x, y, size, color) {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = color;
          for (var petal = 0; petal < 5; petal += 1) {
            var petalAngle = petal * ((Math.PI * 2) / 5);
            ctx.beginPath();
            ctx.ellipse(
              Math.cos(petalAngle) * size * 0.32,
              Math.sin(petalAngle) * size * 0.32,
              size * 0.3,
              size * 0.18,
              petalAngle,
              0,
              Math.PI * 2,
            );
            ctx.fill();
          }
          ctx.fillStyle = "#fde047";
          ctx.beginPath();
          ctx.arc(0, 0, size * 0.2, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawFireflyMushroom(ctx, x, y, size) {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = "#fca5a5";
          ctx.beginPath();
          ctx.arc(0, 0, size * 0.5, Math.PI, 0);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = "#fff7ed";
          ctx.beginPath();
          ctx.arc(-size * 0.16, -size * 0.14, size * 0.08, 0, Math.PI * 2);
          ctx.arc(size * 0.18, -size * 0.22, size * 0.07, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#fef3c7";
          ctx.fillRect(-size * 0.14, 0, size * 0.28, size * 0.42);
          ctx.restore();
        }

        function drawFireflyFrog(ctx, x, y, size) {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = "#4ade80";
          ctx.beginPath();
          ctx.ellipse(0, size * 0.1, size * 0.42, size * 0.32, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(-size * 0.22, -size * 0.22, size * 0.16, 0, Math.PI * 2);
          ctx.arc(size * 0.22, -size * 0.22, size * 0.16, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#052e16";
          ctx.beginPath();
          ctx.arc(-size * 0.22, -size * 0.22, size * 0.06, 0, Math.PI * 2);
          ctx.arc(size * 0.22, -size * 0.22, size * 0.06, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawFireflySnail(ctx, x, y, size) {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = "#fcd34d";
          ctx.beginPath();
          ctx.ellipse(-size * 0.12, size * 0.14, size * 0.4, size * 0.2, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#b45309";
          ctx.lineWidth = Math.max(1.5, size * 0.05);
          ctx.beginPath();
          ctx.arc(size * 0.12, -size * 0.12, size * 0.22, 0, Math.PI * 1.7);
          ctx.stroke();
          ctx.restore();
        }

        function drawFireflySparkle(ctx, x, y, size) {
          ctx.save();
          ctx.translate(x, y);
          ctx.beginPath();
          ctx.moveTo(0, -size);
          ctx.quadraticCurveTo(size * 0.15, -size * 0.15, size, 0);
          ctx.quadraticCurveTo(size * 0.15, size * 0.15, 0, size);
          ctx.quadraticCurveTo(-size * 0.15, size * 0.15, -size, 0);
          ctx.quadraticCurveTo(-size * 0.15, -size * 0.15, 0, -size);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }

        function drawFireflySymbolGlyph(ctx, type, x, y, size) {
          ctx.save();
          ctx.translate(x, y);
          if (type === "rose") {
            var heartSize = size * 0.5;
            ctx.beginPath();
            ctx.moveTo(0, heartSize * 0.6);
            ctx.bezierCurveTo(
              -heartSize,
              -heartSize * 0.3,
              -heartSize * 0.5,
              -heartSize,
              0,
              -heartSize * 0.25,
            );
            ctx.bezierCurveTo(
              heartSize * 0.5,
              -heartSize,
              heartSize,
              -heartSize * 0.3,
              0,
              heartSize * 0.6,
            );
            ctx.closePath();
            ctx.fill();
          } else if (type === "gold") {
            var starOuter = size * 0.55;
            var starInner = starOuter * 0.5;
            ctx.beginPath();
            for (var point = 0; point < 10; point += 1) {
              var pointRadius = point % 2 === 0 ? starOuter : starInner;
              var pointAngle = -Math.PI / 2 + point * (Math.PI / 5);
              var pointX = Math.cos(pointAngle) * pointRadius;
              var pointY = Math.sin(pointAngle) * pointRadius;
              if (point === 0) ctx.moveTo(pointX, pointY);
              else ctx.lineTo(pointX, pointY);
            }
            ctx.closePath();
            ctx.fill();
          } else if (type === "mint") {
            var leafRadius = size * 0.22;
            for (var leaf = 0; leaf < 3; leaf += 1) {
              var leafAngle = -Math.PI / 2 + leaf * ((Math.PI * 2) / 3);
              ctx.beginPath();
              ctx.arc(
                Math.cos(leafAngle) * leafRadius,
                Math.sin(leafAngle) * leafRadius,
                leafRadius,
                0,
                Math.PI * 2,
              );
              ctx.fill();
            }
            ctx.strokeStyle = ctx.fillStyle;
            ctx.lineWidth = Math.max(2, size * 0.09);
            ctx.beginPath();
            ctx.moveTo(0, leafRadius * 0.5);
            ctx.lineTo(0, size * 0.42);
            ctx.stroke();
          } else {
            ctx.beginPath();
            ctx.arc(0, 0, size * 0.4, 0, Math.PI * 2);
            ctx.fill();
            ctx.save();
            ctx.globalCompositeOperation = "destination-out";
            ctx.beginPath();
            ctx.arc(size * 0.18, -size * 0.06, size * 0.34, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }
          ctx.restore();
        }

        function drawFireflyLantern(ctx, lantern, count, goal, highlighted) {
          var ratio = Math.max(0, Math.min(1, count / Math.max(1, goal)));
          ctx.save();
          ctx.globalAlpha = highlighted ? 1 : 0.2;
          ctx.shadowColor = lantern.color;
          ctx.shadowBlur = highlighted ? 34 + ratio * 42 : 0;
          ctx.fillStyle = "rgba(2,6,23,0.84)";
          ctx.strokeStyle = highlighted ? "#ffffff" : "#64748b";
          ctx.lineWidth = highlighted ? 8 : 4;
          ctx.beginPath();
          ctx.rect(lantern.x - 48, lantern.y - 58, 96, 116);
          ctx.fill();
          ctx.stroke();
          if (highlighted) {
            ctx.strokeStyle = lantern.color;
            ctx.lineWidth = 4;
            ctx.strokeRect(lantern.x - 42, lantern.y - 52, 84, 104);
          }
          ctx.fillStyle = lantern.color;
          ctx.globalAlpha = (highlighted ? 1 : 0.25) * (0.2 + ratio * 0.8);
          ctx.fillRect(lantern.x - 38, lantern.y - 42 + (1 - ratio) * 82, 76, ratio * 82);
          ctx.globalAlpha = highlighted ? 1 : 0.28;
          ctx.fillStyle = lantern.color;
          drawFireflySymbolGlyph(ctx, lantern.type, lantern.x, lantern.y - 4, 38);
          ctx.shadowBlur = 0;
          ctx.fillStyle = highlighted ? "#f8fafc" : "#94a3b8";
          ctx.font = "bold 18px Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(count + "/" + goal, lantern.x, lantern.y + 82);
          ctx.restore();
        }

        function drawFireflyFriend(ctx, friend, x, y) {
          ctx.save();
          ctx.globalAlpha = friend.connected ? 1 : 0.4;
          ctx.strokeStyle = friend.color || "#f8fafc";
          ctx.fillStyle = "rgba(2,6,23,0.78)";
          ctx.lineWidth = 5;
          ctx.beginPath();
          ctx.arc(x, y, 44, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          var active = friend.activeFirefly;
          if (active) {
            var colors = { rose: "#fb7185", gold: "#facc15", mint: "#34d399", blue: "#60a5fa" };
            if (active.isGolden) {
              ctx.save();
              ctx.globalAlpha = 0.6 + 0.4 * Math.sin(Date.now() / 180);
              ctx.strokeStyle = "#fde047";
              ctx.lineWidth = 4;
              ctx.beginPath();
              ctx.arc(x, y, 60, 0, Math.PI * 2);
              ctx.stroke();
              ctx.restore();
              ctx.fillStyle = "#fde047";
              for (var sparkle = 0; sparkle < 4; sparkle += 1) {
                var sparkleAngle = Date.now() / 700 + (sparkle * Math.PI) / 2;
                drawFireflySparkle(
                  ctx,
                  x + Math.cos(sparkleAngle) * 66,
                  y + Math.sin(sparkleAngle) * 66,
                  8,
                );
              }
            }
            ctx.shadowColor = colors[active.type];
            ctx.shadowBlur = 28;
            ctx.fillStyle = colors[active.type];
            drawFireflySymbolGlyph(ctx, active.type, x, y, 36);
            ctx.shadowBlur = 0;
            var responseMax = snapshotResponseWindow(active.remainingMs);
            var progress = Math.max(0, Math.min(1, active.remainingMs / responseMax));
            ctx.strokeStyle = active.status === "confused" ? "#f8fafc" : colors[active.type];
            ctx.lineWidth = 6;
            ctx.beginPath();
            ctx.arc(x, y, 54, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress);
            ctx.stroke();
          } else {
            ctx.fillStyle = "#fef08a";
            drawFireflySparkle(ctx, x, y, 15);
          }
          ctx.fillStyle = "#f8fafc";
          ctx.font = "bold 17px Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(friend.displayName, x, y + 72);
          ctx.restore();
        }

        function snapshotResponseWindow(remainingMs) {
          if (remainingMs > 3600) return 4200;
          if (remainingMs > 3100) return 3600;
          return 3100;
        }

        function renderFireflyFriendsResults(snapshot) {
          canvas.style.display = "none";
          var rating = snapshot.gardenRating || 0;
          var stars = "";
          for (var index = 0; index < 3; index += 1) stars += index < rating ? "★" : "☆";
          var result = snapshot.result;
          var participants = (result && result.participants) || [];
          var rows = "";
          for (var rowIndex = 0; rowIndex < participants.length; rowIndex += 1) {
            var participant = participants[rowIndex];
            rows +=
              '<li><span class="tv-final-rank-number">✦</span><strong>' +
              escapeHtml(participant.displayName) +
              '</strong><span class="tv-final-rank-detail">' +
              escapeHtml(String(participant.correctGuides)) +
              " guided" +
              (participant.averageResponseMs !== null
                ? " · " + escapeHtml((participant.averageResponseMs / 1000).toFixed(1)) + "s avg"
                : "") +
              (participant.goldenGuides > 0
                ? " · " + escapeHtml(String(participant.goldenGuides)) + " golden"
                : "") +
              (participant.awards.length
                ? " · 🏅 " + escapeHtml(participant.awards.join(" · "))
                : "") +
              "</span></li>";
          }
          root.innerHTML =
            '<section class="tv-finale tv-firefly-results">' +
            renderFinaleConfetti() +
            '<p class="tv-finale-kicker">NIGHT COMPLETE</p>' +
            '<div class="quickdraw-winner-icon" aria-hidden="true">🏮</div>' +
            "<h1>" +
            escapeHtml(snapshot.won ? "The Garden Is Glowing!" : "Look What You Lit Together") +
            "</h1>" +
            '<div class="tv-firefly-stars">' +
            stars +
            "</div><p>" +
            escapeHtml(String(snapshot.correctGuides)) +
            " FIREFLIES · LONGEST CHAIN ×" +
            escapeHtml(String(snapshot.longestGlowChain)) +
            " · " +
            escapeHtml(String(snapshot.blooms.length)) +
            " BLOOMS</p><p>GARDEN SCORE " +
            escapeHtml(String(snapshot.gardenScore || 0)) +
            "</p>" +
            (rows ? '<ol class="tv-final-rankings">' + rows + "</ol>" : "") +
            renderTvPostGameActions() +
            "</section>";
        }

        function renderCritterCrossingSnapshot(snapshot) {
          latestCritterCrossingSnapshot = snapshot;
          if (snapshot.phase === "completed" && snapshot.result) {
            renderCritterCrossingResults(snapshot);
            return;
          }
          canvas.style.display = "block";
          if (canvas.width !== 1280 || canvas.height !== 720) {
            canvas.width = 1280;
            canvas.height = 720;
          }
          drawCritterCrossing(snapshot);
          var headline =
            snapshot.phase === "opening"
              ? "WATCH THE GARDEN"
              : snapshot.phase === "countdown"
                ? "GET READY TO CROSS"
                : snapshot.phase === "crossingResult"
                  ? "GARDEN COMPLETE!"
                  : snapshot.phase === "intermission"
                    ? "NEXT GARDEN…"
                    : snapshot.phase === "paused"
                      ? "WAITING FOR THE CREW"
                      : "CROSS TOGETHER";
          root.innerHTML =
            '<section class="tv-critter-hud" data-testid="tv-critter-crossing" data-critter-phase="' +
            escapeAttribute(snapshot.phase) +
            '"><div class="tv-critter-room">CRITTER CROSSING · ' +
            escapeHtml(roomCode) +
            "</div><h1>" +
            escapeHtml(headline) +
            '</h1><div class="tv-critter-timer">' +
            escapeHtml(String(Math.ceil(snapshot.timeRemainingMs / 1000))) +
            "s</div></section>";
        }

        function drawCritterCrossing(snapshot) {
          var ctx = canvas.getContext("2d");
          if (!ctx) return;

          var boardX = 70;
          var boardY = 72;
          var cell = Math.min(42, 590 / snapshot.board.height);
          var boardWidth = snapshot.board.width * cell;
          var boardHeight = snapshot.board.height * cell;

          // The garden background, panel, and tile grid never change between
          // frames of the same board, only the critters/toys/sprinklers on
          // top of it do. Redrawing all of that (a gradient rebuild plus a
          // full tile grid) 20 times a second was expensive enough on
          // lower-power TV hardware to make the game visibly fall behind the
          // actual simulation. Render it once per board into an offscreen
          // canvas and blit that instead.
          var boardLayerKey =
            snapshot.board.id + ":" + canvas.width + ":" + canvas.height + ":" + cell;
          if (!critterBoardLayer || critterBoardLayerKey !== boardLayerKey) {
            var layer = document.createElement("canvas");
            layer.width = canvas.width;
            layer.height = canvas.height;
            var layerCtx = layer.getContext("2d");
            var gradient = layerCtx.createLinearGradient(0, 0, canvas.width, canvas.height);
            gradient.addColorStop(0, "#d9f99d");
            gradient.addColorStop(0.58, "#bbf7d0");
            gradient.addColorStop(1, "#bae6fd");
            layerCtx.fillStyle = gradient;
            layerCtx.fillRect(0, 0, canvas.width, canvas.height);
            layerCtx.fillStyle = "rgba(255,255,255,0.45)";
            cupcakeRoundedRect(
              layerCtx,
              boardX - 10,
              boardY - 10,
              boardWidth + 20,
              boardHeight + 20,
              20,
            );
            layerCtx.fill();
            for (var row = 0; row < snapshot.board.height; row += 1) {
              for (var column = 0; column < snapshot.board.width; column += 1) {
                drawCritterTile(
                  layerCtx,
                  snapshot.board.tiles[row][column],
                  boardX + column * cell,
                  boardY + row * cell,
                  cell,
                );
              }
            }
            critterBoardLayer = layer;
            critterBoardLayerKey = boardLayerKey;
          }
          ctx.drawImage(critterBoardLayer, 0, 0);

          for (
            var sprinklerIndex = 0;
            sprinklerIndex < snapshot.sprinklers.length;
            sprinklerIndex += 1
          ) {
            var sprinkler = snapshot.sprinklers[sprinklerIndex];
            if (sprinkler.phase === "recovery") continue;
            for (var wetIndex = 0; wetIndex < sprinkler.tiles.length; wetIndex += 1) {
              var wetTile = sprinkler.tiles[wetIndex];
              var wetX = boardX + wetTile.x * cell;
              var wetY = boardY + wetTile.y * cell;
              ctx.save();
              ctx.lineWidth = sprinkler.phase === "active" ? 5 : 3;
              ctx.strokeStyle = sprinkler.phase === "active" ? "#0284c7" : "#0369a1";
              ctx.fillStyle =
                sprinkler.phase === "active" ? "rgba(56,189,248,0.62)" : "rgba(186,230,253,0.38)";
              ctx.setLineDash(sprinkler.phase === "active" ? [] : [3, 6]);
              ctx.fillRect(wetX + 2, wetY + 2, cell - 4, cell - 4);
              ctx.beginPath();
              for (var wave = 3; wave < cell - 3; wave += 8) {
                ctx.moveTo(wetX + 2, wetY + wave);
                ctx.quadraticCurveTo(
                  wetX + cell / 2,
                  wetY + wave - 5,
                  wetX + cell - 2,
                  wetY + wave,
                );
              }
              ctx.stroke();
              ctx.restore();
            }
          }

          for (
            var warningIndex = 0;
            warningIndex < snapshot.toyWarnings.length;
            warningIndex += 1
          ) {
            var warning = snapshot.toyWarnings[warningIndex];
            ctx.save();
            ctx.strokeStyle = "#7c2d12";
            ctx.fillStyle = "#fbbf24";
            ctx.lineWidth = 5;
            ctx.setLineDash([10, 7]);
            if (warning.axis === "horizontal") {
              var warningY = boardY + (warning.line + 0.5) * cell;
              var warningX = warning.direction === 1 ? boardX - 24 : boardX + boardWidth + 24;
              ctx.beginPath();
              ctx.moveTo(warningX, warningY - 16);
              ctx.lineTo(warningX, warningY + 16);
              ctx.stroke();
              ctx.setLineDash([]);
              ctx.fillStyle = "#7c2d12";
              ctx.beginPath();
              var arrowTipX = warning.direction === 1 ? warningX + 14 : warningX - 14;
              ctx.moveTo(arrowTipX, warningY);
              ctx.lineTo(warningX - (warning.direction === 1 ? 1 : -1) * 4, warningY - 11);
              ctx.lineTo(warningX - (warning.direction === 1 ? 1 : -1) * 4, warningY + 11);
              ctx.closePath();
              ctx.fill();
            }
            ctx.restore();
          }

          for (var cloverIndex = 0; cloverIndex < snapshot.board.clovers.length; cloverIndex += 1) {
            var clover = snapshot.board.clovers[cloverIndex];
            if (clover.collected) continue;
            drawCritterClover(
              ctx,
              boardX + (clover.position.x + 0.5) * cell,
              boardY + (clover.position.y + 0.5) * cell,
              cell * 0.68,
            );
          }

          for (var toyIndex = 0; toyIndex < snapshot.toys.length; toyIndex += 1) {
            var toy = snapshot.toys[toyIndex];
            drawCritterToy(
              ctx,
              toy.kind,
              boardX + (toy.x + 0.5) * cell,
              boardY + (toy.y + 0.5) * cell,
              cell * 0.72,
            );
          }

          for (var playerIndex = 0; playerIndex < snapshot.players.length; playerIndex += 1) {
            var player = snapshot.players[playerIndex];
            if (!player.active) continue;
            var offsetX = ((playerIndex % 2) - 0.5) * cell * 0.18;
            var offsetY = ((Math.floor(playerIndex / 2) % 2) - 0.5) * cell * 0.18;
            var playerX = boardX + (player.x + 0.5) * cell + offsetX;
            var playerY = boardY + (player.y + 0.5) * cell + offsetY;
            ctx.save();
            ctx.globalAlpha = player.connected
              ? player.protectedRemainingMs > 0
                ? 0.68
                : 1
              : 0.38;
            drawCritterAnimal(
              ctx,
              player.species,
              playerX,
              playerY,
              cell * 0.82,
              player.color || "#65a30d",
            );
            drawCritterMarker(ctx, player.marker, playerX + cell * 0.27, playerY - cell * 0.27, 6);
            ctx.restore();
          }

          var sideX = boardX + boardWidth + 55;
          ctx.fillStyle = "rgba(247,254,231,0.9)";
          cupcakeRoundedRect(ctx, sideX, 105, 1280 - sideX - 45, 500, 22);
          ctx.fill();
          ctx.fillStyle = "#365314";
          ctx.textAlign = "left";
          ctx.textBaseline = "alphabetic";
          ctx.font = "bold 34px Arial";
          ctx.fillText(snapshot.board.title, sideX + 28, 150);
          ctx.font = "bold 22px Arial";
          ctx.fillText(
            "Garden " + (snapshot.boardIndex + 1) + " of " + snapshot.boardCount,
            sideX + 28,
            184,
          );
          ctx.fillText(
            "CLOVERS " +
              snapshot.board.clovers.filter(function (clover) {
                return clover.collected;
              }).length +
              "/" +
              snapshot.board.clovers.length,
            sideX + 28,
            218,
          );
          if (snapshot.helpingTimeAddedMs > 0) {
            ctx.fillStyle = "#166534";
            ctx.font = "bold 25px Arial";
            ctx.fillText(
              "+" + snapshot.helpingTimeAddedMs / 1000 + "s FOR THE CREW",
              sideX + 28,
              260,
            );
          }
          ctx.font = "bold 20px Arial";
          for (var rosterIndex = 0; rosterIndex < snapshot.players.length; rosterIndex += 1) {
            var rosterPlayer = snapshot.players[rosterIndex];
            var rosterY = 320 + rosterIndex * 62;
            drawCritterAnimal(
              ctx,
              rosterPlayer.species,
              sideX + 42,
              rosterY - 8,
              42,
              rosterPlayer.color || "#365314",
            );
            ctx.fillStyle = rosterPlayer.color || "#365314";
            ctx.fillText(rosterPlayer.displayName, sideX + 72, rosterY);
            ctx.fillStyle = "#365314";
            ctx.font = "16px Arial";
            ctx.fillText(
              rosterPlayer.finished
                ? "SAFE AT THE GATE"
                : rosterPlayer.standby
                  ? "JOINS NEXT GARDEN"
                  : rosterPlayer.connected
                    ? rosterPlayer.bumps + " bumps · " + rosterPlayer.cloverFinds + " clovers"
                    : "RECONNECTING…",
              sideX + 72,
              rosterY + 24,
            );
            ctx.font = "bold 20px Arial";
          }
        }

        function drawCritterAnimal(ctx, species, x, y, size, playerColor) {
          var unit = size / 42;
          ctx.save();
          ctx.translate(x, y);
          ctx.lineJoin = "round";
          ctx.lineCap = "round";
          ctx.lineWidth = Math.max(1.5, 2.2 * unit);
          ctx.strokeStyle = "#3f3a2f";

          if (species === "duckling") {
            ctx.fillStyle = "#facc15";
            ctx.beginPath();
            ctx.ellipse(-5 * unit, 6 * unit, 13 * unit, 11 * unit, -0.15, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(5 * unit, -5 * unit, 11 * unit, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = "#fde047";
            ctx.beginPath();
            ctx.ellipse(-7 * unit, 5 * unit, 7 * unit, 4 * unit, -0.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = "#fb923c";
            ctx.beginPath();
            ctx.moveTo(14 * unit, -4 * unit);
            ctx.lineTo(22 * unit, 0);
            ctx.lineTo(14 * unit, 3 * unit);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            drawCritterEyes(ctx, 2 * unit, -8 * unit, 7 * unit, -7 * unit, unit);
          } else if (species === "frog") {
            ctx.fillStyle = "#4ade80";
            ctx.beginPath();
            ctx.ellipse(0, 3 * unit, 16 * unit, 13 * unit, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(-9 * unit, -8 * unit, 7 * unit, 0, Math.PI * 2);
            ctx.arc(9 * unit, -8 * unit, 7 * unit, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            drawCritterEyes(ctx, -9 * unit, -9 * unit, 9 * unit, -9 * unit, unit);
            ctx.strokeStyle = "#166534";
            ctx.beginPath();
            ctx.arc(0, 1 * unit, 8 * unit, 0.15, Math.PI - 0.15);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(-13 * unit, 12 * unit);
            ctx.lineTo(-20 * unit, 17 * unit);
            ctx.moveTo(13 * unit, 12 * unit);
            ctx.lineTo(20 * unit, 17 * unit);
            ctx.stroke();
          } else {
            ctx.fillStyle = "#8b5a3c";
            ctx.beginPath();
            for (var spike = 0; spike < 16; spike += 1) {
              var spikeAngle = -Math.PI + (spike * Math.PI * 2) / 16;
              var spikeRadius = (spike % 2 === 0 ? 20 : 15) * unit;
              var spikeX = Math.cos(spikeAngle) * spikeRadius;
              var spikeY = Math.sin(spikeAngle) * spikeRadius;
              if (spike === 0) ctx.moveTo(spikeX, spikeY);
              else ctx.lineTo(spikeX, spikeY);
            }
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = "#e7b98a";
            ctx.beginPath();
            ctx.ellipse(4 * unit, 2 * unit, 13 * unit, 11 * unit, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = "#3f3a2f";
            ctx.beginPath();
            ctx.arc(17 * unit, 1 * unit, 3 * unit, 0, Math.PI * 2);
            ctx.fill();
            drawCritterEyes(ctx, 3 * unit, -3 * unit, 3 * unit, -3 * unit, unit);
            ctx.strokeStyle = "#7c3f2c";
            ctx.beginPath();
            ctx.arc(7 * unit, 4 * unit, 5 * unit, 0.3, 1.4);
            ctx.stroke();
          }

          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = Math.max(2, 3 * unit);
          ctx.beginPath();
          ctx.arc(0, 0, 18 * unit, 0.55, 2.58);
          ctx.stroke();
          ctx.strokeStyle = playerColor;
          ctx.lineWidth = Math.max(2, 2 * unit);
          ctx.stroke();
          ctx.restore();
        }

        function drawCritterEyes(ctx, leftX, leftY, rightX, rightY, unit) {
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(leftX, leftY, 2.6 * unit, 0, Math.PI * 2);
          ctx.arc(rightX, rightY, 2.6 * unit, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#17202a";
          ctx.beginPath();
          ctx.arc(leftX + unit * 0.5, leftY, 1.25 * unit, 0, Math.PI * 2);
          ctx.arc(rightX + unit * 0.5, rightY, 1.25 * unit, 0, Math.PI * 2);
          ctx.fill();
        }

        function drawCritterTile(ctx, kind, x, y, size) {
          var colors = {
            grass: "#86c95a",
            path: "#d6c393",
            hedge: "#2f6b3b",
            flowerBed: "#8b5e3c",
            puddle: "#67c4df",
            restStone: "#d1d5db",
            start: "#fef3c7",
            gate: "#fde68a",
          };
          ctx.fillStyle = colors[kind] || colors.grass;
          ctx.fillRect(x, y, size, size);
          ctx.strokeStyle = "rgba(23,53,31,0.16)";
          ctx.lineWidth = 1;
          ctx.strokeRect(x, y, size, size);
          if (kind === "puddle") {
            ctx.strokeStyle = "rgba(3,105,161,0.6)";
            ctx.beginPath();
            ctx.moveTo(x + 6, y + size / 2);
            ctx.quadraticCurveTo(x + size / 2, y + size / 2 - 5, x + size - 6, y + size / 2);
            ctx.stroke();
          } else if (kind === "restStone") {
            ctx.strokeStyle = "#ffffff";
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.arc(x + size / 2, y + size / 2, size * 0.34, 0, Math.PI * 2);
            ctx.stroke();
          } else if (kind === "gate") {
            var poleX = x + size * 0.36;
            ctx.strokeStyle = "#ffffff";
            ctx.lineWidth = Math.max(1.5, size * 0.06);
            ctx.beginPath();
            ctx.moveTo(poleX, y + size * 0.18);
            ctx.lineTo(poleX, y + size * 0.86);
            ctx.stroke();
            ctx.fillStyle = "#dc2626";
            ctx.beginPath();
            ctx.moveTo(poleX, y + size * 0.18);
            ctx.lineTo(poleX + size * 0.42, y + size * 0.3);
            ctx.lineTo(poleX, y + size * 0.42);
            ctx.closePath();
            ctx.fill();
          }
        }

        function drawCritterClover(ctx, x, y, size) {
          ctx.save();
          ctx.translate(x, y);
          var leafRadius = size * 0.22;
          ctx.fillStyle = "#22c55e";
          ctx.strokeStyle = "#166534";
          ctx.lineWidth = Math.max(1, size * 0.05);
          for (var leaf = 0; leaf < 3; leaf += 1) {
            var leafAngle = -Math.PI / 2 + leaf * ((Math.PI * 2) / 3);
            ctx.beginPath();
            ctx.ellipse(
              Math.cos(leafAngle) * leafRadius,
              Math.sin(leafAngle) * leafRadius,
              leafRadius,
              leafRadius * 0.78,
              leafAngle,
              0,
              Math.PI * 2,
            );
            ctx.fill();
            ctx.stroke();
          }
          ctx.strokeStyle = "#166534";
          ctx.beginPath();
          ctx.moveTo(0, leafRadius * 0.4);
          ctx.lineTo(0, size * 0.42);
          ctx.stroke();
          ctx.restore();
        }

        function drawCritterToy(ctx, kind, x, y, size) {
          ctx.save();
          ctx.translate(x, y);
          if (kind === "ball") {
            ctx.fillStyle = "#f8fafc";
            ctx.strokeStyle = "#1e293b";
            ctx.lineWidth = Math.max(1.2, size * 0.045);
            ctx.beginPath();
            ctx.arc(0, 0, size * 0.42, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = "#1e293b";
            ctx.beginPath();
            ctx.arc(0, 0, size * 0.13, 0, Math.PI * 2);
            ctx.fill();
            for (var pentagon = 0; pentagon < 5; pentagon += 1) {
              var pentagonAngle = -Math.PI / 2 + pentagon * ((Math.PI * 2) / 5);
              ctx.beginPath();
              ctx.moveTo(0, 0);
              ctx.lineTo(
                Math.cos(pentagonAngle) * size * 0.32,
                Math.sin(pentagonAngle) * size * 0.32,
              );
              ctx.stroke();
            }
          } else if (kind === "truck") {
            ctx.fillStyle = "#f97316";
            cupcakeRoundedRect(ctx, -size * 0.44, -size * 0.22, size * 0.62, size * 0.32, 4);
            ctx.fill();
            ctx.fillStyle = "#fb923c";
            cupcakeRoundedRect(ctx, size * 0.16, -size * 0.3, size * 0.28, size * 0.4, 4);
            ctx.fill();
            ctx.fillStyle = "#bae6fd";
            ctx.fillRect(size * 0.2, -size * 0.24, size * 0.16, size * 0.14);
            ctx.fillStyle = "#1e293b";
            ctx.beginPath();
            ctx.arc(-size * 0.24, size * 0.14, size * 0.12, 0, Math.PI * 2);
            ctx.arc(size * 0.26, size * 0.14, size * 0.12, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.strokeStyle = "#ef4444";
            ctx.lineWidth = Math.max(2, size * 0.09);
            ctx.beginPath();
            ctx.arc(0, 0, size * 0.34, 0, Math.PI * 2);
            ctx.arc(0, 0, size * 0.22, 0, Math.PI * 2);
            ctx.stroke();
            ctx.fillStyle = "#ef4444";
            ctx.beginPath();
            ctx.arc(size * 0.34, 0, size * 0.07, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }

        function drawCritterMarker(ctx, marker, x, y, radius) {
          ctx.save();
          ctx.fillStyle = "#ffffff";
          ctx.strokeStyle = "#17351f";
          ctx.lineWidth = 2;
          ctx.beginPath();
          if (marker === "square") ctx.rect(x - radius, y - radius, radius * 2, radius * 2);
          else if (marker === "triangle") {
            ctx.moveTo(x, y - radius);
            ctx.lineTo(x + radius, y + radius);
            ctx.lineTo(x - radius, y + radius);
            ctx.closePath();
          } else if (marker === "star") {
            for (var point = 0; point < 8; point += 1) {
              var angle = -Math.PI / 2 + (point * Math.PI) / 4;
              var distance = point % 2 === 0 ? radius : radius * 0.45;
              var starX = x + Math.cos(angle) * distance;
              var starY = y + Math.sin(angle) * distance;
              if (point === 0) ctx.moveTo(starX, starY);
              else ctx.lineTo(starX, starY);
            }
            ctx.closePath();
          } else ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.restore();
        }

        function renderCritterCrossingResults(snapshot) {
          canvas.style.display = "none";
          var result = snapshot.result;
          var ratio = result.eligibleCrossings
            ? result.completedCrossings / result.eligibleCrossings
            : 0;
          var headline =
            ratio === 1
              ? "PERFECT CROSSING!"
              : ratio >= 0.75
                ? "MOSTLY ACROSS!"
                : "GARDEN ADVENTURERS!";
          var rows = "";
          for (var index = 0; index < result.participants.length; index += 1) {
            var player = result.participants[index];
            rows +=
              '<li><span class="tv-final-rank-number">' +
              (player.species === "duckling" ? "🐥" : player.species === "frog" ? "🐸" : "🦔") +
              "</span><strong>" +
              escapeHtml(player.displayName) +
              '</strong><span class="tv-final-rank-detail">' +
              escapeHtml(String(player.crossingsCompleted)) +
              "/" +
              escapeHtml(String(player.boardsEligible)) +
              " gates · " +
              escapeHtml(String(player.bumps)) +
              " bumps" +
              (player.awards.length ? " · 🏅 " + escapeHtml(player.awards.join(" · ")) : "") +
              "</span></li>";
          }
          root.innerHTML =
            '<section class="tv-finale tv-critter-results" data-testid="tv-critter-results">' +
            renderFinaleConfetti() +
            '<p class="tv-finale-kicker">THE CREW MADE IT HOME</p>' +
            '<div class="quickdraw-winner-icon" aria-hidden="true">🌿🏁🌿</div><h1>' +
            escapeHtml(headline) +
            '</h1><p class="tv-winner-name">' +
            escapeHtml(String(result.starsEarned)) +
            "/9 STARS · " +
            escapeHtml(String(result.crewScore)) +
            ' POINTS</p><ol class="tv-final-rankings">' +
            rows +
            "</ol>" +
            renderTvPostGameActions() +
            "</section>";
        }

        function cupcakeRoundedRect(ctx, x, y, width, height, radius) {
          var safeRadius = Math.min(radius, width / 2, height / 2);
          ctx.beginPath();
          ctx.moveTo(x + safeRadius, y);
          ctx.lineTo(x + width - safeRadius, y);
          ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
          ctx.lineTo(x + width, y + height - safeRadius);
          ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
          ctx.lineTo(x + safeRadius, y + height);
          ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
          ctx.lineTo(x, y + safeRadius);
          ctx.quadraticCurveTo(x, y, x + safeRadius, y);
          ctx.closePath();
        }

        function renderRoshamboShowdownSnapshot(snapshot) {
          latestRoshamboSnapshot = snapshot;
          if (snapshot.phase === "completed") {
            renderRoshamboShowdownResults(snapshot);
            return;
          }
          canvas.style.display = "none";

          // Sound effects and state transitions
          var phaseChanged = snapshot.phase !== previousRoshamboPhase;
          var roundChanged = snapshot.roundId !== previousRoshamboRoundId;
          previousRoshamboPhase = snapshot.phase;
          previousRoshamboRoundId = snapshot.roundId;

          if (phaseChanged) {
            sfx.initCtx();
            if (snapshot.phase === "reveal") {
              sfx.playRacingCountdown(true);
            } else if (snapshot.phase === "matchComplete") {
              sfx.playChime();
            }
          }

          // Trigger player KOs sound if anyone was eliminated in this round reveal
          if (phaseChanged && snapshot.phase === "reveal" && snapshot.latestRoundResult) {
            var elims = snapshot.latestRoundResult.eliminatedPlayerIds || [];
            if (elims.length > 0) {
              sfx.playPlayerDowned();
            }
          }

          // Countdown tick sound
          if (snapshot.phase === "countdown") {
            var currentSec = Math.ceil(snapshot.timeRemainingMs / 1000);
            if (currentSec !== lastRoshamboCountdownSec) {
              lastRoshamboCountdownSec = currentSec;
              sfx.playRacingCountdown(false);
            }
          } else {
            lastRoshamboCountdownSec = null;
          }

          var headline = "ROSHAMBO SHOWDOWN";
          var kicker = "Match " + snapshot.currentMatch + " of " + snapshot.maxMatches;
          var detailsHtml = "";

          if (snapshot.paused) {
            headline = "GAME PAUSED";
            detailsHtml =
              '<div class="tv-roshambo-round-result-alert drew">Waiting for players to reconnect...</div>';
          } else if (snapshot.phase === "selection") {
            headline = "MAKE YOUR CHOICES";
            var secsLeft = Math.ceil(snapshot.timeRemainingMs / 1000);
            detailsHtml =
              '<div style="font-size: 32px; font-weight: bold; margin-bottom: 20px;">' +
              secsLeft +
              "s Remaining</div>";
            if (snapshot.bannedChoice !== "none") {
              detailsHtml +=
                '<div class="tv-roshambo-round-result-alert drew">⚠️ ' +
                snapshot.bannedChoice.toUpperCase() +
                " IS BANNED!</div>";
            }
          } else if (snapshot.phase === "countdown") {
            headline = "LOCK IN!";
            var tick = Math.ceil(snapshot.timeRemainingMs / 1000);
            detailsHtml =
              '<div class="tv-roshambo-countdown-container"><div class="tv-roshambo-countdown-huge">' +
              tick +
              "</div></div>";
          } else if (snapshot.phase === "reveal") {
            var result = snapshot.latestRoundResult;
            if (result) {
              if (result.drew) {
                headline = "DRAW ROUND!";
                detailsHtml =
                  '<div class="tv-roshambo-round-result-alert drew">' +
                  (result.bannedChoice !== "none"
                    ? "All three choices present! " +
                      result.bannedChoice.toUpperCase() +
                      " banned next round."
                    : "Draw! Resolved by lock-in speed!") +
                  "</div>";
              } else {
                headline = "CLASH REVEAL!";
                detailsHtml =
                  '<div class="tv-roshambo-round-result-alert win">WINNING HAND: ' +
                  result.winningChoice.toUpperCase() +
                  "</div>";
              }
            }
          } else if (snapshot.phase === "matchComplete") {
            var winner = snapshot.players.find(function (p) {
              return p.playerId === snapshot.matchWinnerPlayerId;
            });
            headline = "MATCH COMPLETE!";
            detailsHtml =
              '<div class="tv-roshambo-round-result-alert win">' +
              (winner ? escapeHtml(winner.displayName) : "Someone") +
              " wins the Match!</div>";
          }

          // Build player cards grid
          var cardsHtml = "";
          for (var i = 0; i < snapshot.players.length; i++) {
            var p = snapshot.players[i];

            // Determine avatar letter and choice reveal
            var initials = escapeHtml(p.displayName.slice(0, 2).toUpperCase());
            var statusLabel = p.alive ? (p.hasChosen ? "READY ✓" : "SELECTING...") : "ELIMINATED";
            var cardClass =
              "tv-roshambo-card " +
              (p.alive ? "alive" : "dead") +
              (p.hasChosen ? " has-chosen" : "") +
              (!p.connected ? " disconnected" : "");

            var choiceRevealHtml = "";
            if (snapshot.phase === "reveal" || snapshot.phase === "matchComplete") {
              var choiceLabel = "NO PICK";
              var pChoice =
                snapshot.latestRoundResult && snapshot.latestRoundResult.choices[p.playerId];
              if (pChoice === "rock") choiceLabel = "ROCK";
              else if (pChoice === "paper") choiceLabel = "PAPER";
              else if (pChoice === "scissors") choiceLabel = "SCISSORS";
              choiceRevealHtml =
                '<div class="reveal-choice" data-roshambo-choice="' +
                escapeAttribute(pChoice || "none") +
                '">' +
                escapeHtml(choiceLabel) +
                "</div>";
            }

            var avatarStyle =
              "background-color: " +
              p.color +
              "; box-shadow: 0 0 16px " +
              p.color +
              "bb; color: #fff; text-shadow: 0 1px 2px rgba(0,0,0,0.4);";
            var cardStyle = p.alive
              ? "border-color: " +
                p.color +
                "66; box-shadow: 0 12px 30px -4px " +
                p.color +
                "33, 0 4px 12px -2px " +
                p.color +
                "1a;"
              : "";
            if (!p.alive) {
              avatarStyle = "background-color: #475569; color: #cbd5e1; opacity: 0.6;";
            }

            cardsHtml +=
              '<div class="' +
              cardClass +
              '" style="' +
              cardStyle +
              '">' +
              '<div class="avatar" style="' +
              avatarStyle +
              '">' +
              initials +
              "</div>" +
              '<div class="player-name">' +
              escapeHtml(p.displayName) +
              "</div>" +
              '<div class="player-stats">' +
              p.matchWins +
              " Match Wins</div>" +
              '<div class="player-status">' +
              statusLabel +
              "</div>" +
              choiceRevealHtml +
              "</div>";
          }

          root.innerHTML =
            '<section class="tv-roshambo-container">' +
            '<div class="tv-roshambo-header">' +
            '<div class="subtitle">' +
            escapeHtml(kicker) +
            "</div>" +
            "<h1>" +
            escapeHtml(headline) +
            "</h1>" +
            "</div>" +
            detailsHtml +
            '<div class="tv-roshambo-grid">' +
            cardsHtml +
            "</div></section>";
        }

        function renderRoshamboShowdownResults(snapshot) {
          canvas.style.display = "none";
          var champion = snapshot.players.find(function (p) {
            return p.playerId === snapshot.tournamentWinnerPlayerId;
          });

          var rankings = "";
          var sorted = snapshot.players.slice().sort(function (a, b) {
            return b.matchWins - a.matchWins;
          });
          for (var index = 0; index < sorted.length; index += 1) {
            var standing = sorted[index];
            rankings +=
              '<li><span class="tv-final-rank-number">' +
              (index + 1) +
              "</span>" +
              "<strong>" +
              escapeHtml(standing.displayName) +
              "</strong>" +
              '<span class="tv-final-rank-detail">' +
              standing.matchWins +
              " Match Wins</span>" +
              "</li>";
          }

          root.innerHTML =
            '<section class="tv-finale tv-roshambo-results">' +
            renderFinaleConfetti() +
            '<p class="tv-finale-kicker">ROSHAMBO CHAMPIONSHIPS</p>' +
            '<div class="quickdraw-winner-icon" aria-hidden="true">🏆</div>' +
            "<h1>Roshambo Champion</h1>" +
            '<p class="tv-winner-name">' +
            (champion ? escapeHtml(champion.displayName) : "Someone") +
            "</p>" +
            '<p style="font-size:28px">The Showdown tournament has concluded.</p>' +
            '<ol class="tv-final-rankings">' +
            rankings +
            "</ol>" +
            renderTvPostGameActions() +
            "</section>";
        }

        function renderMysterySoundToggle() {
          return (
            '<button class="tv-mystery-sound-toggle" type="button" onclick="handleMysterySoundToggle()" aria-pressed="' +
            (sfx.muted ? "true" : "false") +
            '">' +
            (sfx.muted ? "🔇 Mystery sound off" : "🔊 Mystery sound on") +
            "</button>"
          );
        }

        function playMidnightAlibisCue(snapshot) {
          var chapterStarted =
            snapshot.phase === "investigation" &&
            snapshot.checkpoint === "search" &&
            (previousMysteryPhase !== "investigation" ||
              snapshot.chapter !== previousMysteryChapter);
          var revealSignature = (snapshot.latestReveals || []).join("|");
          var suspicionAdded = snapshot.suspicions.length > previousMysterySuspicionCount;
          if (
            (snapshot.phase === "reconstruction" || snapshot.phase === "completed") &&
            previousMysteryPhase !== "reconstruction" &&
            previousMysteryPhase !== "completed"
          ) {
            sfx.playMysterySting();
          } else {
            if (chapterStarted) sfx.playMysteryClock();
            if (suspicionAdded) sfx.playMysteryGasp();
            else if (revealSignature && revealSignature !== previousMysteryRevealSignature) {
              sfx.playMysteryPaper();
            }
          }
          previousMysteryPhase = snapshot.phase;
          previousMysteryChapter = snapshot.chapter;
          previousMysteryRevealSignature = revealSignature;
          previousMysterySuspicionCount = snapshot.suspicions.length;
        }

        function renderMidnightAlibisSnapshot(snapshot) {
          latestMysterySnapshot = snapshot;
          playMidnightAlibisCue(snapshot);
          var phaseIcon = "🎭";
          if (snapshot.phase === "investigation" && snapshot.checkpoint === "search") {
            phaseIcon = "🔎";
          } else if (snapshot.phase === "investigation" && snapshot.checkpoint === "testimony") {
            phaseIcon = "💬";
          } else if (snapshot.phase === "investigation" && snapshot.checkpoint === "suspicion") {
            phaseIcon = "👁️";
          } else if (snapshot.phase === "verdict") {
            phaseIcon = "⚖️";
          } else if (snapshot.phase === "reaction" || snapshot.phase === "reactionReveal") {
            phaseIcon = "🎬";
          }
          var instruction = "Everyone: check your controller for your next action.";
          if (snapshot.phase === "opening") {
            instruction =
              "Everyone: privately read your Character, Secret Role, memory, and objective.";
          } else if (snapshot.phase === "investigation" && snapshot.checkpoint === "search") {
            instruction =
              "Everyone: check your controller and choose where your character searches.";
          } else if (snapshot.phase === "investigation" && snapshot.checkpoint === "testimony") {
            instruction = "Everyone: check your controller and choose how your character responds.";
          } else if (snapshot.phase === "investigation" && snapshot.checkpoint === "suspicion") {
            instruction =
              "Everyone: privately mark a current suspect. This is not the Final Verdict.";
          } else if (snapshot.phase === "verdict") {
            instruction = "Everyone: use the Evidence and Testimony to lock one Final Verdict.";
          } else if (snapshot.phase === "reaction") {
            instruction = "The Murderer is privately choosing one final theatrical reaction.";
          } else if (snapshot.phase === "reactionReveal") {
            instruction = "Watch closely. The truth comes out next.";
          }
          if (snapshot.reviewing) {
            instruction = "NEW CLUE! Read it aloud. Talk: What does it prove?";
          }
          var evidence = "";
          for (var e = 0; e < snapshot.evidence.length; e += 1) {
            evidence += "<li>" + escapeHtml(snapshot.evidence[e]) + "</li>";
          }
          var testimony = "";
          for (var t = 0; t < snapshot.testimony.length; t += 1) {
            testimony += "<li>" + escapeHtml(snapshot.testimony[t]) + "</li>";
          }
          var suspicions = "";
          for (var s = 0; s < snapshot.suspicions.length; s += 1) {
            suspicions += "<li>" + escapeHtml(snapshot.suspicions[s]) + "</li>";
          }
          var cast = "";
          var entranceCards = "";
          for (var c = 0; c < snapshot.cast.length; c += 1) {
            cast +=
              "<li data-mystery-cast-entry><strong>" +
              escapeHtml(snapshot.cast[c].guestName) +
              "</strong> — " +
              escapeHtml(snapshot.cast[c].displayName) +
              "</li>";
            entranceCards +=
              '<article class="tv-mystery-entrance-card" data-mystery-entrance-card><div class="tv-mystery-portrait" aria-label="Portrait silhouette"><span aria-hidden="true">' +
              escapeHtml(snapshot.cast[c].portraitSymbol) +
              "</span></div><div><h2>" +
              escapeHtml(snapshot.cast[c].nickname) +
              "</h2><strong>" +
              escapeHtml(snapshot.cast[c].guestName) +
              '</strong><p class="tv-mystery-player-name">Played by ' +
              escapeHtml(snapshot.cast[c].displayName) +
              "</p><p>" +
              escapeHtml(snapshot.cast[c].publicDetail) +
              "</p></div></article>";
          }
          var keyClues = "";
          for (var k = 0; k < snapshot.keyClues.length; k += 1) {
            keyClues += "<li>" + escapeHtml(snapshot.keyClues[k]) + "</li>";
          }
          var latestReveals = "";
          for (var n = 0; n < snapshot.latestReveals.length; n += 1) {
            latestReveals += "<li>" + escapeHtml(snapshot.latestReveals[n]) + "</li>";
          }
          if (snapshot.phase === "reactionReveal") {
            root.innerHTML =
              '<section class="tv-lobby tv-mystery-panel"><div class="tv-mystery-reaction-stage"><div class="reaction-icon" aria-hidden="true">🎬</div><p class="eyebrow">One last dramatic moment</p><h1>' +
              escapeHtml(snapshot.publicReaction || "The Murderer remains unreadable…") +
              "</h1><p>The Case Truth is about to be revealed.</p></div>" +
              renderMysterySoundToggle() +
              "</section>";
            return;
          }
          if (snapshot.phase === "completed" || snapshot.phase === "reconstruction") {
            var solution = snapshot.solution || {};
            var outcome = snapshot.outcome || {};
            var reconstruction = "";
            var reconstructionBeats = solution.reconstruction || [];
            for (var r = 0; r < reconstructionBeats.length; r += 1) {
              reconstruction += "<li>" + escapeHtml(reconstructionBeats[r]) + "</li>";
            }
            var guesses = "";
            var verdictResults = snapshot.verdictResults || [];
            for (var v = 0; v < verdictResults.length; v += 1) {
              guesses +=
                "<li><strong>" +
                escapeHtml(verdictResults[v].displayName) +
                ":</strong> " +
                escapeHtml(verdictResults[v].accusedGuestName) +
                " · " +
                escapeHtml(String(verdictResults[v].correctParts)) +
                "/3 correct</li>";
            }
            var awards = "";
            var awardResults = snapshot.awards || [];
            for (var awardIndex = 0; awardIndex < awardResults.length; awardIndex += 1) {
              awards +=
                '<article class="tv-mystery-award"><span aria-hidden="true">🏅</span><strong>' +
                escapeHtml(awardResults[awardIndex].title) +
                "</strong><b>" +
                escapeHtml(awardResults[awardIndex].displayName) +
                "</b><small>" +
                escapeHtml(awardResults[awardIndex].detail) +
                "</small></article>";
            }
            var rankings = renderFinalRankings(snapshot.standings, false);
            root.innerHTML =
              '<section class="tv-finale tv-mystery-results">' +
              renderFinaleConfetti() +
              '<div class="tv-mystery-finale-card">' +
              '<p class="eyebrow">The truth comes out</p>' +
              '<div class="tv-mystery-finale-seal" aria-hidden="true">🔎</div>' +
              "<h1>" +
              escapeHtml(outcome.headline || "The truth comes out!") +
              "</h1><h2>" +
              escapeHtml(solution.murdererName || "The murderer") +
              " murdered Lucien.</h2><p>Played by " +
              escapeHtml(solution.murdererPlayerName || "Unknown Player") +
              "</p><p><strong>Why?</strong> " +
              escapeHtml(solution.motive || "") +
              "</p><p><strong>When?</strong> " +
              escapeHtml(solution.criticalMoment || "") +
              "</p><p><strong>Fake clue:</strong> " +
              escapeHtml(solution.coverUp || "") +
              (snapshot.publicReaction
                ? "</p><p><strong>Final reaction:</strong> " + escapeHtml(snapshot.publicReaction)
                : "") +
              '</p><div class="tv-mystery-finale-grid"><section><h2>How we know</h2><ol>' +
              reconstruction +
              "</ol></section><section><h2>Everyone's guesses</h2><ul>" +
              guesses +
              '</ul></section></div><h2>Tonight\'s Awards</h2><div class="tv-mystery-awards">' +
              awards +
              '</div><ol class="tv-final-rankings">' +
              rankings +
              "</ol></div>" +
              renderMysterySoundToggle() +
              (snapshot.phase === "completed" ? renderTvPostGameActions() : "") +
              "</section>";
            return;
          }
          var latestPanel = snapshot.latestReveals.length
            ? '<section class="tv-mystery-latest"><h2>' +
              (snapshot.reviewing
                ? "📺 NEW CLUE — TALK IT OUT!"
                : "🔎 NEW CLUE — READ IT BEFORE YOU CHOOSE") +
              "</h2><ul>" +
              (latestReveals || "<li>No new public clue.</li>") +
              "</ul>" +
              (snapshot.spotlight
                ? "<p><strong>Someone spotlighted:</strong> " +
                  escapeHtml(snapshot.spotlight) +
                  ". It may be a red herring!</p>"
                : "") +
              "</section>"
            : "";
          var mysteryBody =
            snapshot.phase === "opening"
              ? '<section class="tv-mystery-entrance-grid">' + entranceCards + "</section>"
              : snapshot.phase === "reaction"
                ? '<section class="tv-mystery-reaction-stage waiting"><div class="reaction-icon" aria-hidden="true">🎭</div><h1>The Murderer is choosing…</h1><p>Remain calm, act shocked, or offer one mysterious smile?</p><p>' +
                  Math.ceil(snapshot.timeRemainingMs / 1000) +
                  "s</p></section>"
                : '<div class="tv-mystery-board"><section><h2>🎭 Tonight\'s Cast</h2><ul>' +
                  (cast || "<li>The cast is being assigned.</li>") +
                  "</ul></section><section><h2>🗝️ Key Clues</h2><ul>" +
                  (keyClues || "<li>The first key clue is coming soon.</li>") +
                  "</ul></section><section><h2>💬 What Guests Said</h2><ul>" +
                  (testimony || "<li>No testimony recorded yet.</li>") +
                  "</ul></section><section><h2>👁️ Suspicion Board</h2><ul>" +
                  (suspicions || "<li>No suspicions recorded yet.</li>") +
                  "</ul></section></div>";
          root.innerHTML =
            '<section class="tv-lobby tv-mystery-panel">' +
            '<p class="eyebrow">Midnight Alibis · ' +
            escapeHtml(snapshot.phase) +
            '</p><div class="tv-mystery-heading"><div class="tv-mystery-phase-icon" aria-hidden="true">' +
            phaseIcon +
            "</div><h1>" +
            escapeHtml(snapshot.headline) +
            '</h1></div><p class="tv-mystery-story">' +
            escapeHtml(snapshot.publicStory) +
            '</p><p class="status">' +
            escapeHtml(instruction) +
            '</p><div class="tv-mystery-mood-strip" aria-hidden="true"><span>🕯️ A party at midnight</span><span>🔎 Follow every clue</span><span>🤫 Everyone has a secret</span></div>' +
            latestPanel +
            "<p>Chapter " +
            snapshot.chapter +
            " of " +
            snapshot.chapterCount +
            " · " +
            Math.ceil(snapshot.timeRemainingMs / 1000) +
            "s</p>" +
            mysteryBody +
            (snapshot.phase === "investigation" || snapshot.phase === "verdict"
              ? "<p>" +
                snapshot.answeredCount +
                " of " +
                snapshot.participantCount +
                " choices locked</p>"
              : "") +
            renderMysterySoundToggle() +
            "</section>";
        }

        function renderFuseLobbyPowerUpGuide() {
          var cards = [
            ["💣", "Extra Charge", "Place one more bomb at a time.", false],
            ["↔", "Long Spark", "Blasts reach one cell farther.", false],
            ["»", "Quick Soles", "Move faster for the Round.", false],
            ["◆", "Bubble Shield", "Absorb one blast hit.", false],
            ["⚡", "Remote Spark", "Use TRIGGER to detonate your bombs.", false],
            ["✦", "Piercing Spark", "Blast through one destroyed crate.", false],
            ["◒", "Kick Toe", "Walk into a bomb to kick it down the corridor.", false],
            ["⌁", "Hover Belt", "Cross marked low-hazard shortcuts briefly.", false],
            ["●", "Sticky Soles", "Risk: movement slows for 6 seconds.", true],
            ["🔥", "Hot Fuse", "Risk: your next bomb explodes very quickly.", true],
          ];
          var html =
            '<section class="tv-fuse-power-guide"><h2>POWER-UP QUICK GUIDE</h2><div class="tv-fuse-power-grid">';
          for (var cardIndex = 0; cardIndex < cards.length; cardIndex += 1) {
            var card = cards[cardIndex];
            html +=
              '<div class="tv-fuse-power-card' +
              (card[3] ? " risk" : "") +
              '"><b><span aria-hidden="true">' +
              card[0] +
              "</span> " +
              card[1] +
              "</b><span>" +
              card[2] +
              "</span></div>";
          }
          return html + "</div></section>";
        }

        function renderFinalRankings(standings, quickdraw) {
          var html = "";
          for (var index = 0; index < standings.length; index += 1) {
            var standing = standings[index];
            var medal =
              index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : "#" + (index + 1);
            var detail = quickdraw
              ? standing.score + " pts · " + standing.roundWins + " wins"
              : standing.score + " points";
            html +=
              "<li><span>" +
              escapeHtml(medal) +
              "</span><strong>" +
              escapeHtml(standing.displayName) +
              '</strong><span class="tv-final-score">' +
              escapeHtml(detail) +
              "</span></li>";
          }
          return html;
        }

        function renderFinaleConfetti() {
          var html = '<div class="tv-finale-confetti" aria-hidden="true">';
          for (var index = 0; index < 12; index += 1) {
            html +=
              '<span style="--confetti-left:' +
              (4 + index * 8) +
              "%;--confetti-delay:" +
              index +
              '"></span>';
          }
          return html + "</div>";
        }

        function renderTvPostGameActions(platform) {
          var current = platform || latestPlatformSnapshot;
          var ready = 0;
          var connected = 0;
          var slots = current && current.slots ? current.slots : [];
          for (var index = 0; index < slots.length; index += 1) {
            if (!slots[index].connected) continue;
            connected += 1;
            if (slots[index].ready) ready += 1;
          }
          return (
            '<aside class="tv-post-game-actions"><h2>Play Again</h2><p>' +
            escapeHtml(String(ready)) +
            " of " +
            escapeHtml(String(connected)) +
            " players ready. Players can tap <strong>Ready Up</strong> on their phones.</p>" +
            (tvCanControlRoom
              ? '<button type="button" onclick="handleTvPlayAgain()">' +
                escapeHtml(
                  tvLaunchMode === "fixed" && fixedGameChoice
                    ? "Play " + choiceTitle(fixedGameChoice) + " Again"
                    : "Choose Another Game",
                ) +
                "</button>"
              : "<p>The Host can choose another game from their phone.</p>") +
            "<p>Room <strong>" +
            escapeHtml(roomCode) +
            "</strong> stays open.</p></aside>"
          );
        }

        function loadTriviaMedia(snapshot) {
          var questionId = snapshot.questionId || null;
          if (questionId === triviaMediaQuestionId) return;
          triviaMediaQuestionId = questionId;
          triviaMedia = null;
          if (!questionId || !snapshot.mediaQuery) return;
          questionMediaFinder.find(snapshot.mediaQuery).then(function (media) {
            if (triviaMediaQuestionId !== questionId) return;
            triviaMedia = media;
            if (latestTriviaSnapshot && latestTriviaSnapshot.questionId === questionId) {
              renderTriviaSnapshot(latestTriviaSnapshot);
            }
          });
        }

        function triggerBannerAlert(event) {
          var container = byId("banner-overlay");
          if (!container) return;

          var el = document.createElement("div");
          el.className = "banner-alert";

          if (event.type === "playerDowned") {
            el.style.backgroundColor = "rgba(127, 29, 29, 0.9)";
            el.style.borderColor = "#ef4444";
            el.style.color = "#fca5a5";
            el.innerHTML = "🚨 " + escapeHtml(event.message).toUpperCase();
          } else if (event.type === "playerRevived") {
            el.style.backgroundColor = "rgba(20, 83, 45, 0.9)";
            el.style.borderColor = "#22c55e";
            el.style.color = "#bbf7d0";
            el.innerHTML = "💚 " + escapeHtml(event.message).toUpperCase();
          } else if (event.type === "playerRespawned") {
            el.style.backgroundColor = "rgba(14, 55, 120, 0.9)";
            el.style.borderColor = "#38bdf8";
            el.style.color = "#bae6fd";
            el.innerHTML = "🔵 " + escapeHtml(event.message).toUpperCase();
          } else if (event.type === "waveStarted") {
            el.style.backgroundColor = "rgba(12, 70, 60, 0.92)";
            el.style.borderColor = "#2dd4bf";
            el.style.color = "#99f6e4";
            el.style.fontSize = "38px";
            el.innerHTML = "🌊 " + escapeHtml(event.message).toUpperCase();
          } else if (event.type === "teamWiped") {
            el.style.backgroundColor = "rgba(90, 10, 10, 0.95)";
            el.style.borderColor = "#dc2626";
            el.style.color = "#fecaca";
            el.style.fontSize = "38px";
            el.innerHTML = "💀 " + escapeHtml(event.message).toUpperCase();
          } else if (event.type === "powerUpPicked") {
            el.style.backgroundColor = "rgba(20, 80, 20, 0.85)";
            el.style.borderColor = "#22c55e";
            el.style.color = "#bbf7d0";
            el.style.fontSize = "22px";
            el.style.padding = "10px 28px";
            el.innerHTML = "⚡ " + escapeHtml(event.message);
          } else if (event.type === "comboCallout") {
            el.style.backgroundColor = "rgba(80, 50, 10, 0.92)";
            el.style.borderColor = "#facc15";
            el.style.color = "#fef3c7";
            el.style.fontSize = "34px";
            el.innerHTML = "★ " + escapeHtml(event.message).toUpperCase();
          } else if (event.type === "streakCallout") {
            el.style.backgroundColor = "rgba(30, 64, 175, 0.92)";
            el.style.borderColor = "#93c5fd";
            el.style.color = "#dbeafe";
            el.style.fontSize = "34px";
            el.innerHTML = "STREAK: " + escapeHtml(event.message).toUpperCase();
          } else if (event.type === "chaosStarted") {
            el.style.backgroundColor = "rgba(88, 28, 135, 0.95)";
            el.style.borderColor = "#c084fc";
            el.style.color = "#f3e8ff";
            el.style.fontSize = "36px";
            el.innerHTML = "CHAOS: " + escapeHtml(event.message).toUpperCase();
          } else {
            return;
          }

          container.appendChild(el);
          var lifetime =
            event.type === "waveStarted" ||
            event.type === "teamWiped" ||
            event.type === "chaosStarted" ||
            event.type === "streakCallout"
              ? 3500
              : 2000;
          window.setTimeout(function () {
            el.remove();
          }, lifetime);
        }

        function renderRoomList(sessions, status) {
          var existingRoomInput = byId("room");
          if (existingRoomInput && existingRoomInput.value) {
            roomCode = normalizeRoomCode(existingRoomInput.value);
          }
          var html =
            '<section class="panel">' +
            "<h1>Watch Room</h1>" +
            '<label>Room Code <input id="room" maxlength="16" placeholder="LOCALTV" value="' +
            escapeAttribute(roomCode) +
            '" /></label>';
          if (!sessions.length) {
            html +=
              '<p class="status">' +
              escapeHtml(
                status || "No rooms are available. Create a room on a player device, then refresh.",
              ) +
              "</p>";
          } else {
            html += '<div class="room-list">';
            for (var i = 0; i < sessions.length; i += 1) {
              var session = sessions[i];
              var definition = gameDefinition(session.gameType);
              var variantTitle = "";
              if (session.variantId) {
                for (
                  var variantIndex = 0;
                  variantIndex < definition.variants.length;
                  variantIndex += 1
                ) {
                  if (definition.variants[variantIndex].id === session.variantId) {
                    variantTitle = definition.variants[variantIndex].title;
                    break;
                  }
                }
              }
              var sessionTitle =
                session.gameType === "tanks"
                  ? session.mode === "campaign"
                    ? "Campaign"
                    : "Survival"
                  : variantTitle || definition.title;
              html +=
                '<button class="room-row" type="button" onclick="handleJoinRowClick(\'' +
                escapeAttribute(session.roomCode) +
                "')\"><strong>" +
                escapeHtml(session.roomCode) +
                "</strong>" +
                escapeHtml(sessionTitle) +
                " (" +
                escapeHtml(String(session.playerCount)) +
                "/" +
                escapeHtml(String(session.maxPlayers)) +
                " players)</button>";
            }
            html += "</div>";
          }
          html +=
            '<button id="refresh-tv" type="button" onclick="handleRefreshClick()">Refresh</button>' +
            '<button id="watch-code-tv" type="button" onclick="handleWatchCodeClick()">Watch Code</button>' +
            '<button id="tv-setup" type="button" onclick="handleTvSetupClick()">Host a Game from This TV</button>' +
            '<a class="benchmark-link" href="./?tv=0">Use This Computer as a Controller</a>' +
            '<a class="benchmark-link" href="./tv-benchmark.html">Run TV Graphics Benchmark</a>' +
            "</section>";
          root.innerHTML = html;
        }

        function renderTankSnapshot(snapshot) {
          if (!snapshot) return;
          processTankEvents(snapshot);
          tankSnapshotRenderer.submit(snapshot);
        }

        function resetTankRenderer() {
          tankSnapshotRenderer.stop();
          lastTankHudAt = 0;
          previousProjectiles.clear();
          impactRings.length = 0;
          tankArenaLayer = null;
          tankArenaLayerKey = "";
        }

        function renderTankFrame(snapshot, now) {
          var activePlay = snapshot.team.state !== "gameOver" && snapshot.team.state !== "victory";
          latestSnapshot = snapshot;
          resizeCanvas();
          canvas.style.display = "block";
          drawSnapshot(snapshot);
          if (!activePlay || now - lastTankHudAt >= 100) {
            lastTankHudAt = now;
            root.innerHTML = hud(snapshot, 0, 0);
          }
        }

        function processTankEvents(snapshot) {
          var events = snapshot.events || [];
          for (var ev = 0; ev < events.length; ev += 1) {
            var event = events[ev];
            if (event.id <= lastEventId) continue;
            lastEventId = event.id;
            triggerBannerAlert(event);
            if (event.type === "enemyKilled") sfx.playKill();
            else if (event.type === "playerDowned") sfx.playPlayerDowned();
            else if (event.type === "playerRevived") sfx.playPlayerRevived();
            else if (event.type === "powerUpPicked") sfx.playPowerUp();
            else if (event.type === "waveStarted") sfx.playChime();
            else if (
              ["comboCallout", "streakCallout", "chaosStarted", "teamWiped"].includes(event.type)
            ) {
              sfx.playChime();
            }
          }
        }

        function tankScreenX(x) {
          return tankOffsetX + (tankArenaWidth - x) * tankScale;
        }

        function tankScreenY(z) {
          return tankOffsetY + z * tankScale;
        }

        function renderRacingSnapshot(snapshot) {
          if (!snapshot || snapshot.audience !== "public") return;
          processRacingAudio(snapshot);
          racingSnapshotRenderer.submit(snapshot);
        }

        function renderDungeonRelaySnapshot(snapshot) {
          if (!snapshot || snapshot.audience !== "public") return;
          latestDungeonSnapshot = snapshot;
          processDungeonAudio(snapshot);
          updateDungeonEffects(snapshot);
          dungeonSnapshotRenderer.submit(snapshot);
        }

        function resetDungeonRenderer() {
          dungeonSnapshotRenderer.stop();
          lastDungeonHudAt = 0;
          dungeonHudRenderKey = "";
          dungeonRoomLayer = null;
          dungeonRoomLayerKey = "";
          dungeonRoomLayerSnapshot = null;
          latestDungeonSnapshot = null;
          lastDungeonEventId = 0;
          lastDungeonAttackSoundAt = 0;
          dungeonEffects.length = 0;
          previousDungeonEnemies.clear();
          previousDungeonRifts.clear();
          previousDungeonHeroes.clear();
          previousDungeonPickups.clear();
        }

        function processDungeonAudio(snapshot) {
          var events = snapshot.events || [];
          for (var index = 0; index < events.length; index += 1) {
            var event = events[index];
            if (event.id <= lastDungeonEventId) continue;
            lastDungeonEventId = event.id;
            if (event.type === "attack") {
              var now = Date.now();
              if (now - lastDungeonAttackSoundAt >= 90) {
                lastDungeonAttackSoundAt = now;
                sfx.playDungeonAttack();
              }
            } else if (event.type === "ability") {
              sfx.playDungeonMagic();
              for (var heroIndex = 0; heroIndex < snapshot.heroes.length; heroIndex += 1) {
                var hero = snapshot.heroes[heroIndex];
                if (String(event.message).indexOf(hero.displayName) === 0) {
                  addDungeonEffect(
                    "magic",
                    hero.x,
                    hero.y,
                    dungeonRoleColor(hero.role, hero.color),
                  );
                  break;
                }
              }
            } else if (event.type === "riftDestroyed") sfx.playDungeonRift();
            else if (event.type === "heroDowned") sfx.playPlayerDowned();
            else if (event.type === "heroRevived") sfx.playPlayerRevived();
            else if (event.type === "pickup") {
              if (String(event.message).indexOf("gold") !== -1) sfx.playDungeonCoin();
              else sfx.playPowerUp();
            } else if (
              event.type === "relic" ||
              event.type === "upgrade" ||
              event.type === "gateUnlocked"
            )
              sfx.playChime();
          }
        }

        function addDungeonEffect(kind, x, y, color) {
          if (dungeonEffects.length >= 48) dungeonEffects.shift();
          dungeonEffects.push({
            kind: kind,
            x: x,
            y: y,
            color: color,
            age: 0,
            duration:
              kind === "rift" ? 700 : kind === "vanish" ? 650 : kind === "death" ? 500 : 360,
          });
        }

        function updateDungeonEffects(snapshot) {
          var currentEnemies = new Map();
          for (var enemyIndex = 0; enemyIndex < snapshot.enemies.length; enemyIndex += 1) {
            var enemy = snapshot.enemies[enemyIndex];
            var oldEnemy = previousDungeonEnemies.get(enemy.id);
            currentEnemies.set(enemy.id, { x: enemy.x, y: enemy.y, health: enemy.health });
            if (oldEnemy && enemy.health < oldEnemy.health)
              addDungeonEffect("hit", enemy.x, enemy.y, "#fef08a");
          }
          previousDungeonEnemies.forEach(function (enemy, id) {
            if (!currentEnemies.has(id)) addDungeonEffect("death", enemy.x, enemy.y, "#fb7185");
          });
          previousDungeonEnemies = currentEnemies;

          var currentRifts = new Map();
          for (var riftIndex = 0; riftIndex < snapshot.rifts.length; riftIndex += 1) {
            var rift = snapshot.rifts[riftIndex];
            var oldRift = previousDungeonRifts.get(rift.id);
            currentRifts.set(rift.id, { x: rift.x, y: rift.y, health: rift.health });
            if (oldRift && rift.health < oldRift.health)
              addDungeonEffect(rift.health <= 0 ? "rift" : "hit", rift.x, rift.y, "#e879f9");
          }
          previousDungeonRifts = currentRifts;

          var currentHeroes = new Map();
          for (var heroIndex = 0; heroIndex < snapshot.heroes.length; heroIndex += 1) {
            var hero = snapshot.heroes[heroIndex];
            var oldHero = previousDungeonHeroes.get(hero.playerId);
            currentHeroes.set(hero.playerId, {
              x: hero.x,
              y: hero.y,
              health: hero.health,
              reachedExit: hero.reachedExit,
            });
            if (oldHero && hero.health < oldHero.health)
              addDungeonEffect("hurt", hero.x, hero.y, "#ef4444");
            if (hero.reachedExit && oldHero && !oldHero.reachedExit)
              addDungeonEffect("vanish", hero.x, hero.y, dungeonRoleColor(hero.role, hero.color));
          }
          previousDungeonHeroes = currentHeroes;

          var currentPickups = new Map();
          for (
            var pickupIndex = 0;
            pickupIndex < (snapshot.pickups || []).length;
            pickupIndex += 1
          ) {
            var pickup = snapshot.pickups[pickupIndex];
            currentPickups.set(pickup.id, true);
            if (!previousDungeonPickups.has(pickup.id))
              addDungeonEffect("spawn", pickup.x, pickup.y, "#fde047");
          }
          previousDungeonPickups = currentPickups;
        }

        function renderDungeonFrame(snapshot, now) {
          latestSnapshot = null;
          if (canvas.style.display !== "block") canvas.style.display = "block";
          drawDungeonRelaySnapshot(snapshot);
          if (now - lastDungeonHudAt >= 100 || snapshot.phase === "completed") {
            lastDungeonHudAt = now;
            renderDungeonRelayHud(snapshot);
          }
        }

        function dungeonProjection(snapshot) {
          var ratio = window.devicePixelRatio || 1;
          var top = 76 * ratio;
          var margin = 26 * ratio;
          var scale = Math.min(
            (canvas.width - margin * 2) / snapshot.bounds.width,
            (canvas.height - top - margin) / snapshot.bounds.height,
          );
          return {
            scale: scale,
            x: function (value) {
              return margin + value * scale;
            },
            y: function (value) {
              return top + value * scale;
            },
          };
        }

        function dungeonRoleColor(role, fallback) {
          if (role === "vanguard") return "#22d3ee";
          if (role === "ranger") return "#f59e0b";
          if (role === "arcanist") return "#c084fc";
          if (role === "warden") return "#4ade80";
          return fallback || "#ffffff";
        }

        function dungeonShadeColor(hex, amount) {
          var normalized = String(hex || "#ffffff").replace("#", "");
          if (normalized.length === 3) {
            normalized = normalized
              .split("")
              .map(function (character) {
                return character + character;
              })
              .join("");
          }
          var value = parseInt(normalized, 16);
          if (Number.isNaN(value)) return hex;
          var r = (value >> 16) & 0xff;
          var g = (value >> 8) & 0xff;
          var b = value & 0xff;
          var mix = amount > 0 ? 255 : 0;
          var weight = Math.abs(amount);
          r = Math.round(r + (mix - r) * weight);
          g = Math.round(g + (mix - g) * weight);
          b = Math.round(b + (mix - b) * weight);
          return "rgb(" + r + "," + g + "," + b + ")";
        }

        function dungeonLighten(hex, amount) {
          return dungeonShadeColor(hex, Math.abs(amount));
        }

        function dungeonDarken(hex, amount) {
          return dungeonShadeColor(hex, -Math.abs(amount));
        }

        // Each Floor gets its own stonework/lighting so the 25 authored
        // Chambers read as five distinct dungeon zones instead of one room
        // repainted many times over. Chamber indices follow the fixed route
        // from DungeonRelayCoordinator's CHAMBERS array: 0-4 Outer Crypts,
        // 5-9 Sunken Halls, 10-14 Bone Archive, 15-19 Iron Depths, 20-24 The
        // Vault (the finale floor keeps the original palette).
        var DUNGEON_FLOOR_THEMES = [
          {
            name: "crypt",
            gradientTop: "#180705",
            gradientBottom: "#4a1f14",
            floorTint: "#3a1c14",
            floorLight: "rgba(251,146,60,0.22)",
            floorLightMid: "rgba(194,65,12,0.1)",
            floorLightEdge: "rgba(8,3,2,0.4)",
            tileA: "rgba(254,215,170,0.1)",
            runeColor: "rgba(251,191,36,0.2)",
            border: "#78482f",
            wallFill: "#7c5138",
            wallStroke: "#fdba74",
            wallHighlight: "rgba(254,215,170,0.42)",
            torchGlow: "rgba(251,146,60,0.6)",
            landmark: "tombstone",
          },
          {
            name: "mossy",
            gradientTop: "#04140d",
            gradientBottom: "#163824",
            floorTint: "#122a1c",
            floorLight: "rgba(74,222,128,0.16)",
            floorLightMid: "rgba(21,128,61,0.1)",
            floorLightEdge: "rgba(2,10,6,0.4)",
            tileA: "rgba(190,242,100,0.08)",
            runeColor: "rgba(163,230,53,0.18)",
            border: "#3f6b4f",
            wallFill: "#3f5c47",
            wallStroke: "#bef264",
            wallHighlight: "rgba(217,249,157,0.35)",
            torchGlow: "rgba(163,230,53,0.42)",
            landmark: "moss",
          },
          {
            name: "library",
            gradientTop: "#04101a",
            gradientBottom: "#153347",
            floorTint: "#132836",
            floorLight: "rgba(56,189,248,0.18)",
            floorLightMid: "rgba(14,116,144,0.1)",
            floorLightEdge: "rgba(2,7,12,0.4)",
            tileA: "rgba(186,230,253,0.09)",
            runeColor: "rgba(125,211,252,0.2)",
            border: "#3c6479",
            wallFill: "#3d5a68",
            wallStroke: "#7dd3fc",
            wallHighlight: "rgba(224,242,254,0.4)",
            torchGlow: "rgba(251,191,36,0.5)",
            landmark: "bookshelf",
          },
          {
            name: "catacombs",
            gradientTop: "#0d0d0f",
            gradientBottom: "#33333a",
            floorTint: "#26262c",
            floorLight: "rgba(214,211,209,0.16)",
            floorLightMid: "rgba(120,113,108,0.1)",
            floorLightEdge: "rgba(5,5,6,0.42)",
            tileA: "rgba(231,229,228,0.08)",
            runeColor: "rgba(214,211,209,0.18)",
            border: "#6b6b70",
            wallFill: "#5c5c62",
            wallStroke: "#d6d3d1",
            wallHighlight: "rgba(245,245,244,0.35)",
            torchGlow: "rgba(214,211,209,0.4)",
            landmark: "bones",
          },
          {
            name: "labyrinth",
            gradientTop: "#07020f",
            gradientBottom: "#2b1242",
            floorTint: "#241933",
            floorLight: "rgba(147,51,234,0.24)",
            floorLightMid: "rgba(76,29,149,0.1)",
            floorLightEdge: "rgba(3,1,8,0.38)",
            tileA: "rgba(216,180,254,0.1)",
            runeColor: "rgba(192,132,252,0.18)",
            border: "#786987",
            wallFill: "#715b83",
            wallStroke: "#d8b4fe",
            wallHighlight: "rgba(240,171,252,0.48)",
            torchGlow: "rgba(251,191,36,0.58)",
            landmark: "riftVeins",
          },
        ];

        function dungeonFloorTheme(chamberIndex) {
          if (chamberIndex <= 4) return DUNGEON_FLOOR_THEMES[0];
          if (chamberIndex <= 9) return DUNGEON_FLOOR_THEMES[1];
          if (chamberIndex <= 14) return DUNGEON_FLOOR_THEMES[2];
          if (chamberIndex <= 19) return DUNGEON_FLOOR_THEMES[3];
          return DUNGEON_FLOOR_THEMES[4];
        }

        function drawDungeonRelaySnapshot(snapshot) {
          var ctx = primaryCanvasContext;
          if (!ctx) return;
          var wallCount = String((snapshot.walls || []).length);
          if (canvas.getAttribute("data-dungeon-wall-count") !== wallCount)
            canvas.setAttribute("data-dungeon-wall-count", wallCount);
          if (canvas.getAttribute("data-dungeon-render-mode") !== "legends")
            canvas.setAttribute("data-dungeon-render-mode", "legends");
          if (
            canvas.getAttribute("data-dungeon-rich-features") !==
            "floor-lighting wall-depth hero-glow combat-effects"
          )
            canvas.setAttribute(
              "data-dungeon-rich-features",
              "floor-lighting wall-depth hero-glow combat-effects",
            );
          var projection = dungeonProjection(snapshot);
          var scale = projection.scale;
          var sx = projection.x;
          var sy = projection.y;
          if (!dungeonRoomLayer || dungeonRoomLayerSnapshot !== snapshot) {
            var wallSignature = "";
            for (
              var wallKeyIndex = 0;
              wallKeyIndex < (snapshot.walls || []).length;
              wallKeyIndex += 1
            ) {
              var keyedWall = snapshot.walls[wallKeyIndex];
              wallSignature +=
                ":" +
                keyedWall.id +
                "," +
                keyedWall.x +
                "," +
                keyedWall.y +
                "," +
                keyedWall.width +
                "," +
                keyedWall.height;
            }
            var layerKey =
              "legends:" +
              snapshot.chamberIndex +
              ":" +
              canvas.width +
              ":" +
              canvas.height +
              ":" +
              scale +
              wallSignature;
            dungeonRoomLayerSnapshot = snapshot;
            if (!dungeonRoomLayer || dungeonRoomLayerKey !== layerKey) {
              var theme = dungeonFloorTheme(snapshot.chamberIndex);
              var layer = document.createElement("canvas");
              canvas.setAttribute(
                "data-dungeon-rendered-wall-count",
                String((snapshot.walls || []).length),
              );
              layer.width = canvas.width;
              layer.height = canvas.height;
              var background = layer.getContext("2d");
              var gradient = background.createLinearGradient(0, 0, 0, layer.height);
              gradient.addColorStop(0, theme.gradientTop);
              gradient.addColorStop(1, theme.gradientBottom);
              background.fillStyle = gradient;
              background.fillRect(0, 0, layer.width, layer.height);
              background.fillStyle = theme.floorTint;
              background.fillRect(
                sx(0),
                sy(0),
                snapshot.bounds.width * scale,
                snapshot.bounds.height * scale,
              );
              var floorLight = background.createRadialGradient(
                sx(snapshot.bounds.width * 0.5),
                sy(snapshot.bounds.height * 0.42),
                scale,
                sx(snapshot.bounds.width * 0.5),
                sy(snapshot.bounds.height * 0.5),
                Math.max(snapshot.bounds.width, snapshot.bounds.height) * scale * 0.72,
              );
              floorLight.addColorStop(0, theme.floorLight);
              floorLight.addColorStop(0.55, theme.floorLightMid);
              floorLight.addColorStop(1, theme.floorLightEdge);
              background.fillStyle = floorLight;
              background.fillRect(
                sx(0),
                sy(0),
                snapshot.bounds.width * scale,
                snapshot.bounds.height * scale,
              );
              for (var floorX = 0; floorX < snapshot.bounds.width; floorX += 1) {
                for (var floorY = 0; floorY < snapshot.bounds.height; floorY += 1) {
                  if ((floorX + floorY + snapshot.chamberIndex) % 3 !== 0) continue;
                  background.fillStyle =
                    (floorX + floorY) % 2 === 0 ? theme.tileA : "rgba(0,0,0,0.12)";
                  background.fillRect(sx(floorX), sy(floorY), scale, scale);
                }
              }
              background.strokeStyle = theme.runeColor;
              background.lineWidth = Math.max(1, scale * 0.025);
              background.lineCap = "round";
              background.lineJoin = "round";
              for (
                var pathMarkerX = 2.6;
                pathMarkerX < snapshot.bounds.width - 1.4;
                pathMarkerX += 3.2
              ) {
                var markerCenterX = sx(pathMarkerX);
                var markerCenterY = sy(6.75);
                background.beginPath();
                background.moveTo(markerCenterX - scale * 0.16, markerCenterY - scale * 0.12);
                background.lineTo(markerCenterX, markerCenterY);
                background.lineTo(markerCenterX - scale * 0.16, markerCenterY + scale * 0.12);
                background.stroke();
              }
              background.strokeStyle = "rgba(255,255,255,0.055)";
              background.lineWidth = 1;
              for (var tileX = 0; tileX <= snapshot.bounds.width; tileX += 1) {
                background.beginPath();
                background.moveTo(sx(tileX), sy(0));
                background.lineTo(sx(tileX), sy(snapshot.bounds.height));
                background.stroke();
              }
              for (var tileY = 0; tileY <= snapshot.bounds.height; tileY += 1) {
                background.beginPath();
                background.moveTo(sx(0), sy(tileY));
                background.lineTo(sx(snapshot.bounds.width), sy(tileY));
                background.stroke();
              }
              background.strokeStyle = theme.border;
              background.lineWidth = Math.max(8, scale * 0.38);
              background.strokeRect(
                sx(0) + background.lineWidth / 2,
                sy(0) + background.lineWidth / 2,
                snapshot.bounds.width * scale - background.lineWidth,
                snapshot.bounds.height * scale - background.lineWidth,
              );
              for (var wallIndex = 0; wallIndex < (snapshot.walls || []).length; wallIndex += 1) {
                drawDungeonStoneWall(background, snapshot.walls[wallIndex], sx, sy, scale, theme);
              }
              for (var rune = 0; rune < 3; rune += 1) {
                drawDungeonFloorRune(
                  background,
                  sx(5.2 + rune * 6.4),
                  sy(rune % 2 === 0 ? 3.2 : 10.3),
                  scale,
                  snapshot.chamberIndex + rune,
                  theme,
                );
              }
              for (var torch = 3; torch < snapshot.bounds.width; torch += 6) {
                drawDungeonTorch(background, sx(torch), sy(0.7), scale, theme);
              }
              drawDungeonFloorLandmark(background, theme, sx, sy, scale, snapshot);
              dungeonRoomLayer = layer;
              dungeonRoomLayerKey = layerKey;
            }
          }
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.drawImage(dungeonRoomLayer, 0, 0);

          ctx.save();
          ctx.translate(sx(snapshot.exit.x), sy(snapshot.exit.y));
          ctx.fillStyle = snapshot.exitOpen ? "rgba(34,197,94,0.3)" : "rgba(30,41,59,0.88)";
          ctx.strokeStyle = snapshot.exitOpen ? "#86efac" : "#94a3b8";
          ctx.lineWidth = Math.max(3, scale * 0.09);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.56, scale * 0.62);
          ctx.lineTo(-scale * 0.56, -scale * 0.05);
          ctx.arc(0, -scale * 0.05, scale * 0.56, Math.PI, 0);
          ctx.lineTo(scale * 0.56, scale * 0.62);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          if (!snapshot.exitOpen) {
            ctx.strokeStyle = "#cbd5e1";
            ctx.lineWidth = Math.max(2, scale * 0.06);
            ctx.beginPath();
            ctx.moveTo(-scale * 0.28, -scale * 0.35);
            ctx.lineTo(scale * 0.28, scale * 0.35);
            ctx.moveTo(scale * 0.28, -scale * 0.35);
            ctx.lineTo(-scale * 0.28, scale * 0.35);
            ctx.stroke();
          }
          ctx.fillStyle = snapshot.exitOpen ? "#bbf7d0" : "#cbd5e1";
          ctx.font = "bold " + Math.max(10, scale * 0.22) + "px Arial";
          ctx.textAlign = "center";
          ctx.fillText(snapshot.exitOpen ? "EXIT OPEN" : "EXIT LOCKED", 0, scale * 1.02);
          ctx.restore();

          for (var gateIndex = 0; gateIndex < (snapshot.gates || []).length; gateIndex += 1) {
            drawDungeonGate(ctx, snapshot.gates[gateIndex], sx, sy, scale, snapshot.serverTick);
          }

          for (
            var pickupIndex = 0;
            pickupIndex < (snapshot.pickups || []).length;
            pickupIndex += 1
          ) {
            var pickup = snapshot.pickups[pickupIndex];
            drawDungeonPickup(ctx, pickup, sx(pickup.x), sy(pickup.y), scale, snapshot.serverTick);
          }

          for (var riftIndex = 0; riftIndex < snapshot.rifts.length; riftIndex += 1) {
            var rift = snapshot.rifts[riftIndex];
            if (rift.health <= 0) continue;
            ctx.save();
            ctx.translate(sx(rift.x), sy(rift.y));
            ctx.rotate((snapshot.serverTick % 180) * 0.035);
            for (var ring = 0; ring < 3; ring += 1) {
              ctx.strokeStyle = ring === 0 ? "#f0abfc" : ring === 1 ? "#a855f7" : "#4c1d95";
              ctx.lineWidth = Math.max(3, scale * (0.13 - ring * 0.025));
              ctx.beginPath();
              ctx.arc(0, 0, scale * (0.42 + ring * 0.18), ring * 0.8, Math.PI * 1.6 + ring);
              ctx.stroke();
            }
            ctx.restore();
            drawDungeonHealthBar(
              ctx,
              sx(rift.x),
              sy(rift.y) - scale * 0.9,
              rift.health,
              rift.maxHealth,
              scale * 1.3,
              "#d946ef",
            );
          }

          for (var healerIndex = 0; healerIndex < snapshot.enemies.length; healerIndex += 1) {
            var healerEnemy = snapshot.enemies[healerIndex];
            if (healerEnemy.kind !== "healer" || !healerEnemy.healingTargetId) continue;
            var healTarget = null;
            for (var targetIndex = 0; targetIndex < snapshot.enemies.length; targetIndex += 1) {
              if (snapshot.enemies[targetIndex].id === healerEnemy.healingTargetId) {
                healTarget = snapshot.enemies[targetIndex];
                break;
              }
            }
            if (!healTarget) continue;
            drawDungeonHealBeam(
              ctx,
              sx(healerEnemy.x),
              sy(healerEnemy.y),
              sx(healTarget.x),
              sy(healTarget.y),
              scale,
              snapshot.serverTick,
            );
          }

          for (var enemyIndex = 0; enemyIndex < snapshot.enemies.length; enemyIndex += 1) {
            var enemy = snapshot.enemies[enemyIndex];
            drawDungeonEnemy(ctx, enemy, sx(enemy.x), sy(enemy.y), scale, snapshot.serverTick);
            drawDungeonHealthBar(
              ctx,
              sx(enemy.x),
              sy(enemy.y) - scale * 0.62,
              enemy.health,
              enemy.maxHealth,
              scale * 0.82,
              "#fb7185",
            );
          }

          if (snapshot.boss) {
            var boss = snapshot.boss;
            drawDungeonBoss(ctx, boss, sx(boss.x), sy(boss.y), scale, snapshot.serverTick);
            drawDungeonHealthBar(
              ctx,
              sx(boss.x),
              sy(boss.y) - scale * 1.35,
              boss.health,
              boss.maxHealth,
              scale * 2.4,
              "#ef4444",
            );
          }

          for (
            var projectileIndex = 0;
            projectileIndex < snapshot.projectiles.length;
            projectileIndex += 1
          ) {
            var projectile = snapshot.projectiles[projectileIndex];
            if (projectile.kind === "arrow") {
              drawDungeonArrow(
                ctx,
                sx(projectile.x),
                sy(projectile.y),
                projectile.angle || 0,
                scale,
              );
              continue;
            }
            if (projectile.kind === "bolt") {
              drawDungeonBolt(ctx, sx(projectile.x), sy(projectile.y), scale, snapshot.serverTick);
              continue;
            }
            var projectileColor = dungeonRoleColor(projectile.role, "#ffffff");
            ctx.globalAlpha = 0.18;
            ctx.fillStyle = projectileColor;
            ctx.beginPath();
            ctx.arc(sx(projectile.x), sy(projectile.y), Math.max(8, scale * 0.28), 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 0.5;
            ctx.beginPath();
            ctx.arc(sx(projectile.x), sy(projectile.y), Math.max(5, scale * 0.18), 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;
            ctx.fillStyle = "#fff7ed";
            ctx.beginPath();
            ctx.arc(sx(projectile.x), sy(projectile.y), Math.max(3, scale * 0.1), 0, Math.PI * 2);
            ctx.fill();
          }

          drawDungeonEffects(ctx, sx, sy, scale);

          for (var heroIndex = 0; heroIndex < snapshot.heroes.length; heroIndex += 1) {
            if (snapshot.heroes[heroIndex].reachedExit) continue;
            drawDungeonHero(ctx, snapshot.heroes[heroIndex], sx, sy, scale);
          }
        }

        function drawDungeonFloorRune(ctx, x, y, scale, seed, theme) {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate((seed % 8) * 0.38);
          ctx.strokeStyle = theme ? theme.runeColor : "rgba(192,132,252,0.18)";
          ctx.lineWidth = Math.max(1, scale * 0.035);
          ctx.beginPath();
          ctx.arc(0, 0, scale * 0.52, 0, Math.PI * 2);
          ctx.moveTo(-scale * 0.42, scale * 0.22);
          ctx.lineTo(0, -scale * 0.45);
          ctx.lineTo(scale * 0.42, scale * 0.22);
          ctx.closePath();
          ctx.moveTo(-scale * 0.34, -scale * 0.18);
          ctx.lineTo(scale * 0.34, -scale * 0.18);
          ctx.lineTo(0, scale * 0.43);
          ctx.closePath();
          ctx.stroke();
          ctx.restore();
        }

        // One small recurring set-dressing piece per Floor theme, baked into
        // the same cached background layer as the walls/torches above — this
        // costs nothing per frame since it is drawn once per Chamber, not
        // once per tick.
        function drawDungeonFloorLandmark(ctx, theme, sx, sy, scale, snapshot) {
          var spots = [
            { x: snapshot.bounds.width * 0.14, y: snapshot.bounds.height * 0.84 },
            { x: snapshot.bounds.width * 0.86, y: snapshot.bounds.height * 0.16 },
          ];
          for (var spotIndex = 0; spotIndex < spots.length; spotIndex += 1) {
            var px = sx(spots[spotIndex].x);
            var py = sy(spots[spotIndex].y);
            if (theme.landmark === "tombstone") drawDungeonLandmarkTombstone(ctx, px, py, scale);
            else if (theme.landmark === "moss") drawDungeonLandmarkMoss(ctx, px, py, scale);
            else if (theme.landmark === "bookshelf")
              drawDungeonLandmarkBookshelf(ctx, px, py, scale);
            else if (theme.landmark === "bones") drawDungeonLandmarkBones(ctx, px, py, scale);
            else
              drawDungeonLandmarkRiftVeins(
                ctx,
                px,
                py,
                scale,
                snapshot.chamberIndex,
                (snapshot.boss ? 1 : 0) + spotIndex,
              );
          }
        }

        function drawDungeonLandmarkTombstone(ctx, x, y, scale) {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.42, scale * 0.5, scale * 0.14, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#57402f";
          ctx.strokeStyle = "#a8785a";
          ctx.lineWidth = Math.max(1, scale * 0.04);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.32, scale * 0.4);
          ctx.lineTo(-scale * 0.32, -scale * 0.12);
          ctx.arc(0, -scale * 0.12, scale * 0.32, Math.PI, 0);
          ctx.lineTo(scale * 0.32, scale * 0.4);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.strokeStyle = "rgba(20,10,5,0.5)";
          ctx.lineWidth = Math.max(1, scale * 0.03);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.1, -scale * 0.3);
          ctx.lineTo(scale * 0.06, scale * 0.05);
          ctx.lineTo(-scale * 0.04, scale * 0.32);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonLandmarkMoss(ctx, x, y, scale) {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = "rgba(0,0,0,0.3)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.3, scale * 0.55, scale * 0.16, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "rgba(101,163,13,0.55)";
          for (var patch = 0; patch < 4; patch += 1) {
            var patchAngle = (patch / 4) * Math.PI * 2;
            ctx.beginPath();
            ctx.ellipse(
              Math.cos(patchAngle) * scale * 0.3,
              scale * 0.24 + Math.sin(patchAngle) * scale * 0.08,
              scale * 0.22,
              scale * 0.13,
              patchAngle,
              0,
              Math.PI * 2,
            );
            ctx.fill();
          }
          ctx.strokeStyle = "rgba(125,211,252,0.35)";
          ctx.lineWidth = Math.max(1, scale * 0.03);
          ctx.beginPath();
          ctx.moveTo(0, -scale * 0.1);
          ctx.lineTo(0, scale * 0.18);
          ctx.stroke();
          ctx.fillStyle = "rgba(125,211,252,0.5)";
          ctx.beginPath();
          ctx.arc(0, scale * 0.2, scale * 0.045, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonLandmarkBookshelf(ctx, x, y, scale) {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.4, scale * 0.55, scale * 0.14, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#3f2d1c";
          ctx.strokeStyle = "#7c5a37";
          ctx.lineWidth = Math.max(1, scale * 0.035);
          ctx.fillRect(-scale * 0.42, -scale * 0.42, scale * 0.84, scale * 0.78);
          ctx.strokeRect(-scale * 0.42, -scale * 0.42, scale * 0.84, scale * 0.78);
          var spineColors = ["#b45309", "#7f1d1d", "#166534", "#7dd3fc", "#a16207"];
          for (var book = 0; book < 5; book += 1) {
            ctx.fillStyle = spineColors[book % spineColors.length];
            ctx.fillRect(
              -scale * 0.36 + book * scale * 0.16,
              -scale * 0.34,
              scale * 0.12,
              scale * 0.6,
            );
          }
          ctx.fillStyle = "#fef3c7";
          ctx.beginPath();
          ctx.ellipse(scale * 0.28, scale * 0.28, scale * 0.16, scale * 0.08, -0.3, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonLandmarkBones(ctx, x, y, scale) {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = "rgba(0,0,0,0.32)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.24, scale * 0.5, scale * 0.15, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#e7e5e4";
          ctx.lineWidth = Math.max(2, scale * 0.07);
          ctx.lineCap = "round";
          for (var bone = 0; bone < 3; bone += 1) {
            var boneAngle = -0.6 + bone * 0.6;
            ctx.beginPath();
            ctx.moveTo(Math.cos(boneAngle) * -scale * 0.34, scale * 0.16 + bone * scale * 0.02);
            ctx.lineTo(Math.cos(boneAngle) * scale * 0.34, scale * 0.1 + bone * scale * 0.02);
            ctx.stroke();
          }
          ctx.fillStyle = "#d6d3d1";
          ctx.strokeStyle = "#78716c";
          ctx.lineWidth = Math.max(1, scale * 0.03);
          ctx.beginPath();
          ctx.arc(0, -scale * 0.1, scale * 0.22, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#1c1917";
          ctx.beginPath();
          ctx.arc(-scale * 0.08, -scale * 0.13, scale * 0.05, 0, Math.PI * 2);
          ctx.arc(scale * 0.08, -scale * 0.13, scale * 0.05, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonLandmarkRiftVeins(ctx, x, y, scale, chamberIndex, intensity) {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(chamberIndex * 0.6);
          ctx.strokeStyle = intensity > 0 ? "rgba(232,121,249,0.5)" : "rgba(192,132,252,0.3)";
          ctx.lineWidth = Math.max(1, scale * 0.045);
          ctx.lineCap = "round";
          for (var vein = 0; vein < 3; vein += 1) {
            var veinAngle = (vein / 3) * Math.PI * 2;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(Math.cos(veinAngle) * scale * 0.3, Math.sin(veinAngle) * scale * 0.2);
            ctx.lineTo(
              Math.cos(veinAngle + 0.3) * scale * 0.55,
              Math.sin(veinAngle + 0.3) * scale * 0.32,
            );
            ctx.stroke();
          }
          ctx.fillStyle = intensity > 0 ? "rgba(232,121,249,0.4)" : "rgba(192,132,252,0.22)";
          ctx.beginPath();
          ctx.arc(0, 0, scale * 0.1, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonEffects(ctx, sx, sy, scale) {
          for (var index = dungeonEffects.length - 1; index >= 0; index -= 1) {
            var effect = dungeonEffects[index];
            effect.age += 33;
            if (effect.age >= effect.duration) {
              dungeonEffects.splice(index, 1);
              continue;
            }
            var progress = effect.age / effect.duration;
            var x = sx(effect.x);
            var y = sy(effect.y);
            ctx.save();
            ctx.globalAlpha = 1 - progress;
            ctx.strokeStyle = effect.color;
            ctx.fillStyle = effect.color;
            ctx.lineWidth = Math.max(2, scale * 0.065 * (1 - progress * 0.4));
            if (effect.kind === "rift" || effect.kind === "death") {
              var ringRadius = scale * (0.18 + progress * (effect.kind === "rift" ? 1.3 : 0.75));
              ctx.beginPath();
              ctx.arc(x, y, ringRadius, 0, Math.PI * 2);
              ctx.stroke();
              if (effect.kind === "rift") {
                ctx.beginPath();
                ctx.arc(x, y, ringRadius * 0.62, 0, Math.PI * 2);
                ctx.stroke();
              }
            } else if (effect.kind === "vanish") {
              ctx.translate(x, y);
              var vanishRays = 10;
              var vanishOuter = scale * 0.85 * (1 - progress);
              var vanishInner = scale * 0.12 * (1 - progress);
              for (var vanishRay = 0; vanishRay < vanishRays; vanishRay += 1) {
                var vanishAngle = (vanishRay / vanishRays) * Math.PI * 2 + progress * 2;
                ctx.beginPath();
                ctx.moveTo(
                  Math.cos(vanishAngle) * vanishOuter,
                  Math.sin(vanishAngle) * vanishOuter,
                );
                ctx.lineTo(
                  Math.cos(vanishAngle) * vanishInner,
                  Math.sin(vanishAngle) * vanishInner,
                );
                ctx.stroke();
              }
              ctx.beginPath();
              ctx.arc(0, 0, scale * 0.55 * (1 - progress), 0, Math.PI * 2);
              ctx.stroke();
            } else if (effect.kind === "spawn") {
              ctx.translate(x, y);
              ctx.rotate(progress * Math.PI);
              for (var ray = 0; ray < 6; ray += 1) {
                ctx.rotate(Math.PI / 3);
                ctx.beginPath();
                ctx.moveTo(scale * 0.24, 0);
                ctx.lineTo(scale * (0.35 + progress * 0.35), 0);
                ctx.stroke();
              }
            } else {
              var rays = effect.kind === "hurt" ? 8 : 6;
              for (var spark = 0; spark < rays; spark += 1) {
                var angle = (spark / rays) * Math.PI * 2 + progress;
                var inner = scale * (0.12 + progress * 0.18);
                var outer = scale * (0.28 + progress * 0.5);
                ctx.beginPath();
                ctx.moveTo(x + Math.cos(angle) * inner, y + Math.sin(angle) * inner);
                ctx.lineTo(x + Math.cos(angle) * outer, y + Math.sin(angle) * outer);
                ctx.stroke();
              }
            }
            ctx.restore();
          }
        }

        function drawDungeonStoneWall(ctx, wall, sx, sy, scale, theme) {
          var x = sx(wall.x);
          var y = sy(wall.y);
          var width = wall.width * scale;
          var height = wall.height * scale;
          ctx.fillStyle = "rgba(0,0,0,0.7)";
          ctx.fillRect(x + scale * 0.2, y + scale * 0.23, width, height);
          ctx.fillStyle = theme ? theme.wallFill : "#715b83";
          ctx.strokeStyle = theme ? theme.wallStroke : "#d8b4fe";
          ctx.lineWidth = Math.max(2, scale * 0.06);
          ctx.fillRect(x, y, width, height);
          ctx.strokeRect(x, y, width, height);
          ctx.fillStyle = theme ? theme.wallHighlight : "rgba(240,171,252,0.48)";
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + scale * 0.14, y + scale * 0.14);
          ctx.lineTo(x + width - scale * 0.14, y + scale * 0.14);
          ctx.lineTo(x + width, y);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = "rgba(12,6,20,0.68)";
          ctx.beginPath();
          ctx.moveTo(x + width, y);
          ctx.lineTo(x + width - scale * 0.14, y + scale * 0.14);
          ctx.lineTo(x + width - scale * 0.14, y + height - scale * 0.14);
          ctx.lineTo(x + width, y + height);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = "rgba(30,20,38,0.55)";
          ctx.lineWidth = Math.max(1, scale * 0.025);
          var block = Math.max(scale * 0.55, 14);
          for (var rowY = y + block; rowY < y + height; rowY += block) {
            ctx.beginPath();
            ctx.moveTo(x, rowY);
            ctx.lineTo(x + width, rowY);
            ctx.stroke();
          }
          for (var columnX = x + block; columnX < x + width; columnX += block) {
            ctx.beginPath();
            ctx.moveTo(columnX, y);
            ctx.lineTo(columnX, y + height);
            ctx.stroke();
          }
        }

        function drawDungeonGate(ctx, gate, sx, sy, scale, tick) {
          var x = sx(gate.x);
          var y = sy(gate.y);
          var width = gate.width * scale;
          var height = gate.height * scale;
          ctx.save();
          if (gate.locked) {
            ctx.fillStyle = "rgba(20,14,8,0.6)";
            ctx.fillRect(x, y, width, height);
            ctx.strokeStyle = "#78716c";
            ctx.lineWidth = Math.max(2, scale * 0.05);
            ctx.strokeRect(x, y, width, height);
            ctx.strokeStyle = "#d6d3d1";
            ctx.lineWidth = Math.max(1.5, scale * 0.035);
            for (var bar = 1; bar < 4; bar += 1) {
              var barX = x + (width * bar) / 4;
              ctx.beginPath();
              ctx.moveTo(barX, y);
              ctx.lineTo(barX, y + height);
              ctx.stroke();
            }
            var lockPulse = 0.75 + Math.sin((tick || 0) * 0.15) * 0.25;
            ctx.save();
            ctx.globalAlpha = lockPulse;
            ctx.fillStyle = "#facc15";
            ctx.beginPath();
            ctx.arc(x + width / 2, y + height / 2, Math.max(3, scale * 0.13), 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = "#78350f";
            ctx.lineWidth = Math.max(1, scale * 0.03);
            ctx.stroke();
            ctx.restore();
          } else {
            ctx.globalAlpha = 0.3;
            ctx.strokeStyle = "#a8a29e";
            ctx.lineWidth = Math.max(1, scale * 0.03);
            ctx.setLineDash([Math.max(2, scale * 0.08), Math.max(2, scale * 0.08)]);
            ctx.strokeRect(x, y, width, height);
          }
          ctx.restore();
        }

        function drawDungeonTorch(ctx, x, y, scale, theme) {
          ctx.save();
          var torchGlowRadius = scale * 0.95;
          var glow = ctx.createRadialGradient(x, y, 0, x, y, torchGlowRadius);
          glow.addColorStop(0, theme ? theme.torchGlow : "rgba(251,191,36,0.58)");
          glow.addColorStop(1, "rgba(251,191,36,0)");
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(x, y, torchGlowRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#713f12";
          ctx.lineWidth = Math.max(3, scale * 0.08);
          ctx.beginPath();
          ctx.moveTo(x, y + scale * 0.12);
          ctx.lineTo(x, y + scale * 0.42);
          ctx.stroke();
          ctx.fillStyle = "#f97316";
          ctx.beginPath();
          ctx.moveTo(x, y - scale * 0.3);
          ctx.quadraticCurveTo(x + scale * 0.26, y, x, y + scale * 0.18);
          ctx.quadraticCurveTo(x - scale * 0.24, y, x, y - scale * 0.3);
          ctx.fill();
          ctx.fillStyle = "#fef08a";
          ctx.beginPath();
          ctx.arc(x, y - scale * 0.01, Math.max(2, scale * 0.09), 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonEnemy(ctx, enemy, x, y, scale, tick) {
          if (enemy.kind === "skeleton") drawDungeonSkeleton(ctx, x, y, scale, tick);
          else if (enemy.kind === "slime") drawDungeonSlime(ctx, x, y, scale, tick);
          else if (enemy.kind === "bat") drawDungeonBat(ctx, x, y, scale, tick);
          else if (enemy.kind === "archer") drawDungeonArcher(ctx, x, y, scale, tick);
          else if (enemy.kind === "warrior") drawDungeonWarrior(ctx, x, y, scale, tick);
          else if (enemy.kind === "wizard") drawDungeonWizard(ctx, x, y, scale, tick);
          else if (enemy.kind === "healer") drawDungeonHealer(ctx, x, y, scale, tick);
          else drawDungeonSkitter(ctx, x, y, scale, tick);
        }

        function drawDungeonHealBeam(ctx, fromX, fromY, toX, toY, scale, tick) {
          var pulse = 0.55 + Math.sin((tick || 0) * 0.25) * 0.35;
          ctx.save();
          ctx.globalAlpha = pulse;
          ctx.strokeStyle = "#34d399";
          ctx.lineWidth = Math.max(2, scale * 0.06);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(fromX, fromY);
          ctx.lineTo(toX, toY);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonHealer(ctx, x, y, scale, tick) {
          var bob = Math.sin((tick || 0) * 0.11 + x) * scale * 0.03;
          var aura = 0.35 + Math.sin((tick || 0) * 0.18 + x) * 0.2;
          ctx.save();
          ctx.translate(x, y + bob);
          ctx.fillStyle = "rgba(0,0,0,0.4)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.44, scale * 0.44, scale * 0.16, 0, 0, Math.PI * 2);
          ctx.fill();

          ctx.save();
          ctx.globalAlpha = aura;
          ctx.strokeStyle = "#6ee7b7";
          ctx.lineWidth = Math.max(2, scale * 0.05);
          ctx.beginPath();
          ctx.arc(0, scale * 0.05, scale * 0.56, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();

          ctx.fillStyle = "#ecfdf5";
          ctx.strokeStyle = "#34d399";
          ctx.lineWidth = Math.max(2, scale * 0.05);
          ctx.beginPath();
          ctx.moveTo(0, -scale * 0.24);
          ctx.lineTo(-scale * 0.3, scale * 0.42);
          ctx.quadraticCurveTo(0, scale * 0.52, scale * 0.3, scale * 0.42);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = "#d1fae5";
          ctx.strokeStyle = "#6ee7b7";
          ctx.lineWidth = Math.max(1.5, scale * 0.04);
          ctx.beginPath();
          ctx.arc(0, -scale * 0.34, scale * 0.19, Math.PI * 0.05, Math.PI * 0.95, true);
          ctx.lineTo(-scale * 0.16, -scale * 0.14);
          ctx.lineTo(scale * 0.16, -scale * 0.14);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          ctx.save();
          ctx.globalAlpha = 0.7 + Math.sin((tick || 0) * 0.3) * 0.3;
          ctx.strokeStyle = "#059669";
          ctx.lineWidth = Math.max(1.5, scale * 0.05);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(0, scale * 0.02);
          ctx.lineTo(0, scale * 0.26);
          ctx.moveTo(-scale * 0.11, scale * 0.14);
          ctx.lineTo(scale * 0.11, scale * 0.14);
          ctx.stroke();
          ctx.restore();
          ctx.restore();
        }

        function drawDungeonBoss(ctx, boss, x, y, scale, tick) {
          ctx.save();
          ctx.translate(x, y);
          var bossPulse = 1 + Math.sin((tick || 0) * 0.08) * 0.035;
          ctx.scale(bossPulse, bossPulse);
          if (boss.kind === "bone-sentinel") drawDungeonBossBoneSentinel(ctx, scale, tick);
          else if (boss.kind === "marsh-stalker") drawDungeonBossMarshStalker(ctx, scale, tick);
          else if (boss.kind === "shadow-archon") drawDungeonBossShadowArchon(ctx, scale, tick);
          else if (boss.kind === "chained-colossus")
            drawDungeonBossChainedColossus(ctx, scale, tick);
          else drawDungeonBossVaultWarden(ctx, scale, tick);
          ctx.restore();
        }

        function drawDungeonBossVaultWarden(ctx, scale, tick) {
          ctx.fillStyle = "rgba(0,0,0,0.48)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.68, scale * 1.05, scale * 0.32, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#7f1d1d";
          ctx.strokeStyle = "#fca5a5";
          ctx.lineWidth = Math.max(4, scale * 0.12);
          ctx.beginPath();
          for (var spike = 0; spike < 12; spike += 1) {
            var angle = (spike / 12) * Math.PI * 2;
            var radius = scale * (spike % 2 === 0 ? 1.05 : 0.72);
            var px = Math.cos(angle) * radius;
            var py = Math.sin(angle) * radius;
            if (spike === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#29213a";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.2, scale * 0.66, scale * 0.83, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#a16207";
          ctx.beginPath();
          ctx.moveTo(-scale * 0.55, -scale * 0.35);
          ctx.lineTo(-scale * 0.85, -scale * 0.82);
          ctx.lineTo(-scale * 0.25, -scale * 0.58);
          ctx.moveTo(scale * 0.55, -scale * 0.35);
          ctx.lineTo(scale * 0.85, -scale * 0.82);
          ctx.lineTo(scale * 0.25, -scale * 0.58);
          ctx.fill();
          ctx.fillStyle = "#fef08a";
          ctx.beginPath();
          ctx.arc(-scale * 0.22, -scale * 0.15, scale * 0.075, 0, Math.PI * 2);
          ctx.arc(scale * 0.22, -scale * 0.15, scale * 0.075, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#d6d3d1";
          ctx.lineWidth = Math.max(4, scale * 0.11);
          ctx.beginPath();
          ctx.moveTo(scale * 0.58, scale * 0.25);
          ctx.lineTo(scale * 1.05, -scale * 0.62);
          ctx.stroke();
          ctx.fillStyle = "#78716c";
          ctx.beginPath();
          ctx.moveTo(scale * 0.87, -scale * 0.72);
          ctx.lineTo(scale * 1.32, -scale * 0.9);
          ctx.lineTo(scale * 1.14, -scale * 0.42);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        }

        function drawDungeonBossBoneSentinel(ctx, scale, tick) {
          ctx.fillStyle = "rgba(0,0,0,0.46)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.66, scale * 0.92, scale * 0.3, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#78350f";
          ctx.strokeStyle = "#d97706";
          ctx.lineWidth = Math.max(3, scale * 0.09);
          ctx.beginPath();
          ctx.ellipse(-scale * 0.72, scale * 0.05, scale * 0.32, scale * 0.52, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#e7e5e4";
          ctx.strokeStyle = "#a8a29e";
          ctx.lineWidth = Math.max(3, scale * 0.1);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.55, scale * 0.72);
          ctx.lineTo(-scale * 0.62, -scale * 0.22);
          ctx.quadraticCurveTo(0, -scale * 0.5, scale * 0.62, -scale * 0.22);
          ctx.lineTo(scale * 0.55, scale * 0.72);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#d6d3d1";
          ctx.strokeStyle = "#78716c";
          ctx.lineWidth = Math.max(2.5, scale * 0.07);
          ctx.beginPath();
          ctx.arc(0, -scale * 0.62, scale * 0.4, Math.PI * 0.95, Math.PI * 0.05, true);
          ctx.lineTo(scale * 0.34, -scale * 0.42);
          ctx.lineTo(-scale * 0.34, -scale * 0.42);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#1c1917";
          ctx.fillRect(-scale * 0.045, -scale * 0.62, scale * 0.09, scale * 0.24);
          var glow = 0.65 + Math.sin((tick || 0) * 0.12) * 0.3;
          ctx.save();
          ctx.globalAlpha = glow;
          ctx.fillStyle = "#fde047";
          ctx.beginPath();
          ctx.arc(-scale * 0.15, -scale * 0.5, scale * 0.055, 0, Math.PI * 2);
          ctx.arc(scale * 0.15, -scale * 0.5, scale * 0.055, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          ctx.strokeStyle = "#e7e5e4";
          ctx.lineWidth = Math.max(4, scale * 0.13);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(scale * 0.6, scale * 0.3);
          ctx.lineTo(scale * 1.15, -scale * 0.72);
          ctx.stroke();
          ctx.fillStyle = "#57534e";
          ctx.fillRect(scale * 0.55, scale * 0.14, scale * 0.16, scale * 0.28);
        }

        function drawDungeonBossMarshStalker(ctx, scale, tick) {
          var wobble = Math.sin((tick || 0) * 0.1) * scale * 0.05;
          ctx.fillStyle = "rgba(0,0,0,0.44)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.62, scale * 0.95, scale * 0.3, 0, 0, Math.PI * 2);
          ctx.fill();
          for (var tendril = 0; tendril < 4; tendril += 1) {
            var tAngle = (tendril / 4) * Math.PI * 2 + (tick || 0) * 0.03;
            ctx.strokeStyle = "#3f6212";
            ctx.lineWidth = Math.max(3, scale * 0.08);
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(Math.cos(tAngle) * scale * 0.4, Math.sin(tAngle) * scale * 0.3);
            ctx.lineTo(Math.cos(tAngle) * scale * 0.95, Math.sin(tAngle) * scale * 0.7 + wobble);
            ctx.stroke();
          }
          ctx.fillStyle = "#365314";
          ctx.strokeStyle = "#84cc16";
          ctx.lineWidth = Math.max(3, scale * 0.09);
          ctx.beginPath();
          ctx.ellipse(0, wobble * 0.3, scale * 0.72, scale * 0.62, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#4d7c0f";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.15 + wobble * 0.2, scale * 0.5, scale * 0.4, 0, 0, Math.PI * 2);
          ctx.fill();
          var eyeGlow = 0.6 + Math.sin((tick || 0) * 0.2) * 0.35;
          ctx.save();
          ctx.globalAlpha = eyeGlow;
          ctx.fillStyle = "#bef264";
          ctx.beginPath();
          ctx.arc(-scale * 0.22, -scale * 0.1, scale * 0.09, 0, Math.PI * 2);
          ctx.arc(scale * 0.22, -scale * 0.1, scale * 0.09, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          ctx.fillStyle = "#1a2e05";
          ctx.beginPath();
          ctx.arc(-scale * 0.22, -scale * 0.1, scale * 0.04, 0, Math.PI * 2);
          ctx.arc(scale * 0.22, -scale * 0.1, scale * 0.04, 0, Math.PI * 2);
          ctx.fill();
        }

        function drawDungeonBossShadowArchon(ctx, scale, tick) {
          var glow = 0.6 + Math.sin((tick || 0) * 0.18) * 0.4;
          ctx.fillStyle = "rgba(0,0,0,0.44)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.64, scale * 0.8, scale * 0.26, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#312e81";
          ctx.strokeStyle = "#a78bfa";
          ctx.lineWidth = Math.max(3, scale * 0.08);
          ctx.beginPath();
          ctx.moveTo(0, -scale * 0.55);
          ctx.lineTo(-scale * 0.68, scale * 0.62);
          ctx.quadraticCurveTo(0, scale * 0.82, scale * 0.68, scale * 0.62);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#4338ca";
          ctx.beginPath();
          ctx.moveTo(-scale * 0.42, -scale * 0.58);
          ctx.lineTo(scale * 0.02, -scale * 1.28);
          ctx.lineTo(scale * 0.46, -scale * 0.54);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.save();
          ctx.globalAlpha = glow;
          ctx.fillStyle = "#c4b5fd";
          ctx.beginPath();
          ctx.arc(-scale * 0.15, -scale * 0.58, scale * 0.06, 0, Math.PI * 2);
          ctx.arc(scale * 0.15, -scale * 0.58, scale * 0.06, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          ctx.strokeStyle = "#6d5637";
          ctx.lineWidth = Math.max(3, scale * 0.08);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(scale * 0.56, scale * 0.6);
          ctx.lineTo(scale * 0.82, -scale * 0.85);
          ctx.stroke();
          ctx.save();
          ctx.globalAlpha = glow;
          ctx.fillStyle = "#ddd6fe";
          ctx.beginPath();
          ctx.arc(scale * 0.82, -scale * 0.98, scale * 0.18, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonBossChainedColossus(ctx, scale, tick) {
          ctx.fillStyle = "rgba(0,0,0,0.48)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.72, scale * 1, scale * 0.32, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#44403c";
          ctx.strokeStyle = "#78716c";
          ctx.lineWidth = Math.max(3, scale * 0.1);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.68, scale * 0.6);
          ctx.lineTo(-scale * 0.78, -scale * 0.3);
          ctx.quadraticCurveTo(0, -scale * 0.62, scale * 0.78, -scale * 0.3);
          ctx.lineTo(scale * 0.68, scale * 0.6);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          var crack = 0.5 + Math.sin((tick || 0) * 0.16) * 0.4;
          ctx.save();
          ctx.globalAlpha = crack;
          ctx.strokeStyle = "#fb923c";
          ctx.lineWidth = Math.max(2, scale * 0.045);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.3, -scale * 0.1);
          ctx.lineTo(-scale * 0.1, scale * 0.15);
          ctx.lineTo(-scale * 0.2, scale * 0.4);
          ctx.moveTo(scale * 0.3, -scale * 0.15);
          ctx.lineTo(scale * 0.12, scale * 0.1);
          ctx.lineTo(scale * 0.22, scale * 0.38);
          ctx.stroke();
          ctx.restore();
          ctx.strokeStyle = "#57534e";
          ctx.lineWidth = Math.max(2.5, scale * 0.06);
          for (var link = 0; link < 3; link += 1) {
            ctx.beginPath();
            ctx.ellipse(
              -scale * 0.5 + link * scale * 0.35,
              -scale * 0.5 + link * scale * 0.3,
              scale * 0.12,
              scale * 0.18,
              0.6,
              0,
              Math.PI * 2,
            );
            ctx.stroke();
          }
          ctx.fillStyle = "#292524";
          ctx.strokeStyle = "#a8a29e";
          ctx.lineWidth = Math.max(2.5, scale * 0.06);
          ctx.beginPath();
          ctx.arc(-scale * 0.85, scale * 0.05, scale * 0.28, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(scale * 0.85, scale * 0.05, scale * 0.28, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.save();
          ctx.globalAlpha = crack;
          ctx.fillStyle = "#fdba74";
          ctx.beginPath();
          ctx.arc(-scale * 0.18, -scale * 0.48, scale * 0.06, 0, Math.PI * 2);
          ctx.arc(scale * 0.18, -scale * 0.48, scale * 0.06, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonWarrior(ctx, x, y, scale, tick) {
          var bob = Math.sin((tick || 0) * 0.1 + x) * scale * 0.03;
          ctx.save();
          ctx.translate(x, y + bob);
          ctx.fillStyle = "rgba(0,0,0,0.44)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.46, scale * 0.5, scale * 0.18, 0, 0, Math.PI * 2);
          ctx.fill();

          // Shield arm, drawn first so the body/sword layer over it.
          ctx.fillStyle = "#78350f";
          ctx.strokeStyle = "#d97706";
          ctx.lineWidth = Math.max(2, scale * 0.05);
          ctx.beginPath();
          ctx.ellipse(-scale * 0.42, scale * 0.05, scale * 0.2, scale * 0.32, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.strokeStyle = "#fde68a";
          ctx.lineWidth = Math.max(1, scale * 0.03);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.42, -scale * 0.2);
          ctx.lineTo(-scale * 0.42, scale * 0.3);
          ctx.stroke();

          ctx.fillStyle = "#57534e";
          ctx.strokeStyle = "#a8a29e";
          ctx.lineWidth = Math.max(2, scale * 0.06);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.34, scale * 0.44);
          ctx.lineTo(-scale * 0.4, -scale * 0.14);
          ctx.quadraticCurveTo(0, -scale * 0.34, scale * 0.4, -scale * 0.14);
          ctx.lineTo(scale * 0.34, scale * 0.44);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = "#78716c";
          ctx.strokeStyle = "#d6d3d1";
          ctx.lineWidth = Math.max(1.5, scale * 0.04);
          ctx.beginPath();
          ctx.arc(0, -scale * 0.42, scale * 0.28, Math.PI * 0.95, Math.PI * 0.05, true);
          ctx.lineTo(scale * 0.24, -scale * 0.28);
          ctx.lineTo(-scale * 0.24, -scale * 0.28);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#1c1917";
          ctx.fillRect(-scale * 0.03, -scale * 0.42, scale * 0.06, scale * 0.16);
          ctx.fillStyle = "#fde047";
          ctx.beginPath();
          ctx.arc(-scale * 0.1, -scale * 0.36, scale * 0.035, 0, Math.PI * 2);
          ctx.arc(scale * 0.1, -scale * 0.36, scale * 0.035, 0, Math.PI * 2);
          ctx.fill();

          ctx.strokeStyle = "#e7e5e4";
          ctx.lineWidth = Math.max(2, scale * 0.06);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(scale * 0.4, scale * 0.1);
          ctx.lineTo(scale * 0.66, -scale * 0.42);
          ctx.stroke();
          ctx.fillStyle = "#57534e";
          ctx.fillRect(scale * 0.36, scale * 0.02, scale * 0.1, scale * 0.16);
          ctx.restore();
        }

        function drawDungeonWizard(ctx, x, y, scale, tick) {
          var bob = Math.sin((tick || 0) * 0.13 + x) * scale * 0.04;
          var glow = 0.6 + Math.sin((tick || 0) * 0.2 + x) * 0.4;
          ctx.save();
          ctx.translate(x, y + bob);
          ctx.fillStyle = "rgba(0,0,0,0.4)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.44, scale * 0.46, scale * 0.16, 0, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = "#312e81";
          ctx.strokeStyle = "#818cf8";
          ctx.lineWidth = Math.max(2, scale * 0.05);
          ctx.beginPath();
          ctx.moveTo(0, -scale * 0.3);
          ctx.lineTo(-scale * 0.4, scale * 0.42);
          ctx.quadraticCurveTo(0, scale * 0.55, scale * 0.4, scale * 0.42);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = "#4338ca";
          ctx.beginPath();
          ctx.moveTo(-scale * 0.26, -scale * 0.32);
          ctx.lineTo(scale * 0.02, -scale * 0.78);
          ctx.lineTo(scale * 0.3, -scale * 0.3);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#e0e7ff";
          ctx.beginPath();
          ctx.arc(0, -scale * 0.3, scale * 0.16, 0, Math.PI * 2);
          ctx.fill();

          ctx.save();
          ctx.globalAlpha = glow;
          ctx.fillStyle = "#a5b4fc";
          ctx.beginPath();
          ctx.arc(-scale * 0.09, -scale * 0.34, scale * 0.035, 0, Math.PI * 2);
          ctx.arc(scale * 0.09, -scale * 0.34, scale * 0.035, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          ctx.strokeStyle = "#6d5637";
          ctx.lineWidth = Math.max(2, scale * 0.05);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(scale * 0.34, scale * 0.4);
          ctx.lineTo(scale * 0.5, -scale * 0.5);
          ctx.stroke();
          ctx.save();
          ctx.globalAlpha = glow;
          ctx.fillStyle = "#c4b5fd";
          ctx.beginPath();
          ctx.arc(scale * 0.5, -scale * 0.58, scale * 0.11, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          ctx.restore();
        }

        function drawDungeonArcher(ctx, x, y, scale, tick) {
          var bob = Math.sin((tick || 0) * 0.12 + x) * scale * 0.035;
          ctx.save();
          ctx.translate(x, y + bob);
          ctx.fillStyle = "rgba(0,0,0,0.42)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.42, scale * 0.48, scale * 0.17, 0, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = "#3f6212";
          ctx.strokeStyle = "#bef264";
          ctx.lineWidth = Math.max(2, scale * 0.055);
          ctx.beginPath();
          ctx.moveTo(0, -scale * 0.48);
          ctx.lineTo(-scale * 0.43, scale * 0.43);
          ctx.quadraticCurveTo(0, scale * 0.58, scale * 0.43, scale * 0.43);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = "#1c1917";
          ctx.beginPath();
          ctx.arc(0, -scale * 0.27, scale * 0.3, Math.PI, 0);
          ctx.lineTo(scale * 0.22, -scale * 0.12);
          ctx.quadraticCurveTo(0, scale * 0.03, -scale * 0.22, -scale * 0.12);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = "#fde68a";
          ctx.beginPath();
          ctx.arc(-scale * 0.085, -scale * 0.22, scale * 0.04, 0, Math.PI * 2);
          ctx.arc(scale * 0.085, -scale * 0.22, scale * 0.04, 0, Math.PI * 2);
          ctx.fill();

          ctx.strokeStyle = "#d97706";
          ctx.lineWidth = Math.max(2, scale * 0.055);
          ctx.beginPath();
          ctx.arc(scale * 0.35, 0, scale * 0.45, -Math.PI * 0.5, Math.PI * 0.5);
          ctx.stroke();
          ctx.strokeStyle = "#fef3c7";
          ctx.lineWidth = Math.max(1, scale * 0.025);
          ctx.beginPath();
          ctx.moveTo(scale * 0.35, -scale * 0.45);
          ctx.lineTo(scale * 0.35, scale * 0.45);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonArrow(ctx, x, y, angle, scale) {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(angle);
          ctx.strokeStyle = "#f5deb3";
          ctx.lineWidth = Math.max(2, scale * 0.055);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(-scale * 0.38, 0);
          ctx.lineTo(scale * 0.36, 0);
          ctx.stroke();
          ctx.fillStyle = "#cbd5e1";
          ctx.beginPath();
          ctx.moveTo(scale * 0.48, 0);
          ctx.lineTo(scale * 0.25, -scale * 0.12);
          ctx.lineTo(scale * 0.25, scale * 0.12);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = "#fb7185";
          ctx.lineWidth = Math.max(1, scale * 0.035);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.34, 0);
          ctx.lineTo(-scale * 0.48, -scale * 0.13);
          ctx.moveTo(-scale * 0.34, 0);
          ctx.lineTo(-scale * 0.48, scale * 0.13);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonBolt(ctx, x, y, scale, tick) {
          var pulse = 0.85 + Math.sin((tick || 0) * 0.5) * 0.15;
          ctx.save();
          ctx.translate(x, y);
          var glow = ctx.createRadialGradient(0, 0, 0, 0, 0, scale * 0.4 * pulse);
          glow.addColorStop(0, "rgba(196,181,253,0.85)");
          glow.addColorStop(1, "rgba(129,140,248,0)");
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(0, 0, scale * 0.4 * pulse, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#e0e7ff";
          ctx.beginPath();
          ctx.arc(0, 0, scale * 0.14 * pulse, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonSkeleton(ctx, x, y, scale, tick) {
          var bob = Math.sin((tick || 0) * 0.15 + x) * scale * 0.035;
          ctx.save();
          ctx.translate(x, y + bob);
          ctx.fillStyle = "rgba(0,0,0,0.42)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.38, scale * 0.42, scale * 0.16, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#e7e5e4";
          ctx.lineWidth = Math.max(3, scale * 0.09);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(0, -scale * 0.08);
          ctx.lineTo(0, scale * 0.34);
          ctx.moveTo(0, scale * 0.02);
          ctx.lineTo(-scale * 0.35, scale * 0.18);
          ctx.moveTo(0, scale * 0.02);
          ctx.lineTo(scale * 0.35, scale * 0.18);
          ctx.moveTo(0, scale * 0.32);
          ctx.lineTo(-scale * 0.27, scale * 0.55);
          ctx.moveTo(0, scale * 0.32);
          ctx.lineTo(scale * 0.27, scale * 0.55);
          ctx.stroke();
          ctx.lineWidth = Math.max(2, scale * 0.055);
          for (var rib = 0; rib < 3; rib += 1) {
            var ribY = scale * (0.04 + rib * 0.1);
            ctx.beginPath();
            ctx.moveTo(-scale * 0.23, ribY);
            ctx.quadraticCurveTo(0, ribY + scale * 0.12, scale * 0.23, ribY);
            ctx.stroke();
          }
          ctx.fillStyle = "#d6d3d1";
          ctx.strokeStyle = "#57534e";
          ctx.lineWidth = Math.max(2, scale * 0.055);
          ctx.beginPath();
          ctx.arc(0, -scale * 0.34, scale * 0.3, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#1c1917";
          ctx.beginPath();
          ctx.arc(-scale * 0.11, -scale * 0.39, scale * 0.07, 0, Math.PI * 2);
          ctx.arc(scale * 0.11, -scale * 0.39, scale * 0.07, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#78716c";
          ctx.lineWidth = Math.max(1, scale * 0.035);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.13, -scale * 0.2);
          ctx.lineTo(scale * 0.13, -scale * 0.2);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonSlime(ctx, x, y, scale, tick) {
          var wobble = Math.sin((tick || 0) * 0.2 + x);
          ctx.save();
          ctx.translate(x, y + wobble * scale * 0.035);
          ctx.fillStyle = "rgba(0,0,0,0.4)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.35, scale * 0.5, scale * 0.17, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#65a30d";
          ctx.strokeStyle = "#d9f99d";
          ctx.lineWidth = Math.max(2, scale * 0.06);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.48, scale * 0.35);
          ctx.quadraticCurveTo(-scale * 0.42, -scale * 0.35, 0, -scale * (0.46 + wobble * 0.04));
          ctx.quadraticCurveTo(scale * 0.42, -scale * 0.35, scale * 0.48, scale * 0.35);
          ctx.quadraticCurveTo(scale * 0.25, scale * 0.48, 0, scale * 0.37);
          ctx.quadraticCurveTo(-scale * 0.25, scale * 0.48, -scale * 0.48, scale * 0.35);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#ecfccb";
          ctx.beginPath();
          ctx.arc(-scale * 0.14, -scale * 0.09, scale * 0.085, 0, Math.PI * 2);
          ctx.arc(scale * 0.14, -scale * 0.09, scale * 0.085, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#1a2e05";
          ctx.beginPath();
          ctx.arc(-scale * 0.14, -scale * 0.08, scale * 0.04, 0, Math.PI * 2);
          ctx.arc(scale * 0.14, -scale * 0.08, scale * 0.04, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#365314";
          ctx.lineWidth = Math.max(2, scale * 0.045);
          ctx.beginPath();
          ctx.arc(0, scale * 0.11, scale * 0.14, 0.15, Math.PI - 0.15);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonBat(ctx, x, y, scale, tick) {
          var flap = 0.72 + Math.abs(Math.sin((tick || 0) * 0.28 + x)) * 0.35;
          var bob = Math.sin((tick || 0) * 0.17 + x) * scale * 0.1;
          ctx.save();
          ctx.translate(x, y - scale * 0.08 + bob);
          ctx.fillStyle = "rgba(0,0,0,0.36)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.48, scale * 0.48, scale * 0.14, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#312e81";
          ctx.strokeStyle = "#c4b5fd";
          ctx.lineWidth = Math.max(2, scale * 0.055);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.08, 0);
          ctx.quadraticCurveTo(-scale * 0.45, -scale * flap, -scale * 0.72, -scale * 0.16);
          ctx.lineTo(-scale * 0.48, scale * 0.02);
          ctx.lineTo(-scale * 0.3, scale * 0.28);
          ctx.lineTo(-scale * 0.08, scale * 0.13);
          ctx.closePath();
          ctx.moveTo(scale * 0.08, 0);
          ctx.quadraticCurveTo(scale * 0.45, -scale * flap, scale * 0.72, -scale * 0.16);
          ctx.lineTo(scale * 0.48, scale * 0.02);
          ctx.lineTo(scale * 0.3, scale * 0.28);
          ctx.lineTo(scale * 0.08, scale * 0.13);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#4c1d95";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.02, scale * 0.22, scale * 0.34, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(-scale * 0.17, -scale * 0.23);
          ctx.lineTo(-scale * 0.09, -scale * 0.5);
          ctx.lineTo(scale * 0.01, -scale * 0.25);
          ctx.moveTo(scale * 0.17, -scale * 0.23);
          ctx.lineTo(scale * 0.09, -scale * 0.5);
          ctx.lineTo(-scale * 0.01, -scale * 0.25);
          ctx.fill();
          ctx.fillStyle = "#fef08a";
          ctx.beginPath();
          ctx.arc(-scale * 0.075, -scale * 0.08, scale * 0.045, 0, Math.PI * 2);
          ctx.arc(scale * 0.075, -scale * 0.08, scale * 0.045, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonSkitter(ctx, x, y, scale, tick) {
          var bob = Math.sin((tick || 0) * 0.18 + x) * scale * 0.04;
          var scurry = Math.sin((tick || 0) * 0.4 + x) * 0.12;
          ctx.save();
          ctx.translate(x, y + bob);
          ctx.fillStyle = "rgba(0,0,0,0.42)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.3, scale * 0.46, scale * 0.18, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#7f1d1d";
          ctx.lineWidth = Math.max(2, scale * 0.065);
          for (var legPair = -1; legPair <= 1; legPair += 1) {
            for (var legSide = -1; legSide <= 1; legSide += 2) {
              var legSpread = (legPair + 2) * 0.16;
              var legKick = legSide * (Math.sin((tick || 0) * 0.35 + legPair + x) * 0.06);
              ctx.beginPath();
              ctx.moveTo(legPair * scale * 0.14, legSide * scale * 0.14);
              ctx.lineTo(
                legPair * scale * 0.14 + (legPair >= 0 ? 1 : -1) * scale * (0.32 + legSpread * 0.2),
                legSide * scale * (0.34 + legKick),
              );
              ctx.stroke();
            }
          }
          ctx.fillStyle = "#991b1b";
          ctx.strokeStyle = "#fecaca";
          ctx.lineWidth = Math.max(1, scale * 0.03);
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.14, scale * 0.3, scale * 0.24, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(0, -scale * 0.02, scale * 0.36, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.strokeStyle = "rgba(69,10,10,0.55)";
          ctx.lineWidth = Math.max(1, scale * 0.03);
          for (var plate = 0; plate < 2; plate += 1) {
            ctx.beginPath();
            ctx.arc(0, scale * 0.12, scale * (0.14 + plate * 0.1), 0.25, Math.PI - 0.25);
            ctx.stroke();
          }
          ctx.fillStyle = "#450a0a";
          ctx.beginPath();
          ctx.moveTo(-scale * 0.28 + scurry * scale, -scale * 0.25);
          ctx.lineTo(-scale * 0.52 + scurry * scale, -scale * 0.52);
          ctx.lineTo(-scale * 0.08, -scale * 0.34);
          ctx.moveTo(scale * 0.28 + scurry * scale, -scale * 0.25);
          ctx.lineTo(scale * 0.52 + scurry * scale, -scale * 0.52);
          ctx.lineTo(scale * 0.08, -scale * 0.34);
          ctx.fill();
          ctx.fillStyle = "#fde047";
          ctx.beginPath();
          ctx.arc(-scale * 0.14, -scale * 0.05, scale * 0.055, 0, Math.PI * 2);
          ctx.arc(scale * 0.14, -scale * 0.05, scale * 0.055, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        function dungeonPickupGlowColor(kind) {
          var colors = {
            gold: "#facc15",
            food: "#fb7185",
            fury: "#ef4444",
            haste: "#38bdf8",
            reach: "#a78bfa",
            ward: "#4ade80",
            vitality: "#34d399",
            key: "#fbbf24",
          };
          return colors[kind] || "#ffffff";
        }

        function dungeonPickupLabel(kind) {
          var labels = {
            gold: "GOLD",
            food: "FOOD +20",
            fury: "FURY",
            haste: "HASTE",
            reach: "LONGSHOT",
            ward: "WARD",
            vitality: "VITALITY",
            key: "GATE KEY",
          };
          return labels[kind] || "POWER";
        }

        function drawDungeonPickup(ctx, pickup, x, y, scale, tick) {
          var bob = Math.sin((tick || 0) * 0.12 + x) * scale * 0.08;
          var pickupOpacity =
            typeof pickup.remainingMs === "number" && pickup.remainingMs < 3000
              ? Math.max(0, pickup.remainingMs / 3000)
              : 1;
          ctx.save();
          ctx.translate(x, y + bob);
          ctx.globalAlpha = pickupOpacity;
          var glow = ctx.createRadialGradient(0, 0, scale * 0.05, 0, 0, scale * 0.55);
          glow.addColorStop(0, dungeonPickupGlowColor(pickup.kind));
          glow.addColorStop(1, "rgba(0,0,0,0)");
          ctx.save();
          ctx.globalAlpha *= 0.32;
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(0, 0, scale * 0.55, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          if (pickup.kind === "gold") drawDungeonGoldIcon(ctx, scale, tick);
          else if (pickup.kind === "food") drawDungeonFoodIcon(ctx, scale);
          else if (pickup.kind === "fury") drawDungeonFuryIcon(ctx, scale);
          else if (pickup.kind === "haste") drawDungeonHasteIcon(ctx, scale);
          else if (pickup.kind === "reach") drawDungeonReachIcon(ctx, scale);
          else if (pickup.kind === "ward") drawDungeonWardIcon(ctx, scale);
          else if (pickup.kind === "key") drawDungeonKeyIcon(ctx, scale, tick);
          else drawDungeonVitalityIcon(ctx, scale, tick);

          ctx.fillStyle = "#ffffff";
          ctx.strokeStyle = "#100a18";
          ctx.lineWidth = 3;
          ctx.font = "bold " + Math.max(8, scale * 0.15) + "px Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "alphabetic";
          var label = dungeonPickupLabel(pickup.kind);
          ctx.strokeText(label, 0, scale * 0.62);
          ctx.fillText(label, 0, scale * 0.62);
          ctx.restore();
        }

        function drawDungeonGoldIcon(ctx, scale, tick) {
          var spin = Math.cos((tick || 0) * 0.1);
          var squish = 0.45 + Math.abs(spin) * 0.55;
          ctx.save();
          ctx.scale(squish, 1);
          var coinGradient = ctx.createRadialGradient(
            -scale * 0.08,
            -scale * 0.1,
            scale * 0.02,
            0,
            0,
            scale * 0.3,
          );
          coinGradient.addColorStop(0, "#fff6c8");
          coinGradient.addColorStop(0.5, "#facc15");
          coinGradient.addColorStop(1, "#a16207");
          ctx.fillStyle = coinGradient;
          ctx.beginPath();
          ctx.arc(0, 0, scale * 0.28, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#78350f";
          ctx.lineWidth = Math.max(1.5, scale * 0.03);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(0, 0, scale * 0.18, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(120,53,15,0.6)";
          ctx.lineWidth = Math.max(1, scale * 0.02);
          ctx.stroke();
          ctx.restore();
          ctx.fillStyle = "#78350f";
          ctx.font = "bold " + Math.max(9, scale * 0.22) + "px Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("$", 0, 1);
        }

        function drawDungeonFoodIcon(ctx, scale) {
          ctx.save();
          var bodyGradient = ctx.createRadialGradient(
            -scale * 0.1,
            -scale * 0.08,
            scale * 0.02,
            0,
            0,
            scale * 0.3,
          );
          bodyGradient.addColorStop(0, "#fca5a5");
          bodyGradient.addColorStop(0.6, "#ef4444");
          bodyGradient.addColorStop(1, "#991b1b");
          ctx.fillStyle = bodyGradient;
          ctx.beginPath();
          ctx.moveTo(0, -scale * 0.22);
          ctx.bezierCurveTo(scale * 0.28, -scale * 0.3, scale * 0.32, scale * 0.15, 0, scale * 0.3);
          ctx.bezierCurveTo(
            -scale * 0.32,
            scale * 0.15,
            -scale * 0.28,
            -scale * 0.3,
            0,
            -scale * 0.22,
          );
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = "#7f1d1d";
          ctx.lineWidth = Math.max(1, scale * 0.02);
          ctx.stroke();
          ctx.strokeStyle = "#4d7c0f";
          ctx.lineWidth = Math.max(2, scale * 0.05);
          ctx.beginPath();
          ctx.moveTo(0, -scale * 0.22);
          ctx.lineTo(scale * 0.03, -scale * 0.36);
          ctx.stroke();
          ctx.fillStyle = "#65a30d";
          ctx.beginPath();
          ctx.ellipse(
            scale * 0.12,
            -scale * 0.34,
            scale * 0.09,
            scale * 0.05,
            -0.6,
            0,
            Math.PI * 2,
          );
          ctx.fill();
          ctx.restore();
        }

        function drawDungeonFuryIcon(ctx, scale) {
          ctx.save();
          var flameGradient = ctx.createLinearGradient(0, scale * 0.3, 0, -scale * 0.35);
          flameGradient.addColorStop(0, "#7f1d1d");
          flameGradient.addColorStop(0.55, "#ef4444");
          flameGradient.addColorStop(1, "#fde68a");
          ctx.fillStyle = flameGradient;
          ctx.beginPath();
          ctx.moveTo(0, scale * 0.3);
          ctx.bezierCurveTo(
            scale * 0.26,
            scale * 0.12,
            scale * 0.16,
            -scale * 0.12,
            0,
            -scale * 0.34,
          );
          ctx.bezierCurveTo(
            -scale * 0.05,
            -scale * 0.12,
            -scale * 0.05,
            0,
            -scale * 0.02,
            scale * 0.02,
          );
          ctx.bezierCurveTo(
            -scale * 0.2,
            -scale * 0.05,
            -scale * 0.26,
            scale * 0.14,
            0,
            scale * 0.3,
          );
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = "#7f1d1d";
          ctx.lineWidth = Math.max(1, scale * 0.02);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonHasteIcon(ctx, scale) {
          ctx.save();
          ctx.fillStyle = "#38bdf8";
          ctx.beginPath();
          ctx.moveTo(-scale * 0.06, -scale * 0.34);
          ctx.lineTo(scale * 0.16, -scale * 0.04);
          ctx.lineTo(scale * 0.02, -scale * 0.04);
          ctx.lineTo(scale * 0.14, scale * 0.34);
          ctx.lineTo(-scale * 0.18, scale * 0.02);
          ctx.lineTo(-scale * 0.04, scale * 0.02);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = "#0369a1";
          ctx.lineWidth = Math.max(1, scale * 0.02);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonReachIcon(ctx, scale) {
          ctx.save();
          ctx.rotate(-Math.PI / 4);
          ctx.strokeStyle = "#a78bfa";
          ctx.lineWidth = Math.max(2, scale * 0.05);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.28, 0);
          ctx.lineTo(scale * 0.2, 0);
          ctx.stroke();
          ctx.fillStyle = "#a78bfa";
          ctx.beginPath();
          ctx.moveTo(scale * 0.32, 0);
          ctx.lineTo(scale * 0.14, -scale * 0.12);
          ctx.lineTo(scale * 0.14, scale * 0.12);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = "#c4b5fd";
          ctx.lineWidth = Math.max(1, scale * 0.03);
          ctx.beginPath();
          ctx.moveTo(-scale * 0.28, 0);
          ctx.lineTo(-scale * 0.2, -scale * 0.08);
          ctx.moveTo(-scale * 0.23, 0);
          ctx.lineTo(-scale * 0.15, scale * 0.08);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonWardIcon(ctx, scale) {
          ctx.save();
          var shieldGradient = ctx.createLinearGradient(0, -scale * 0.3, 0, scale * 0.3);
          shieldGradient.addColorStop(0, "#bbf7d0");
          shieldGradient.addColorStop(1, "#16a34a");
          ctx.fillStyle = shieldGradient;
          ctx.beginPath();
          ctx.moveTo(0, -scale * 0.32);
          ctx.lineTo(scale * 0.24, -scale * 0.2);
          ctx.lineTo(scale * 0.24, scale * 0.08);
          ctx.quadraticCurveTo(scale * 0.24, scale * 0.26, 0, scale * 0.34);
          ctx.quadraticCurveTo(-scale * 0.24, scale * 0.26, -scale * 0.24, scale * 0.08);
          ctx.lineTo(-scale * 0.24, -scale * 0.2);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = "#14532d";
          ctx.lineWidth = Math.max(1.5, scale * 0.03);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonVitalityIcon(ctx, scale, tick) {
          var pulse = 1 + Math.sin((tick || 0) * 0.15) * 0.06;
          ctx.save();
          ctx.scale(pulse, pulse);
          var heartGradient = ctx.createRadialGradient(
            -scale * 0.08,
            -scale * 0.12,
            scale * 0.02,
            0,
            0,
            scale * 0.32,
          );
          heartGradient.addColorStop(0, "#a7f3d0");
          heartGradient.addColorStop(0.6, "#34d399");
          heartGradient.addColorStop(1, "#047857");
          ctx.fillStyle = heartGradient;
          ctx.strokeStyle = "#065f46";
          ctx.lineWidth = Math.max(1.5, scale * 0.03);
          ctx.beginPath();
          ctx.moveTo(0, scale * 0.28);
          ctx.bezierCurveTo(
            -scale * 0.4,
            -scale * 0.02,
            -scale * 0.24,
            -scale * 0.34,
            0,
            -scale * 0.12,
          );
          ctx.bezierCurveTo(
            scale * 0.24,
            -scale * 0.34,
            scale * 0.4,
            -scale * 0.02,
            0,
            scale * 0.28,
          );
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.restore();
          ctx.strokeStyle = "#ecfdf5";
          ctx.lineWidth = Math.max(1.5, scale * 0.035);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(scale * 0.32, -scale * 0.32);
          ctx.lineTo(scale * 0.32, -scale * 0.16);
          ctx.moveTo(scale * 0.24, -scale * 0.24);
          ctx.lineTo(scale * 0.4, -scale * 0.24);
          ctx.stroke();
        }

        function drawDungeonKeyIcon(ctx, scale, tick) {
          var pulse = 1 + Math.sin((tick || 0) * 0.15) * 0.06;
          ctx.save();
          ctx.scale(pulse, pulse);
          ctx.fillStyle = "#fbbf24";
          ctx.strokeStyle = "#92400e";
          ctx.lineWidth = Math.max(1.5, scale * 0.03);
          ctx.beginPath();
          ctx.arc(-scale * 0.16, 0, scale * 0.16, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(-scale * 0.16, 0, scale * 0.06, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#92400e";
          ctx.lineWidth = Math.max(2, scale * 0.09);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(scale * 0.42, 0);
          ctx.stroke();
          ctx.lineWidth = Math.max(1.5, scale * 0.05);
          ctx.beginPath();
          ctx.moveTo(scale * 0.26, 0);
          ctx.lineTo(scale * 0.26, scale * 0.14);
          ctx.moveTo(scale * 0.38, 0);
          ctx.lineTo(scale * 0.38, scale * 0.14);
          ctx.stroke();
          ctx.restore();
        }

        function drawDungeonHero(ctx, hero, sx, sy, scale) {
          var color = dungeonRoleColor(hero.role, hero.color);
          ctx.save();
          ctx.translate(sx(hero.x), sy(hero.y));
          ctx.rotate(hero.facing || 0);
          ctx.globalAlpha = hero.connected ? 1 : 0.45;
          if (!hero.downed) {
            var heroGlow = ctx.createRadialGradient(0, 0, scale * 0.08, 0, 0, scale * 0.92);
            heroGlow.addColorStop(0, color);
            heroGlow.addColorStop(1, "rgba(0,0,0,0)");
            ctx.save();
            ctx.globalAlpha *= 0.28;
            ctx.fillStyle = heroGlow;
            ctx.beginPath();
            ctx.arc(0, 0, scale * 0.92, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }
          ctx.fillStyle = "rgba(0,0,0,0.4)";
          ctx.beginPath();
          ctx.ellipse(0, scale * 0.34, scale * 0.48, scale * 0.2, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = hero.downed ? "#ffffff" : "#111827";
          ctx.lineWidth = Math.max(2, scale * 0.065);
          if (hero.effects && hero.effects.ward > 0) {
            ctx.strokeStyle = "#86efac";
            ctx.lineWidth = Math.max(3, scale * 0.08);
            ctx.beginPath();
            ctx.arc(0, 0, scale * 0.7, 0, Math.PI * 2);
            ctx.stroke();
            ctx.strokeStyle = hero.downed ? "#ffffff" : "#111827";
          }
          if (hero.effects && hero.effects.fury > 0) {
            ctx.strokeStyle = "rgba(248,113,113,0.85)";
            ctx.lineWidth = Math.max(2, scale * 0.055);
            for (var flame = -1; flame <= 1; flame += 1) {
              ctx.beginPath();
              ctx.moveTo(-scale * 0.3 + flame * scale * 0.28, scale * 0.38);
              ctx.lineTo(-scale * 0.2 + flame * scale * 0.28, scale * 0.67);
              ctx.lineTo(-scale * 0.05 + flame * scale * 0.28, scale * 0.4);
              ctx.stroke();
            }
          }
          if (hero.effects && hero.effects.haste > 0) {
            ctx.strokeStyle = "rgba(125,211,252,0.8)";
            ctx.lineWidth = Math.max(2, scale * 0.05);
            for (var streak = -1; streak <= 1; streak += 1) {
              ctx.beginPath();
              ctx.moveTo(-scale * 0.42, streak * scale * 0.2);
              ctx.lineTo(-scale * (0.72 + Math.abs(streak) * 0.12), streak * scale * 0.2);
              ctx.stroke();
            }
          }
          if (hero.effects && hero.effects.reach > 0) {
            ctx.strokeStyle = "rgba(216,180,254,0.72)";
            ctx.lineWidth = Math.max(2, scale * 0.045);
            ctx.setLineDash([Math.max(3, scale * 0.1), Math.max(3, scale * 0.08)]);
            ctx.beginPath();
            ctx.arc(0, 0, scale * 0.58, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
          }
          if (hero.attacking && !hero.downed) {
            ctx.strokeStyle = color;
            ctx.globalAlpha *= 0.7;
            ctx.lineWidth = Math.max(3, scale * 0.085);
            ctx.beginPath();
            ctx.arc(scale * 0.18, 0, scale * 0.7, -0.58, 0.58);
            ctx.stroke();
            ctx.globalAlpha = hero.connected ? 1 : 0.45;
          }

          // Boots and a readable humanoid body establish these as adventurers, not tokens.
          ctx.fillStyle = "#271b2d";
          ctx.fillRect(-scale * 0.28, scale * 0.22, scale * 0.18, scale * 0.3);
          ctx.fillRect(scale * 0.08, scale * 0.22, scale * 0.18, scale * 0.3);
          var cloakGradient = ctx.createLinearGradient(
            -scale * 0.36,
            -scale * 0.4,
            scale * 0.38,
            scale * 0.34,
          );
          cloakGradient.addColorStop(0, dungeonLighten(color, 0.35));
          cloakGradient.addColorStop(0.55, color);
          cloakGradient.addColorStop(1, dungeonDarken(color, 0.35));
          ctx.fillStyle = cloakGradient;
          ctx.beginPath();
          ctx.moveTo(-scale * 0.36, scale * 0.32);
          ctx.lineTo(-scale * 0.28, -scale * 0.28);
          ctx.quadraticCurveTo(0, -scale * 0.48, scale * 0.3, -scale * 0.24);
          ctx.lineTo(scale * 0.38, scale * 0.34);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          var faceGradient = ctx.createRadialGradient(
            -scale * 0.06,
            -scale * 0.55,
            scale * 0.04,
            0,
            -scale * 0.48,
            scale * 0.25,
          );
          faceGradient.addColorStop(0, "#ffe0bd");
          faceGradient.addColorStop(1, "#e0a675");
          ctx.fillStyle = faceGradient;
          ctx.beginPath();
          ctx.arc(0, -scale * 0.48, scale * 0.23, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          if (!hero.downed) {
            ctx.fillStyle = "#1f2937";
            ctx.beginPath();
            ctx.arc(-scale * 0.08, -scale * 0.5, scale * 0.035, 0, Math.PI * 2);
            ctx.arc(scale * 0.09, -scale * 0.5, scale * 0.035, 0, Math.PI * 2);
            ctx.fill();
          }

          if (hero.role === "vanguard") {
            ctx.fillStyle = "#cbd5e1";
            ctx.beginPath();
            ctx.arc(0, -scale * 0.5, scale * 0.26, Math.PI, 0);
            ctx.lineTo(scale * 0.24, -scale * 0.43);
            ctx.lineTo(-scale * 0.24, -scale * 0.43);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = "#0e7490";
            ctx.beginPath();
            ctx.arc(scale * 0.38, scale * 0.03, scale * 0.29, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = "#e2e8f0";
            ctx.stroke();
            ctx.strokeStyle = "#f8fafc";
            ctx.lineWidth = Math.max(3, scale * 0.08);
            ctx.beginPath();
            ctx.moveTo(-scale * 0.15, -scale * 0.05);
            ctx.lineTo(-scale * 0.6, scale * 0.08);
            ctx.stroke();
          } else if (hero.role === "ranger") {
            ctx.fillStyle = "#78350f";
            ctx.beginPath();
            ctx.arc(0, -scale * 0.48, scale * 0.31, Math.PI * 0.82, Math.PI * 2.18);
            ctx.lineTo(0, -scale * 0.35);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.strokeStyle = "#fde68a";
            ctx.lineWidth = Math.max(2, scale * 0.055);
            ctx.beginPath();
            ctx.arc(scale * 0.32, 0, scale * 0.38, -Math.PI / 2, Math.PI / 2);
            ctx.moveTo(scale * 0.32, -scale * 0.38);
            ctx.lineTo(scale * 0.32, scale * 0.38);
            ctx.stroke();
          } else if (hero.role === "arcanist") {
            ctx.fillStyle = "#6b21a8";
            ctx.beginPath();
            ctx.moveTo(-scale * 0.32, -scale * 0.58);
            ctx.lineTo(scale * 0.04, -scale * 1.05);
            ctx.lineTo(scale * 0.34, -scale * 0.55);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.strokeStyle = "#d8b4fe";
            ctx.lineWidth = Math.max(3, scale * 0.07);
            ctx.beginPath();
            ctx.moveTo(scale * 0.38, scale * 0.38);
            ctx.lineTo(scale * 0.55, -scale * 0.58);
            ctx.stroke();
            ctx.fillStyle = "#e9d5ff";
            ctx.beginPath();
            ctx.arc(scale * 0.57, -scale * 0.68, scale * 0.13, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillStyle = "#166534";
            ctx.beginPath();
            ctx.arc(0, -scale * 0.5, scale * 0.31, Math.PI * 0.82, Math.PI * 2.18);
            ctx.lineTo(0, -scale * 0.34);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.strokeStyle = "#bbf7d0";
            ctx.lineWidth = Math.max(3, scale * 0.07);
            ctx.beginPath();
            ctx.moveTo(scale * 0.38, scale * 0.42);
            ctx.lineTo(scale * 0.5, -scale * 0.58);
            ctx.stroke();
            ctx.fillStyle = "#fef3c7";
            ctx.beginPath();
            ctx.moveTo(scale * 0.5, -scale * 0.7);
            ctx.lineTo(scale * 0.38, -scale * 0.52);
            ctx.lineTo(scale * 0.62, -scale * 0.52);
            ctx.closePath();
            ctx.fill();
          }
          ctx.restore();
          drawDungeonHealthBar(
            ctx,
            sx(hero.x),
            sy(hero.y) - scale * 0.72,
            hero.health,
            hero.maxHealth,
            scale * 1.1,
            color,
          );
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold " + Math.max(11, scale * 0.28) + "px Arial";
          ctx.textAlign = "center";
          ctx.fillText(hero.displayName, sx(hero.x), sy(hero.y) + scale * 0.78);
        }

        function drawDungeonHealthBar(ctx, x, y, health, maxHealth, width, color) {
          var ratio = Math.max(0, Math.min(1, health / Math.max(1, maxHealth)));
          ctx.fillStyle = "rgba(0,0,0,0.75)";
          ctx.fillRect(x - width / 2, y, width, 7);
          ctx.fillStyle = color;
          ctx.fillRect(x - width / 2 + 1, y + 1, (width - 2) * ratio, 5);
        }

        function renderDungeonRelayHud(snapshot) {
          var heroRows = "";
          var hudKey =
            snapshot.phase +
            ":" +
            snapshot.chamberIndex +
            ":" +
            snapshot.chamberCount +
            ":" +
            snapshot.chamberTitle +
            ":" +
            snapshot.objective +
            ":" +
            snapshot.curseLevel +
            ":" +
            snapshot.partyScore +
            ":" +
            snapshot.partyKeys +
            ":" +
            (snapshot.gates || [])
              .map(function (gate) {
                return gate.id + "," + gate.locked;
              })
              .join(",") +
            ":" +
            (snapshot.phase === "shrine" ? Math.ceil(snapshot.timeRemainingMs / 1000) : "") +
            ":" +
            roomCode;
          for (var keyIndex = 0; keyIndex < snapshot.heroes.length; keyIndex += 1) {
            var keyedHero = snapshot.heroes[keyIndex];
            hudKey +=
              ":" +
              keyedHero.playerId +
              "," +
              keyedHero.displayName +
              "," +
              keyedHero.role +
              "," +
              keyedHero.color +
              "," +
              Math.ceil(keyedHero.health) +
              "," +
              keyedHero.maxHealth +
              "," +
              keyedHero.downed +
              "," +
              keyedHero.reachedExit +
              "," +
              keyedHero.gold +
              "," +
              keyedHero.upgradeReady +
              "," +
              JSON.stringify(keyedHero.upgrades);
          }
          if (snapshot.phase !== "completed" && hudKey === dungeonHudRenderKey) return;
          dungeonHudRenderKey = hudKey;
          for (var index = 0; index < snapshot.heroes.length; index += 1) {
            var hero = snapshot.heroes[index];
            heroRows +=
              '<span style="color:' +
              dungeonRoleColor(hero.role, hero.color) +
              '"><b>' +
              escapeHtml(hero.displayName) +
              "</b> " +
              Math.ceil(hero.health) +
              "/" +
              hero.maxHealth +
              (hero.downed ? " DOWN" : hero.reachedExit ? " AT THE EXIT" : "") +
              "</span>";
          }
          var waitingCount = snapshot.heroes.filter(function (hero) {
            return hero.reachedExit;
          }).length;
          var waitingBanner =
            snapshot.exitOpen && waitingCount > 0 && waitingCount < snapshot.heroes.length
              ? '<div style="margin:2vh auto 0;width:max-content;padding:12px 28px;border-radius:14px;background:rgba(21,128,61,.85);font-size:20px"><b>' +
                waitingCount +
                "/" +
                snapshot.heroes.length +
                " Heroes at the Exit</b> · waiting for the rest of the Party</div>"
              : "";
          root.innerHTML =
            '<section style="position:fixed;inset:0;pointer-events:none;color:white;font-family:Arial,sans-serif">' +
            '<header style="display:flex;justify-content:space-between;gap:18px;padding:12px 24px;background:linear-gradient(180deg,rgba(8,6,18,.96),rgba(8,6,18,.65),transparent);font-size:18px">' +
            '<div><b style="color:#c4b5fd;font-size:25px">DUNGEON RELAY</b><br>Chamber ' +
            (snapshot.chamberIndex + 1) +
            "/" +
            snapshot.chamberCount +
            " · " +
            escapeHtml(snapshot.chamberTitle) +
            '</div><div style="text-align:center"><b>' +
            escapeHtml(snapshot.objective) +
            "</b><br>Curse " +
            snapshot.curseLevel +
            " · Score " +
            snapshot.partyScore +
            (snapshot.partyKeys > 0 ? " · 🔑 " + snapshot.partyKeys : "") +
            '</div><div style="display:grid;gap:2px;text-align:right">' +
            heroRows +
            "</div></header>" +
            (snapshot.phase === "shrine" ? renderDungeonShrinePanel(snapshot) : "") +
            (snapshot.phase === "intro"
              ? '<div style="margin:18vh auto 0;width:max-content;padding:25px 45px;border-radius:18px;background:rgba(25,17,40,.92);font-size:34px"><b>THE DELVE BEGINS</b></div>'
              : "") +
            waitingBanner +
            (snapshot.phase === "completed" ? renderTvPostGameActions() : "") +
            "</section>";
        }

        function renderDungeonShrinePanel(snapshot) {
          var upgradeKinds = [
            { key: "health", label: "HP" },
            { key: "attack", label: "ATK" },
            { key: "speed", label: "SPD" },
            { key: "range", label: "RNG" },
          ];
          var cards = "";
          for (var heroIndex = 0; heroIndex < snapshot.heroes.length; heroIndex += 1) {
            var hero = snapshot.heroes[heroIndex];
            var color = dungeonRoleColor(hero.role, hero.color);
            var rows = "";
            for (var kindIndex = 0; kindIndex < upgradeKinds.length; kindIndex += 1) {
              var kind = upgradeKinds[kindIndex];
              var level = (hero.upgrades && hero.upgrades[kind.key]) || 0;
              var pips = "";
              for (var pip = 0; pip < 5; pip += 1) pips += pip < level ? "●" : "○";
              rows +=
                '<div style="display:flex;justify-content:space-between;gap:16px;font-size:16px;padding:2px 0">' +
                '<span style="opacity:.85">' +
                kind.label +
                '</span><span style="letter-spacing:3px">' +
                pips +
                "</span></div>";
            }
            cards +=
              '<div style="border:2px solid ' +
              color +
              ';border-radius:16px;padding:16px 22px;background:rgba(15,10,28,.9);min-width:210px">' +
              '<div style="display:flex;justify-content:space-between;align-items:center;gap:14px;margin-bottom:10px">' +
              '<b style="color:' +
              color +
              ';font-size:20px">' +
              escapeHtml(hero.displayName) +
              "</b>" +
              (hero.upgradeReady
                ? '<span style="color:#4ade80;font-size:15px;font-weight:bold">✓ READY</span>'
                : '<span style="color:#facc15;font-size:15px;font-weight:bold">🪙 ' +
                  (hero.gold || 0) +
                  "</span>") +
              "</div>" +
              rows +
              "</div>";
          }
          return (
            '<div style="position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:26px;background:rgba(9,6,18,.78)">' +
            '<div style="text-align:center"><b style="font-size:42px;color:#c4b5fd;letter-spacing:1px">UPGRADE YOUR HEROES</b><br>' +
            '<span style="font-size:21px">Rest Shrine · Choose a Relic and spend Gold on your controller · ' +
            Math.ceil(snapshot.timeRemainingMs / 1000) +
            "s left</span></div>" +
            '<div style="display:flex;gap:20px;flex-wrap:wrap;justify-content:center;max-width:92vw">' +
            cards +
            "</div>" +
            "</div>"
          );
        }

        function resetRacingRenderer() {
          racingSnapshotRenderer.stop();
          lastRacingHudAt = 0;
          previousRacingPhase = null;
          previousRacingCountdown = 0;
          racingPerPlayerById.clear();
          racingSharedById.clear();
        }

        function racingScreenX(x) {
          return racingOffsetX + x * racingScale;
        }

        function racingScreenZ(z) {
          return racingOffsetZ + z * racingScale;
        }

        function renderRacingFrame(snapshot, now) {
          latestSnapshot = null;
          resizeCanvas();
          canvas.style.display = "block";
          drawRacingSnapshot(snapshot);
          if (snapshot.phase !== "racing" || now - lastRacingHudAt >= 100) {
            lastRacingHudAt = now;
            renderRacingHud(snapshot);
          }
        }

        function renderRacingHud(snapshot) {
          var phaseLabel = snapshot.phase === "racing" ? "" : snapshot.phase;
          var carsByRacerId = new Map();
          for (var carIndex = 0; carIndex < snapshot.cars.length; carIndex += 1) {
            carsByRacerId.set(snapshot.cars[carIndex].racerId, snapshot.cars[carIndex]);
          }
          if (snapshot.phase === "countdown") {
            phaseLabel = String(Math.max(1, Math.ceil(snapshot.timeRemainingMs / 1000)));
          } else if (snapshot.phase === "racing") {
            var leader = snapshot.cars.find(function (car) {
              return car.place === 1;
            });
            phaseLabel = leader ? "LEADER · LAP " + leader.lap + "/" + snapshot.track.laps : "";
          } else if (snapshot.phase === "garage") {
            phaseLabel = "GARAGE · " + Math.ceil(snapshot.timeRemainingMs / 1000) + "s";
          } else if (snapshot.phase === "raceResults") {
            phaseLabel = "RACE COMPLETE";
          } else if (snapshot.phase === "championshipResults") {
            phaseLabel = "CHAMPIONSHIP COMPLETE";
          }
          var rows = "";
          for (var index = 0; index < snapshot.standings.length; index += 1) {
            var standing = snapshot.standings[index];
            var standingCar = carsByRacerId.get(standing.racerId);
            var standingValue =
              snapshot.phase === "racing" || snapshot.phase === "countdown"
                ? standingCar && standingCar.finished
                  ? "FIN"
                  : "LAP " + (standingCar ? standingCar.lap : 1) + "/" + snapshot.track.laps
                : standing.finished
                  ? "FIN"
                  : standing.points + " pts";
            rows +=
              '<div class="racing-tv-row"><span>#' +
              escapeHtml(String(standing.place)) +
              " " +
              escapeHtml(standing.displayName) +
              "</span><b>" +
              escapeHtml(String(standingValue)) +
              "</b></div>";
          }
          root.innerHTML =
            '<section class="racing-tv-hud">' +
            '<div class="racing-tv-title"><b>POCKET RALLY</b><br>Race ' +
            (snapshot.raceIndex + 1) +
            "/" +
            snapshot.raceCount +
            " · " +
            escapeHtml(snapshot.track.title) +
            "</div>" +
            '<div class="racing-tv-phase">' +
            escapeHtml(phaseLabel.toUpperCase()) +
            "</div>" +
            '<div class="racing-tv-standings">' +
            rows +
            (snapshot.phase === "garage"
              ? "<div>" +
                snapshot.garageReadyPlayerIds.length +
                " builds ready — choose upgrades on phones</div>"
              : "") +
            "</div>" +
            (snapshot.phase === "championshipResults" ? renderTvPostGameActions() : "") +
            "</section>";
          mountTvShellGameInfo(root.querySelector(".racing-tv-standings"));
        }

        function processRacingAudio(snapshot) {
          if (
            snapshot.raceIndex < previousRacingRaceIndex ||
            (snapshot.raceIndex === 0 && previousRacingPhase === "championshipResults")
          ) {
            lastRacingEventId = 0;
          }
          previousRacingRaceIndex = snapshot.raceIndex;
          var countdown =
            snapshot.phase === "countdown"
              ? Math.max(1, Math.ceil(snapshot.timeRemainingMs / 1000))
              : 0;
          if (countdown > 0 && countdown !== previousRacingCountdown) {
            sfx.playRacingCountdown(false);
          }
          previousRacingCountdown = countdown;

          var events = snapshot.events || [];
          for (var index = 0; index < events.length; index += 1) {
            var event = events[index];
            if (event.id <= lastRacingEventId) continue;
            lastRacingEventId = event.id;
            if (event.type === "raceStarted") sfx.playRacingCountdown(true);
            else if (event.type === "pickup") sfx.playRacingPickup();
            else if (event.type === "powerup") sfx.playRacingUpgrade();
            else if (event.type === "hit") sfx.playRacingHit();
            else if (event.type === "finish") sfx.playRacingFinish();
            else if (event.type === "sportUnlocked") sfx.playRacingUpgrade();
            else if (event.type === "finalLap") sfx.playChime();
          }

          if (snapshot.phase === "racing" && snapshot.cars.length > 0) {
            var totalSpeed = 0;
            for (var carIndex = 0; carIndex < snapshot.cars.length; carIndex += 1) {
              totalSpeed += Math.abs(snapshot.cars[carIndex].speed || 0);
            }
            sfx.updateRacingEngine(totalSpeed / snapshot.cars.length / 15, true);
          } else if (previousRacingPhase === "racing") {
            sfx.stopRacingEngine();
          }
          previousRacingPhase = snapshot.phase;
        }

        function drawRacingCar(ctx, car, scale) {
          var outlineColor =
            car.freezeMs > 0 ? "#dbeafe" : car.shieldMs > 0 ? "#67e8f9" : "#111827";
          var effectOutline = car.freezeMs > 0 || car.shieldMs > 0;

          ctx.save();

          // A single baked-looking ellipse reads more naturally than a blurred effect and is cheap.
          ctx.fillStyle = "rgba(15,23,42,0.42)";
          ctx.beginPath();
          ctx.ellipse(scale * 0.04, scale * 0.1, scale * 0.61, scale * 0.34, 0, 0, Math.PI * 2);
          ctx.fill();

          // Four chunky tires make the silhouette read as a car even from across a room.
          ctx.fillStyle = "#0f172a";
          ctx.fillRect(-scale * 0.43, -scale * 0.43, scale * 0.28, scale * 0.18);
          ctx.fillRect(-scale * 0.43, scale * 0.25, scale * 0.28, scale * 0.18);
          ctx.fillRect(scale * 0.25, -scale * 0.43, scale * 0.28, scale * 0.18);
          ctx.fillRect(scale * 0.25, scale * 0.25, scale * 0.28, scale * 0.18);

          // Tapered RC-car body: the pointed nose faces in the direction of travel.
          ctx.beginPath();
          ctx.moveTo(scale * 0.68, 0);
          ctx.lineTo(scale * 0.48, scale * 0.3);
          ctx.lineTo(-scale * 0.42, scale * 0.34);
          ctx.lineTo(-scale * 0.58, scale * 0.24);
          ctx.lineTo(-scale * 0.58, -scale * 0.24);
          ctx.lineTo(-scale * 0.42, -scale * 0.34);
          ctx.lineTo(scale * 0.48, -scale * 0.3);
          ctx.closePath();
          ctx.fillStyle = car.color;
          ctx.fill();
          ctx.strokeStyle = outlineColor;
          ctx.lineWidth = effectOutline ? Math.max(5, scale * 0.12) : Math.max(2, scale * 0.06);
          ctx.stroke();

          // Dark cabin and bright windshield add enough detail without becoming visual noise.
          ctx.beginPath();
          ctx.moveTo(scale * 0.2, scale * 0.23);
          ctx.lineTo(-scale * 0.27, scale * 0.25);
          ctx.lineTo(-scale * 0.38, scale * 0.17);
          ctx.lineTo(-scale * 0.38, -scale * 0.17);
          ctx.lineTo(-scale * 0.27, -scale * 0.25);
          ctx.lineTo(scale * 0.2, -scale * 0.23);
          ctx.closePath();
          ctx.fillStyle = "rgba(15,23,42,0.88)";
          ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255,0.35)";
          ctx.lineWidth = Math.max(1, scale * 0.035);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(scale * 0.2, scale * 0.2);
          ctx.lineTo(scale * 0.07, scale * 0.17);
          ctx.lineTo(scale * 0.07, -scale * 0.17);
          ctx.lineTo(scale * 0.2, -scale * 0.2);
          ctx.closePath();
          ctx.fillStyle = car.freezeMs > 0 ? "#e0f2fe" : "#7dd3fc";
          ctx.fill();

          // Hood stripe, headlights, and tail lights sell the miniature RC-car look.
          ctx.fillStyle = "rgba(255,255,255,0.68)";
          ctx.fillRect(scale * 0.28, -scale * 0.045, scale * 0.25, scale * 0.09);
          ctx.fillStyle = "#fef08a";
          ctx.fillRect(scale * 0.48, -scale * 0.24, scale * 0.1, scale * 0.08);
          ctx.fillRect(scale * 0.48, scale * 0.16, scale * 0.1, scale * 0.08);
          ctx.fillStyle = "#fb7185";
          ctx.fillRect(-scale * 0.57, -scale * 0.2, scale * 0.07, scale * 0.08);
          ctx.fillRect(-scale * 0.57, scale * 0.12, scale * 0.07, scale * 0.08);

          ctx.restore();
        }

        function drawRacingBackdrop(ctx, width, height, scale) {
          var tile = Math.max(26, scale * 1.25);
          for (var y = 0; y < height; y += tile) {
            for (var x = 0; x < width; x += tile) {
              if ((Math.floor(x / tile) + Math.floor(y / tile)) % 2 === 0) {
                ctx.fillStyle = "rgba(255,255,255,0.025)";
                ctx.fillRect(x, y, tile, tile);
              }
            }
          }
          ctx.strokeStyle = "rgba(15,23,42,0.12)";
          ctx.lineWidth = Math.max(1, scale * 0.025);
          for (var grass = 0; grass < 70; grass += 1) {
            var grassX = (((grass * 83) % 997) / 997) * width;
            var grassY = (((grass * 47 + 19) % 991) / 991) * height;
            ctx.beginPath();
            ctx.moveTo(grassX - scale * 0.12, grassY + scale * 0.1);
            ctx.lineTo(grassX, grassY - scale * 0.12);
            ctx.lineTo(grassX + scale * 0.12, grassY + scale * 0.1);
            ctx.stroke();
          }
        }

        function drawRacingScenery(ctx, minX, maxX, minZ, maxZ, sx, sz, scale) {
          var scenery = [
            [0.12, 0.15, "bush"],
            [0.5, 0.16, "tires"],
            [0.86, 0.14, "bush"],
            [0.16, 0.5, "cone"],
            [0.5, 0.5, "bush"],
            [0.84, 0.52, "tires"],
            [0.12, 0.84, "tires"],
            [0.52, 0.86, "cone"],
            [0.88, 0.82, "bush"],
          ];
          for (var prop = 0; prop < scenery.length; prop += 1) {
            var item = scenery[prop];
            var px = sx(minX + (maxX - minX) * item[0]);
            var pz = sz(minZ + (maxZ - minZ) * item[1]);
            ctx.save();
            ctx.translate(px, pz);
            if (item[2] === "bush") {
              ctx.fillStyle = "rgba(15,23,42,0.28)";
              ctx.beginPath();
              ctx.arc(scale * 0.12, scale * 0.15, scale * 0.48, 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = "#1f6b3b";
              ctx.beginPath();
              ctx.arc(-scale * 0.18, 0, scale * 0.34, 0, Math.PI * 2);
              ctx.arc(scale * 0.18, -scale * 0.08, scale * 0.3, 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = "#4f9b4c";
              ctx.beginPath();
              ctx.arc(-scale * 0.05, -scale * 0.18, scale * 0.2, 0, Math.PI * 2);
              ctx.fill();
            } else if (item[2] === "tires") {
              ctx.fillStyle = "#111827";
              ctx.strokeStyle = "#64748b";
              ctx.lineWidth = Math.max(1, scale * 0.04);
              for (var tire = -1; tire <= 1; tire += 1) {
                ctx.beginPath();
                ctx.arc(tire * scale * 0.3, 0, scale * 0.24, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
              }
            } else {
              ctx.fillStyle = "#fb923c";
              ctx.beginPath();
              ctx.moveTo(0, -scale * 0.42);
              ctx.lineTo(scale * 0.3, scale * 0.34);
              ctx.lineTo(-scale * 0.3, scale * 0.34);
              ctx.closePath();
              ctx.fill();
              ctx.fillStyle = "#fff7ed";
              ctx.fillRect(-scale * 0.19, scale * 0.04, scale * 0.38, scale * 0.1);
            }
            ctx.restore();
          }
        }

        function drawRacingCurbs(ctx, points, trackWidth, sx, sz, scale) {
          for (var segment = 0; segment < points.length; segment += 1) {
            var a = points[segment];
            var b = points[(segment + 1) % points.length];
            var dx = b.x - a.x;
            var dz = b.z - a.z;
            var length = Math.max(0.01, Math.sqrt(dx * dx + dz * dz));
            var nx = -dz / length;
            var nz = dx / length;
            var chunks = Math.max(1, Math.ceil(length / 0.75));
            for (var chunk = 0; chunk < chunks; chunk += 1) {
              var t0 = chunk / chunks;
              var t1 = Math.min(1, (chunk + 0.82) / chunks);
              ctx.strokeStyle = (segment + chunk) % 2 === 0 ? "#ef4444" : "#fff7ed";
              ctx.lineWidth = Math.max(3, scale * 0.2);
              for (var side = -1; side <= 1; side += 2) {
                var edge = (trackWidth / 2) * side;
                ctx.beginPath();
                ctx.moveTo(sx(a.x + dx * t0 + nx * edge), sz(a.z + dz * t0 + nz * edge));
                ctx.lineTo(sx(a.x + dx * t1 + nx * edge), sz(a.z + dz * t1 + nz * edge));
                ctx.stroke();
              }
            }
          }
        }

        function drawRacingTrackFeatures(ctx, track, points, sx, sz, scale) {
          var drawSegmentOval = function (segmentIndex, color, highlight) {
            var a = points[segmentIndex % points.length];
            var b = points[(segmentIndex + 1) % points.length];
            var mx = (a.x + b.x) / 2;
            var mz = (a.z + b.z) / 2;
            ctx.save();
            ctx.translate(sx(mx), sz(mz));
            ctx.rotate(Math.atan2(b.z - a.z, b.x - a.x));
            ctx.scale(1.5, 0.65);
            ctx.beginPath();
            ctx.arc(0, 0, scale * 0.42, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.strokeStyle = highlight;
            ctx.lineWidth = Math.max(2, scale * 0.05);
            ctx.stroke();
            ctx.restore();
          };

          for (var oil = 0; oil < track.oilSegmentIndexes.length; oil += 1) {
            drawSegmentOval(track.oilSegmentIndexes[oil], "#111827", "#475569");
          }
          for (var puddle = 0; puddle < track.puddleSegmentIndexes.length; puddle += 1) {
            drawSegmentOval(track.puddleSegmentIndexes[puddle], "#38bdf8", "#bae6fd");
          }
          for (var boost = 0; boost < track.boostSegmentIndexes.length; boost += 1) {
            var index = track.boostSegmentIndexes[boost] % points.length;
            var start = points[index];
            var end = points[(index + 1) % points.length];
            ctx.save();
            ctx.translate(sx((start.x + end.x) / 2), sz((start.z + end.z) / 2));
            ctx.rotate(Math.atan2(end.z - start.z, end.x - start.x));
            ctx.strokeStyle = "#ef4444";
            ctx.lineWidth = Math.max(3, scale * 0.12);
            for (var arrow = -1; arrow <= 1; arrow += 1) {
              var arrowX = arrow * scale * 0.42;
              ctx.beginPath();
              ctx.moveTo(arrowX - scale * 0.2, -scale * 0.34);
              ctx.lineTo(arrowX + scale * 0.14, 0);
              ctx.lineTo(arrowX - scale * 0.2, scale * 0.34);
              ctx.stroke();
            }
            ctx.restore();
          }

          var finish = points[0];
          var finishNext = points[1];
          ctx.save();
          ctx.translate(sx(finish.x), sz(finish.z));
          ctx.rotate(Math.atan2(finishNext.z - finish.z, finishNext.x - finish.x));
          var columns = 8;
          var square = (track.width * scale) / columns;
          for (var row = 0; row < 2; row += 1) {
            for (var column = 0; column < columns; column += 1) {
              ctx.fillStyle = (row + column) % 2 === 0 ? "#f8fafc" : "#111827";
              ctx.fillRect(
                (row - 1) * square,
                (-track.width * scale) / 2 + column * square,
                square,
                square,
              );
            }
          }
          ctx.restore();
        }

        function drawRacingPickupSprite(ctx, pickup, x, y, size, available, respawnMs) {
          var cells = {
            rocketBoost: [0, 0],
            oilSlick: [1, 0],
            bomb: [1, 0],
            missile: [2, 0],
            rollCage: [3, 0],
            freezeRay: [0, 1],
            partyPopper: [1, 1],
            ammo: [2, 1],
            scrap: [3, 1],
          };
          var cell = cells[pickup.kind];
          var sprite = byId("racing-powerup-sprite-asset");
          ctx.save();
          ctx.globalAlpha = available ? 1 : 0.22;
          if (cell && sprite && sprite.complete && sprite.naturalWidth > 0) {
            var sourceWidth = sprite.naturalWidth / 4;
            var sourceHeight = sprite.naturalHeight / 2;
            ctx.drawImage(
              sprite,
              cell[0] * sourceWidth,
              cell[1] * sourceHeight,
              sourceWidth,
              sourceHeight,
              x - size / 2,
              y - size / 2,
              size,
              size,
            );
          } else {
            ctx.translate(x, y);
            ctx.rotate(Math.PI / 4);
            ctx.fillStyle = pickup.kind === "letter" ? "#ffffff" : "#facc15";
            ctx.fillRect(-size * 0.28, -size * 0.28, size * 0.56, size * 0.56);
            ctx.rotate(-Math.PI / 4);
            ctx.fillStyle = "#111827";
            ctx.font = "900 " + Math.max(10, size * 0.4) + "px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(pickup.letter || pickup.kind.charAt(0).toUpperCase(), 0, 0);
          }
          ctx.restore();
          if (!available && respawnMs > 0) {
            ctx.save();
            ctx.strokeStyle = "rgba(255,255,255,0.65)";
            ctx.lineWidth = Math.max(2, size * 0.07);
            ctx.beginPath();
            ctx.arc(
              x,
              y,
              size * 0.42,
              -Math.PI / 2,
              -Math.PI / 2 + Math.PI * 2 * (1 - respawnMs / 8000),
            );
            ctx.stroke();
            ctx.restore();
          }
        }

        function drawRacingSnapshot(snapshot) {
          var ctx = primaryCanvasContext;
          if (!ctx) return;
          var width = canvas.width;
          var height = canvas.height;
          var standingsRailWidth = Math.min(width * 0.22, 230 * (window.devicePixelRatio || 1));
          var courseWidth = width - standingsRailWidth;
          var points = snapshot.track.centerline || [];
          if (points.length < 2) return;
          var minX = points[0].x;
          var maxX = points[0].x;
          var minZ = points[0].z;
          var maxZ = points[0].z;
          for (var p = 1; p < points.length; p += 1) {
            minX = Math.min(minX, points[p].x);
            maxX = Math.max(maxX, points[p].x);
            minZ = Math.min(minZ, points[p].z);
            maxZ = Math.max(maxZ, points[p].z);
          }
          var margin = 80 * (window.devicePixelRatio || 1);
          var scale = Math.min(
            (courseWidth - margin * 2) / Math.max(1, maxX - minX),
            (height - margin * 2) / Math.max(1, maxZ - minZ),
          );
          racingOffsetX = (courseWidth - (maxX - minX) * scale) / 2 - minX * scale;
          racingOffsetZ = (height - (maxZ - minZ) * scale) / 2 - minZ * scale;
          racingScale = scale;
          var sx = racingScreenX;
          var sz = racingScreenZ;
          var trackLayerKey =
            snapshot.track.id + ":" + width + ":" + height + ":" + standingsRailWidth;
          if (!racingTrackLayer || racingTrackLayerKey !== trackLayerKey) {
            var layer = document.createElement("canvas");
            layer.width = width;
            layer.height = height;
            var layerCtx = layer.getContext("2d");
            layerCtx.fillStyle = snapshot.track.background || "#315b36";
            layerCtx.fillRect(0, 0, width, height);
            drawRacingBackdrop(layerCtx, width, height, scale);
            drawRacingScenery(layerCtx, minX, maxX, minZ, maxZ, sx, sz, scale);
            layerCtx.lineJoin = "round";
            layerCtx.lineCap = "round";
            layerCtx.beginPath();
            layerCtx.moveTo(sx(points[0].x), sz(points[0].z));
            for (var line = 1; line < points.length; line += 1) {
              layerCtx.lineTo(sx(points[line].x), sz(points[line].z));
            }
            layerCtx.closePath();
            layerCtx.strokeStyle = "#5f4b36";
            layerCtx.lineWidth = (snapshot.track.width + 1.4) * scale;
            layerCtx.stroke();
            layerCtx.strokeStyle = "#b7a27f";
            layerCtx.lineWidth = snapshot.track.width * scale;
            layerCtx.stroke();
            // Cached road wear adds depth without adding any steady-state draw calls.
            layerCtx.strokeStyle = "rgba(62,50,39,0.18)";
            layerCtx.lineWidth = Math.max(2, snapshot.track.width * scale * 0.46);
            layerCtx.stroke();
            layerCtx.setLineDash([12, 14]);
            layerCtx.strokeStyle = "rgba(255,255,255,0.45)";
            layerCtx.lineWidth = Math.max(2, scale * 0.08);
            layerCtx.stroke();
            layerCtx.setLineDash([]);
            drawRacingCurbs(layerCtx, points, snapshot.track.width, sx, sz, scale);
            drawRacingTrackFeatures(layerCtx, snapshot.track, points, sx, sz, scale);
            racingTrackLayer = layer;
            racingTrackLayerKey = trackLayerKey;
          }
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.drawImage(racingTrackLayer, 0, 0);
          racingPerPlayerById.clear();
          for (var pp = 0; pp < snapshot.perPlayerPickups.length; pp += 1) {
            racingPerPlayerById.set(
              snapshot.perPlayerPickups[pp].pickupId,
              snapshot.perPlayerPickups[pp],
            );
          }
          racingSharedById.clear();
          for (var sp = 0; sp < snapshot.sharedPickups.length; sp += 1) {
            racingSharedById.set(snapshot.sharedPickups[sp].pickupId, snapshot.sharedPickups[sp]);
          }
          for (var pick = 0; pick < snapshot.track.pickups.length; pick += 1) {
            var pickup = snapshot.track.pickups[pick];
            var perPlayer = racingPerPlayerById.get(pickup.id);
            var shared = racingSharedById.get(pickup.id);
            if (perPlayer && perPlayer.remainingPlayerIds.length === 0) continue;
            var pickupSize = Math.max(24, scale * 0.92);
            drawRacingPickupSprite(
              ctx,
              pickup,
              sx(pickup.position.x),
              sz(pickup.position.z),
              pickupSize,
              !shared || shared.available,
              shared ? shared.respawnMs : 0,
            );
          }
          for (var c = 0; c < snapshot.cars.length; c += 1) {
            var car = snapshot.cars[c];
            ctx.save();
            ctx.translate(sx(car.x), sz(car.z));
            ctx.rotate(car.heading);
            if (car.boostMs > 0) {
              ctx.fillStyle = "#fb923c";
              ctx.beginPath();
              ctx.moveTo(-scale * 0.48, scale * 0.16);
              ctx.lineTo(-scale * 0.95, scale * 0.27);
              ctx.lineTo(-scale * 0.7, scale * 0.04);
              ctx.lineTo(-scale * 0.48, scale * 0.04);
              ctx.closePath();
              ctx.fill();
              ctx.beginPath();
              ctx.moveTo(-scale * 0.48, -scale * 0.16);
              ctx.lineTo(-scale * 0.95, -scale * 0.27);
              ctx.lineTo(-scale * 0.7, -scale * 0.04);
              ctx.lineTo(-scale * 0.48, -scale * 0.04);
              ctx.closePath();
              ctx.fill();
            }
            drawRacingCar(ctx, car, scale);
            ctx.restore();
            if (car.playerId) {
              ctx.fillStyle = "#ffffff";
              ctx.font = "bold " + Math.max(13, scale * 0.3) + "px Arial";
              ctx.textAlign = "center";
              ctx.fillText(car.displayName, sx(car.x), sz(car.z) - scale * 0.82);
            }
          }
        }

        function tankCampaignEncounter(team) {
          return Math.max(1, Math.min(12, (team.level - 1) * 4 + team.wave));
        }

        function tankEnemyThreatSummary(snapshot) {
          var counts = { drone: 0, scout: 0, spinner: 0, hulk: 0 };
          var enemies = snapshot.enemies || [];
          for (var enemyIndex = 0; enemyIndex < enemies.length; enemyIndex += 1) {
            var kind = enemies[enemyIndex].kind || "drone";
            if (typeof counts[kind] === "number") counts[kind] += 1;
          }
          var definitions = [
            ["drone", "DRONE", "#f97316", "standard"],
            ["scout", "SCOUT", "#84cc16", "rapid"],
            ["spinner", "SPINNER", "#f472b6", "spread"],
            ["hulk", "HULK", "#a78bfa", "heavy"],
          ];
          var chips = "";
          for (
            var definitionIndex = 0;
            definitionIndex < definitions.length;
            definitionIndex += 1
          ) {
            var definition = definitions[definitionIndex];
            if (counts[definition[0]] <= 0) continue;
            chips +=
              '<div class="tv-threat-chip" style="--threat-color:' +
              definition[2] +
              '"><span>' +
              definition[1] +
              " ×" +
              counts[definition[0]] +
              "</span><span>" +
              definition[3] +
              "</span></div>";
          }
          return (
            '<section class="tv-threat-radar"><h2>THREAT RADAR</h2>' +
            (chips || '<div style="text-align:center;color:#64748b">Arena clear</div>') +
            "</section>"
          );
        }

        function hud(snapshot, playerCount, spectatorCount) {
          if (!snapshot) {
            return (
              '<section class="tv-hud">' +
              "<span><strong>Room</strong> " +
              escapeHtml(roomCode) +
              "</span>" +
              "<span><strong>Players</strong> " +
              playerCount +
              "</span>" +
              "<span><strong>Watching</strong> " +
              spectatorCount +
              "</span>" +
              "<span><strong>Net</strong> " +
              escapeHtml(connectionStatus) +
              "</span>" +
              "</section>"
            );
          }

          if (snapshot.team.state === "gameOver" || snapshot.team.state === "victory") {
            var players = snapshot.players || [];

            // 1. Most Damage
            var maxDamage = -1;
            var damageMvp = null;
            for (var p = 0; p < players.length; p++) {
              var val = players[p].damageDealt || 0;
              if (val > maxDamage) {
                maxDamage = val;
                damageMvp = players[p];
              }
            }

            // 2. Most Revives
            var maxRevives = 0;
            var revivesMvp = null;
            for (var p = 0; p < players.length; p++) {
              var val = players[p].revivesDealt || 0;
              if (val > maxRevives) {
                maxRevives = val;
                revivesMvp = players[p];
              }
            }

            // 3. Most Pickups
            var maxPickups = -1;
            var pickupsMvp = null;
            for (var p = 0; p < players.length; p++) {
              var val = players[p].pickupsCollected || 0;
              if (val > maxPickups) {
                maxPickups = val;
                pickupsMvp = players[p];
              }
            }

            // 4. Clutch Saves
            var maxClutch = 0;
            var clutchMvp = null;
            for (var p = 0; p < players.length; p++) {
              var val = players[p].clutchSaves || 0;
              if (val > maxClutch) {
                maxClutch = val;
                clutchMvp = players[p];
              }
            }

            var mvpCardsHtml = "";
            if (damageMvp && maxDamage > 0) {
              mvpCardsHtml +=
                '<div class="mvp-card damage">' +
                '<span class="mvp-title">💥 DAMAGE DEALER</span>' +
                '<span class="mvp-value">' +
                maxDamage +
                " dmg</span>" +
                '<span class="mvp-name" style="color:' +
                escapeAttribute(damageMvp.color) +
                '">' +
                escapeHtml(damageMvp.name) +
                "</span>" +
                "</div>";
            }
            if (pickupsMvp && maxPickups > 0) {
              mvpCardsHtml +=
                '<div class="mvp-card pickups">' +
                '<span class="mvp-title">⚡ COMBAT LOOTER</span>' +
                '<span class="mvp-value">' +
                maxPickups +
                " pickups</span>" +
                '<span class="mvp-name" style="color:' +
                escapeAttribute(pickupsMvp.color) +
                '">' +
                escapeHtml(pickupsMvp.name) +
                "</span>" +
                "</div>";
            }
            if (revivesMvp && maxRevives > 0) {
              mvpCardsHtml +=
                '<div class="mvp-card revives">' +
                '<span class="mvp-title">💚 LIFE SAVER</span>' +
                '<span class="mvp-value">' +
                maxRevives +
                " revives</span>" +
                '<span class="mvp-name" style="color:' +
                escapeAttribute(revivesMvp.color) +
                '">' +
                escapeHtml(revivesMvp.name) +
                "</span>" +
                "</div>";
            }
            if (clutchMvp && maxClutch > 0) {
              mvpCardsHtml +=
                '<div class="mvp-card clutch">' +
                '<span class="mvp-title">🏆 CLUTCH SAVIOR</span>' +
                '<span class="mvp-value">' +
                maxClutch +
                " saves</span>" +
                '<span class="mvp-name" style="color:' +
                escapeAttribute(clutchMvp.color) +
                '">' +
                escapeHtml(clutchMvp.name) +
                "</span>" +
                "</div>";
            }

            var isVictory = snapshot.team.state === "victory";
            var stateClass = isVictory ? "victory" : "defeat";
            var stateTitle = isVictory ? "VICTORY - CAMPAIGN CLEAR!" : "SQUAD DEFEATED";

            var squadTags = "";
            for (var p = 0; p < players.length; p++) {
              squadTags +=
                '<span class="squad-tag" style="border-color:' +
                escapeAttribute(players[p].color) +
                ';color:#ffffff">' +
                escapeHtml(players[p].name) +
                "</span>";
            }

            return (
              '<div class="tv-post-game-overlay">' +
              '<div class="tv-post-game-card ' +
              stateClass +
              '">' +
              '<h1 class="tv-post-game-title">' +
              stateTitle +
              "</h1>" +
              '<div class="tv-post-game-grid">' +
              '<div class="tv-section photo-finish">' +
              "<h2>PHOTO FINISH</h2>" +
              '<div class="finish-stats">' +
              '<div class="finish-stat-row"><span class="label">SQUAD SCORE</span><span class="value score-glow">' +
              snapshot.team.score +
              "</span></div>" +
              '<div class="finish-stat-row"><span class="label">WAVE REACHED</span><span class="value">' +
              snapshot.team.wave +
              "</span></div>" +
              '<div class="finish-stat-row"><span class="label">LEVEL REACHED</span><span class="value">' +
              snapshot.team.level +
              "</span></div>" +
              "</div>" +
              '<div class="squad-container">' +
              "<h3>SQUAD MEMBERS</h3>" +
              '<div class="squad-tags">' +
              squadTags +
              "</div>" +
              "</div>" +
              "</div>" +
              '<div class="tv-section mvps">' +
              "<h2>SQUAD MVPS</h2>" +
              '<div class="mvp-list">' +
              (mvpCardsHtml ||
                '<div style="color:#64748b;font-size:16px;text-align:center;padding:40px 0">Deploy next time to earn MVPs!</div>') +
              "</div>" +
              "</div>" +
              "</div>" +
              renderTvPostGameActions() +
              "</div>" +
              "</div>"
            );
          }

          var roster = "";
          var rankedPlayers = (snapshot.players || []).slice().sort(function (a, b) {
            return (b.score || 0) - (a.score || 0);
          });

          for (var i = 0; i < rankedPlayers.length; i += 1) {
            var player = rankedPlayers[i];
            var badgesHtml = "";
            if (player.modifiers) {
              if (player.modifiers.rapidFireTimer > 0)
                badgesHtml +=
                  '<span class="tv-badge" style="background:#ffd447;color:#000" title="Rapid Fire">⚡</span>';
              if (player.modifiers.shieldTimer > 0 || player.reviveShieldActive)
                badgesHtml +=
                  '<span class="tv-badge" style="background:#57e8ff;color:#000" title="Shield">🛡️</span>';
              if (player.modifiers.spreadShotTimer > 0)
                badgesHtml +=
                  '<span class="tv-badge" style="background:#ff65e5;color:#000" title="Spread Shot">⤱</span>';
              if (player.modifiers.heavyShellsTimer > 0)
                badgesHtml +=
                  '<span class="tv-badge" style="background:#ff6b35;color:#000" title="Heavy Shells">🔥</span>';
              if (player.modifiers.overdriveTimer > 0)
                badgesHtml +=
                  '<span class="tv-badge" style="background:#8cff3d;color:#000" title="Overdrive">🚀</span>';
            }
            if (player.synergyActive) {
              badgesHtml +=
                '<span class="tv-badge" style="background:#8cff3d;color:#000" title="Synergy Active">🔗</span>';
            }
            if (player.downed) {
              badgesHtml +=
                '<span class="tv-badge tv-badge-downed" style="background:#ef4444;color:#fff">DOWNED</span>';
            }
            if (!player.connected) {
              badgesHtml +=
                '<span class="tv-badge" style="background:#334155;color:#94a3b8">DISC</span>';
            }

            roster +=
              '<div class="roster-row">' +
              '<span class="rank-num">#' +
              (i + 1) +
              "</span>" +
              '<span class="swatch" style="background:' +
              escapeAttribute(player.color) +
              '"></span>' +
              '<div class="player-info">' +
              '<div class="player-name-row">' +
              '<span class="player-name">' +
              escapeHtml(player.name) +
              "</span>" +
              '<span class="player-score">' +
              (player.score || 0) +
              " pts</span>" +
              "</div>" +
              '<div class="hp-bar-bg">' +
              '<div class="hp-bar-fg" style="width:' +
              Math.max(0, Math.min(100, player.hp)) +
              "%;background:" +
              (player.hp < 35 ? "#ef4444" : player.hp < 65 ? "#eab308" : "#22c55e") +
              '"></div>' +
              "</div>" +
              "</div>" +
              '<div class="badge-container">' +
              badgesHtml +
              "</div>" +
              "</div>";
          }

          var tipsHtml =
            '<section class="tv-tips-panel">' +
            tankEnemyThreatSummary(snapshot) +
            '<h2 style="margin-top:0;color:#93c5fd;font-size:22px;border-bottom:1px solid #334155;padding-bottom:10px;text-align:center">CO-OP TIPS</h2>' +
            '<div class="tip-card" style="margin-bottom:16px">' +
            '<strong style="color:#22c55e;font-size:16px">💚 Teammate Revival</strong>' +
            '<p style="font-size:13px;line-height:1.4;margin:4px 0 0">When a teammate is downed, drive next to them and stand inside their green revival circle for 3 seconds to revive them!</p>' +
            "</div>" +
            '<div class="tip-card" style="margin-bottom:16px">' +
            '<strong style="color:#ffd447;font-size:16px">⚡ Power-ups</strong>' +
            '<p style="font-size:13px;line-height:1.4;margin:4px 0 0">Collect glowing cubes to upgrade your tank. Repair restores health, Rapid Fire increases reload speed, and Shield blocks all damage!</p>' +
            "</div>" +
            '<div class="tip-card" style="margin-bottom:16px">' +
            '<strong style="color:#a3e635;font-size:16px">🔗 Proximity Synergy</strong>' +
            '<p style="font-size:13px;line-height:1.4;margin:4px 0 0">Stay close to your teammates (within 2 units) to link together and gain a +20% firing rate bonus!</p>' +
            "</div>" +
            '<div class="tip-card">' +
            '<strong style="color:#fb923c;font-size:16px">🛡️ Friendly Bumping</strong>' +
            '<p style="font-size:13px;line-height:1.4;margin:4px 0 0">Your shells don\'t hurt teammates—they push them! Shoot a teammate to boost their speed or push them out of danger.</p>' +
            "</div>" +
            "</section>";

          return (
            '<section class="tv-hud">' +
            (snapshot.team.mode === "campaign"
              ? "<span><strong>Campaign</strong> " +
                tankCampaignEncounter(snapshot.team) +
                "/12</span>" +
                "<span><strong>Stage</strong> " +
                snapshot.team.level +
                "-" +
                snapshot.team.wave +
                "</span>"
              : "<span><strong>Wave</strong> " +
                snapshot.team.wave +
                "</span><span><strong>Run</strong> Survival</span>") +
            "<span><strong>Score</strong> " +
            snapshot.team.score +
            "</span>" +
            "<span><strong>Lives</strong> " +
            snapshot.team.lives +
            "</span>" +
            (snapshot.team.chaosEffect
              ? "<span><strong>Chaos</strong> " +
                escapeHtml(snapshot.team.chaosEffect.label) +
                " " +
                Math.ceil(snapshot.team.chaosEffect.timer) +
                "s</span>"
              : "") +
            "<span><strong>Room</strong> " +
            escapeHtml(roomCode) +
            "</span>" +
            "<span><strong>Net</strong> " +
            escapeHtml(connectionStatus) +
            "</span>" +
            "</section>" +
            '<section class="tv-roster">' +
            roster +
            "</section>" +
            tipsHtml
          );
        }

        // Countdown GO! flash tracking (module-level)
        var goFlashUntil = 0;
        var lastCountdownWasActive = false;
        var tankArenaLayer = null;
        var tankArenaLayerKey = "";

        function drawSnapshot(snapshot) {
          var ctx = primaryCanvasContext;
          if (!ctx || !snapshot) return;
          var width = canvas.width;
          var height = canvas.height;
          var now = performance.now();
          ctx.clearRect(0, 0, width, height);

          // Track projectile impacts without allocating a Set and position objects every frame.
          previousProjectiles.forEach(function (tracked) {
            tracked.seen = false;
          });
          for (var p = 0; p < snapshot.projectiles.length; p += 1) {
            var shot = snapshot.projectiles[p];
            var tracked = previousProjectiles.get(shot.id);
            if (!tracked) {
              tracked = { x: shot.position.x, y: shot.position.z, seen: true };
              previousProjectiles.set(shot.id, tracked);
              sfx.playShoot();
            } else {
              tracked.x = shot.position.x;
              tracked.y = shot.position.z;
              tracked.seen = true;
            }
          }

          previousProjectiles.forEach(function (tracked, id) {
            if (tracked.seen) return;
            var ring = impactRings.length >= 32 ? impactRings.shift() : null;
            if (!ring) ring = { x: 0, y: 0, radius: 2, maxRadius: 18, age: 0, maxAge: 12 };
            ring.x = tracked.x;
            ring.y = tracked.y;
            ring.age = 0;
            impactRings.push(ring);
            previousProjectiles.delete(id);
          });

          var arena = snapshot.arena;
          var margin = 54;
          var scale = Math.min(
            (width - margin * 2) / arena.width,
            (height - margin * 2) / arena.height,
          );
          tankOffsetX = (width - arena.width * scale) / 2;
          tankOffsetY = (height - arena.height * scale) / 2;
          tankScale = scale;
          tankArenaWidth = arena.width;

          ctx.drawImage(
            tankArenaLayerFor(arena, width, height, scale, tankOffsetX, tankOffsetY),
            0,
            0,
          );

          // Draw and update a hard-bounded pool of impact rings in place.
          ctx.save();
          ctx.lineWidth = 2;
          var retainedRingCount = 0;
          for (var r = 0; r < impactRings.length; r += 1) {
            var ring = impactRings[r];
            ring.age++;
            var pct = ring.age / ring.maxAge;
            var currentRadius = ring.radius + (ring.maxRadius - ring.radius) * pct;
            ctx.strokeStyle = "rgba(253, 224, 71, " + (1 - pct) + ")";
            ctx.lineWidth = 2 + (1 - pct) * 3;
            ctx.beginPath();
            ctx.arc(tankScreenX(ring.x), tankScreenY(ring.y), currentRadius, 0, Math.PI * 2);
            ctx.stroke();
            ctx.fillStyle = "rgba(255, 120, 45, " + 0.35 * (1 - pct) + ")";
            circle(ctx, tankScreenX(ring.x), tankScreenY(ring.y), Math.max(1, 5 * (1 - pct)));

            if (ring.age < ring.maxAge) {
              impactRings[retainedRingCount] = ring;
              retainedRingCount += 1;
            }
          }
          impactRings.length = retainedRingCount;
          ctx.restore();

          // Draw synergy links with native line-glow approximation (fast on WebOS)
          ctx.save();
          for (var i = 0; i < snapshot.players.length; i++) {
            for (var j = i + 1; j < snapshot.players.length; j++) {
              var p1 = snapshot.players[i];
              var p2 = snapshot.players[j];
              if (p1.synergyActive && p2.synergyActive) {
                // Wide soft neon backing glow
                ctx.strokeStyle = "rgba(140, 255, 61, 0.22)";
                ctx.lineWidth = 9;
                ctx.beginPath();
                ctx.moveTo(tankScreenX(p1.position.x), tankScreenY(p1.position.z));
                ctx.lineTo(tankScreenX(p2.position.x), tankScreenY(p2.position.z));
                ctx.stroke();

                // Core solid green line
                ctx.strokeStyle = "#8cff3d";
                ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.moveTo(tankScreenX(p1.position.x), tankScreenY(p1.position.z));
                ctx.lineTo(tankScreenX(p2.position.x), tankScreenY(p2.position.z));
                ctx.stroke();
              }
            }
          }
          ctx.restore();

          for (var p = 0; p < snapshot.projectiles.length; p += 1) {
            var shot = snapshot.projectiles[p];
            var shotX = tankScreenX(shot.position.x);
            var shotY = tankScreenY(shot.position.z);
            var shotRadius = Math.max(3, scale * ((shot.size || 0.18) * 0.6));
            var shotVariant = shot.variant || "shell";
            var shotColor =
              shot.owner === "player"
                ? "#fde047"
                : shotVariant === "rapid"
                  ? "#67e8f9"
                  : shotVariant === "heavy"
                    ? "#fb923c"
                    : shotVariant === "spark"
                      ? "#f0abfc"
                      : "#fb7185";
            ctx.globalAlpha = 0.2;
            ctx.fillStyle = shotColor;
            circle(ctx, shotX, shotY, shotRadius * 2.8);
            ctx.globalAlpha = 0.55;
            circle(ctx, shotX, shotY, shotRadius * 1.7);
            ctx.globalAlpha = 1;
            ctx.fillStyle = shotVariant === "heavy" ? "#ffedd5" : "#fff7d6";
            circle(ctx, shotX, shotY, shotRadius);
            if (shotVariant === "spark") {
              ctx.strokeStyle = "#ffffff";
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(shotX - shotRadius * 1.5, shotY);
              ctx.lineTo(shotX + shotRadius * 1.5, shotY);
              ctx.moveTo(shotX, shotY - shotRadius * 1.5);
              ctx.lineTo(shotX, shotY + shotRadius * 1.5);
              ctx.stroke();
            }
          }
          for (var e = 0; e < snapshot.enemies.length; e += 1) {
            var enemy = snapshot.enemies[e];
            var enemyRadius = scale * (enemy.radius || 0.42);
            drawTankEnemy(
              ctx,
              enemy,
              tankScreenX(enemy.position.x),
              tankScreenY(enemy.position.z),
              scale,
            );
            unitStatus(
              ctx,
              tankScreenX(enemy.position.x),
              tankScreenY(enemy.position.z),
              enemyRadius,
              enemy.hp,
              enemy.maxHp,
              "",
              tankEnemyColor(enemy.kind),
            );
          }
          for (var pl = 0; pl < snapshot.players.length; pl += 1) {
            var player = snapshot.players[pl];
            if (!player.connected) {
              // Greyed-out ghost for disconnected players
              ctx.save();
              ctx.globalAlpha = 0.35;
              tank(
                ctx,
                tankScreenX(player.position.x),
                tankScreenY(player.position.z),
                scale * 0.38 * (player.scale || 1),
                "#475569",
                player.hullAngle,
                player.turretAngle,
                true,
                false,
              );
              ctx.restore();
              // DISC label above ghost
              ctx.save();
              ctx.globalAlpha = 0.7;
              ctx.fillStyle = "#94a3b8";
              ctx.font = "bold " + Math.round(scale * 0.28) + "px monospace";
              ctx.textAlign = "center";
              ctx.fillText(
                "DISC",
                tankScreenX(player.position.x),
                tankScreenY(player.position.z) - scale * 0.55,
              );
              ctx.restore();
            } else {
              tank(
                ctx,
                tankScreenX(player.position.x),
                tankScreenY(player.position.z),
                scale * 0.38 * (player.scale || 1),
                player.color,
                player.hullAngle,
                player.turretAngle,
                player.downed,
                false,
              );
              if (player.shieldActive || player.reviveShieldActive) {
                ctx.save();
                ctx.strokeStyle = player.reviveShieldActive ? "#86efac" : "#67e8f9";
                ctx.lineWidth = Math.max(2, scale * 0.08);
                ctx.globalAlpha = 0.65 + Math.sin(now * 0.008) * 0.15;
                ctx.beginPath();
                ctx.arc(
                  tankScreenX(player.position.x),
                  tankScreenY(player.position.z),
                  scale * 0.62 * (player.scale || 1),
                  0,
                  Math.PI * 2,
                );
                ctx.stroke();
                ctx.restore();
              }
              if (player.dashing) {
                drawSpeedLines(
                  ctx,
                  tankScreenX(player.position.x),
                  tankScreenY(player.position.z),
                  scale * 0.38,
                  player.hullAngle,
                  player.color,
                );
              }
            }
            unitStatus(
              ctx,
              tankScreenX(player.position.x),
              tankScreenY(player.position.z),
              scale * 0.38 * (player.scale || 1),
              player.hp,
              player.maxHp,
              player.name,
              player.color,
            );
          }

          // Draw power-up icons as coloured diamonds
          for (var pu = 0; pu < snapshot.powerUps.length; pu += 1) {
            var powerUp = snapshot.powerUps[pu];
            var puColor = tankPowerUpColors[powerUp.kind] || "#f8fafc";
            var px = tankScreenX(powerUp.position.x);
            var pz = tankScreenY(powerUp.position.z);
            var pr = scale * 0.28;
            var pulse = 1 + Math.sin(now * 0.006 + pu) * 0.12;
            ctx.save();
            ctx.translate(px, pz);
            ctx.rotate(now * 0.0012 + pu * 0.7);
            ctx.strokeStyle = puColor;
            ctx.fillStyle = puColor;
            ctx.globalAlpha = 0.22;
            ctx.lineWidth = Math.max(2, scale * 0.06);
            ctx.beginPath();
            ctx.arc(0, 0, pr * 1.65 * pulse, 0, Math.PI * 2);
            ctx.stroke();
            ctx.globalAlpha = 0.88;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(0, -pr * pulse);
            ctx.lineTo(pr * pulse, 0);
            ctx.lineTo(0, pr * pulse);
            ctx.lineTo(-pr * pulse, 0);
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = "#f8fafc";
            ctx.globalAlpha = 0.8;
            ctx.stroke();
            ctx.restore();
          }

          // Countdown overlay drawn on canvas (3/2/1/GO!)
          var countdown = snapshot.team.countdown;
          if (countdown > 0) {
            if (!lastCountdownWasActive) lastCountdownWasActive = true;
            var countNum = Math.ceil(countdown);
            var fontSize = Math.min(width, height) * 0.22;
            ctx.save();
            ctx.globalAlpha = 0.92;
            ctx.fillStyle = "rgba(8, 17, 31, 0.55)";
            ctx.fillRect(0, 0, width, height);
            ctx.globalAlpha = 1.0;
            ctx.font = "bold " + fontSize + "px Arial, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.strokeStyle = "#020617";
            ctx.lineWidth = fontSize * 0.08;
            ctx.strokeText(String(countNum), width / 2, height / 2);
            ctx.fillStyle = countNum <= 1 ? "#f97316" : countNum <= 2 ? "#fbbf24" : "#38bdf8";
            ctx.fillText(String(countNum), width / 2, height / 2);
            ctx.restore();
          } else if (lastCountdownWasActive) {
            // Countdown just ended — flash GO!
            lastCountdownWasActive = false;
            goFlashUntil = now + 900;
          }

          if (goFlashUntil > now) {
            var fontSize2 = Math.min(width, height) * 0.2;
            ctx.save();
            ctx.globalAlpha = Math.min(1, (goFlashUntil - now) / 300);
            ctx.font = "bold " + fontSize2 + "px Arial, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.strokeStyle = "#020617";
            ctx.lineWidth = fontSize2 * 0.08;
            ctx.strokeText("GO!", width / 2, height / 2);
            ctx.fillStyle = "#22c55e";
            ctx.fillText("GO!", width / 2, height / 2);
            ctx.restore();
          }

          // Game Over / Victory overlay
          if (snapshot.team.state === "gameOver" || snapshot.team.state === "victory") {
            var isVictory = snapshot.team.state === "victory";
            ctx.save();
            ctx.globalAlpha = 0.78;
            ctx.fillStyle = isVictory ? "rgba(12, 60, 12, 0.92)" : "rgba(60, 8, 8, 0.92)";
            ctx.fillRect(0, 0, width, height);
            ctx.globalAlpha = 1.0;
            var titleSize = Math.min(width, height) * 0.12;
            ctx.font = "bold " + titleSize + "px Arial, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillStyle = isVictory ? "#4ade80" : "#f87171";
            ctx.strokeStyle = "#020617";
            ctx.lineWidth = titleSize * 0.06;
            var titleY = height / 2 - titleSize * 0.8;
            ctx.strokeText(isVictory ? "VICTORY!" : "GAME OVER", width / 2, titleY);
            ctx.fillText(isVictory ? "VICTORY!" : "GAME OVER", width / 2, titleY);
            var subSize = titleSize * 0.42;
            ctx.font = "bold " + subSize + "px Arial, sans-serif";
            ctx.fillStyle = "#e2e8f0";
            ctx.lineWidth = subSize * 0.06;
            ctx.strokeText(
              "Score: " + snapshot.team.score + "  |  Wave " + snapshot.team.wave,
              width / 2,
              titleY + titleSize * 1.1,
            );
            ctx.fillText(
              "Score: " + snapshot.team.score + "  |  Wave " + snapshot.team.wave,
              width / 2,
              titleY + titleSize * 1.1,
            );
            ctx.restore();
          }
        }

        function tankArenaLayerFor(arena, width, height, scale, offsetX, offsetY) {
          var key =
            arena.id +
            ":" +
            width +
            ":" +
            height +
            ":" +
            Math.round(scale * 100) +
            ":" +
            Math.round(offsetX) +
            ":" +
            Math.round(offsetY);
          if (tankArenaLayer && tankArenaLayerKey === key) return tankArenaLayer;

          var layer = document.createElement("canvas");
          layer.width = width;
          layer.height = height;
          var ctx = layer.getContext("2d");
          if (!ctx) return layer;
          var arenaWidth = arena.width * scale;
          var arenaHeight = arena.height * scale;
          var edgeX = offsetX + arenaWidth;
          var edgeY = offsetY + arenaHeight;

          var backdrop = ctx.createLinearGradient(0, 0, 0, height);
          backdrop.addColorStop(0, "#0b1724");
          backdrop.addColorStop(0.55, "#07111b");
          backdrop.addColorStop(1, "#03070d");
          ctx.fillStyle = backdrop;
          ctx.fillRect(0, 0, width, height);

          ctx.fillStyle = "#101c24";
          ctx.fillRect(offsetX - 15, offsetY - 15, arenaWidth + 30, arenaHeight + 30);
          ctx.strokeStyle = "#38bdf8";
          ctx.globalAlpha = 0.28;
          ctx.lineWidth = 3;
          ctx.strokeRect(offsetX - 9, offsetY - 9, arenaWidth + 18, arenaHeight + 18);
          ctx.globalAlpha = 1;

          ctx.fillStyle = "#18262d";
          ctx.fillRect(offsetX, offsetY, arenaWidth, arenaHeight);
          for (var row = 0; row < arena.height; row += 1) {
            for (var col = 0; col < arena.width; col += 1) {
              var tileX = offsetX + (arena.width - col - 1) * scale;
              var tileY = offsetY + row * scale;
              if (arena.tiles[row] && arena.tiles[row][col] === 1) continue;
              ctx.fillStyle = (row + col) % 2 === 0 ? "#1d2e34" : "#1a292f";
              ctx.fillRect(tileX, tileY, scale, scale);
              if ((row * 7 + col * 11) % 13 === 0) {
                ctx.fillStyle = "rgba(91, 148, 151, 0.14)";
                ctx.fillRect(tileX + scale * 0.18, tileY + scale * 0.72, scale * 0.28, 2);
              }
            }
          }

          ctx.strokeStyle = "rgba(125, 211, 252, 0.12)";
          ctx.lineWidth = 1;
          for (col = 0; col <= arena.width; col += 1) {
            var gridX = offsetX + col * scale;
            ctx.beginPath();
            ctx.moveTo(gridX, offsetY);
            ctx.lineTo(gridX, edgeY);
            ctx.stroke();
          }
          for (row = 0; row <= arena.height; row += 1) {
            var gridY = offsetY + row * scale;
            ctx.beginPath();
            ctx.moveTo(offsetX, gridY);
            ctx.lineTo(edgeX, gridY);
            ctx.stroke();
          }

          for (row = 0; row < arena.tiles.length; row += 1) {
            var cols = arena.tiles[row] || [];
            for (col = 0; col < cols.length; col += 1) {
              if (cols[col] !== 1) continue;
              var wallX = offsetX + (arena.width - col - 1) * scale;
              var wallY = offsetY + row * scale;
              var inset = Math.max(2, scale * 0.07);
              ctx.fillStyle = "rgba(0, 0, 0, 0.42)";
              ctx.fillRect(wallX + inset, wallY + inset, scale, scale);
              ctx.fillStyle = "#344754";
              ctx.fillRect(wallX, wallY, scale - inset, scale - inset);
              ctx.fillStyle = "#526b78";
              ctx.beginPath();
              ctx.moveTo(wallX, wallY);
              ctx.lineTo(wallX + scale - inset, wallY);
              ctx.lineTo(wallX + scale - inset * 1.8, wallY + inset * 1.5);
              ctx.lineTo(wallX + inset, wallY + inset * 1.5);
              ctx.closePath();
              ctx.fill();
              ctx.fillStyle = "#22323c";
              ctx.fillRect(wallX, wallY + scale - inset * 2, scale - inset, inset * 2);
              ctx.strokeStyle = "rgba(147, 197, 253, 0.2)";
              ctx.lineWidth = 1;
              ctx.strokeRect(wallX + 1, wallY + 1, scale - inset - 2, scale - inset - 2);
              if ((row + col) % 3 === 0) {
                ctx.fillStyle = "#f59e0b";
                ctx.globalAlpha = 0.45;
                ctx.fillRect(wallX + inset, wallY + scale * 0.48, scale * 0.32, inset * 0.7);
                ctx.globalAlpha = 1;
              }
            }
          }

          ctx.strokeStyle = "rgba(226, 232, 240, 0.32)";
          ctx.lineWidth = 2;
          ctx.strokeRect(offsetX, offsetY, arenaWidth, arenaHeight);

          // Static warning markers strengthen the arena frame at zero steady-state cost.
          var markerCount = 16;
          var markerWidth = arenaWidth / markerCount;
          var markerHeight = Math.max(3, scale * 0.09);
          for (var marker = 0; marker < markerCount; marker += 1) {
            ctx.fillStyle = marker % 2 === 0 ? "#f59e0b" : "#18262d";
            ctx.fillRect(offsetX + marker * markerWidth, offsetY, markerWidth, markerHeight);
            ctx.fillRect(
              offsetX + marker * markerWidth,
              edgeY - markerHeight,
              markerWidth,
              markerHeight,
            );
          }
          tankArenaLayer = layer;
          tankArenaLayerKey = key;
          return layer;
        }

        function circle(ctx, x, y, radius) {
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
        }

        var tankEnemySpriteCache = null;

        function tankEnemyColor(kind) {
          if (kind === "scout") return "#84cc16";
          if (kind === "hulk") return "#a78bfa";
          if (kind === "spinner") return "#f472b6";
          return "#f97316";
        }

        function drawTankEnemy(ctx, enemy, x, y, scale) {
          var kind =
            enemy.kind === "scout" || enemy.kind === "hulk" || enemy.kind === "spinner"
              ? enemy.kind
              : "drone";
          var radius = scale * (enemy.radius || 0.42);
          if (!tankEnemySpriteCache) {
            tankEnemySpriteCache = {};
            var tankKinds = ["drone", "scout", "hulk", "spinner"];
            for (var tankKindIndex = 0; tankKindIndex < tankKinds.length; tankKindIndex += 1) {
              var tankKind = tankKinds[tankKindIndex];
              tankEnemySpriteCache[tankKind] = {
                hull: document.getElementById("tank-" + tankKind + "-hull-asset"),
                turret: document.getElementById("tank-" + tankKind + "-turret-asset"),
              };
            }
          }
          var sprites = tankEnemySpriteCache[kind];
          if (
            !sprites ||
            !sprites.hull ||
            !sprites.turret ||
            !sprites.hull.complete ||
            !sprites.turret.complete ||
            sprites.hull.naturalWidth <= 0 ||
            sprites.turret.naturalWidth <= 0
          ) {
            tank(
              ctx,
              x,
              y,
              radius,
              tankEnemyColor(kind),
              enemy.hullAngle,
              enemy.turretAngle,
              false,
              true,
            );
            return;
          }

          ctx.save();
          ctx.translate(x, y);
          ctx.imageSmoothingEnabled = false;
          ctx.fillStyle = "rgba(0, 0, 0, 0.48)";
          ctx.beginPath();
          ctx.ellipse(radius * 0.16, radius * 0.28, radius * 1.2, radius * 0.78, 0, 0, Math.PI * 2);
          ctx.fill();

          ctx.save();
          ctx.rotate(Math.PI - enemy.hullAngle);
          var hullWidth = radius * 2.55;
          var hullHeight = hullWidth * (sprites.hull.naturalHeight / sprites.hull.naturalWidth);
          ctx.drawImage(sprites.hull, -hullWidth / 2, -hullHeight / 2, hullWidth, hullHeight);
          ctx.restore();

          ctx.rotate(Math.PI - enemy.turretAngle);
          var turretWidth = radius * 1.65;
          var turretHeight =
            turretWidth * (sprites.turret.naturalHeight / sprites.turret.naturalWidth);
          ctx.drawImage(
            sprites.turret,
            -turretWidth / 2,
            -turretHeight * 0.58,
            turretWidth,
            turretHeight,
          );
          ctx.restore();
        }

        function tank(ctx, x, y, radius, color, hullAngle, turretAngle, isDowned, isEnemy) {
          ctx.save();
          ctx.translate(x, y);

          ctx.fillStyle = "rgba(0, 0, 0, 0.48)";
          ctx.beginPath();
          ctx.ellipse(
            radius * 0.16,
            radius * 0.28,
            radius * 1.15,
            radius * 0.78,
            0,
            0,
            Math.PI * 2,
          );
          ctx.fill();

          var hullRotation = Math.PI - hullAngle;
          ctx.rotate(hullRotation);

          if (!isDowned) {
            ctx.fillStyle = color;
            ctx.globalAlpha = 0.16;
            ctx.fillRect(-radius * 1.18, -radius * 1.28, radius * 2.36, radius * 2.56);
            ctx.globalAlpha = 1;
          }

          ctx.fillStyle = "#111820";
          ctx.fillRect(-radius * 1.08, -radius * 0.92, radius * 0.34, radius * 1.84);
          ctx.fillRect(radius * 0.74, -radius * 0.92, radius * 0.34, radius * 1.84);
          ctx.fillStyle = "#50606a";
          for (var tread = -0.72; tread <= 0.72; tread += 0.36) {
            ctx.fillRect(-radius * 1.06, radius * tread, radius * 0.3, radius * 0.12);
            ctx.fillRect(radius * 0.76, radius * tread, radius * 0.3, radius * 0.12);
          }

          ctx.fillStyle = isDowned ? "#475569" : color;
          ctx.strokeStyle = "#020617";
          ctx.lineWidth = Math.max(2, radius * 0.1);
          ctx.beginPath();
          ctx.moveTo(-radius * 0.7, -radius);
          ctx.lineTo(radius * 0.7, -radius);
          ctx.lineTo(radius * 0.9, -radius * 0.68);
          ctx.lineTo(radius * 0.9, radius * 0.82);
          ctx.lineTo(radius * 0.58, radius);
          ctx.lineTo(-radius * 0.58, radius);
          ctx.lineTo(-radius * 0.9, radius * 0.82);
          ctx.lineTo(-radius * 0.9, -radius * 0.68);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = isDowned ? "#64748b" : isEnemy ? "#fdba74" : "rgba(255,255,255,0.42)";
          ctx.fillRect(-radius * 0.55, -radius * 0.76, radius * 1.1, radius * 0.18);

          ctx.rotate(-hullRotation);
          ctx.rotate(Math.PI - turretAngle);
          ctx.fillStyle = isDowned ? "#334155" : isEnemy ? "#9a3412" : "#18212b";
          ctx.strokeStyle = "#020617";
          ctx.lineWidth = Math.max(2, radius * 0.09);
          ctx.fillRect(-radius * 0.14, -radius * 1.5, radius * 0.28, radius * 1.38);
          ctx.strokeRect(-radius * 0.14, -radius * 1.5, radius * 0.28, radius * 1.38);
          ctx.fillStyle = isDowned ? "#475569" : color;
          circle(ctx, 0, 0, radius * 0.52);
          ctx.stroke();
          ctx.fillStyle = isEnemy ? "#fed7aa" : "rgba(255,255,255,0.6)";
          circle(ctx, -radius * 0.12, -radius * 0.15, radius * 0.13);

          if (isDowned) {
            ctx.strokeStyle = "#f87171";
            ctx.lineWidth = Math.max(3, radius * 0.14);
            ctx.beginPath();
            ctx.moveTo(-radius * 0.42, -radius * 0.42);
            ctx.lineTo(radius * 0.42, radius * 0.42);
            ctx.moveTo(radius * 0.42, -radius * 0.42);
            ctx.lineTo(-radius * 0.42, radius * 0.42);
            ctx.stroke();
          }
          ctx.restore();
        }

        function unitStatus(ctx, x, y, radius, hp, maxHp, name, color) {
          var ratio = Math.max(0, Math.min(1, hp / Math.max(1, maxHp || 1)));
          var barWidth = radius * 2.4;
          var barHeight = Math.max(3, radius * 0.16);
          var barY = y - radius * 1.65;
          ctx.save();
          ctx.fillStyle = "rgba(2, 6, 23, 0.88)";
          ctx.fillRect(x - barWidth / 2 - 2, barY - 2, barWidth + 4, barHeight + 4);
          ctx.fillStyle = ratio < 0.3 ? "#ef4444" : ratio < 0.65 ? "#facc15" : "#22c55e";
          ctx.fillRect(x - barWidth / 2, barY, barWidth * ratio, barHeight);
          if (name) {
            ctx.font = "bold " + Math.max(11, Math.round(radius * 0.52)) + "px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            ctx.strokeStyle = "#020617";
            ctx.lineWidth = 3;
            ctx.strokeText(name, x, barY - 5);
            ctx.fillStyle = color;
            ctx.fillText(name, x, barY - 5);
          }
          ctx.restore();
        }

        function drawSpeedLines(ctx, x, y, radius, angle, color) {
          var backX = Math.sin(angle) * radius;
          var backY = -Math.cos(angle) * radius;
          var sideX = Math.cos(angle) * radius * 0.5;
          var sideY = Math.sin(angle) * radius * 0.5;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.65;
          ctx.lineWidth = Math.max(2, radius * 0.1);
          for (var line = -1; line <= 1; line += 1) {
            ctx.beginPath();
            ctx.moveTo(x + backX + sideX * line, y + backY + sideY * line);
            ctx.lineTo(x + backX * 2.2 + sideX * line, y + backY * 2.2 + sideY * line);
            ctx.stroke();
          }
          ctx.restore();
        }

        window.onresize = function () {
          resizeCanvas();
          if (latestSnapshot) drawSnapshot(latestSnapshot);
        };

        document.addEventListener("click", function (e) {
          try {
            sfx.initCtx();
          } catch (_e) {}
          var statusEl = byId("status");
          if (statusEl) {
            statusEl.innerHTML =
              "Click: " +
              e.target.tagName +
              " id=" +
              e.target.id +
              " x=" +
              Math.round(e.clientX) +
              " y=" +
              Math.round(e.clientY);
          }
        });

        renderRoomList([]);
        if (roomCode) joinRoom(roomCode);
        else listRooms();
      })();
    